import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import FullCalendar from '@fullcalendar/react';
import tippy from 'tippy.js';
import rrulePlugin from '@fullcalendar/rrule';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import * as XLSX from 'xlsx';
import { jsPDF } from "jspdf";
import autoTable from 'jspdf-autotable';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import MultiRoomSelector from '../components/MultiRoomSelector';
import { 
  XMarkIcon, 
  PlusIcon,
  DocumentTextIcon,
  ReceiptPercentIcon,
  TrashIcon, 
  PencilIcon, 
  ClockIcon, 
  MapPinIcon, 
  UserIcon,
  HomeIcon, 
  ChartBarIcon,
  ClipboardIcon,
  CheckCircleIcon as CheckIcon,
  UserGroupIcon,
  CalendarIcon,
  ArrowDownTrayIcon,
  InformationCircleIcon,
  CurrencyDollarIcon,
  TicketIcon
} from '@heroicons/react/24/outline';


const Calendar = () => {
  const { user, api } = useContext(AuthContext);
  const calendarRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const dateParam = queryParams.get('date');
  
  // Référence pour le titre du calendrier (pour l'export)
  const calendarTitleRef = useRef(null);

  // état pour les invités 
  const [eventGuests, setEventGuests] = useState([]);
  
  // États pour les données
  const [events, setEvents] = useState([]);
  
  // État pour la vue du calendrier (persistance)
  const [calendarView, setCalendarView] = useState(() => {
    // Récupérer la vue sauvegardée dans le sessionStorage ou utiliser la vue par défaut
    return sessionStorage.getItem('calendarView') || 'timeGridWeek';
  });
  
  // Pour chaque série récurrente, on garde le tableau complet des jours
  const recurrenceMap = useRef({});
  useEffect(() => {
    const map = {};
    events.forEach(e => {
      if (e.recurrenceId && !map[e.recurrenceId]) {
        map[e.recurrenceId] = Array.isArray(e.recurrenceDays)
          ? e.recurrenceDays.map(Number)
          : [];
      }
    });
    recurrenceMap.current = map;
  }, [events]);

  const seriesDatesMap  = useRef({});

useEffect(() => {
  const daysMap = {};
  const datesMap = {};

  events.forEach(e => {
    const id = e.extendedProps.recurrenceId;
    // 1) on garde le tableau des jours comme avant
    if (id && !daysMap[id]) {
      daysMap[id] = normalizeRecurrenceDays(e.recurrenceDays);
    }
    // 2) on collecte **toutes** les dates de la série
    if (id) {
      if (!datesMap[id]) datesMap[id] = [];
      // formatDateForInput(e.start) renvoie 'YYYY-MM-DD'
      datesMap[id].push(formatDateForInput(e.start));
    }
  });

  // tri croissant, suppression doublons
  Object.keys(datesMap).forEach(id => {
    datesMap[id] = Array.from(new Set(datesMap[id])).sort();
  });

  recurrenceMap.current  = daysMap;
  seriesDatesMap.current = datesMap;
}, [events]);
  
  const [rooms, setRooms] = useState([]);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);

  // ---- Helpers Salles (robustes) ----
  // 1) Map <id, name> pour lookup instantané
  const roomsById = useMemo(() => {
    const m = new Map();
    (rooms || []).forEach(r => m.set(Number(r.id), r.name));
    return m;
  }, [rooms]);

  // 2) ID -> Nom (tolérant)
  const getRoomNameById = useCallback((id) => {
    if (id == null) return '';
    return roomsById.get(Number(id)) || '';
  }, [roomsById]);

  // 3) Reconstruire le nom de salle depuis diverses formes de payload
  const resolveConflictRoomName = useCallback((conflictUser, fallbackEvent) => {
    // a) si l'API envoie déjà le nom…
    if (conflictUser?.conflictingEventRoom) return conflictUser.conflictingEventRoom;

    // b) essayer différentes clés d'ID qu'on voit souvent dans les payloads
    const pick = (...ids) => {
      for (const v of ids) {
        const name = getRoomNameById(v);
        if (name) return name;
      }
      return '';
    };
    let name = pick(
      conflictUser?.conflictingEventRoomId,
      conflictUser?.roomId,
      conflictUser?.room_id
    );

    // c) repli via l'événement associé (si on a un id d'événement)
    if (!name && conflictUser?.eventId && Array.isArray(events)) {
      const e = events.find(x => String(x.id) === String(conflictUser.eventId));
      const rid = e?.extendedProps?.room?.id ?? e?.roomId;
      name = pick(rid);
    }

    // d) repli via l'événement courant passé en fallback
    if (!name && fallbackEvent) {
      const rid = fallbackEvent?.room?.id ?? fallbackEvent?.roomId;
      name = pick(rid);
    }

    return name || '';
  }, [events, getRoomNameById]);

  // 4) Pour les événements multi-salles (optionnel)
  const resolveEventRoomNames = useCallback((ev) => {
    // essaie plusieurs formes : roomIds[], room.id, roomId
    const ids = Array.isArray(ev?.roomIds)
      ? ev.roomIds
      : (ev?.room?.id != null) ? [ev.room.id]
      : (ev?.roomId != null) ? [ev.roomId]
      : [];
    return ids.map(getRoomNameById).filter(Boolean).join(', ');
  }, [getRoomNameById]);
  
  // Fonction pour sauvegarder l'événement en cours de création
  const saveEventDraft = () => {
    if (showModal && newEvent.title) {
      // Assurez-vous que recurrenceDays est correctement sauvegardé
      const eventToSave = {
        ...newEvent,
        modalType,
        activeModalTab
      };
      
      // Convertir les jours de récurrence en tableau d'entiers pour éviter les problèmes de sérialisation
      if (eventToSave.recurrenceDays && Array.isArray(eventToSave.recurrenceDays)) {
        eventToSave.recurrenceDays = eventToSave.recurrenceDays.map(day => Number(day));
      }
      
      sessionStorage.setItem('eventDraft', JSON.stringify(eventToSave));
      // Notification discrète de sauvegarde
      setSuccess('Brouillon sauvegardé - vous pourrez reprendre votre événement plus tard');
      setTimeout(() => setSuccess(''), 3000);
    }
  };
  
  // Fonction pour restaurer l'événement en cours de création
  const restoreEventDraft = () => {
    const savedDraft = sessionStorage.getItem('eventDraft');
    if (savedDraft) {
      try {
        const draft = JSON.parse(savedDraft);
        const { modalType: savedModalType, activeModalTab: savedActiveTab, ...eventData } = draft;
        
        // S'assurer que recurrenceDays est un tableau d'entiers
        if (eventData.recurrenceDays && Array.isArray(eventData.recurrenceDays)) {
          eventData.recurrenceDays = eventData.recurrenceDays.map(day => Number(day));
        } else {
          eventData.recurrenceDays = [];
        }
        
        setNewEvent(eventData);
        setModalType(savedModalType || 'add');
        setActiveModalTab(savedActiveTab || 'general');
        setShowModal(true);
        // Notification de restauration
        setSuccess('Brouillon restauré - vous pouvez continuer votre événement');
        setTimeout(() => setSuccess(''), 4000);
        // Supprimer le brouillon après restauration
        sessionStorage.removeItem('eventDraft');
      } catch (error) {
        console.error('Erreur lors de la restauration du brouillon:', error);
      }
    }
  };
  
  // États pour les filtres
  const [roomFilters, setRoomFilters] = useState(() => {
    const savedRoomFilters = sessionStorage.getItem('calendarRoomFilters');
    return savedRoomFilters ? JSON.parse(savedRoomFilters) : [];
  });
  const [typeFilters, setTypeFilters] = useState(() => {
    const savedTypeFilters = sessionStorage.getItem('calendarTypeFilters');
    // Si des filtres sont sauvegardés, les utiliser, sinon aucun filtre par défaut
    return savedTypeFilters ? JSON.parse(savedTypeFilters) : [];
  });
  const [showAllEvents, setShowAllEvents] = useState(() => {
    // Pour les administrateurs, on permet de voir tous les événements par défaut
    // Pour les autres utilisateurs, on filtre par défaut
    const savedShowAllEvents = sessionStorage.getItem('calendarShowAllEvents');
    if (savedShowAllEvents !== null) {
      return JSON.parse(savedShowAllEvents);
    } else {
      // Par défaut: true pour les administrateurs, false pour les autres utilisateurs
      // Pour les régisseurs, toujours false pour qu'ils ne voient que les événements auxquels ils sont assignés
      if (user.role === 'regie') {
        return false;
      }
      return user.role === 'administrateur';
    }
  });

const navigateToEvent = useCallback((event) => {
    if (!calendarRef.current || !event) return;
    
    const calendarApi = calendarRef.current.getApi();
    
    // Aller à la date de l'événement
    if (event.start) {
      calendarApi.gotoDate(event.start);
      
      // Masquer tous les événements sauf celui sélectionné
      const allEvents = calendarApi.getEvents();
      allEvents.forEach(evt => {
        evt.setProp('display', evt.id === event.id ? 'auto' : 'none');
      });
      
      // Marquer qu'une recherche est active
      setSearchState(prev => ({
        ...prev,
        hasActiveSearch: true,
        showResults: false
      }));
      
      // Mettre en évidence l'événement
      setTimeout(() => {
        const eventEl = document.querySelector(`[data-event-id="${event.id}"]`);
        if (eventEl) {
          eventEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
          eventEl.style.transform = 'scale(1.05)';
          eventEl.style.transition = 'transform 0.3s ease';
          setTimeout(() => {
            eventEl.style.transform = 'scale(1)';
          }, 1000);
        }
      }, 100);
    }
  }, []);

  // NOUVELLE BARRE DE RECHERCHE OPTIMISÉE
  const [searchState, setSearchState] = useState({
    term: '',
    results: [],
    isSearching: false,
    showResults: false,
    selectedIndex: -1,
    hasActiveSearch: false
  });

   const performSearch = useCallback((searchTerm) => {
    if (!searchTerm || searchTerm.trim() === '' || searchIndexRef.current.size === 0) {
      return [];
    }
    
    const term = searchTerm.toLowerCase().trim();
    const results = [];
    
    // Recherche simple et efficace
    searchIndexRef.current.forEach((data) => {
      const { event, title, description, roomName } = data;
      
      // Vérifier si le terme correspond au titre, description ou salle
      if (title.includes(term) || 
          description.includes(term) || 
          roomName.includes(term)) {
        results.push(event);
      }
    });
    
    // Limiter à 10 résultats pour éviter la surcharge
    return results.slice(0, 10);
  }, []);

  const clearSearch = useCallback(() => {
    // Annuler tout timeout de recherche en cours
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }
    
    // Réinitialiser l'état de recherche
    setSearchState({
      term: '',
      results: [],
      isSearching: false,
      showResults: false,
      selectedIndex: -1,
      hasActiveSearch: false
    });
  }, []);

 

 // Navigation au clavier simplifiée
 const handleSearchKeyDown = useCallback((e) => {
   if (!searchState.showResults || searchState.results.length === 0) return;
   
   const { selectedIndex, results } = searchState;
   const maxIndex = results.length - 1;
   
   switch (e.key) {
     case 'ArrowDown':
       e.preventDefault();
       setSearchState(prev => ({
         ...prev,
         selectedIndex: selectedIndex < maxIndex ? selectedIndex + 1 : 0
       }));
       break;
       
     case 'ArrowUp':
       e.preventDefault();
       setSearchState(prev => ({
         ...prev,
         selectedIndex: selectedIndex > 0 ? selectedIndex - 1 : maxIndex
       }));
       break;
       
     case 'Enter':
       if (selectedIndex >= 0 && results[selectedIndex]) {
         e.preventDefault();
         navigateToEvent(results[selectedIndex]);
       }
       break;
       
     case 'Escape':
       e.preventDefault();
       clearSearch();
       break;
   }
 }, [searchState, navigateToEvent, clearSearch]);

  // Index de recherche ultra-rapide
  const searchIndexRef = useRef(new Map());
  const searchTimeoutRef = useRef(null);
  
  // Fonction pour créer l'index de recherche optimisé
  const buildSearchIndex = useCallback(() => {
    const index = new Map();
    
    events.forEach(event => {
      const title = (event.title || '').toLowerCase().trim();
      const description = (event.extendedProps?.description || '').toLowerCase().trim();
      const roomName = (event.extendedProps?.room?.name || '').toLowerCase().trim();
      
      // Créer un texte de recherche combiné
      const searchText = `${title} ${description} ${roomName}`.replace(/\s+/g, ' ');
      
      index.set(event.id, {
        event,
        title,
        description,
        roomName,
        searchText,
        titleWords: title.split(/\s+/).filter(w => w.length > 0),
        descWords: description.split(/\s+/).filter(w => w.length > 0)
      });
    });
    
    searchIndexRef.current = index;
  }, [events]);

  
  // Nouvelle fonction pour effacer la recherche
  
  
  // Fonction pour réinitialiser tous les filtres (type, salle, recherche)
  const resetAllFilters = useCallback(() => {
    // Réinitialiser les filtres de type et de salle
    setTypeFilters([]);
    setRoomFilters([]);
    
    // Pour les administrateurs, afficher tous les événements par défaut
    // Pour les autres utilisateurs, filtrer par défaut
    setShowAllEvents(user.role === 'administrateur');
    
    // Mettre à jour les filtres dans le sessionStorage
    sessionStorage.removeItem('calendarRoomFilters');
    sessionStorage.removeItem('calendarTypeFilters');
    
    // Effacer la recherche
    clearSearch();
  }, [clearSearch]);
  
  
  // Construire l'index quand les événements changent
  useEffect(() => {
    buildSearchIndex();
  }, [events, buildSearchIndex]);
  

  
  
  // Nouvelle fonction pour gérer la saisie dans la barre de recherche
  const handleSearchInput = useCallback((e) => {
    const newTerm = e.target.value;
    
    // Annuler le timeout précédent
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Mettre à jour immédiatement l'état pour l'UI
    setSearchState(prev => ({
      ...prev,
      term: newTerm,
      isSearching: newTerm.trim().length > 0,
      showResults: newTerm.trim().length > 0,
      selectedIndex: -1
    }));
    
    // Si le champ est vide, effacer la recherche
    if (newTerm.trim() === '') {
      clearSearch();
      return;
    }
    
    // Recherche avec debounce pour les suggestions
    searchTimeoutRef.current = setTimeout(() => {
      const results = performSearch(newTerm);
      
      setSearchState(prev => ({
        ...prev,
        results,
        isSearching: false,
        hasActiveSearch: true
      }));
    }, 150); // Délai optimisé pour la fluidité
  }, [performSearch, clearSearch]);
  
  // Nouvelle fonction pour gérer la navigation au clavier
  const handleKeyDown = useCallback((e) => {
    if (!searchState.showResults || searchState.results.length === 0) return;
    
    const { selectedIndex, results } = searchState;
    const maxIndex = Math.min(results.length - 1, 9); // Limiter à 10 résultats
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSearchState(prev => ({
          ...prev,
          selectedIndex: selectedIndex < maxIndex ? selectedIndex + 1 : 0
        }));
        break;
        
      case 'ArrowUp':
        e.preventDefault();
        setSearchState(prev => ({
          ...prev,
          selectedIndex: selectedIndex > 0 ? selectedIndex - 1 : maxIndex
        }));
        break;
        
      case 'Enter':
        if (selectedIndex >= 0 && selectedIndex <= maxIndex) {
          e.preventDefault();
          navigateToEvent(results[selectedIndex]);
        }
        break;
        
      case 'Escape':
        e.preventDefault();
        clearSearch();
        break;
        
      default:
        break;
    }
  }, [searchState, navigateToEvent, clearSearch]);
  
  // Fonction pour sélectionner un événement depuis la liste
  const selectEvent = useCallback((event) => {
    navigateToEvent(event);
  }, [navigateToEvent]);
  
  

    // États pour les PDF
    const [showPdfSection, setShowPdfSection] = useState(false);
    const [eventPdfs, setEventPdfs] = useState([]);
    const [pdfFile, setPdfFile] = useState(null);
    const [pdfFiles, setPdfFiles] = useState([]);
    const [pdfPreviews, setPdfPreviews] = useState([]);
    const [uploadingPdf, setUploadingPdf] = useState(false);
    const [selectedPdfCategory, setSelectedPdfCategory] = useState('contrat');
    const [isDragging, setIsDragging] = useState(false);
    

    // États pour la recherche et les utilisateurs récents
    const [userSearch, setUserSearch]     = useState('');
    const [recentUsers, setRecentUsers]   = useState([]);

    
 // Met à jour la liste filtrée dès que 'users' ou 'userSearch' évoluent
 useEffect(() => {
   const search = userSearch.trim().toLowerCase();
   if (!search) {
     setFilteredUsers(users);
   } else {
     setFilteredUsers(
       users.filter(u =>
         (u.firstName && u.firstName.toLowerCase().includes(search)) ||
         (u.lastName && u.lastName.toLowerCase().includes(search)) ||
         (u.email ? u.email.toLowerCase().includes(search) : false)
       )
     );
   }
 }, [users, userSearch]);
 
 // Restaurer le brouillon d'événement au chargement du composant
 useEffect(() => {
   // Attendre que les données soient chargées avant de restaurer
   if (users.length > 0 && rooms.length > 0) {
     restoreEventDraft();
   }
 }, [users, rooms]);

 
    
    // Catégories de PDF disponibles
    const pdfCategories = [
      { id: 'contrat', label: 'Contrat', multiple: false },
      { id: 'bilan_provisoire', label: 'Bilan Provisoire', multiple: false },
      { id: 'facture_sacd', label: 'Facture SACD', multiple: false },
      { id: 'facture_sacem', label: 'Facture SACEM', multiple: false },
      { id: 'facture_droit_direct', label: 'Facture Droit Direct', multiple: false },
      { id: 'facture_producteur', label: 'Facture Producteur', multiple: false },
      { id: 'decors', label: 'Décors', multiple: false },
      { id: 'bilan_definitif', label: 'Bilan Définitif', multiple: false },
      { id: 'divers', label: 'Divers', multiple: true }
    ];    
// ---------- PDF helpers (robustes) ----------
/** Retourne toujours la liste de PDFs, quelle que soit la clé utilisée par l'API */
const getAllPdfs = (ev) => {
  if (!ev) return [];
  if (Array.isArray(ev.EventPdfs)) return ev.EventPdfs;
  if (Array.isArray(ev.Pdfs)) return ev.Pdfs;
  return [];
};

/** Cherche une catégorie exacte dans une liste de PDFs */
const hasPdfCategory = (pdfs, category) => {
  if (!Array.isArray(pdfs)) return false;
  return pdfs.some(pdf => pdf && pdf.category === category);
};

/** Cherche dans les noms de fichiers (fallback au cas où la catégorie n'est pas bien renseignée) */
const fileNameIncludes = (pdfs, needles) => {
  if (!Array.isArray(pdfs)) return false;
  const ns = (Array.isArray(needles) ? needles : [needles])
    .map(s => String(s || '').toLowerCase());
  return pdfs.some(pdf => {
    const name = String(pdf?.filename || '').toLowerCase();
    return name && ns.some(n => name.includes(n));
  });
};

// Calendar.js
// … en haut du composant Calendar, avant le return:
const renderEventContent = (eventInfo) => {
  const { timeText, event, view } = eventInfo;
  const roomName = event.extendedProps.room?.name;
  const eventColor = event.backgroundColor || '#4D94FF';
  const isProvisionalShow = (event.type === 'show' || event.extendedProps.type === 'show') && event.extendedProps.showStatus === 'provisional';
  
  // Déterminer si nous sommes en vue mensuelle
  const isMonthView = view.type === 'dayGridMonth';
  
  // Préparer le texte d'affichage de l'heure (avec heure de fin en vue mensuelle)
  let displayTime = timeText;
  if (isMonthView && event.end) {
    const endTime = event.end.toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
    displayTime = `${timeText} - ${endTime}`;
  }

  // Style de base pour tous les événements (supprime les bordures par défaut)
  const baseStyle = {
    border: 'none', // Supprime toutes les bordures par défaut
    outline: 'none', // Supprime les contours
    boxShadow: 'none', // Supprime les ombres qui pourraient créer un effet de bordure
    width: '100%', // Assure que le conteneur prend toute la largeur disponible
    height: '100%', // Assure que le conteneur prend toute la hauteur disponible
    overflow: 'hidden' // Empêche le contenu de déborder
  };
  
  // Style spécifique pour les spectacles provisoires (tous types de spectacles)
  const containerStyle = isProvisionalShow ? {
    ...baseStyle,
    backgroundColor: 'white', // Fond blanc opaque
    color: 'black', // Texte noir
    border: '1px solid black', // Uniquement la bordure extérieure
    width: '100%', // Assure que le conteneur prend toute la largeur disponible
    height: '100%', // Assure que le conteneur prend toute la hauteur disponible
    borderRadius: '4px', // Coins arrondis
    overflow: 'hidden', // Empêche le contenu de déborder
    padding: '2px' // Ajoute un peu d'espace intérieur
  } : baseStyle;

  // Style pour le texte des spectacles provisoires
  const textStyle = isProvisionalShow ? {
    color: 'black'
  } : {
    color: 'white'
  };

  return (
    <div className="flex flex-col leading-tight p-1 w-full h-full" style={{...containerStyle, position: 'relative'}}>
      {/* Conteneur sans bordures intérieures */}
      <div className="flex flex-col w-full h-full" style={{ border: 'none', position: 'relative', zIndex: 1 }}>
        {/* 1) Heure avec icône horloge */}
        <div className="flex items-center text-xs w-full" style={{ ...textStyle, border: 'none', marginBottom: '2px' }}>
          <ClockIcon className="w-4 h-4 mr-1 opacity-80 flex-shrink-0" style={{ border: 'none' }} />
          <span style={{ 
            border: 'none',
            display: 'inline-block',
            width: 'calc(100% - 20px)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontWeight: 'bold'
          }}>{displayTime}</span>
        </div>

        {/* Pictogramme de salle en position absolue */}
        {roomName && (
          <div style={{ 
            position: 'absolute', 
            left: '0px', 
            top: '17px',
            border: 'none',
            zIndex: 10
          }}>
            {(() => {
              // Fonction pour obtenir le pictogramme selon la salle
              const getRoomIcon = (roomName) => {
                const iconStyle = {
                  fontSize: '16px',
                  color: '#FFD700', // Jaune vif
                  fontWeight: 'bold',
                  textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
                  ...textStyle
                };
                
                switch (roomName) {
                  case "Comédie d'Aix":
                    return <span style={iconStyle}>■</span>; // Carré
                  case "Comédie des Suds":
                    return <span style={iconStyle}>♥</span>; // Coeur
                  case "Comédie de Marseille":
                    return <span style={iconStyle}>⬟</span>; // Pentagone
                  case "Comédie Le Mans":
                    return <span style={iconStyle}>✚</span>; // Croix (style suisse/pharmacie)
                  case "Comédie La Rochelle":
                    return <span style={iconStyle}>●</span>; // Cercle
                  case "La Fontaine d'Argent":
                    return <span style={iconStyle}>▲</span>; // Triangle
                  case "Tournée":
                    return <span style={iconStyle}>★</span>; // Étoile
                  case "Salle cours le Mans":
                    return <span style={iconStyle}>◆</span>;
                  case "Atelier 76":
                    return <span style={iconStyle}>⬢</span>;
                  case "Grande salle":
                    return <span style={iconStyle}>⬛</span>;
                  case "Petite Salle":
                    return <span style={iconStyle}>⬤</span>;
                  case "Bureau":
                    return <span style={iconStyle}>▣</span>;
                  case "KAWA THEATRE":
                    return <span style={iconStyle}>K</span>;
                  default:
                }
              };
              return getRoomIcon(roomName);
            })()
            }
          </div>
        )}

        {/* 2) Titre avec point coloré */}
        <div className="flex items-start mt-0.5" style={{ border: 'none' }}>
          <span 
            className="inline-block w-2 h-2 rounded-full mr-2 mt-1" 
            style={{ backgroundColor: eventColor, border: 'none' }}
          />
          <span 
            className="text-sm font-semibold" 
            style={{ 
              ...textStyle, 
              border: 'none',
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
              maxWidth: roomName ? 'calc(100% - 32px)' : 'calc(100% - 12px)',
              display: 'inline-block'
            }}
          >
            {event.title}
          </span>
        </div>
      </div>
    </div>
  );
};

// Calcule la liste des événements à afficher en fonction des filtres
let filteredEvents;

// 1) Si AUCUN filtre salle ET aucun filtre type n'est coché, on n'affiche rien
if (roomFilters.length === 0 && typeFilters.length === 0) {
  filteredEvents = [];
} else {
  // 2) Sinon, on filtre
  filteredEvents = events.filter(evt => {
    // Construire la liste des IDs de salles associés à l'événement
    // Supporte les événements multi-salles (indisponibilités) via extendedProps.roomIds/rooms
    let eventRoomIds = [];

    // 1) Priorité aux roomIds si présents
    if (Array.isArray(evt.extendedProps?.roomIds) && evt.extendedProps.roomIds.length > 0) {
      eventRoomIds = evt.extendedProps.roomIds.map(id => String(id));
    }

    // 2) Sinon, extraire depuis rooms si disponible
    if (eventRoomIds.length === 0 && Array.isArray(evt.extendedProps?.rooms) && evt.extendedProps.rooms.length > 0) {
      eventRoomIds = evt.extendedProps.rooms
        .map(r => r && r.id != null ? String(r.id) : null)
        .filter(Boolean);
    }

    // 3) En dernier recours, utiliser la salle unique
    if (eventRoomIds.length === 0 && evt.extendedProps?.room?.id != null) {
      eventRoomIds = [String(evt.extendedProps.room.id)];
    }

    // Si aucune salle n’est cochée → matchRoom = true.
    // Sinon, l'événement matche si au moins une de ses salles est dans les filtres.
    const matchRoom = roomFilters.length === 0 || eventRoomIds.some(id => roomFilters.includes(id));

    // Si aucun type n'est coché → matchType = true. Sinon on teste inclusion sur evt.type ou evt.extendedProps.type
    const matchType =
      typeFilters.length === 0 || typeFilters.includes(evt.type) || typeFilters.includes(evt.extendedProps.type);

    // Filtre de recherche - si une recherche est active, vérifier si l'événement correspond
    let matchSearch = true;
    if (searchState.hasActiveSearch && searchState.term.trim() !== '') {
      const searchTerm = searchState.term.toLowerCase().trim();
      const title = (evt.title || '').toLowerCase();
      const description = (evt.extendedProps?.description || '').toLowerCase();
      // Recherche sur les noms de salles (supporte multi-salles)
      const roomNames = (() => {
        if (Array.isArray(evt.extendedProps?.rooms) && evt.extendedProps.rooms.length > 0) {
          return evt.extendedProps.rooms
            .map(r => (r?.name || '').toLowerCase())
            .filter(Boolean);
        }
        const singleName = (evt.extendedProps?.room?.name || '').toLowerCase();
        return singleName ? [singleName] : [];
      })();

      matchSearch = title.includes(searchTerm) || 
                   description.includes(searchTerm) || 
                   roomNames.some(n => n.includes(searchTerm));
    }

    // Pour les utilisateurs non-admin, on affiche les événements selon leur rôle
    if (!showAllEvents) {
      const isUserAssigned =
        Array.isArray(evt.extendedProps.users) &&
        evt.extendedProps.users.some(u => u.id === user.id);
      const isShow = evt.type === 'show' || evt.extendedProps.type === 'show';
      const isCreatedByUser = evt.extendedProps.creator && evt.extendedProps.creator.id === user.id;

      // Pour les artistes (artistesProd, artistesCoProd, artistes), on montre uniquement les spectacles auxquels ils sont assignés et leurs indisponibilités
      if (user.role === 'artistesProd' || user.role === 'artistesCoProd' || user.role === 'artistes') {
        const isUnavailabilityEvent = ['disponible', 'non-disponible'].includes(evt.type) || ['disponible', 'non-disponible'].includes(evt.extendedProps.type);
        return matchRoom && matchType && matchSearch && ((isShow && isUserAssigned) || (isUnavailabilityEvent && isCreatedByUser));
      } else {
        // Pour les autres non-administrateurs, on montre les événements de type 'show', ceux auxquels l'utilisateur est assigné, ou ceux qu'il a créés
        return matchRoom && matchType && matchSearch && (isShow || isUserAssigned || isCreatedByUser);
      }
    }
    

    // Pour les admins, exiger seulement matchRoom ET matchType ET matchSearch
    return matchRoom && matchType && matchSearch;
  });
}

  // États pour le modal
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('add'); // 'add', 'edit', 'view'
  const [currentEvent, setCurrentEvent] = useState(null);
  const [newEvent, setNewEvent] = useState({
    title: '',
    start: '',
    end: '',
    roomId: '',
    roomIds: [], // Pour la sélection multiple de salles
    type: 'show',
    personnelType: 'autre',
    showType: 'production', // Type de spectacle: production, coproduction ou one
    showStatus: 'provisional',
    rentalStatus: 'provisional',
    description: '',
    coRealizationPercentage: '',
    color: '',
    assignedUsers: [],
    userDetails: [],
    ticketingLocation: 'online',
    hasDecor: false,
    decorDetails: '',
    hasSoundTrack: false,
    soundTrackDetails: '',
    flyersOrdered: false,
    vatPercentage: null,
    hotelOrdered: false,
    transferOrdered: false,
    apartmentOrdered: false,
    sacdDeclared: false,
    sacemDeclared: false,
    productionContact: '',
    stageManagerContact: '',
    expectedAttendees: 0,
    isRecurring: false,
    recurrenceId: '',
    recurrenceEndDate: '',
    recurrenceDays: [], // jours de la semaine [1=lundi, 2=mardi, ..., 7=dimanche]
    seriesDates: [],
    eventGuests: [],
    category: 'divers',
    address: '',
    jauge: '',
  });
  // États pour la sélection d'événement
  const [showSelectionDialog, setShowSelectionDialog] = useState(false);
  const [selectedTimeInfo, setSelectedTimeInfo] = useState(null);
  
  // États pour les messages
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // État pour le presse-papier
  const [clipboardEvent, setClipboardEvent] = useState(null);

  // Onglet pour Modal 
  const [activeModalTab, setActiveModalTab] = useState('general');
  
  // États pour les onglets
  const [activeTab, setActiveTab] = useState('general');
  const [activeUserDetailsId, setActiveUserDetailsId] = useState(null);
  
  // États pour les PDF
 
  const [pdfToDelete, setPdfToDelete] = useState(null);
const [showDeletePdfConfirm, setShowDeletePdfConfirm] = useState(false);

// États pour la suppression d'événements récurrents

  // Initialiser la couleur pour les événements de type 'rental'
  useEffect(() => {
    if (newEvent.type === 'rental' && !newEvent.color) {
      setNewEvent(prev => ({
        ...prev,
        color: getRentalColor(prev.rentalStatus || 'provisional')
      }));
    }
  }, [newEvent.type, newEvent.rentalStatus]);

const [showDeleteEventConfirm, setShowDeleteEventConfirm] = useState(false);
const [eventToDelete, setEventToDelete] = useState(null);
const [deleteOption, setDeleteOption] = useState('single'); // 'single' ou 'series'

// État pour la fonctionnalité d'annulation de suppression
const [recentlyDeletedEvent, setRecentlyDeletedEvent] = useState(null);
const [showUndoDelete, setShowUndoDelete] = useState(false);
  
  // Options pour les types d'événements
  const eventTypes = [
    { value: 'show', label: 'Spectacle', color: '#9ED2F4', canOverlapWith: ['permanence', 'ticketing', 'disponible', 'non-disponible','personnel', 'régie'] },
    { value: 'permanence', label: 'Permanence', color: '#90EE90', canOverlapWith: ['show', 'ticketing', 'régie', 'rental', 'permanence', 'personnel', 'disponible', 'non-disponible', 'calage', 'event', 'cours'] },
    { value: 'ticketing', label: 'Billetterie', color: '#FFC0CB', canOverlapWith: ['show', 'ticketing', 'permanence', 'régie', 'rental', 'calage', 'event', 'personnel', 'disponible', 'non-disponible', 'cours'] },
    { value: 'régie', label: 'Régie', color: '#654321', canOverlapWith: ['show', 'permanence', 'ticketing', 'rental', 'régie', 'calage', 'personnel', 'event', 'disponible', 'non-disponible', 'cours'] },
    { value: 'rental', label: 'Location salle', color: '#FFD700', canOverlapWith: ['permanence', 'ticketing', 'régie', 'disponible', 'personnel', 'non-disponible', 'cours'] },
    { value: 'calage', label: 'Calage', color: '#B5927D', canOverlapWith: ['permanence', 'ticketing', 'calage', 'régie', 'disponible', 'personnel', 'non-disponible', 'cours'] },
    { value: 'event', label: 'Événement', color: '#D6406E', canOverlapWith: ['permanence', 'ticketing', 'régie', 'disponible', 'personnel', 'non-disponible', 'cours'] },
    { value: 'personnel', label: 'Gestion du personnel', color: '#000000', canOverlapWith: ['show', 'ticketing', 'régie', 'rental', 'calage', 'event', 'permanence', 'personnel', 'disponible', 'non-disponible', 'cours'] },
    { value: 'cours', label: 'Cours', color: '#FF6347', canOverlapWith: ['permanence', 'ticketing', 'régie', 'rental', 'calage', 'event', 'personnel', 'disponible', 'non-disponible'] }, // Orange-rouge
    // Type 'disponible' mis en commentaire pour qu'il ne s'affiche pas dans le calendrier
    // { value: 'disponible', label: 'Disponible', color: '#4CAF50', canOverlapWith: ['show', 'ticketing', 'régie', 'rental', 'calage', 'event', 'permanence', 'disponible', 'non-disponible', 'personnel' ] }, // Vert
    { value: 'non-disponible', label: 'Non disponible', color: '#9E9E9E', canOverlapWith: ['show', 'ticketing', 'régie', 'rental', 'calage', 'event', 'permanence', 'disponible', 'non-disponible', 'personnel', 'cours'] } // Gris clair
  ];
  
  // Options pour les types de spectacle
  const showTypeOptions = [
    { value: 'production', label: 'Production', color: '#4D94FF' }, // Bleu
    { value: 'coproduction', label: 'Co-production', color: '#8A2BE2' }, // Violet vif
    { value: 'one', label: 'ONE', color: '#FF0000' } // Rouge vif
  ];
  
  // Options pour les statuts de spectacle
  const showStatusOptions = [
    { value: 'confirmed', label: 'Confirmé', color: '#9ED2F4' }, // Bleu clair
    { value: 'provisional', label: 'Provisoire', color: '#e5e5e5' }, // Gris clair
    { value: 'ticketsOpen', label: 'Billetterie ouverte', color: '#0038FF' },
    { value: 'cancelled', label: 'Annulé', color: '#000000' } // Noir pour les spectacles annulés
  ];

  // Options pour les statuts de location de salle
  const rentalStatusOptions = [
    { value: 'provisional', label: 'Provisoire', color: '#F5EBC1' }, // Couleur claire pour provisoire
    { value: 'definitive', label: 'Définitif', color: '#FFD700' } // Jaune flashy pour définitif
  ];
  
  // Options pour les types de personnel
  const personnelTypeOptions = [
    { value: 'maladie', label: 'Maladie', color: '#000000' },
    { value: 'absence', label: 'Absence', color: '#000000' },
    { value: 'CP', label: 'Congés payés', color: '#000000' },
    { value: 'télétravail', label: 'Télétravail', color: '#000000' },
    { value: 'autre', label: 'Autre', color: '#000000' }
  ];
  
  // Fonction pour obtenir la couleur en fonction du type et du statut du spectacle
  const getShowColor = (showType, showStatus) => {
    // Si le spectacle est annulé, on utilise toujours la couleur noire
    if (showStatus === 'cancelled') {
      return '#000000'; // Noir pour les spectacles annulés
    }
    
    // Si le spectacle est provisoire, on utilise toujours la couleur blanche (le style sera appliqué via CSS)
    if (showStatus === 'provisional') {
      return '#FFFFFF'; // Blanc pour les spectacles provisoires
    }
    
    // Pour les statuts spécifiques, on adapte la couleur en fonction du type de spectacle
    if (showType === 'one') {
      // ONE: rouge vif pour billetterie ouverte, rouge très clair pour confirmé
      if (showStatus === 'ticketsOpen') {
        return '#FF0000'; // Rouge vif pour ONE avec billetterie ouverte
      } else if (showStatus === 'confirmed') {
        return '#FFCCCC'; // Rouge très clair pour ONE confirmé
      } else {
        return '#FF8080'; // Rouge intermédiaire pour les autres statuts
      }
    } else if (showType === 'coproduction') {
      // CO PRODUCTION: violet vif pour billetterie ouverte, violet très pâle pour confirmé
      if (showStatus === 'ticketsOpen') {
        return '#8A2BE2'; // Violet vif pour CO PRODUCTION avec billetterie ouverte
      } else if (showStatus === 'confirmed') {
        return '#E6D9F2'; // Violet très pâle pour CO PRODUCTION confirmé
      } else {
        return '#C39AE6'; // Violet intermédiaire pour les autres statuts
      }
    } else {
      // PRODUCTION: on garde le principe du bleu
      if (showStatus === 'ticketsOpen') {
        return '#0038FF'; // Bleu vif pour PRODUCTION avec billetterie ouverte
      } else if (showStatus === 'confirmed') {
        return '#9ED2F4'; // Bleu clair pour PRODUCTION confirmé
      } else {
        return '#4D94FF'; // Bleu intermédiaire pour les autres statuts
      }
    }
  };
  
  // Fonction pour obtenir la couleur en fonction du statut de la location
  const getRentalColor = (rentalStatus) => {
    // Si la location est définitive, on utilise un jaune plus flashy
    if (rentalStatus === 'definitive') {
      return '#FFD700'; // Jaune flashy pour définitif
    }
    
    // Si la location est provisoire, on utilise une couleur plus claire
    if (rentalStatus === 'provisional') {
      return '#F5EBC1'; // Version plus claire pour provisoire
    }
    
    // Par défaut, retourner la couleur provisoire
    return '#F5EBC1';
  };
  
  // Fonction pour récupérer les événements
  const fetchEvents = useCallback(async () => {
    try {
      setError(null); // Effacer les erreurs précédentes
      const params = new URLSearchParams();
      
      // Gestion des filtres multiples pour les salles
      if (roomFilters.length > 0) {
        roomFilters.forEach(roomId => {
          params.append('roomId', roomId);
        });
      }
      
      // Gestion des filtres multiples pour les types d'événements
      // Pour le rôle consultation, on n'applique pas de filtre de type
      if (typeFilters.length > 0 && user.role !== 'consultation') {
        typeFilters.forEach(type => {
          params.append('type', type);
        });
      }
      
      // Gestion du filtre "Mes événements uniquement"
      if (!showAllEvents && user.role !== 'consultation') {
        // Le backend gère automatiquement la logique pour afficher
        // les événements créés ET ceux où l'utilisateur est assigné
        // On passe juste l'ID utilisateur comme creatorId et le backend fait le reste
        params.append('creatorId', user.id);
      }
      
      // Pour le rôle consultation, on affiche toujours tous les événements et tous les types
      if (user.role === 'consultation') {
        setShowAllEvents(true);
        // Supprimer le filtre de type pour le rôle consultation
        params.delete('type');
      }
      
         // On ajoute un timestamp pour forcer la requête
         const cacheBuster = `_=${Date.now()}`;
         const sep = params.toString() ? '&' : '?';
         const url = `/events${params.toString() ? `?${params.toString()}` : ''}${sep}${cacheBuster}`;
         const response = await api.get(url, {
           // ou, si vous préférez headers :
           // headers: { 'Cache-Control': 'no-cache' }
         });
         
      
      if (!response.data || !response.data.data || !Array.isArray(response.data.data.events)) {
        throw new Error('Format de réponse invalide');
      }
  
      const fetchedEvents = response.data.data.events.map(event => ({
        id: event.id,
        title: event.title,
        start: event.start,
        end: event.end,
        color: event.color,
          showStatus: event.showStatus,
          rentalStatus: event.rentalStatus,
          showType: event.showType,
          type: event.type,
          personnelType: event.personnelType,
          coRealizationPercentage: event.coRealizationPercentage,
          hasDecor: event.hasDecor,
          decorDetails: event.decorDetails,
          flyersOrdered: event.flyersOrdered,
          vatPercentage: event.vatPercentage,
          address: event.address,
          jauge: event.jauge,
          hotelOrdered: event.hotelOrdered,
          transferOrdered: event.transferOrdered,
          apartmentOrdered: event.apartmentOrdered,
          sacdDeclared: event.sacdDeclared,
          sacemDeclared: event.sacemDeclared,
          productionContact: event.productionContact,
          stageManagerContact: event.stageManagerContact,
          expectedAttendees: event.expectedAttendees,
          hasSoundTrack: event.hasSoundTrack,
          soundTrackDetails: event.soundTrackDetails,
          isRecurring: event.isRecurring,
          recurrenceId: event.recurrenceId,
          recurrenceEndDate: event.recurrenceEndDate,
          // S'assurer que recurrenceDays est toujours un tableau de nombres uniques
          recurrenceDays: event.recurrenceDays,
          seriesDates: event.seriesDates,
          eventGuests: event.EventGuests,
          category: event.category,
          eventGuests: (event.Guests || []).map(g => ({
            id:              g.id,
            userId:          g.UserId,
            name:            g.User ? `${g.User.firstName} ${g.User.lastName}` : '',
            accommodation:   g.accommodation,
            arrivalLocation: g.arrivalLocation,
            arrivalTime:     g.arrivalTime,
            departureLocation: g.departureLocation,
            departureTime:   g.departureTime,
            additionalFee:   g.additionalFee,
            notes:           g.notes,
            arrivalTransfer: g.arrivalTransfer,
            departureTransfer: g.departureTransfer
          })),
          extendedProps: {
            type: event.type,
            isShow: event.type === 'show', // Ajouter la propriété isShow basée sur le type
            description: event.description,
            ticketingLocation: event.ticketingLocation,
            category: event.category,
            room: event.room,
            roomIds: event.roomIds, // Ajouter roomIds aux extendedProps
            rooms: event.rooms, // Ajouter rooms aux extendedProps
            creator: event.Creator,
            lastModifier: event.LastModifier, // Ajouter le dernier modificateur aux extendedProps
            createdAt: event.createdAt,
            updatedAt: event.updatedAt,
            showStatus: event.showStatus, // Ajouter showStatus aux extendedProps
            rentalStatus: event.rentalStatus,
            showType: event.showType, // Ajouter showType aux extendedProps
            recurrenceId: event.recurrenceId, // Ajout du recurrenceId pour identifier les événements d'une même série
            // S'assurer que les jours de récurrence dans extendedProps sont identiques à ceux de l'événement principal
            recurrenceDays: event.recurrenceDays,
            recurrenceEndDate: event.recurrenceEndDate,
            seriesDates: event.seriesDates,
            users: event.Users.map(u => {
              // Récupérer les détails de la relation EventUsers
              // Vérifier si les détails sont dans EventUser ou dans la propriété _through
              const eventUserDetails = u.EventUser || u.EventUsers || {};
              
              console.log('Détails utilisateur reçus du backend:', u.id, eventUserDetails);
              
              return {
                id: u.id,
                firstName: u.firstName,
                lastName: u.lastName,
                role: u.role,
                // Stocker les détails à la fois dans details et directement dans _through pour faciliter l'accès
                details: {
                  accommodation: eventUserDetails.accommodation || '',
                  arrivalLocation: eventUserDetails.arrivalLocation || '',
                  arrivalTime: eventUserDetails.arrivalTime || null,
                  departureLocation: eventUserDetails.departureLocation || '',
                  departureTime: eventUserDetails.departureTime || null,
                  additionalFee: eventUserDetails.additionalFee || '',
                  notes: eventUserDetails.notes || '',
                  arrivalTransfer: eventUserDetails.arrivalTransfer || '',
                  departureTransfer: eventUserDetails.departureTransfer || ''
                },
                _through: eventUserDetails,
                // Autres propriétés de l'événement
                flyersOrdered: event.flyersOrdered,
                vatPercentage: event.vatPercentage || '',
                apartmentOrdered: event.apartmentOrdered,
                address: event.address,
                jauge: event.jauge,
                sacemDeclared: event.sacemDeclared,
                sacdDeclared: event.sacdDeclared,
                hotelOrdered: event.hotelOrdered,
                transferOrdered: event.transferOrdered,
                productionContact: event.productionContact,
                stageManagerContact: event.stageManagerContact,
                expectedAttendees: event.expectedAttendees,
                hasSoundTrack: event.hasSoundTrack,
                soundTrackDetails: event.soundTrackDetails,
                isRecurring: event.isRecurring,
                recurrenceId: event.recurrenceId, // Ajout du recurrenceId pour identifier les événements d'une même série
                recurrenceEndDate: event.recurrenceEndDate,
                // La clé recurrenceDays a été supprimée ici pour éviter la duplication
                eventGuests: event.EventGuests,
              };
            }),
        }
        

      }));
      
      setEvents(fetchedEvents);
    } catch (error) {
      console.error('Error fetching events:', error);
      setError(error.response?.data?.message || 'Erreur lors de la récupération des événements');
    }
  }, [api, roomFilters, typeFilters, showAllEvents, user.id, user.role]);
  
  // Fonction pour récupérer les salles
  const fetchRooms = useCallback(async () => {
    try {
      const response = await api.get('/rooms');
      setRooms(response.data.data.rooms);
    } catch (error) {
      setError(error.response?.data?.message || 'Erreur lors de la récupération des salles');
    }
  }, [api]);
  
  // Fonction pour récupérer les utilisateurs
  const fetchUsers = useCallback(async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data.data.users);
    } catch (error) {
      setError(error.response?.data?.message || 'Erreur lors de la récupération des utilisateurs');
    }
  }, [api]);

  const fetchEventPdfs = async (eventId) => {
    if (!eventId) {
      console.error('ID d\'événement non valide');
      setError('ID d\'événement non valide pour récupérer les PDFs');
      return;
    }
    
    try {
      console.log(`Récupération des PDFs pour l'événement ${eventId}`);
      const response = await api.get(`/events/${eventId}/pdfs`);
      
      if (response.data && response.data.data) {
        const pdfs = response.data.data.pdfs || [];
        console.log(`${pdfs.length} PDFs trouvés pour l'événement ${eventId}`, pdfs);
        setEventPdfs(pdfs);
      } else {
        console.log('Aucun PDF trouvé ou format de réponse incorrect');
        setEventPdfs([]);
      }
    } catch (error) {
      console.error('Error fetching PDFs:', error);
      // Message d'erreur plus convivial
      if (error.response && error.response.status === 404) {
        // Ne pas afficher d'erreur pour les 404 (aucun PDF trouvé)
        // C'est un cas normal pour un nouvel événement
        setEventPdfs([]);
      } else {
        setError('Erreur lors de la récupération des PDFs');
      }
    }
  };
  
  // Effet pour charger les données initiales
  useEffect(() => {
    fetchRooms();
    fetchUsers();
    fetchEvents();
  }, [fetchRooms, fetchUsers, fetchEvents]);
  
  // Mettre à jour les filtres de salle
  const handleRoomFilterChange = (roomId) => {
    setRoomFilters(prev => {
      const newFilters = prev.includes(roomId)
        ? prev.filter(id => id !== roomId)
        : [...prev, roomId];
      sessionStorage.setItem('calendarRoomFilters', JSON.stringify(newFilters));
      return newFilters;
    });
  };
  
  // Mettre à jour les filtres de type
  const handleTypeFilterChange = (type) => {
    setTypeFilters(prev => {
      const newFilters = prev.includes(type)
        ? prev.filter(t => t !== type)
        : [...prev, type];
      sessionStorage.setItem('calendarTypeFilters', JSON.stringify(newFilters));
      return newFilters;
    });
  };
  
  // Mettre à jour l'affichage de tous les événements
  const handleShowAllEventsChange = (value) => {
    // Pour les régisseurs, ne pas permettre de voir tous les événements
    if (user.role === 'regie') {
      setShowAllEvents(false);
      sessionStorage.setItem('calendarShowAllEvents', JSON.stringify(false));
      return;
    }
    // Permettre le changement pour tous les autres utilisateurs
    setShowAllEvents(value);
    sessionStorage.setItem('calendarShowAllEvents', JSON.stringify(value));
  };
  
  // Effet pour sauvegarder les filtres dans le sessionStorage quand ils changent
  useEffect(() => {
    sessionStorage.setItem('calendarRoomFilters', JSON.stringify(roomFilters));
  }, [roomFilters]);
  
  useEffect(() => {
    sessionStorage.setItem('calendarTypeFilters', JSON.stringify(typeFilters));
  }, [typeFilters]);
  
  useEffect(() => {
    sessionStorage.setItem('calendarShowAllEvents', JSON.stringify(showAllEvents));
  }, [showAllEvents]);
  
  // Nous avons fusionné cette logique dans resetAllFilters
  
  // 📦 Récupère exclusivement MES événements assignés, indépendant des filtres UI
  const getAssignedEventsForExport = useCallback(async (start, end) => {
    try {
      const params = new URLSearchParams();
      // On force "mes événements" côté API pour n'obtenir QUE ce qui m'est assigné
      params.append('userId', user.id);
      // Si tu veux inclure les shows quel que soit l'état de l'UI :
      params.append('includeShows', 'true');
      // Anti-cache
      params.append('_', Date.now());

      const { data } = await api.get(`/events?${params.toString()}`);
      const raw = data?.data?.events || data?.events || data || [];

      // On mappe juste ce qui est nécessaire au PDF
      const mapped = raw.map(event => ({
        id: event.id,
        title: event.title,
        start: new Date(event.start),
        end: event.end ? new Date(event.end) : new Date(event.start),
        extendedProps: {
          type: event.type,
          description: event.description,
          room: event.room || null,
          users: (event.Users || event.users || []).map(u => ({
            id: u.id,
            firstName: u.firstName,
            lastName: u.lastName,
          })),
          showStatus: event.showStatus,
          rentalStatus: event.rentalStatus,
          showType: event.showType,
          expectedAttendees: event.expectedAttendees,
          recurrenceId: event.recurrenceId,
          recurrenceEndDate: event.recurrenceEndDate,
          recurrenceDays: event.recurrenceDays,
          seriesDates: event.seriesDates,
        },
      }));

      // Filtrage par période (chevauchement)
      return mapped.filter(evt => {
        const s = evt.start;
        const e = evt.end || evt.start;
        return s < end && e >= start;
      });
    } catch (e) {
      console.error('Export fetch error:', e);
      // 🔙 Fallback (au cas où) : on retombe sur le calendrier, mais en gardant seulement tes assignations
      const calendarApi = calendarRef.current?.getApi();
      const fallback = (calendarApi?.getEvents() || []).filter(evt =>
        (evt.extendedProps?.users || []).some(u => u.id === user.id)
      ).map(evt => ({
        id: evt.id,
        title: evt.title,
        start: evt.start,
        end: evt.end || evt.start,
        extendedProps: evt.extendedProps || {},
      }));
      return fallback.filter(evt => evt.start < end && (evt.end || evt.start) >= start);
    }
  }, [api, user.id]);

  // Fonction pour exporter le calendrier en PDF
  // Nouvelle fonction d'export PDF indépendante des filtres
  const exportPersonalPDF = async (period) => {
    try {
      // Calculer les dates de début et fin selon la période
      let startDate, endDate;
      
      // Pour la vue mensuelle, utiliser le mois affiché dans le calendrier
      if (period === 'month' && calendarRef.current) {
        const calendarApi = calendarRef.current.getApi();
        const currentDate = calendarApi.getDate();
        startDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1, 0, 0, 0, 0);
        endDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0, 23, 59, 59, 999);
      } else {
        const now = new Date();
        switch (period) {
          case 'week':
            // Début de la semaine (lundi)
            const startOfWeek = new Date(now);
            const day = startOfWeek.getDay();
            const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1);
            startOfWeek.setDate(diff);
            startOfWeek.setHours(0, 0, 0, 0);
            
            startDate = startOfWeek;
            endDate = new Date(startOfWeek);
            endDate.setDate(startOfWeek.getDate() + 6);
            endDate.setHours(23, 59, 59, 999);
            break;
            
          case 'month':
            startDate = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
            endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
            break;
            
          case 'quarter':
            startDate = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
            endDate = new Date(now.getFullYear(), now.getMonth() + 3, 0, 23, 59, 59, 999);
            break;
            
          default:
            throw new Error('Période non supportée');
        }
      }
      
      // Récupérer tous les événements directement depuis l'API
      const response = await api.get('/events', {
        params: {
          start: startDate.toISOString(),
          end: endDate.toISOString(),
          userId: user.id // Filtrer par utilisateur assigné
        }
      });
      
      // Vérifier la structure de la réponse
      if (!response.data || !response.data.data || !Array.isArray(response.data.data.events)) {
        throw new Error('Format de réponse invalide');
      }
      
      const userEvents = response.data.data.events.filter(event => {
        // Vérifier si l'utilisateur est assigné à cet événement
        if (!event.Users || !event.Users.some(u => u.id === user.id)) {
          return false;
        }
        
        // Filtrage strict par période - l'événement doit commencer dans la période
        const eventStart = new Date(event.start);
        return eventStart >= startDate && eventStart <= endDate;
      });
      
      if (userEvents.length === 0) {
        alert('Aucun événement trouvé pour cette période.');
        return;
      }
      
      // Trier les événements par date
      const sortedEvents = userEvents.sort((a, b) => new Date(a.start) - new Date(b.start));
      
      // Créer le PDF
      const doc = new jsPDF();
      
      // Gestion spéciale pour l'export trimestriel avec découpage par mois
      if (period === 'quarter') {
        // 1) Grouper les événements par mois
        const eventsByMonth = {};
        sortedEvents.forEach(ev => {
          const s = new Date(ev.start);
          const key = s.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
          (eventsByMonth[key] ||= []).push(ev);
        });

        // Ordonner les mois chronologiquement
        const orderedMonths = Object.entries(eventsByMonth)
          .map(([label, evts]) => ({ label, ts: new Date(evts[0].start).getTime(), evts }))
          .sort((a,b) => a.ts - b.ts);

        const FOOTER_H = 15;
        const BOTTOM_MARGIN = 20;

        for (let i = 0; i < orderedMonths.length; i++) {
          const { label, evts: monthEvents } = orderedMonths[i];

          // Titre de mois
          doc.setFillColor(128, 0, 0);
          doc.rect(0, 0, doc.internal.pageSize.getWidth(), 30, 'F');
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(18);
          doc.setFont('helvetica', 'bold');
          doc.text(`Mon Planning Personnel – ${label}`, 14, 18);

          // 2) Tableau des événements du mois
          const tableData = monthEvents.map(event => {
            const start = new Date(event.start);
            const end = event.end ? new Date(event.end) : start;
            const startDate = start.toLocaleDateString('fr-FR');
            const startTime = start.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
            const endTime = end.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
            const room = event.room?.name || '';
            const type = event.type || '';
            const typeLabel = (type === 'personnel' && event.personnelType)
              ? (personnelTypeOptions.find(t => t.value === event.personnelType)?.label || event.personnelType)
              : (eventTypes.find(t => t.value === type)?.label || type);
            return [startDate, startTime, endTime, event.title, room, typeLabel, '', event.description || ''];
          });

          autoTable(doc, {
            startY: 40,
            head: [['Date','Début','Fin','Titre','Salle','Type','Détails','Description']],
            body: tableData,
            theme: 'grid',
            headStyles: { fillColor: [128, 0, 0], textColor: 255, fontStyle: 'bold' },
            styles: { fontSize: 9, cellPadding: 2, overflow: 'linebreak' },
            columnStyles: {
              0: { cellWidth: 20 },
              1: { cellWidth: 15 },
              2: { cellWidth: 15 },
              3: { cellWidth: 40 },
              4: { cellWidth: 20 },
              5: { cellWidth: 25 },
              6: { cellWidth: 25 },
              7: { cellWidth: 30 }
            },
            margin: { bottom: BOTTOM_MARGIN },
            didDrawPage: function(data) {
              const pageHeight = doc.internal.pageSize.getHeight();
              const pageCount = doc.internal.getNumberOfPages();
              const currentPage = doc.internal.getCurrentPageInfo().pageNumber;
              const exportDate = new Date().toLocaleDateString('fr-FR');
              
              doc.setFillColor(128, 0, 0);
              doc.rect(0, pageHeight - 15, doc.internal.pageSize.getWidth(), 15, 'F');
              doc.setTextColor(255, 255, 255);
              doc.setFontSize(8);
              doc.text(`Exporté le ${exportDate}`, 14, pageHeight - 5);
              doc.text(`Page ${currentPage} sur ${pageCount}`, doc.internal.pageSize.getWidth() - 30, pageHeight - 5);
            }
          });

          // 3) Résumé du mois
          const afterTableY = (doc.lastAutoTable?.finalY || 45) + 15;
          const usableBottom = doc.internal.pageSize.getHeight() - BOTTOM_MARGIN - FOOTER_H;
          
          let cursorY = afterTableY;
          if (cursorY + 12 > usableBottom) {
            doc.addPage();
            cursorY = 20;
          }

          // Helper pour gérer la pagination
          const ensureSpace = (inc = 0) => {
            const usableBottom = doc.internal.pageSize.getHeight() - BOTTOM_MARGIN - FOOTER_H;
            if (cursorY + inc > usableBottom) {
              doc.addPage();
              cursorY = 20;
            }
          };

          // Calculer les statistiques du mois
          const monthPersonnelEvents = monthEvents.filter(event => event.type === 'personnel');
          const monthWorkEvents = monthEvents.filter(event => event.type !== 'personnel');
          const monthWorkHours = monthWorkEvents.reduce((total, event) => {
            const start = new Date(event.start);
            const end = event.end ? new Date(event.end) : start;
            const durationMs = end.getTime() - start.getTime();
            return total + (durationMs / (1000 * 60 * 60));
          }, 0);

          const monthPersonnelStats = {};
          monthPersonnelEvents.forEach(event => {
            const personnelType = event.personnelType || 'autre';
            const start = new Date(event.start);
            const end = event.end ? new Date(event.end) : start;
            const durationMs = end.getTime() - start.getTime();
            const durationHours = durationMs / (1000 * 60 * 60);
            
            if (!monthPersonnelStats[personnelType]) {
              monthPersonnelStats[personnelType] = { hours: 0, days: 0, events: 0 };
            }
            
            monthPersonnelStats[personnelType].hours += durationHours;
            monthPersonnelStats[personnelType].events += 1;
            
            if (personnelType === 'CP') {
              monthPersonnelStats[personnelType].days = monthPersonnelStats[personnelType].hours / 7;
            }
          });

          const monthTotalHours = monthWorkHours + Object.values(monthPersonnelStats).reduce((sum, stat) => sum + stat.hours, 0);

          // Titre du résumé mensuel
          ensureSpace(10);
          doc.setFillColor(70, 130, 180);
          doc.rect(14, cursorY, doc.internal.pageSize.getWidth() - 28, 10, 'F');
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(14);
          doc.setFont('helvetica', 'bold');
          doc.text(`RÉSUMÉ - ${label.toUpperCase()}`, 16, cursorY + 7);
          cursorY += 20;

          // Section heures de travail
          ensureSpace(6);
          doc.setTextColor(0, 0, 0);
          doc.setFillColor(240, 248, 255);
          doc.rect(14, cursorY, doc.internal.pageSize.getWidth() - 28, 6, 'F');
          doc.setFont('helvetica', 'bold');
          doc.text('HEURES DE TRAVAIL', 16, cursorY + 4);
          cursorY += 10;

          ensureSpace(6);
          doc.setFont('helvetica', 'normal');
          doc.text(`• Total des heures travaillées: ${monthWorkHours.toFixed(1)}h`, 20, cursorY);
          cursorY += 10;

          // Section gestion du personnel
          if (monthPersonnelEvents.length > 0) {
            ensureSpace(6);
            doc.setFillColor(255, 248, 220);
            doc.rect(14, cursorY, doc.internal.pageSize.getWidth() - 28, 6, 'F');
            doc.setFont('helvetica', 'bold');
            doc.text('GESTION DU PERSONNEL', 16, cursorY + 4);
            cursorY += 10;

            doc.setFont('helvetica', 'normal');
            Object.entries(monthPersonnelStats).forEach(([type, stats]) => {
              const typeLabel = personnelTypeOptions.find(t => t.value === type)?.label || type;
              ensureSpace(6);
              if (type === 'CP') {
                doc.text(`• ${typeLabel}: ${stats.days.toFixed(1)} jour${stats.days !== 1 ? 's' : ''} (${stats.hours.toFixed(1)}h)`, 20, cursorY);
              } else {
                doc.text(`• ${typeLabel}: ${stats.hours.toFixed(1)}h`, 20, cursorY);
              }
              cursorY += 6;
            });
            cursorY += 4;
          }

          // Récapitulatif mensuel
          ensureSpace(6);
          doc.setFillColor(245, 245, 245);
          doc.rect(14, cursorY, doc.internal.pageSize.getWidth() - 28, 6, 'F');
          doc.setFont('helvetica', 'bold');
          doc.text('RÉCAPITULATIF MENSUEL', 16, cursorY + 4);
          cursorY += 10;

          ensureSpace(6);
          doc.setFont('helvetica', 'normal');
          doc.text(`• Total général d'heures: ${monthTotalHours.toFixed(1)}h`, 20, cursorY);

          // Nouvelle page pour le mois suivant (sauf pour le dernier mois)
          if (i < orderedMonths.length - 1) {
            doc.addPage();
          }
        }

        // Sauvegarder le PDF trimestriel
        const fileName = `activite_user_${startDate.getFullYear()}_${user.firstName}_${user.lastName}_trimestre_${startDate.toLocaleDateString('fr-FR').replace(/\//g, '-')}.pdf`;
        doc.save(fileName);
        return;
      }
      
      // En-tête
      const periodLabels = {
        week: 'Semaine',
        month: 'Mois', 
        quarter: '3 Mois'
      };
      
      const title = `Mon Planning Personnel - ${periodLabels[period]}`;
      const periodText = `Période: ${startDate.toLocaleDateString('fr-FR')} au ${endDate.toLocaleDateString('fr-FR')}`;
      
      doc.setFillColor(128, 0, 0);
      doc.rect(0, 0, doc.internal.pageSize.getWidth(), 30, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text(title, 14, 15);
      
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text(periodText, 14, 25);
      
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(10);
      doc.text(`Export personnel - ${user.firstName} ${user.lastName}`, 14, 40);
      
      // Calculer les heures par jour et le total
      const dailyHours = {};
      let totalHours = 0;
      
      sortedEvents.forEach(event => {
        const start = new Date(event.start);
        const end = event.end ? new Date(event.end) : start;
        const dateKey = start.toLocaleDateString('fr-FR');
        
        // Calculer la durée en heures
        const durationMs = end.getTime() - start.getTime();
        const durationHours = durationMs / (1000 * 60 * 60);
        
        if (!dailyHours[dateKey]) {
          dailyHours[dateKey] = 0;
        }
        dailyHours[dateKey] += durationHours;
        totalHours += durationHours;
      });
      
      // Préparer les données du tableau
      const tableData = sortedEvents.map(event => {
        const start = new Date(event.start);
        const end = event.end ? new Date(event.end) : start;
        
        const startDate = start.toLocaleDateString('fr-FR');
        const startTime = start.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
        const endTime = end.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
        
        // Calculer la durée de cet événement
        const durationMs = end.getTime() - start.getTime();
        const durationHours = (durationMs / (1000 * 60 * 60)).toFixed(2);
        
        const room = event.room?.name || '';
        const type = event.type || '';
        
        // Gérer les types de personnel
        let typeLabel;
        if (type === 'personnel' && event.personnelType) {
          const personnelType = event.personnelType;
          typeLabel = personnelTypeOptions.find(t => t.value === personnelType)?.label || personnelType;
        } else {
          typeLabel = eventTypes.find(t => t.value === type)?.label || type;
        }
        
        // Informations supplémentaires
        let additionalInfo = '';
        if (type === 'show') {
          const showType = event.showType || '';
          const showStatus = event.showStatus || '';
          const showTypeLabel = showTypeOptions.find(t => t.value === showType)?.label || showType;
          const showStatusLabel = showStatusOptions.find(s => s.value === showStatus)?.label || showStatus;
          additionalInfo = `Type: ${showTypeLabel}, Statut: ${showStatusLabel}`;
          
          if (event.expectedAttendees) {
            additionalInfo += `, Spectateurs: ${event.expectedAttendees}`;
          }
          
          // Ajouter les informations d'hôtel et de transfert
          if (event.hotelName) {
            additionalInfo += `, Hôtel: ${event.hotelName}`;
          }
          if (event.arrivalDate && event.arrivalTime) {
            additionalInfo += `, Arrivée: ${new Date(event.arrivalDate).toLocaleDateString('fr-FR')} ${event.arrivalTime}`;
          }
          if (event.departureDate && event.departureTime) {
            additionalInfo += `, Départ: ${new Date(event.departureDate).toLocaleDateString('fr-FR')} ${event.departureTime}`;
          }
          if (event.transferArrival) {
            additionalInfo += `, Transfert arrivée: ${event.transferArrival}`;
          }
          if (event.transferDeparture) {
            additionalInfo += `, Transfert départ: ${event.transferDeparture}`;
          }
          if (event.productionContact) {
            additionalInfo += `, Contact prod: ${event.productionContact}`;
          }
          if (event.stageManagerContact) {
            additionalInfo += `, Contact technique: ${event.stageManagerContact}`;
          }
          if (event.ticketPrice) {
            additionalInfo += `, Prix billet: ${event.ticketPrice}€`;
          }
          if (event.coRealizationPercentage) {
            additionalInfo += `, Co-réalisation: ${event.coRealizationPercentage}%`;
          }
        } else if (type === 'rental') {
          const rentalStatus = event.rentalStatus || '';
          const rentalStatusLabel = rentalStatusOptions.find(s => s.value === rentalStatus)?.label || rentalStatus;
          additionalInfo = `Statut: ${rentalStatusLabel}`;
          
          if (event.rentalPrice) {
            additionalInfo += `, Prix: ${event.rentalPrice}€`;
          }
          if (event.vatPercentage) {
            additionalInfo += `, TVA: ${event.vatPercentage}%`;
          }
        }
        
        return [
          startDate,
          startTime,
          endTime,
          durationHours + 'h',
          event.title,
          room,
          typeLabel,
          additionalInfo,
          event.description || ''
        ];
      });
      
      // Ajouter le tableau principal
      autoTable(doc, {
        startY: 55,
        head: [['Date', 'Début', 'Fin', 'Durée', 'Titre', 'Salle', 'Type', 'Détails', 'Description']],
        body: tableData,
        theme: 'grid',
        styles: { fontSize: 8, cellPadding: 2, lineColor: [200, 200, 200] },
        headStyles: { 
          fillColor: [128, 0, 0],
          textColor: 255,
          fontStyle: 'bold',
          halign: 'center'
        },
        alternateRowStyles: { fillColor: [245, 245, 245] },
        columnStyles: {
          0: { cellWidth: 20 }, // Date
          1: { cellWidth: 12 }, // Début
          2: { cellWidth: 12 }, // Fin
          3: { cellWidth: 12, halign: 'center' }, // Durée
          4: { cellWidth: 30, fontStyle: 'bold' }, // Titre
          5: { cellWidth: 20 }, // Salle
          6: { cellWidth: 20 }, // Type
          7: { cellWidth: 25 }, // Détails
          8: { cellWidth: 35 } // Description
        },
        margin: { top: 60, left: 15, right: 15, bottom: 20 },
        didDrawPage: function(data) {
          const pageHeight = doc.internal.pageSize.getHeight();
          doc.setFillColor(128, 0, 0);
          doc.rect(0, pageHeight - 15, doc.internal.pageSize.getWidth(), 15, 'F');
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(8);
          
          const exportDate = new Date().toLocaleString('fr-FR');
          const pageCount = doc.internal.getNumberOfPages();
          const currentPage = data.pageNumber;
          
          doc.text(`Exporté le ${exportDate}`, 14, pageHeight - 5);
          doc.text(`Page ${currentPage} sur ${pageCount}`, doc.internal.pageSize.getWidth() - 30, pageHeight - 5);
        }
      });
      
      // Total période (sécurise la marge basse + footer)
      const afterTableY = (doc.lastAutoTable?.finalY || 45) + 15;
      const FOOTER_H = 15;        // ta barre en pied de page
      const BOTTOM_MARGIN = 20;   // margin.bottom d'autotable
      const usableBottom = doc.internal.pageSize.getHeight() - BOTTOM_MARGIN - FOOTER_H;
      
      let y = afterTableY;
      if (y + 12 > usableBottom) {   // ~12px de hauteur pour la bande
        doc.addPage();
        y = 20; // marge haute
      }
      
      doc.setFillColor(128, 0, 0);
      doc.rect(14, y, 100, 10, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text(`TOTAL ${periodLabels[period].toUpperCase()}: ${totalHours.toFixed(2)}h`, 16, y + 6);
      
      // On continue à partir d'ici
      let cursorY = y + 20;
      
      // Calculer les statistiques de gestion du personnel
      const personnelEvents = sortedEvents.filter(event => event.type === 'personnel');
      
      // Calculer les heures de travail (hors personnel)
      const workEvents = sortedEvents.filter(event => event.type !== 'personnel');
      const workHours = workEvents.reduce((total, event) => {
        const start = new Date(event.start);
        const end = event.end ? new Date(event.end) : start;
        const durationMs = end.getTime() - start.getTime();
        return total + (durationMs / (1000 * 60 * 60));
      }, 0);
      
      // Grouper par type de personnel
      const personnelStats = {};
      
      personnelEvents.forEach(event => {
        const personnelType = event.personnelType || 'autre';
        const start = new Date(event.start);
        const end = event.end ? new Date(event.end) : start;
        const durationMs = end.getTime() - start.getTime();
        const durationHours = durationMs / (1000 * 60 * 60);
        
        if (!personnelStats[personnelType]) {
          personnelStats[personnelType] = { hours: 0, days: 0, events: 0 };
        }
        
        personnelStats[personnelType].hours += durationHours;
        personnelStats[personnelType].events += 1;
        
        // Pour les congés payés, calculer en jours (7h = 1 jour)
        if (personnelType === 'CP') {
          personnelStats[personnelType].days = personnelStats[personnelType].hours / 7;
        }
      });
      
      // Ajouter le résumé de gestion du personnel
      const resumeY = cursorY;
      
      // Helper pour gérer la pagination
      const ensureSpace = (inc = 0) => {
        const usableBottom = doc.internal.pageSize.getHeight() - BOTTOM_MARGIN - FOOTER_H;
        if (cursorY + inc > usableBottom) {
          doc.addPage();
          cursorY = 20;
        }
      };
      
      // En-tête du résumé avec une couleur différente pour la distinction
      ensureSpace(10);
      doc.setFillColor(70, 130, 180); // Bleu acier
      doc.rect(14, cursorY, doc.internal.pageSize.getWidth() - 28, 10, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('RÉSUMÉ DE GESTION DU PERSONNEL', 16, cursorY + 7);
      
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      
      cursorY += 20;
      
      // Section des heures de travail
      ensureSpace(6);
      doc.setFillColor(240, 248, 255); // Bleu très clair
      doc.rect(14, cursorY, doc.internal.pageSize.getWidth() - 28, 6, 'F');
      doc.setFont('helvetica', 'bold');
      doc.text('HEURES DE TRAVAIL', 16, cursorY + 4);
      cursorY += 10;
      
      ensureSpace(6);
      doc.setFont('helvetica', 'normal');
      doc.text(`• Total des heures travaillées: ${workHours.toFixed(1)}h`, 20, cursorY);
      cursorY += 10;
      
      // Section de gestion du personnel si des événements existent
      if (personnelEvents.length > 0) {
        ensureSpace(6);
        doc.setFillColor(255, 248, 220); // Beige clair
        doc.rect(14, cursorY, doc.internal.pageSize.getWidth() - 28, 6, 'F');
        doc.setFont('helvetica', 'bold');
        doc.text('GESTION DU PERSONNEL', 16, cursorY + 4);
        cursorY += 10;
        
        doc.setFont('helvetica', 'normal');
        
        // Détail par type de personnel avec formatage amélioré
        Object.entries(personnelStats).forEach(([type, stats]) => {
          const typeLabel = personnelTypeOptions.find(t => t.value === type)?.label || type;
          
          ensureSpace(6);
          if (type === 'CP') {
            // Congés payés en jours avec détail des heures
            doc.text(`• ${typeLabel}: ${stats.days.toFixed(1)} jour${stats.days !== 1 ? 's' : ''} (${stats.hours.toFixed(1)}h)`, 20, cursorY);
          } else if (type === 'télétravail') {
            // Télétravail affiché en heures comme demandé
            doc.text(`• ${typeLabel}: ${stats.hours.toFixed(1)}h`, 20, cursorY);
          } else {
            // Autres types (maladie, absence, autre) en heures
            doc.text(`• ${typeLabel}: ${stats.hours.toFixed(1)}h`, 20, cursorY);
          }
          cursorY += 6;
        });
        
        cursorY += 4;
      }
      
      // Section récapitulative
      ensureSpace(6);
      doc.setFillColor(245, 245, 245); // Gris clair
      doc.rect(14, cursorY, doc.internal.pageSize.getWidth() - 28, 6, 'F');
      doc.setFont('helvetica', 'bold');
      doc.text('RÉCAPITULATIF GLOBAL', 16, cursorY + 4);
      cursorY += 10;
      
      ensureSpace(6);
      doc.setFont('helvetica', 'normal');
      doc.text(`• Total général d'heures: ${totalHours.toFixed(1)}h`, 20, cursorY);
      
      // Sauvegarder le PDF
       const fileName = `activite_user_${startDate.getFullYear()}_${user.firstName}_${user.lastName}_${periodLabels[period].toLowerCase()}_${startDate.toLocaleDateString('fr-FR').replace(/\//g, '-')}.pdf`;
       doc.save(fileName);
      
    } catch (error) {
      console.error('Erreur lors de l\'export PDF:', error);
      alert('Erreur lors de l\'export PDF. Veuillez réessayer.');
    }
  };

  const exportToPDF = async (viewType) => {
    // Vérifier si le calendrier est disponible
    if (!calendarRef.current) return;
    
    // Obtenir la vue actuelle du calendrier
    const calendarApi = calendarRef.current.getApi();
    
    // Changer la vue si nécessaire
     if (viewType === 'week' && calendarApi.view.type !== 'timeGridWeek') {
       calendarApi.changeView('timeGridWeek');
     } else if (viewType === 'month' && calendarApi.view.type !== 'dayGridMonth') {
       calendarApi.changeView('dayGridMonth');
     } else if (viewType === 'quarter' && calendarApi.view.type !== 'dayGridMonth') {
       calendarApi.changeView('dayGridMonth');
     }
    
    // IGNORER COMPLETEMENT les règles d'affichage et filtres visuels
    // 1) Récupérer tous les événements chargés dans FullCalendar
    const allLoadedEvents = calendarApi.getEvents();

    // Définir les dates de début et de fin pour le filtrage des événements
    let viewStart, viewEnd;
    
    // Si on est en vue mensuelle, ajuster les dates pour qu'elles correspondent exactement au début et à la fin du mois
    if (viewType === 'month') {
      const currentDate = calendarApi.getDate();
      const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1, 0, 0, 0, 0);
      const lastDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0, 23, 59, 59, 999);
      
      // Utiliser les dates calculées pour la période d'exportation
      viewStart = firstDayOfMonth;
      viewEnd = new Date(lastDayOfMonth.getTime() + 1); // Ajouter 1ms pour inclure le dernier jour
    } else if (viewType === 'quarter') {
      // Pour l'export 3 mois: du 1er du mois courant au dernier jour du mois dans 2 mois
      const currentDate = new Date();
      viewStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1, 0, 0, 0, 0);
      viewEnd = new Date(currentDate.getFullYear(), currentDate.getMonth() + 3, 0, 23, 59, 59, 999);
    } else {
      // Pour les autres vues, utiliser les dates de la vue actuelle
      viewStart = calendarApi.view.activeStart;
      viewEnd = calendarApi.view.activeEnd;
    }

    // 2) Ne garder que ceux inclus (au moins partiellement) dans la période d'export
    //    (événements qui commencent avant la fin ET finissent après le début)
    const eventsInRange = allLoadedEvents.filter(evt => {
      const start = evt.start ? new Date(evt.start) : null;
      const end = evt.end ? new Date(evt.end) : start; // si pas de end, on prend start
      if (!start) return false;
      return start < viewEnd && end >= viewStart;
    });

    console.log('Période d\'exportation:', viewStart.toISOString(), '->', viewEnd.toISOString());
    console.log('Total events chargés:', allLoadedEvents.length);
    console.log('Events dans la période:', eventsInRange.length);

    if (eventsInRange.length === 0) {
      alert('Aucun événement dans la période sélectionnée.');
      return;
    }
    
    // Trier les événements par date de début
    const sortedEvents = eventsInRange.sort((a, b) => new Date(a.start) - new Date(b.start));
    
    // Créer un nouveau document PDF
    const doc = new jsPDF();
    
    // Ajouter un titre avec style amélioré
    const title = `Calendrier - ${viewType === 'week' ? 'Semaine' : 'Mois'} du ${calendarApi.view.title}`;
    const periodText = `Période: ${viewStart.toLocaleDateString('fr-FR')} au ${new Date(viewEnd.getTime() - 1).toLocaleDateString('fr-FR')}`;
    
    // Ajouter un en-tête stylisé
    doc.setFillColor(128, 0, 0); // Rouge bordeaux (couleur primaire du thème)
    doc.rect(0, 0, doc.internal.pageSize.getWidth(), 30, 'F');
    doc.setTextColor(255, 255, 255); // Texte blanc
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text(title, 14, 15);
    
    // Ajouter la période
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(periodText, 14, 25);
    
    // Revenir à la couleur de texte noire pour le reste du document
    doc.setTextColor(0, 0, 0);
    
    // Ajouter une note sur l'export complet
    doc.setFontSize(10);
    let exportNote = 'Export complet: Tous les événements de la période (sans filtres appliqués)';
    
    // Note sur l'export complet
    doc.text('Export complet - Tous les événements de la période sans filtres', 14, 40);
    
    // Préparer les données pour le tableau avec plus de détails
    const tableData = sortedEvents.map(event => {
      const start = event.start ? new Date(event.start) : null;
      const end = event.end ? new Date(event.end) : null;
      
      const startDate = start ? start.toLocaleDateString('fr-FR') : '';
      const startTime = start ? start.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '';
      const endDate = end ? end.toLocaleDateString('fr-FR') : '';
      const endTime = end ? end.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '';
      
      const room = event.extendedProps.room?.name || '';
      const type = event.extendedProps.type || '';
      
      // Pour les événements de personnel, afficher le type spécifique au lieu de "Gestion du personnel"
      let typeLabel;
      if (type === 'personnel' && event.extendedProps.personnelType) {
        const personnelType = event.extendedProps.personnelType;
        typeLabel = personnelTypeOptions.find(t => t.value === personnelType)?.label || personnelType;
      } else {
        typeLabel = eventTypes.find(t => t.value === type)?.label || type;
      }
      
      // Récupérer des informations supplémentaires selon le type d'événement
      let additionalInfo = '';
      if (type === 'show') {
        const showType = event.extendedProps.showType || '';
        const showStatus = event.extendedProps.showStatus || '';
        const showTypeLabel = showTypeOptions.find(t => t.value === showType)?.label || showType;
        const showStatusLabel = showStatusOptions.find(s => s.value === showStatus)?.label || showStatus;
        additionalInfo = `Type: ${showTypeLabel}, Statut: ${showStatusLabel}`;
        
        if (event.extendedProps.expectedAttendees) {
          additionalInfo += `, Spectateurs attendus: ${event.extendedProps.expectedAttendees}`;
        }
        
        // Ajouter les informations d'hôtel et de transfert
        if (event.extendedProps.hotelName) {
          additionalInfo += `, Hôtel: ${event.extendedProps.hotelName}`;
        }
        if (event.extendedProps.arrivalDate) {
          additionalInfo += `, Arrivée: ${new Date(event.extendedProps.arrivalDate).toLocaleDateString('fr-FR')}`;
        }
        if (event.extendedProps.arrivalTime) {
          additionalInfo += ` ${event.extendedProps.arrivalTime}`;
        }
        if (event.extendedProps.departureTransfer) {
          additionalInfo += `, Transfert départ: ${event.extendedProps.departureTransfer}`;
        }
        if (event.extendedProps.arrivalTransfer) {
          additionalInfo += `, Transfert arrivée: ${event.extendedProps.arrivalTransfer}`;
        }
        if (event.extendedProps.contactProd) {
          additionalInfo += `, Contact prod: ${event.extendedProps.contactProd}`;
        }
        if (event.extendedProps.contactTech) {
          additionalInfo += `, Contact tech: ${event.extendedProps.contactTech}`;
        }
        if (event.extendedProps.ticketPrice) {
          additionalInfo += `, Prix: ${event.extendedProps.ticketPrice}€`;
        }
        if (event.extendedProps.coRealizationPercentage) {
          additionalInfo += `, Co-réalisation: ${event.extendedProps.coRealizationPercentage}%`;
        }
      } else if (type === 'rental') {
        const rentalStatus = event.extendedProps.rentalStatus || '';
        const rentalStatusLabel = rentalStatusOptions.find(s => s.value === rentalStatus)?.label || rentalStatus;
        additionalInfo = `Statut: ${rentalStatusLabel}`;
        
        if (event.extendedProps.rentalPrice) {
          additionalInfo += `, Prix: ${event.extendedProps.rentalPrice}€`;
        }
        if (event.extendedProps.vatPercentage) {
          additionalInfo += `, TVA: ${event.extendedProps.vatPercentage}%`;
        }
      }
      
      // Récupérer les utilisateurs assignés
      const assignedUsers = event.extendedProps.users || [];
      const usersList = assignedUsers.map(u => `${u.firstName} ${u.lastName}`).join(', ');
      
      return [
        startDate,
        startTime,
        endTime,
        event.title,
        room,
        typeLabel,
        usersList,
        additionalInfo,
        event.extendedProps.description || ''
      ];
    });
    
    // Ajouter le tableau au PDF avec un style amélioré
    autoTable(doc, {
      startY: 55,
      head: [['Date', 'Début', 'Fin', 'Titre', 'Salle', 'Type', 'Personnes assignées', 'Détails', 'Description']],
      body: tableData,
      theme: 'grid',
      styles: { fontSize: 8, cellPadding: 2, lineColor: [200, 200, 200] },
      headStyles: { 
        fillColor: [128, 0, 0], // Rouge bordeaux (couleur primaire du thème)
        textColor: 255,
        fontStyle: 'bold',
        halign: 'center'
      },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      columnStyles: {
        0: { cellWidth: 20 }, // Date - réduite
        1: { cellWidth: 12 }, // Heure début - réduite
        2: { cellWidth: 12 }, // Heure fin - réduite
        3: { cellWidth: 25, fontStyle: 'bold' }, // Titre en gras - affinée
        4: { cellWidth: 20 }, // Salle - réduite
        5: { cellWidth: 20 }, // Type - réduite
        6: { cellWidth: 25 }, // Personnes assignées - réduite
        7: { cellWidth: 25 }, // Détails - réduite
        8: { cellWidth: 35 } // Description - augmentée avec une largeur fixe
      },
      margin: { top: 60, left: 15, right: 15, bottom: 20 },
      didDrawPage: function(data) {
        // Ajouter un pied de page sur chaque page
        const pageHeight = doc.internal.pageSize.getHeight();
        doc.setFillColor(128, 0, 0); // Rouge bordeaux
        doc.rect(0, pageHeight - 15, doc.internal.pageSize.getWidth(), 15, 'F');
        doc.setTextColor(255, 255, 255); // Texte blanc
        doc.setFontSize(8);
        
        const exportDate = new Date().toLocaleString('fr-FR');
        const pageCount = doc.internal.getNumberOfPages();
        const currentPage = data.pageNumber;
        
        doc.text(`Exporté le ${exportDate}`, 14, pageHeight - 5);
        doc.text(`Page ${currentPage} sur ${pageCount}`, doc.internal.pageSize.getWidth() - 30, pageHeight - 5);
      }
    });
    
    // === RÉCAPITULATIF MENSUEL (ajouté en bas du PDF) ===
    const finalY = doc.lastAutoTable?.finalY || 45;
    let y = finalY + 15;
    
    const parisTz = 'Europe/Paris';
    const HOURS_PER_DAY = 7;
    
    // Agrégats par mois
    const byMonth = {};
    sortedEvents.forEach(evt => {
      const s = evt.start ? new Date(evt.start) : null;
      const e = evt.end ? new Date(evt.end) : s;
      if (!s || !e) return;
    
      // Clé mois libellée en FR et calée sur Europe/Paris
      const monthKey = s.toLocaleString('fr-FR', { timeZone: parisTz, month: 'long', year: 'numeric' });
    
      const type = evt.extendedProps?.type || evt.type || '';
      // On tolère les deux orthographes
      const personnelTypeRaw = evt.extendedProps?.personnelType || evt.personnelType || '';
      const personnelType = personnelTypeRaw === 'teletravail' ? 'télétravail' : personnelTypeRaw;
    
      const dur = (e.getTime() - s.getTime()) / 36e5; // heures
    
      if (!byMonth[monthKey]) {
        byMonth[monthKey] = {
          workedHours: 0,
          congesHours: 0,
          maladieHours: 0,
          absenceHours: 0,
          teletravailHours: 0,
          autreHours: 0,
        };
      }
    
      if (type === 'personnel') {
        switch (personnelType) {
          case 'CP':          byMonth[monthKey].congesHours      += dur; break;
          case 'maladie':     byMonth[monthKey].maladieHours     += dur; break;
          case 'absence':     byMonth[monthKey].absenceHours     += dur; break;
          case 'télétravail': byMonth[monthKey].teletravailHours += dur; break;
          default:            byMonth[monthKey].autreHours       += dur; break;
        }
      } else {
        byMonth[monthKey].workedHours += dur;
      }
    });
    
    // Fonction utilitaire d'affichage avec retour à la ligne si on touche le bas de page
    const ensureSpace = (inc=0) => {
      const bottom = doc.internal.pageSize.getHeight() - 20;
      if (y + inc > bottom) {
        doc.addPage();
        y = 20;
      }
    };
    
    // Titre général
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor(0, 0, 0);
    
    const monthKeys = Object.keys(byMonth);
    if (viewType === 'quarter' && monthKeys.length > 1) {
      // Export 3 mois : bloc par mois avec résumé pour chaque mois
      monthKeys.forEach((mk) => {
        const agg = byMonth[mk];
    
        // En-tête de section
        ensureSpace(22);
        doc.setFillColor(128, 0, 0); // bordeaux
        doc.setTextColor(255, 255, 255);
        doc.rect(14, y, doc.internal.pageSize.getWidth() - 28, 10, 'F');
        doc.text(`RÉCAPITULATIF — ${mk.toUpperCase()}`, 16, y + 7);
    
        // Corps
        y += 16;
        doc.setTextColor(0, 0, 0);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(11);
    
        const congeDays   = agg.congesHours   / HOURS_PER_DAY;
        const maladieDays = agg.maladieHours  / HOURS_PER_DAY;
    
        // Section des heures de travail
        ensureSpace(10);
        doc.setFillColor(240, 248, 255); // Bleu très clair
        doc.rect(14, y, doc.internal.pageSize.getWidth() - 28, 6, 'F');
        doc.setFont('helvetica', 'bold');
        doc.text('HEURES DE TRAVAIL', 16, y + 4);
        y += 10;
        
        doc.setFont('helvetica', 'normal');
        ensureSpace(6); doc.text(`• Total des heures travaillées: ${agg.workedHours.toFixed(1)}h`, 20, y); y += 6;
        
        // Section de gestion du personnel
        ensureSpace(10);
        doc.setFillColor(255, 248, 220); // Beige clair
        doc.rect(14, y, doc.internal.pageSize.getWidth() - 28, 6, 'F');
        doc.setFont('helvetica', 'bold');
        doc.text('GESTION DU PERSONNEL', 16, y + 4);
        y += 10;
        
        doc.setFont('helvetica', 'normal');
        ensureSpace(6); doc.text(`• Congés payés : ${congeDays ? congeDays.toFixed(1) : 0} jour(s) (${agg.congesHours.toFixed(1)}h)`, 20, y); y += 6;
        ensureSpace(6); doc.text(`• Maladie : ${maladieDays ? maladieDays.toFixed(1) : 0} jour(s)`, 20, y); y += 6;
        ensureSpace(6); doc.text(`• Absences : ${agg.absenceHours.toFixed(1)}h`, 20, y); y += 6;
        ensureSpace(6); doc.text(`• Télétravail : ${agg.teletravailHours.toFixed(1)}h`, 20, y); y += 6;
        ensureSpace(6); doc.text(`• Autres (personnel) : ${agg.autreHours.toFixed(1)}h`, 20, y); y += 6;
        
        // Récapitulatif global pour ce mois
        ensureSpace(10);
        doc.setFillColor(245, 245, 245); // Gris clair
        doc.rect(14, y, doc.internal.pageSize.getWidth() - 28, 6, 'F');
        doc.setFont('helvetica', 'bold');
        doc.text('RÉCAPITULATIF GLOBAL', 16, y + 4);
        y += 10;
        
        doc.setFont('helvetica', 'normal');
        const totalHoursMonth = agg.workedHours + agg.congesHours + agg.maladieHours + agg.absenceHours + agg.teletravailHours + agg.autreHours;
        ensureSpace(6); doc.text(`• Total général d'heures: ${totalHoursMonth.toFixed(1)}h`, 20, y); y += 6;
    
        // Marge sous-section
        y += 10;
      });
    } else {
      // Export 1 mois / 1 semaine : un seul bloc avec résumé de gestion du personnel
      const mk = monthKeys[0] || (new Date()).toLocaleString('fr-FR', { timeZone: parisTz, month: 'long', year: 'numeric' });
      const agg = byMonth[mk] || { workedHours: 0, congesHours: 0, maladieHours: 0, absenceHours: 0, teletravailHours: 0, autreHours: 0 };
    
      ensureSpace(22);
      doc.setFillColor(128, 0, 0);
      doc.setTextColor(255, 255, 255);
      doc.rect(14, y, doc.internal.pageSize.getWidth() - 28, 10, 'F');
      doc.text(`RÉSUMÉ DE GESTION DU PERSONNEL — ${mk.toUpperCase()}`, 16, y + 7);
    
      y += 16;
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(11);
    
      const congeDays   = agg.congesHours   / HOURS_PER_DAY;
      const maladieDays = agg.maladieHours  / HOURS_PER_DAY;
      
      // Section des heures de travail
      ensureSpace(10);
      doc.setFillColor(240, 248, 255); // Bleu très clair
      doc.rect(14, y, doc.internal.pageSize.getWidth() - 28, 6, 'F');
      doc.setFont('helvetica', 'bold');
      doc.text('HEURES DE TRAVAIL', 16, y + 4);
      y += 10;
      
      doc.setFont('helvetica', 'normal');
      ensureSpace(6); doc.text(`• Total des heures travaillées: ${agg.workedHours.toFixed(1)}h`, 20, y); y += 6;
      
      // Section de gestion du personnel
      ensureSpace(10);
      doc.setFillColor(255, 248, 220); // Beige clair
      doc.rect(14, y, doc.internal.pageSize.getWidth() - 28, 6, 'F');
      doc.setFont('helvetica', 'bold');
      doc.text('GESTION DU PERSONNEL', 16, y + 4);
      y += 10;
      
      doc.setFont('helvetica', 'normal');
      ensureSpace(6); doc.text(`• Congés payés : ${congeDays ? congeDays.toFixed(1) : 0} jour(s) (${agg.congesHours.toFixed(1)}h)`, 20, y); y += 6;
      ensureSpace(6); doc.text(`• Maladie : ${maladieDays ? maladieDays.toFixed(1) : 0} jour(s)`, 20, y); y += 6;
      ensureSpace(6); doc.text(`• Absences : ${agg.absenceHours.toFixed(1)}h`, 20, y); y += 6;
      ensureSpace(6); doc.text(`• Télétravail : ${agg.teletravailHours.toFixed(1)}h`, 20, y); y += 6;
      ensureSpace(6); doc.text(`• Autres (personnel) : ${agg.autreHours.toFixed(1)}h`, 20, y); y += 6;
      
      // Récapitulatif global
      ensureSpace(10);
      doc.setFillColor(245, 245, 245); // Gris clair
      doc.rect(14, y, doc.internal.pageSize.getWidth() - 28, 6, 'F');
      doc.setFont('helvetica', 'bold');
      doc.text('RÉCAPITULATIF GLOBAL', 16, y + 4);
      y += 10;
      
      doc.setFont('helvetica', 'normal');
      const totalHours = agg.workedHours + agg.congesHours + agg.maladieHours + agg.absenceHours + agg.teletravailHours + agg.autreHours;
      ensureSpace(6); doc.text(`• Total général d'heures: ${totalHours.toFixed(1)}h`, 20, y); y += 6;
    }
    
    // === RÉCAPITULATIF HEBDOMADAIRE PAR UTILISATEUR (heures travaillées) ===
    if (viewType === 'week') {
      // Calculer les heures par utilisateur sur la période (hors CP/maladie/absence)
      const userHoursMap = {};
      sortedEvents.forEach(evt => {
        const s = evt.start ? new Date(evt.start) : null;
        const e = evt.end ? new Date(evt.end) : s;
        if (!s || !e) return;
        const dur = Math.max(0, (e.getTime() - s.getTime()) / 36e5);
        const type = evt.extendedProps?.type || evt.type || '';
        const ptRaw = evt.extendedProps?.personnelType || evt.personnelType || '';
        const pt = ptRaw === 'teletravail' ? 'télétravail' : ptRaw;
        // Compter comme heures réalisées si non-personnel, ou télétravail
        const countsAsWork = type !== 'personnel' || pt === 'télétravail';
        if (!countsAsWork) return;
        const assigned = Array.isArray(evt.extendedProps?.users) ? evt.extendedProps.users : [];
        assigned.forEach(u => {
          const key = `${u.firstName || ''} ${u.lastName || ''}`.trim();
          if (!key) return;
          userHoursMap[key] = (userHoursMap[key] || 0) + dur;
        });
      });

      const summaryRows = Object.keys(userHoursMap)
        .sort((a, b) => a.localeCompare(b, 'fr'))
        .map(name => [name, `${(Math.round(userHoursMap[name] * 10) / 10).toFixed(1)}h`]);

      if (summaryRows.length > 0) {
        // Ajouter un titre de section et le tableau
        const sectionTitle = 'RÉCAPITULATIF HEBDOMADAIRE — Heures par utilisateur';
        const bottom = doc.internal.pageSize.getHeight() - 20;
        if (y + 30 > bottom) { doc.addPage(); y = 20; }
        doc.setFillColor(128, 0, 0);
        doc.setTextColor(255, 255, 255);
        doc.rect(14, y, doc.internal.pageSize.getWidth() - 28, 10, 'F');
        doc.text(sectionTitle, 16, y + 7);
        y += 14;

        autoTable(doc, {
          startY: y,
          head: [['Utilisateur', 'Heures réalisées (semaine)']],
          body: summaryRows,
          theme: 'grid',
          styles: { fontSize: 9, cellPadding: 3 },
          headStyles: {
            fillColor: [128, 0, 0],
            textColor: 255,
            fontStyle: 'bold',
            halign: 'center',
          },
          columnStyles: {
            0: { cellWidth: 80 },
            1: { cellWidth: 35, halign: 'right' },
          },
          margin: { left: 15, right: 15 },
        });
      }
    }

    // Enregistrer le PDF avec un nom plus descriptif
    const dateStr = calendarApi.view.title.replace(/\s/g, '_');
    doc.save(`planning_${viewType === 'week' ? 'semaine' : 'mois'}_${dateStr}.pdf`);
    
    // Note: Nous n'avons pas besoin de restaurer les dates originales car nous n'avons pas modifié
    // les propriétés activeStart et activeEnd de calendarApi.view, nous avons utilisé des variables locales à la place
  };
  
  // Fonction pour exporter uniquement les événements où l'utilisateur est assigné
  const exportMyAssignedEventsToPDF = (viewType) => {
    // Vérifier si le calendrier est disponible
    if (!calendarRef.current) return;
    
    // Obtenir la vue actuelle du calendrier
    const calendarApi = calendarRef.current.getApi();
    
    // Changer la vue si nécessaire
    if (viewType === 'week' && calendarApi.view.type !== 'timeGridWeek') {
      calendarApi.changeView('timeGridWeek');
    } else if (viewType === 'month' && calendarApi.view.type !== 'dayGridMonth') {
      calendarApi.changeView('dayGridMonth');
    }
    
    // Obtenir uniquement les événements où l'utilisateur est assigné
    const myAssignedEvents = calendarApi.getEvents().filter(event => {
      // Vérifier si l'utilisateur est assigné à cet événement
      const isAssigned = event.extendedProps.users?.some(u => u.id === user.id);
      return isAssigned;
    });
    
    // Définir les dates de la vue
    let viewStart, viewEnd;
    if (viewType === 'month') {
      const currentDate = calendarApi.getDate();
      const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1, 0, 0, 0, 0);
      const lastDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0, 23, 59, 59, 999);
      viewStart = firstDayOfMonth;
      viewEnd = new Date(lastDayOfMonth.getTime() + 1);
    } else if (viewType === 'quarter') {
      // Pour l'export 3 mois: du 1er du mois courant au dernier jour du mois dans 2 mois
      const currentDate = new Date();
      viewStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1, 0, 0, 0, 0);
      viewEnd = new Date(currentDate.getFullYear(), currentDate.getMonth() + 3, 0, 23, 59, 59, 999);
    } else {
      viewStart = calendarApi.view.activeStart;
      viewEnd = calendarApi.view.activeEnd;
    }
    
    // Filtrer et trier les événements par date
    const sortedEvents = [...myAssignedEvents].filter(event => {
      const eventStart = event.start;
      const eventEnd = event.end || event.start;
      return eventStart < viewEnd && eventEnd >= viewStart;
    }).sort((a, b) => {
      return a.start.getTime() - b.start.getTime();
    });
    
    // Créer un nouveau document PDF
    const doc = new jsPDF();
    
    // Ajouter un titre avec style amélioré
    const title = `Mon planning - ${viewType === 'week' ? 'Semaine' : viewType === 'month' ? 'Mois' : '3 Mois'} du ${viewType === 'quarter' ? format(viewStart, 'MMMM yyyy', { locale: fr }) : calendarApi.view.title}`;
    const periodText = `Période: ${viewStart.toLocaleDateString('fr-FR')} au ${new Date(viewEnd.getTime() - 1).toLocaleDateString('fr-FR')}`;
    
    // Ajouter un en-tête stylisé
    doc.setFillColor(128, 0, 0);
    doc.rect(0, 0, doc.internal.pageSize.getWidth(), 30, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text(title, 14, 15);
    
    // Ajouter la période
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(periodText, 14, 25);
    
    // Revenir à la couleur de texte noire
    doc.setTextColor(0, 0, 0);
    
    // Ajouter des informations sur le filtre
    doc.setFontSize(10);
    const filterText = `Filtré pour: ${user.firstName} ${user.lastName} (événements assignés uniquement)`;
    doc.text(filterText, 14, 40);
    
    // Grouper les événements par mois pour l'affichage avec totaux mensuels
    const eventsByMonth = {};
    const monthlyHours = {};
    let totalHours = 0;
    let totalCongePayeHours = 0;
    let totalShows = 0; // Compteur de spectacles pour les artistes
    
    // Grouper les événements par mois
    sortedEvents.forEach(event => {
      const start = event.start ? new Date(event.start) : null;
      if (start) {
        const monthKey = start.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
        if (!eventsByMonth[monthKey]) {
          eventsByMonth[monthKey] = [];
        }
        eventsByMonth[monthKey].push(event);
      }
    });
    
    // Préparer les données pour le tableau avec totaux mensuels
    const tableData = [];
    
    // Traiter chaque mois séparément
    Object.entries(eventsByMonth).forEach(([monthKey, monthEvents], monthIndex) => {
      // Ajouter un en-tête de mois si c'est un export de 3 mois
      if (viewType === 'quarter' && Object.keys(eventsByMonth).length > 1) {
        tableData.push([
          { content: monthKey.toUpperCase(), colSpan: 9, styles: { 
            fillColor: [128, 0, 0], 
            textColor: [255, 255, 255], 
            fontStyle: 'bold', 
            halign: 'center',
            fontSize: 10
          }}
        ]);
      }
      
      let monthlyTotal = 0;
      
      // Ajouter les événements du mois
      monthEvents.forEach(event => {
      const start = event.start ? new Date(event.start) : null;
      const end = event.end ? new Date(event.end) : null;
      
      const startDate = start ? start.toLocaleDateString('fr-FR') : '';
      const startTime = start ? start.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '';
      const endTime = end ? end.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '';
      
      // Calculer la durée en heures
      let duration = 0;
      if (start && end) {
        duration = (end.getTime() - start.getTime()) / (1000 * 60 * 60); // en heures
      }
      
      // Vérifier si c'est un événement de personnel (gestion du personnel)
      const isPersonnelManagement = event.extendedProps.personnelType && event.extendedProps.personnelType !== 'teletravail';
      
      // Ne pas afficher les heures dans le tableau - elles seront affichées uniquement dans le résumé final
      const durationText = '';
      
      // Ajouter aux totaux mensuels seulement pour le télétravail
      if (start && duration > 0 && !isPersonnelManagement) {
        if (!monthlyHours[monthKey]) {
          monthlyHours[monthKey] = 0;
        }
        monthlyHours[monthKey] += duration;
        monthlyTotal += duration;
        totalHours += duration;
      }
      
      // Compter les spectacles pour les artistes
      if (event.extendedProps.type === 'show') {
        totalShows++;
      }
      
      // Calculer les heures de congés payés spécifiquement
      if (event.extendedProps.personnelType === 'CP' && start && end) {
        const cpDuration = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
        totalCongePayeHours += cpDuration;
      }
      
      const room = event.extendedProps.room?.name || '';
      const type = event.extendedProps.type || '';
      let typeLabel = eventTypes.find(t => t.value === type)?.label || type;
      
      // Remplacer "Gestion du personnel" par le type spécifique de personnel
      if (type === 'personnel' && event.extendedProps.personnelType) {
        const personnelTypeLabel = personnelTypeOptions.find(p => p.value === event.extendedProps.personnelType)?.label;
        if (personnelTypeLabel) {
          typeLabel = personnelTypeLabel;
        }
      }
      
      // Récupérer des informations supplémentaires selon le type d'événement
      let additionalInfo = '';
      if (type === 'show') {
        const showType = event.extendedProps.showType || '';
        const showStatus = event.extendedProps.showStatus || '';
        const showTypeLabel = showTypeOptions.find(t => t.value === showType)?.label || showType;
        const showStatusLabel = showStatusOptions.find(s => s.value === showStatus)?.label || showStatus;
        additionalInfo = `Type: ${showTypeLabel}, Statut: ${showStatusLabel}`;
        
        if (event.extendedProps.expectedAttendees) {
          additionalInfo += `, Spectateurs attendus: ${event.extendedProps.expectedAttendees}`;
        }
        
        // Ajouter les informations d'hôtel et de transfert
        if (event.extendedProps.hotelName) {
          additionalInfo += `, Hôtel: ${event.extendedProps.hotelName}`;
        }
        if (event.extendedProps.arrivalDate) {
          additionalInfo += `, Arrivée: ${new Date(event.extendedProps.arrivalDate).toLocaleDateString('fr-FR')}`;
        }
        if (event.extendedProps.arrivalTime) {
          additionalInfo += ` ${event.extendedProps.arrivalTime}`;
        }
        if (event.extendedProps.departureTransfer) {
          additionalInfo += `, Transfert départ: ${event.extendedProps.departureTransfer}`;
        }
        if (event.extendedProps.arrivalTransfer) {
          additionalInfo += `, Transfert arrivée: ${event.extendedProps.arrivalTransfer}`;
        }
        if (event.extendedProps.contactProd) {
          additionalInfo += `, Contact prod: ${event.extendedProps.contactProd}`;
        }
        if (event.extendedProps.contactTech) {
          additionalInfo += `, Contact tech: ${event.extendedProps.contactTech}`;
        }
        if (event.extendedProps.ticketPrice) {
          additionalInfo += `, Prix: ${event.extendedProps.ticketPrice}€`;
        }
        if (event.extendedProps.coRealizationPercentage) {
          additionalInfo += `, Co-réalisation: ${event.extendedProps.coRealizationPercentage}%`;
        }
      } else if (type === 'rental') {
        const rentalStatus = event.extendedProps.rentalStatus || '';
        const rentalStatusLabel = rentalStatusOptions.find(s => s.value === rentalStatus)?.label || rentalStatus;
        additionalInfo = `Statut: ${rentalStatusLabel}`;
        
        if (event.extendedProps.rentalPrice) {
          additionalInfo += `, Prix: ${event.extendedProps.rentalPrice}€`;
        }
        if (event.extendedProps.vatPercentage) {
          additionalInfo += `, TVA: ${event.extendedProps.vatPercentage}%`;
        }
      }
      
      tableData.push([
        startDate,
        startTime,
        endTime,
        event.title,
        durationText,
        room,
        typeLabel,
        additionalInfo,
        event.extendedProps.description || ''
      ]);
    });
    
    // Les totaux mensuels ne sont plus affichés dans le tableau - ils seront uniquement dans le résumé final
  });
    
    // Ajouter le tableau au PDF
    autoTable(doc, {
      startY: 55,
      head: [['Date', 'Début', 'Fin', 'Titre', 'Heures', 'Salle', 'Type', 'Détails', 'Description']],
      body: tableData,
      theme: 'grid',
      styles: { fontSize: 8, cellPadding: 2, lineColor: [200, 200, 200] },
      headStyles: { 
        fillColor: [128, 0, 0],
        textColor: 255,
        fontStyle: 'bold',
        halign: 'center'
      },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      columnStyles: {
        0: { cellWidth: 18 },
        1: { cellWidth: 12 },
        2: { cellWidth: 12 },
        3: { cellWidth: 30, fontStyle: 'bold' },
        4: { cellWidth: 15, halign: 'center' },
        5: { cellWidth: 20 },
        6: { cellWidth: 20 },
        7: { cellWidth: 25 },
        8: { cellWidth: 35 }
      },
      margin: { top: 60, left: 15, right: 15, bottom: 20 },
      didDrawPage: function(data) {
        const pageHeight = doc.internal.pageSize.getHeight();
        doc.setFillColor(128, 0, 0);
        doc.rect(0, pageHeight - 15, doc.internal.pageSize.getWidth(), 15, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(8);
        
        const exportDate = new Date().toLocaleString('fr-FR');
        const pageCount = doc.internal.getNumberOfPages();
        const currentPage = data.pageNumber;
        
        doc.text(`Exporté le ${exportDate}`, 14, pageHeight - 5);
        doc.text(`Page ${currentPage} sur ${pageCount}`, doc.internal.pageSize.getWidth() - 30, pageHeight - 5);
      }
    });
    
    // Ajouter le résumé des heures à la fin du PDF
    const startY = finalY + 20;
    
    // Créer un encadré pour le résumé des heures
    const boxWidth = 80;
    const boxHeight = 40; // Hauteur réduite pour 2 lignes seulement
    
    // Dessiner le fond de l'encadré
    doc.setFillColor(248, 249, 250); // Gris très clair
    doc.rect(14, startY, boxWidth, boxHeight, 'F');
    
    // Dessiner la bordure
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.5);
    doc.rect(14, startY, boxWidth, boxHeight, 'S');
    
    // Titre du résumé avec fond coloré
    doc.setFillColor(128, 0, 0); // Rouge bordeaux
    doc.rect(14, startY, boxWidth, 12, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    
    // Vérifier si l'utilisateur est un artiste
    const isArtist = user.role === 'artistesProd' || user.role === 'artistesCoProd';
    
    if (isArtist) {
      doc.text('RÉSUMÉ DES CACHETS', 16, startY + 8);
    } else {
      doc.text('RÉSUMÉ DES HEURES', 16, startY + 8);
    }
    
    // Afficher le total approprié selon le type d'utilisateur
    let yPosition = startY + 20;
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    
    if (isArtist) {
      // Pour les artistes : afficher le total des cachets (spectacles)
      doc.setTextColor(128, 0, 0);
      doc.text('TOTAL CACHETS:', 18, yPosition);
      doc.setFontSize(12);
      doc.text(`${totalShows}`, 70, yPosition);
      
      // Congés payés pour les artistes
      yPosition += 12;
      doc.setFontSize(10);
      doc.text('CONGÉS PAYÉS:', 18, yPosition);
      doc.setFontSize(12);
      doc.text(`${totalCongePayeHours.toFixed(1)}h`, 70, yPosition);
    } else {
      // Pour les autres utilisateurs : afficher le total des heures
      doc.setTextColor(128, 0, 0);
      doc.text('TOTAL HEURES:', 18, yPosition);
      doc.setFontSize(12);
      doc.text(`${totalHours.toFixed(1)}h`, 70, yPosition);
      
      // Congés payés
      yPosition += 12;
      doc.setFontSize(10);
      doc.text('CONGÉS PAYÉS:', 18, yPosition);
      doc.setFontSize(12);
      doc.text(`${totalCongePayeHours.toFixed(1)}h`, 70, yPosition);
    }
    
    // === RÉCAPITULATIF MENSUEL (ajouté en bas du PDF) ===
    const finalY = doc.lastAutoTable?.finalY || 45;
    let y = finalY + 15;
    
    const parisTz = 'Europe/Paris';
    const HOURS_PER_DAY = 7;
    
    // Agrégats par mois
    const byMonth = {};
    sortedEvents.forEach(evt => {
      const s = evt.start ? new Date(evt.start) : null;
      const e = evt.end ? new Date(evt.end) : s;
      if (!s || !e) return;
    
      // Clé mois libellée en FR et calée sur Europe/Paris
      const monthKey = s.toLocaleString('fr-FR', { timeZone: parisTz, month: 'long', year: 'numeric' });
    
      const type = evt.extendedProps?.type || evt.type || '';
      // On tolère les deux orthographes
      const personnelTypeRaw = evt.extendedProps?.personnelType || evt.personnelType || '';
      const personnelType = personnelTypeRaw === 'teletravail' ? 'télétravail' : personnelTypeRaw;
    
      const dur = (e.getTime() - s.getTime()) / 36e5; // heures
    
      if (!byMonth[monthKey]) {
        byMonth[monthKey] = {
          workedHours: 0,
          congesHours: 0,
          maladieHours: 0,
          absenceHours: 0,
          teletravailHours: 0,
          autreHours: 0,
        };
      }
    
      if (type === 'personnel') {
        switch (personnelType) {
          case 'CP':          byMonth[monthKey].congesHours      += dur; break;
          case 'maladie':     byMonth[monthKey].maladieHours     += dur; break;
          case 'absence':     byMonth[monthKey].absenceHours     += dur; break;
          case 'télétravail': byMonth[monthKey].teletravailHours += dur; break;
          default:            byMonth[monthKey].autreHours       += dur; break;
        }
      } else {
        byMonth[monthKey].workedHours += dur;
      }
    });
    
    // Fonction utilitaire d'affichage avec retour à la ligne si on touche le bas de page
    const ensureSpace = (inc=0) => {
      const bottom = doc.internal.pageSize.getHeight() - 20;
      if (y + inc > bottom) {
        doc.addPage();
        y = 20;
      }
    };
    
    // Titre général
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor(0, 0, 0);
    
    const monthKeys = Object.keys(byMonth);
    if (viewType === 'quarter' && monthKeys.length > 1) {
      // Export 3 mois : bloc par mois
      monthKeys.forEach((mk) => {
        const agg = byMonth[mk];
    
        // En-tête de section
        ensureSpace(22);
        doc.setFillColor(128, 0, 0); // bordeaux
        doc.setTextColor(255, 255, 255);
        doc.rect(14, y, doc.internal.pageSize.getWidth() - 28, 10, 'F');
        doc.text(`RÉCAPITULATIF — ${mk.toUpperCase()}`, 16, y + 7);
    
        // Corps
        y += 16;
        doc.setTextColor(0, 0, 0);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(11);
    
        const congeDays   = agg.congesHours   / HOURS_PER_DAY;
        const maladieDays = agg.maladieHours  / HOURS_PER_DAY;
    
        const lines = [
          `• Heures travaillées : ${agg.workedHours.toFixed(1)}h`,
          `• Congés payés : ${congeDays ? congeDays.toFixed(1) : 0} jour(s) (${agg.congesHours.toFixed(1)}h)`,
          `• Maladie : ${maladieDays ? maladieDays.toFixed(1) : 0} jour(s)`,
          `• Absences : ${agg.absenceHours.toFixed(1)}h`,
          `• Télétravail : ${agg.teletravailHours.toFixed(1)}h`,
          `• Autres (personnel) : ${agg.autreHours.toFixed(1)}h`,
        ];
    
        lines.forEach(t => { ensureSpace(6); doc.text(t, 20, y); y += 6; });
    
        // Marge sous-section
        y += 6;
      });
    } else {
      // Export 1 mois / 1 semaine : un seul bloc
      const mk = monthKeys[0] || (new Date()).toLocaleString('fr-FR', { timeZone: parisTz, month: 'long', year: 'numeric' });
      const agg = byMonth[mk] || { workedHours: 0, congesHours: 0, maladieHours: 0, absenceHours: 0, teletravailHours: 0, autreHours: 0 };
    
      ensureSpace(22);
      doc.setFillColor(128, 0, 0);
      doc.setTextColor(255, 255, 255);
      doc.rect(14, y, doc.internal.pageSize.getWidth() - 28, 10, 'F');
      doc.text(`RÉCAPITULATIF — ${mk.toUpperCase()}`, 16, y + 7);
    
      y += 16;
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(11);
    
      const congeDays   = agg.congesHours   / HOURS_PER_DAY;
      const maladieDays = agg.maladieHours  / HOURS_PER_DAY;
    
      const lines = [
        `• Heures travaillées : ${agg.workedHours.toFixed(1)}h`,
        `• Congés payés : ${congeDays ? congeDays.toFixed(1) : 0} jour(s) (${agg.congesHours.toFixed(1)}h)`,
        `• Maladie : ${maladieDays ? maladieDays.toFixed(1) : 0} jour(s)`,
        `• Absences : ${agg.absenceHours.toFixed(1)}h`,
        `• Télétravail : ${agg.teletravailHours.toFixed(1)}h`,
        `• Autres (personnel) : ${agg.autreHours.toFixed(1)}h`,
      ];
    
      lines.forEach(t => { ensureSpace(6); doc.text(t, 20, y); y += 6; });
    }
    
    // Enregistrer le PDF avec un nom descriptif
    const dateStr = viewType === 'quarter' ? format(viewStart, 'yyyy-MM') : calendarApi.view.title.replace(/\s/g, '_');
    const userName = `${user.firstName}_${user.lastName}`.replace(/\s/g, '_');
    const periodName = viewType === 'week' ? 'semaine' : viewType === 'month' ? 'mois' : '3mois';
    doc.save(`mon_planning_${periodName}_${dateStr}_${userName}.pdf`);
  };
  
  // Fonction pour exporter le calendrier en Excel
  const exportToExcel = (viewType) => {
    try {
      // Vérifier si le calendrier est disponible
      if (!calendarRef.current) {
        alert('Calendrier non disponible');
        return;
      }
      
      // Obtenir la vue actuelle du calendrier
      const calendarApi = calendarRef.current.getApi();
      
      // Changer la vue si nécessaire
      if (viewType === 'week' && calendarApi.view.type !== 'timeGridWeek') {
        calendarApi.changeView('timeGridWeek');
      } else if (viewType === 'month' && calendarApi.view.type !== 'dayGridMonth') {
        calendarApi.changeView('dayGridMonth');
      }
        
      // Définir les dates de début et de fin pour le filtrage des événements
      let viewStart, viewEnd, originalStart, originalEnd;
      
      // Si on est en vue mensuelle, ajuster les dates pour qu'elles correspondent exactement au début et à la fin du mois
      if (viewType === 'month') {
        const currentDate = calendarApi.getDate();
        const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1, 0, 0, 0, 0);
        const lastDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0, 23, 59, 59, 999);
        
        // Stocker les dates originales pour référence
        originalStart = calendarApi.view.activeStart;
        originalEnd = calendarApi.view.activeEnd;
        
        // Utiliser les dates calculées pour la période d'exportation
        viewStart = firstDayOfMonth;
        viewEnd = new Date(lastDayOfMonth.getTime() + 1); // Ajouter 1ms pour inclure le dernier jour
      } else {
        // Pour les autres vues, utiliser les dates de la vue actuelle
        viewStart = calendarApi.view.activeStart;
        viewEnd = calendarApi.view.activeEnd;
      }
      
      // Obtenir TOUS les événements sans filtres pour l'export
      const allEvents = calendarApi.getEvents().filter(event => {
        // Vérifier uniquement si l'événement est dans la plage de dates de la vue actuelle
        const eventStart = event.start;
        const eventEnd = event.end || event.start; // Si pas de fin, utiliser le début
        
        // Un événement est visible s'il chevauche la période de la vue
        const isInDateRange = eventStart < viewEnd && eventEnd >= viewStart;
        
        return isInDateRange;
      });
      
      console.log('Période d\'exportation:', viewStart.toLocaleDateString('fr-FR'), 'au', new Date(viewEnd.getTime() - 1).toLocaleDateString('fr-FR'));
      
      console.log('Nombre d\'événements à exporter:', allEvents.length);
      
      if (allEvents.length === 0) {
        alert('Aucun événement à exporter pour cette période.');
        return;
      }
      
      // Regrouper les événements récurrents
      const groupedEvents = {};
      const nonRecurringEvents = [];
      
      allEvents.forEach(event => {
        const recurrenceId = event.extendedProps.recurrenceId;
        
        // Si l'événement fait partie d'une série récurrente
        if (recurrenceId && event.extendedProps.isRecurring) {
          if (!groupedEvents[recurrenceId]) {
            groupedEvents[recurrenceId] = {
              title: event.title,
              events: [],
              room: event.extendedProps.room,
              type: event.extendedProps.type,
              description: event.extendedProps.description,
              users: event.extendedProps.users,
              showStatus: event.extendedProps.showStatus || event.extendedProps.rentalStatus
            };
          }
          groupedEvents[recurrenceId].events.push(event);
        } else {
          // Événement non récurrent
          nonRecurringEvents.push(event);
        }
      });
      
      // Créer un tableau pour les données des événements (format AOA - Array of Arrays)
      const eventHeaders = ['Titre', 'Date début', 'Heure début', 'Date fin', 'Heure fin', 'Salle', 'Type', 'Description', 'Utilisateurs assignés', 'Statut', 'Récurrence'];
      const eventRows = [eventHeaders];
      
      // Ajouter les événements non récurrents
      nonRecurringEvents.forEach(event => {
        const start = event.start ? new Date(event.start) : null;
        const end = event.end ? new Date(event.end) : null;
        
        const room = event.extendedProps.room?.name || '';
        const type = event.extendedProps.type || '';
        let typeLabel = eventTypes.find(t => t.value === type)?.label || type;
        
        // Pour les événements de personnel, afficher le type spécifique au lieu de "Gestion du personnel"
        if (type === 'personnel' && event.extendedProps.personnelType) {
          const personnelTypeLabel = personnelTypeOptions.find(p => p.value === event.extendedProps.personnelType)?.label;
          if (personnelTypeLabel) {
            typeLabel = personnelTypeLabel;
          }
        }
        
        // Liste des utilisateurs assignés
        const assignedUsers = event.extendedProps.users?.map(u => `${u.firstName} ${u.lastName} (${u.role})`).join(', ') || '';
        
        const row = [
          event.title,
          start ? start.toLocaleDateString('fr-FR') : '',
          start ? start.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '',
          end ? end.toLocaleDateString('fr-FR') : '',
          end ? end.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '',
          room,
          typeLabel,
          event.extendedProps.description || '',
          assignedUsers,
          event.extendedProps.showStatus || event.extendedProps.rentalStatus || '',
          'Non' // Non récurrent
        ];
        
        eventRows.push(row);
      });
      
      // Ajouter les événements récurrents (regroupés)
      Object.values(groupedEvents).forEach(group => {
        // Trier les événements par date
        const sortedEvents = group.events.sort((a, b) => a.start - b.start);
        
        // Prendre le premier et le dernier événement de la série
        const firstEvent = sortedEvents[0];
        const lastEvent = sortedEvents[sortedEvents.length - 1];
        
        const firstStart = firstEvent.start ? new Date(firstEvent.start) : null;
        const lastEnd = lastEvent.end ? new Date(lastEvent.end) : null;
        
        const room = group.room?.name || '';
        const type = group.type || '';
        let typeLabel = eventTypes.find(t => t.value === type)?.label || type;
        
        // Pour les événements de personnel, afficher le type spécifique au lieu de "Gestion du personnel"
        if (type === 'personnel' && group.personnelType) {
          const personnelTypeLabel = personnelTypeOptions.find(p => p.value === group.personnelType)?.label;
          if (personnelTypeLabel) {
            typeLabel = personnelTypeLabel;
          }
        }
        
        // Liste des utilisateurs assignés
        const assignedUsers = group.users?.map(u => `${u.firstName} ${u.lastName} (${u.role})`).join(', ') || '';
        
        // Créer une chaîne pour la récurrence
        const recurrenceInfo = `Oui (${sortedEvents.length} occurrences du ${firstStart.toLocaleDateString('fr-FR')} au ${lastEnd.toLocaleDateString('fr-FR')})`;
        
        const row = [
          group.title,
          firstStart ? firstStart.toLocaleDateString('fr-FR') : '',
          firstStart ? firstStart.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '',
          lastEnd ? lastEnd.toLocaleDateString('fr-FR') : '',
          lastEnd ? lastEnd.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '',
          room,
          typeLabel,
          group.description || '',
          assignedUsers,
          group.showStatus || '',
          recurrenceInfo
        ];
        
        eventRows.push(row);
      });
      
      console.log('Données des événements (format tableau):', eventRows);
      
      // Créer un tableau pour les informations de filtrage
      const filterData = [
        ['Calendrier - ' + (viewType === 'week' ? 'Semaine' : 'Mois') + ' du ' + calendarApi.view.title],
        [],
        ['Période exportée:', viewStart.toLocaleDateString('fr-FR') + ' au ' + new Date(viewEnd.getTime() - 1).toLocaleDateString('fr-FR')],
        [],
        ['Filtres appliqués:']
      ];
      
      if (roomFilters.length > 0) {
        const roomNames = roomFilters.map(id => {
          const room = rooms.find(r => r.id.toString() === id);
          return room ? room.name : id;
        }).join(', ');
        filterData.push(['Salles:', roomNames]);
      }
      
      if (typeFilters.length > 0) {
        const typeNames = typeFilters.map(type => {
          const typeObj = eventTypes.find(t => t.value === type);
          return typeObj ? typeObj.label : type;
        }).join(', ');
        filterData.push(['Types:', typeNames]);
      }
      
      if (!showAllEvents) {
        filterData.push(['Affichage:', 'Mes événements uniquement']);
      }
      
      if (roomFilters.length === 0 && typeFilters.length === 0 && showAllEvents) {
        filterData.push(['Filtres:', 'Aucun filtre appliqué']);
      }
      
      filterData.push([]);
      filterData.push(['Date d\'export:', new Date().toLocaleString('fr-FR')]);
      
      console.log('Données des filtres:', filterData);
      
      // Créer un nouveau classeur
      const wb = XLSX.utils.book_new();
      
      // Créer les feuilles à partir des tableaux (AOA - Array of Arrays)
      const wsInfo = XLSX.utils.aoa_to_sheet(filterData);
      const wsEvents = XLSX.utils.aoa_to_sheet(eventRows);
      
      // Améliorer le formatage de la feuille d'informations
      // Définir la largeur des colonnes pour la feuille d'informations
      wsInfo['!cols'] = [{ wch: 25 }, { wch: 50 }];
      
      // Formater le titre principal (première ligne) en gras et plus grand
      if (!wsInfo['!rows']) wsInfo['!rows'] = [];
      wsInfo['!rows'][0] = { hidden: false, hpt: 36 }; // Hauteur plus grande pour le titre
      
      const infoRange = XLSX.utils.decode_range(wsInfo['!ref']);
      // Formater le titre
      const titleCell = XLSX.utils.encode_cell({ r: 0, c: 0 });
      if (wsInfo[titleCell]) {
        wsInfo[titleCell].s = {
          font: { bold: true, sz: 16, color: { rgb: "4472C4" } },
          alignment: { horizontal: "left" },
          border: {
            bottom: { style: "medium", color: { rgb: "4472C4" } }
          }
        };
      }
      
      // Style pour les en-têtes de section
      const sectionHeaderStyle = {
        font: { bold: true, color: { rgb: "FFFFFF" } },
        fill: { fgColor: { rgb: "4472C4" } },
        alignment: { horizontal: "left", vertical: "center" },
        border: {
          top: { style: "thin", color: { rgb: "000000" } },
          bottom: { style: "thin", color: { rgb: "000000" } },
          left: { style: "thin", color: { rgb: "000000" } },
          right: { style: "thin", color: { rgb: "000000" } }
        }
      };
      
      // Style pour les étiquettes
      const labelStyle = {
        font: { bold: true },
        alignment: { horizontal: "left", vertical: "center" },
        border: {
          top: { style: "thin", color: { rgb: "D9D9D9" } },
          bottom: { style: "thin", color: { rgb: "D9D9D9" } },
          left: { style: "thin", color: { rgb: "D9D9D9" } },
          right: { style: "thin", color: { rgb: "D9D9D9" } }
        }
      };
      
      // Style pour les valeurs
      const valueStyle = {
        alignment: { horizontal: "left", vertical: "center" },
        border: {
          top: { style: "thin", color: { rgb: "D9D9D9" } },
          bottom: { style: "thin", color: { rgb: "D9D9D9" } },
          left: { style: "thin", color: { rgb: "D9D9D9" } },
          right: { style: "thin", color: { rgb: "D9D9D9" } }
        }
      };
      
      // Formater les en-têtes de section et les données
      for (let R = 0; R <= infoRange.e.r; ++R) {
        const cell = XLSX.utils.encode_cell({ r: R, c: 0 });
        const valueCell = XLSX.utils.encode_cell({ r: R, c: 1 });
        
        // Appliquer le style d'en-tête de section
        if (wsInfo[cell] && (wsInfo[cell].v === 'Filtres appliqués:' || wsInfo[cell].v === 'Date d\'export:')) {
          wsInfo[cell].s = sectionHeaderStyle;
          // Définir la hauteur de la ligne pour les en-têtes de section
          if (!wsInfo['!rows'][R]) wsInfo['!rows'][R] = {};
          wsInfo['!rows'][R].hpt = 24;
        }
        // Appliquer le style d'étiquette aux autres cellules non vides de la première colonne
        else if (wsInfo[cell] && wsInfo[cell].v && R !== 0) {
          wsInfo[cell].s = labelStyle;
        }
        
        // Appliquer le style de valeur aux cellules de la deuxième colonne
        if (wsInfo[valueCell] && wsInfo[valueCell].v) {
          wsInfo[valueCell].s = valueStyle;
        }
      }
      
      // Définir la largeur des colonnes pour la feuille des événements
      const wscols = [
        { wch: 30 }, // Titre
        { wch: 15 }, // Date début
        { wch: 15 }, // Heure début
        { wch: 15 }, // Date fin
        { wch: 15 }, // Heure fin
        { wch: 20 }, // Salle
        { wch: 15 }, // Type
        { wch: 40 }, // Description
        { wch: 40 }, // Utilisateurs assignés
        { wch: 15 }, // Statut
        { wch: 30 }  // Récurrence
      ];
      wsEvents['!cols'] = wscols;
      
      // Améliorer le formatage des en-têtes (première ligne en gras)
      // Créer un style pour les en-têtes
      if (!wsEvents['!rows']) wsEvents['!rows'] = [];
      wsEvents['!rows'][0] = { hidden: false, hpt: 28 }; // Hauteur plus grande pour la première ligne
      
      // Style pour les en-têtes
      const headerStyle = {
        font: { bold: true, color: { rgb: "FFFFFF" } },
        fill: { fgColor: { rgb: "4472C4" } }, // Bleu foncé
        alignment: { horizontal: "center", vertical: "center" },
        border: {
          top: { style: "thin", color: { rgb: "000000" } },
          bottom: { style: "thin", color: { rgb: "000000" } },
          left: { style: "thin", color: { rgb: "000000" } },
          right: { style: "thin", color: { rgb: "000000" } }
        }
      };
      
      // Style pour les cellules de données
      const dataStyle = {
        alignment: { vertical: "center", wrapText: true },
        border: {
          top: { style: "thin", color: { rgb: "D9D9D9" } },
          bottom: { style: "thin", color: { rgb: "D9D9D9" } },
          left: { style: "thin", color: { rgb: "D9D9D9" } },
          right: { style: "thin", color: { rgb: "D9D9D9" } }
        }
      };
      
      // Style pour les lignes alternées
      const altRowStyle = {
        ...dataStyle,
        fill: { fgColor: { rgb: "E9EFF8" } } // Bleu très clair
      };
      
      // Appliquer un style aux cellules d'en-tête
      const range = XLSX.utils.decode_range(wsEvents['!ref']);
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const cell_address = XLSX.utils.encode_cell({ r: 0, c: C });
        if (!wsEvents[cell_address]) continue;
        wsEvents[cell_address].s = headerStyle;
      }
      
      // Appliquer des styles alternés aux lignes de données
      for (let R = range.s.r + 1; R <= range.e.r; ++R) {
        const rowStyle = R % 2 === 0 ? altRowStyle : dataStyle;
        for (let C = range.s.c; C <= range.e.c; ++C) {
          const cell_address = XLSX.utils.encode_cell({ r: R, c: C });
          if (!wsEvents[cell_address]) continue;
          wsEvents[cell_address].s = rowStyle;
        }
      }
      
      // Ajouter les feuilles au classeur - d'abord les événements pour qu'ils soient la feuille active par défaut
      XLSX.utils.book_append_sheet(wb, wsEvents, 'Événements');
      XLSX.utils.book_append_sheet(wb, wsInfo, 'Informations');

      // === Feuille de récapitulatif hebdomadaire des heures par utilisateur ===
      if (viewType === 'week') {
        const userHoursMap = {};
        allEvents.forEach(evt => {
          const s = evt.start ? new Date(evt.start) : null;
          const e = evt.end ? new Date(evt.end) : s;
          if (!s || !e) return;
          const dur = Math.max(0, (e.getTime() - s.getTime()) / 36e5);
          const type = evt.extendedProps?.type || evt.type || '';
          const ptRaw = evt.extendedProps?.personnelType || evt.personnelType || '';
          const pt = ptRaw === 'teletravail' ? 'télétravail' : ptRaw;
          const countsAsWork = type !== 'personnel' || pt === 'télétravail';
          const assigned = Array.isArray(evt.extendedProps?.users) ? evt.extendedProps.users : [];
          if (!countsAsWork || assigned.length === 0) return;
          assigned.forEach(u => {
            const key = `${u.firstName || ''} ${u.lastName || ''}`.trim();
            if (!key) return;
            userHoursMap[key] = (userHoursMap[key] || 0) + dur;
          });
        });

        const summaryAoA = [['Utilisateur', 'Heures réalisées (semaine)']];
        Object.keys(userHoursMap)
          .sort((a, b) => a.localeCompare(b, 'fr'))
          .forEach(name => summaryAoA.push([name, (Math.round(userHoursMap[name] * 10) / 10)]));

        if (summaryAoA.length > 1) {
          const wsSummary = XLSX.utils.aoa_to_sheet(summaryAoA);
          wsSummary['!cols'] = [{ wch: 35 }, { wch: 20 }];
          // Appliquer style simple aux headers
          const headerCell0 = XLSX.utils.encode_cell({ r: 0, c: 0 });
          const headerCell1 = XLSX.utils.encode_cell({ r: 0, c: 1 });
          wsSummary[headerCell0].s = { font: { bold: true }, alignment: { horizontal: 'center' } };
          wsSummary[headerCell1].s = { font: { bold: true }, alignment: { horizontal: 'center' } };
          XLSX.utils.book_append_sheet(wb, wsSummary, 'Récap Heures');
        }
      }
      
      // Définir la feuille active (0 = première feuille, qui sera 'Événements')
      wb.Workbook = wb.Workbook || {};
      wb.Workbook.Views = wb.Workbook.Views || [];
      wb.Workbook.Views[0] = { RTL: false, ActiveTab: 0 };
      
      // Nom du fichier
      const fileName = `calendrier_${viewType === 'week' ? 'semaine' : 'mois'}_${calendarApi.view.title.replace(/\s/g, '_')}.xlsx`;
      
      // Enregistrer le fichier Excel
      XLSX.writeFile(wb, fileName);
      console.log('Fichier Excel enregistré avec succès:', fileName);
      
      // Note: Nous n'avons pas besoin de restaurer les dates originales car nous n'avons pas modifié
      // les propriétés activeStart et activeEnd de calendarApi.view, nous avons utilisé des variables locales à la place
      
      // Informer l'utilisateur que l'export a réussi et contient deux feuilles
      alert(`Export Excel réussi: ${fileName}\n\nLe fichier contient deux feuilles:\n- Événements: liste complète des événements\n- Informations: détails sur les filtres appliqués`);
    } catch (error) {
      console.error('Erreur lors de l\'exportation Excel:', error);
      alert('Erreur lors de l\'exportation Excel: ' + error.message);
    }
  };

  // Fonction pour exporter l'année complète en Excel (pour les utilisateurs permanents)
  const exportToExcelYear = async () => {
    try {
      // Vérifier si l'utilisateur est permanent ou administrateur
      if (!user.isPermanent && user.role !== 'administrateur') {
        alert('Cette fonctionnalité est réservée aux utilisateurs permanents et aux administrateurs.');
        return;
      }

      // Obtenir l'année courante
      const currentYear = new Date().getFullYear();
      
      // Définir les dates de début et de fin de l'année
      const yearStart = new Date(currentYear, 0, 1, 0, 0, 0, 0); // 1er janvier
      const yearEnd = new Date(currentYear + 1, 0, 1, 0, 0, 0, 0); // 1er janvier de l'année suivante
      
      // Récupérer tous les événements de l'année depuis l'API
      const params = new URLSearchParams();
      params.append('start', yearStart.toISOString());
      params.append('end', yearEnd.toISOString());
      params.append('includeAll', 'true'); // Inclure tous les événements
      
      const response = await api.get(`/events?${params.toString()}`);
      
      if (!response.data || !response.data.data || !Array.isArray(response.data.data.events)) {
        throw new Error('Erreur lors de la récupération des événements');
      }
      
      // Filtrer uniquement les événements de type 'show'
      let showEvents = response.data.data.events.filter(event => event.type === 'show');
      
      // Appliquer le filtre de salle si un filtre est actif
      if (roomFilters.length > 0) {
        showEvents = showEvents.filter(event => {
          return roomFilters.includes(event.roomId?.toString()) || roomFilters.includes(event.room?.id?.toString());
        });
        console.log(`Nombre de spectacles après filtrage par salle pour l'année ${currentYear}:`, showEvents.length);
      } else {
        console.log(`Nombre de spectacles récupérés pour l'année ${currentYear} (toutes salles):`, showEvents.length);
      }
      
      if (showEvents.length === 0) {
        alert(`Aucun spectacle trouvé pour l'année ${currentYear}.`);
        return;
      }

      // Créer un nouveau classeur
      const wb = XLSX.utils.book_new();

      // -------------------------------------------------
      // 🚀 Feuille "ANNEE YYYY" - Structure identique au Dashboard
      // -------------------------------------------------
      
      // 1) Les 7 premières lignes = légende (ajustée au nouveau nombre de colonnes)
      const legendRows = [
        // ligne 1 - DOSSIER CLOS (vert) et CO PROD (jaune)
        ['DOSSIER CLOS','CO PROD', ...Array(27).fill('')],
        // ligne 2 - BILAN ENVOYE (orange) et PRODUCTION (cyan)
        ['BILAN ENVOYE','PRODUCTION', ...Array(27).fill('')],
        // ligne 3 - ONE (rose/magenta)
        ['','ONE','', ' ', ...Array(26).fill('')],
        // ligne 4 - SPECTACLE ANNULEE (noir avec texte blanc)
        ['🔍🔎','SPECTACLE ANNULEE', ...Array(27).fill('')],
        // ligne 5 (vide)
        Array(29).fill(''),
        // ligne 6 (vide)
        Array(29).fill(''),
        // ligne 7 - En-têtes des statuts (tous en orange)
        [
          '', '',
          'DOSSIER CLOS', 'CO PROD', 'PRODUCTION', 'ONE', 'SPECTACLE ANNULEE',
          ...Array(22).fill('')
        ]
      ];

      // 2) En-têtes de colonnes (suppression de 'LOCATION SALLE' et réorganisation)
      const suiviHeaders = [
        'SALLE', 'NOM artistesProd ou COMEDIE', 'du', 'au', 'nb dates', 'horaire', 'Spectacle Annulé', 'nombre VHR',
        'ONE', 'CO PROD', 'PRODUCTION', 'contact Prod',
        'billetterie', 'flyers', 'hotel OK', 'Nom Hôtel', 'lieu arrivée', 'transfert arrivée',
        'départ date', 'lieu départ', 'transfert départ', 'CONTRAT',
        'bilan soirée remis', 'droit direct MES', 'décla SACD', 'SACEM', 'bilan envoyé Prod',
        'date envoi bilan', 'facture Prod drive', 'spectacle clôturé', 'Notes détails artistes', 'Commentaires'
      ];

      // 3) Traitement des événements pour créer les données (logique du Dashboard)
      const suiviData = [];
      
      // Regrouper les événements par nom de spectacle
      const groupedShows = {};
      showEvents.forEach(event => {
        const showName = event.title || '';
        if (!groupedShows[showName]) {
          groupedShows[showName] = [];
        }
        groupedShows[showName].push(event);
      });

      // Créer une ligne pour chaque spectacle
      Object.entries(groupedShows).forEach(([showName, events]) => {
        // Trier les événements par date
        events.sort((a, b) => new Date(a.start) - new Date(b.start));
        
        const firstEvent = events[0];
        const lastEvent = events[events.length - 1];
        
        // Calculer les dates et informations
        const dateDebut = format(new Date(firstEvent.start), 'dd/MM/yyyy');
        const dateFin = format(new Date(lastEvent.end || lastEvent.start), 'dd/MM/yyyy');
        const nbDates = events.length;
        const horaire = `${format(new Date(firstEvent.start), 'HH:mm')} - ${format(new Date(firstEvent.end || firstEvent.start), 'HH:mm')}`;
        
        // Informations du spectacle
        const showType = firstEvent.showType || '';
        const showStatus = firstEvent.showStatus || '';
        
        // Informations des utilisateurs - utiliser le champ productionContact
        const users = firstEvent.Users || [];
        const contactProdName = firstEvent.productionContact || '';
        const contactProdPhone = '';
        const contactProdEmail = '';
        
        // Récupérer les informations de lieu d'arrivée et transfert depuis les utilisateurs assignés
        let lieuArriveeDetails = '';
        let transfertArriveeDetails = '';
        let lieuDepartDetails = '';
        let transfertDepartDetails = '';
        let dateDepartDetails = '';
        
        users.forEach(artist => {
          const userEventDetails = artist.EventUser || artist._through || {};
          
          if (userEventDetails.arrivalLocation) {
            lieuArriveeDetails += `${artist.firstName} ${artist.lastName}: ${userEventDetails.arrivalLocation}\n`;
          }
          
          if (userEventDetails.arrivalTransfer) {
            transfertArriveeDetails += `${artist.firstName} ${artist.lastName}: ${userEventDetails.arrivalTransfer}\n`;
          }
          
          if (userEventDetails.departureLocation) {
            lieuDepartDetails += `${artist.firstName} ${artist.lastName}: ${userEventDetails.departureLocation}\n`;
          }
          
          if (userEventDetails.departureTransfer) {
            transfertDepartDetails += `${artist.firstName} ${artist.lastName}: ${userEventDetails.departureTransfer}\n`;
          }
          
          if (userEventDetails.departureTime) {
            try {
              const departureDate = new Date(userEventDetails.departureTime);
              if (!isNaN(departureDate.getTime())) {
                const departureDateTime = format(departureDate, 'dd/MM/yyyy HH:mm');
                dateDepartDetails += `${artist.firstName} ${artist.lastName}: ${departureDateTime}\n`;
              }
            } catch (error) {
              console.warn('Erreur lors du formatage de la date de départ:', userEventDetails.departureTime, error);
            }
          }
        });
        
        // Nettoyer les chaînes (supprimer le dernier \n)
        lieuArriveeDetails = lieuArriveeDetails.trim();
        transfertArriveeDetails = transfertArriveeDetails.trim();
        lieuDepartDetails = lieuDepartDetails.trim();
        transfertDepartDetails = transfertDepartDetails.trim();
        dateDepartDetails = dateDepartDetails.trim();
        
        // Déterminer la couleur de ligne selon le statut/type
        let rowColor = 'FFFFFFFF'; // Blanc par défaut
        if (showStatus === 'cancelled') {
          rowColor = 'FF000000'; // Noir pour annulé
        } else if (showStatus === 'confirmed' || firstEvent.spectacleCloture) {
          rowColor = 'FF00FF00'; // Vert pour dossier clos
        } else if (firstEvent.bilanEnvoyeProd) {
          rowColor = 'FFFF9900'; // Orange pour bilan envoyé
        } else if (showType === 'coproduction') {
          rowColor = 'FFFFFF00'; // Jaune pour co-production
        } else if (showType === 'production') {
          rowColor = 'FF00FFFF'; // Cyan pour production
        } else if (showType === 'one') {
          rowColor = 'FFFF00FF'; // Magenta pour ONE
        } else if (showType === 'location') {
          rowColor = 'FF7F6000'; // Marron pour location salle
        }
        
        // Récupère tous les PDFs de l'event (robuste à EventPdfs/Pdfs)
        const allPdfs = getAllPdfs(firstEvent);
        
        // Debug pour comprendre pourquoi les champs ne s'ajoutent pas
        console.debug('PDFs pour export (Calendar):', getAllPdfs(firstEvent).map(p => ({
          category: p?.category, filename: p?.filename
        })));
        
        // CONTRAT : catégorie "contrat" OU nom de fichier contenant "contrat"/"convention"
        const hasContract = hasPdfCategory(allPdfs, 'contrat') || fileNameIncludes(allPdfs, ['contrat', 'convention']);
        const contratPdf = hasContract ? 'X' : '';
        
        // Vérifier si le spectacle est annulé - utiliser un chiffre au lieu d'une croix
        const spectacleAnnule = firstEvent.showStatus === 'cancelled' ? '1' : '';
        
        // Bilan soirée remis
        const bilanSoireeRemis = hasPdfCategory(allPdfs, 'bilan_definitif') ? 'X' : '';
        
        // Droit direct MES
        const droitDirectMes = hasPdfCategory(allPdfs, 'facture_droit_direct') ? 'X' : '';
        
        // Décla SACD
        const declaSACD = hasPdfCategory(allPdfs, 'facture_sacd') ? 'X' : '';
        
        // SACEM
        const sacem = hasPdfCategory(allPdfs, 'facture_sacem') ? 'X' : '';
        
        // Bilan envoyé prod - pas de mapping automatique
        const bilanEnvoyeProd = firstEvent.bilanEnvoyeProd ? 'X' : '';
        
        // Billetterie ouverte - afficher une croix si le statut est 'ticketsOpen'
        const billetterieOuverte = firstEvent.showStatus === 'ticketsOpen' ? 'X' : '';
        
        // Flyers commandés - afficher une croix si flyersOrdered est true
        const flyersCommandes = firstEvent.flyersOrdered ? 'X' : '';
        
        // Facture Prod Drive
        const factureProdDrive = hasPdfCategory(allPdfs, 'facture_producteur') ? 'X' : '';
        
        const spectacleCloture = firstEvent.spectacleCloture ? 'X' : '';
        
        // Nouvelles colonnes
        const notesDetailsArtistes = firstEvent.userDetails?.map(detail => detail.notes).filter(note => note).join('; ') || '';
        const commentaires = firstEvent.description || '';

        // Construire la ligne de données (réorganisation selon les nouvelles spécifications)
        const row = [
          firstEvent.room?.name || '',
          showName,
          dateDebut,
          dateFin,
          nbDates,
          horaire,
          spectacleAnnule,
          firstEvent.expectedAttendees || firstEvent.nombreVHR || '',
          showType === 'one' ? 'X' : '',
          showType === 'coproduction' ? 'X' : '',
          showType === 'production' ? 'X' : '',
          contactProdName,
          billetterieOuverte,
          flyersCommandes,
          firstEvent.hotelOrdered ? 'X' : '',
          // Récupérer le nom de l'hôtel depuis les utilisateurs assignés
          (() => {
            console.log('=== DEBUG HOTEL NAMES ===');
            console.log('firstEvent.Users:', firstEvent.Users);
            
            const hotelNames = [];
            if (firstEvent.Users && Array.isArray(firstEvent.Users)) {
              firstEvent.Users.forEach((user, index) => {
                console.log(`User ${index}:`, user);
                console.log(`User ${index} EventUser:`, user.EventUser);
                console.log(`User ${index} EventUsers:`, user.EventUsers);
                
                // Essayer différentes structures possibles
                const eventUserDetails = user.EventUser || user.EventUsers || user.eventUser || user.eventUsers || {};
                console.log(`User ${index} eventUserDetails:`, eventUserDetails);
                
                if (eventUserDetails.accommodation && eventUserDetails.accommodation.trim() !== '') {
                  console.log(`Found accommodation for user ${index}:`, eventUserDetails.accommodation);
                  if (!hotelNames.includes(eventUserDetails.accommodation)) {
                    hotelNames.push(eventUserDetails.accommodation);
                  }
                }
              });
            }
            
            console.log('Final hotelNames:', hotelNames);
            console.log('=== END DEBUG HOTEL NAMES ===');
            return hotelNames.join(', ') || '';
          })(),
          lieuArriveeDetails || firstEvent.lieuArrivee || '',
          transfertArriveeDetails || firstEvent.transfertArrivee || '',
          dateDepartDetails || (firstEvent.departDate ? (() => {
            try {
              const date = new Date(firstEvent.departDate);
              return isNaN(date.getTime()) ? '' : format(date, 'dd/MM/yyyy HH:mm');
            } catch (error) {
              console.warn('Erreur lors du formatage de la date de départ:', firstEvent.departDate, error);
              return '';
            }
          })() : ''),
          lieuDepartDetails || firstEvent.lieuDepart || '',
          transfertDepartDetails || firstEvent.transfertDepart || '',
          contratPdf,
          bilanSoireeRemis,
          droitDirectMes,
          declaSACD,
          sacem,
          bilanEnvoyeProd,
          firstEvent.dateEnvoiBilan ? format(new Date(firstEvent.dateEnvoiBilan), 'dd/MM/yyyy') : '',
          factureProdDrive,
          spectacleCloture,
          notesDetailsArtistes,
          commentaires
          // Suppression de la colonne rowColor (codes hexadécimaux)
        ];
        
        suiviData.push(row);
      });

      // Trier par date de début
      suiviData.sort((a, b) => {
        const dateA = new Date(a[2].split('/').reverse().join('-'));
        const dateB = new Date(b[2].split('/').reverse().join('-'));
        return dateA - dateB;
      });

      // 4) Assembler l'ensemble en une seule matrice
      const allRows = [
        ...legendRows,
        suiviHeaders,
        ...suiviData
      ];
      const wsSuivi = XLSX.utils.aoa_to_sheet(allRows);

      // 5) Largeurs de colonnes – ajustées après suppression de LOCATION SALLE et réorganisation
      wsSuivi['!cols'] = [
        {wch:17.88},{wch:45.00},{wch:13.38},{wch:13.75},{wch:11.13},{wch:11.25},{wch:12.63},
        {wch:11.25},{wch:11.25},{wch:15.00},{wch:18.75},{wch:25.00},
        {wch:13.75},{wch:11.25},{wch:11.25},{wch:22.50},{wch:18.75},
        {wch:22.50},{wch:18.75},{wch:22.50},{wch:18.75},{wch:13.75},
        {wch:18.75},{wch:18.75},{wch:13.75},{wch:11.25},{wch:18.75},
        {wch:18.75},{wch:22.50},{wch:18.75}
      ];

      // 6) Hauteur des lignes
      wsSuivi['!rows'] = [];
      const mainHeaderRowIndex = legendRows.length;
      wsSuivi['!rows'][mainHeaderRowIndex] = { hpt: 53.25 };
      for (let i = mainHeaderRowIndex + 1; i < mainHeaderRowIndex + 1 + suiviData.length; i++) {
        wsSuivi['!rows'][i] = { hpt: 30 };
      }

      // 7) Figer volets
      wsSuivi['!freeze'] = { xSplit: 10, ySplit: legendRows.length + 1 };

      // 8) Appliquer les styles de la légende (ajustés après suppression de LOCATION SALLE)
      const legendFills = {
        'A1':'FF00FF00','B1':'FFFFFF00', // Vert pour DOSSIER CLOS, Jaune pour CO PROD
        'A2':'FFFF9900','B2':'FF00FFFF', // Orange pour BILAN ENVOYE, Cyan pour PRODUCTION
        'B3':'FFFF00FF', // Rose/magenta pour ONE
        'B4':'FF000000','A4':'FF000000' // Noir pour SPECTACLE ANNULEE, Rouge pour 🔍🔎
      };

      Object.entries(legendFills).forEach(([cell, rgb]) => {
        if (!wsSuivi[cell]) return;
        wsSuivi[cell].s = wsSuivi[cell].s || {};
        wsSuivi[cell].s.fill = { patternType:'solid', fgColor:{rgb} };
        wsSuivi[cell].s.alignment = { horizontal: 'center', vertical: 'center' };
        wsSuivi[cell].s.border = {
          top: { style: 'thin', color: { rgb: 'FF000000' } },
          left: { style: 'thin', color: { rgb: 'FF000000' } },
          bottom: { style: 'thin', color: { rgb: 'FF000000' } },
          right: { style: 'thin', color: { rgb: 'FF000000' } }
        };
      });

      // Texte blanc sur fond noir pour SPECTACLE ANNULEE
      ['B4', 'A4'].forEach(cell => {
        if(wsSuivi[cell]) {
          wsSuivi[cell].s = wsSuivi[cell].s || {};
          wsSuivi[cell].s.font = { color:{ rgb:'FFFFFFFF' }, bold: true };
        }
      });
      
      // 9) Styles des en-têtes principaux
      for (let i = 0; i < suiviHeaders.length; i++) {
        const cellRef = XLSX.utils.encode_cell({r: mainHeaderRowIndex, c: i});
        if (!wsSuivi[cellRef]) continue;
        wsSuivi[cellRef].s = {
          font: { bold: true, color: { rgb: 'FF000000' } },
          fill: { patternType: 'solid', fgColor: { rgb: 'FFFF9900' } }, // Orange pour les en-têtes
          alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
          border: {
            top: { style: 'thin', color: { rgb: 'FF000000' } },
            left: { style: 'thin', color: { rgb: 'FF000000' } },
            bottom: { style: 'thin', color: { rgb: 'FF000000' } },
            right: { style: 'thin', color: { rgb: 'FF000000' } }
          }
        };
      }

      // 10) Appliquer les couleurs aux lignes de données
      // Créer un mapping des couleurs par spectacle
      const rowColors = {};
      Object.entries(groupedShows).forEach(([showName, events]) => {
        const firstEvent = events[0];
        const showType = firstEvent.showType || '';
        const showStatus = firstEvent.showStatus || '';
        
        let rowColor = 'FFFFFFFF'; // Blanc par défaut
        if (showStatus === 'cancelled') {
          rowColor = 'FF000000'; // Noir pour annulé
        } else if (showStatus === 'confirmed' || firstEvent.spectacleCloture) {
          rowColor = 'FF00FF00'; // Vert pour dossier clos
        } else if (firstEvent.bilanEnvoyeProd) {
          rowColor = 'FFFF9900'; // Orange pour bilan envoyé
        } else if (showType === 'coproduction') {
          rowColor = 'FFFFFF00'; // Jaune pour co-production
        } else if (showType === 'production') {
          rowColor = 'FF00FFFF'; // Cyan pour production
        } else if (showType === 'one') {
          rowColor = 'FFFF00FF'; // Magenta pour ONE
        } else if (showType === 'location') {
          rowColor = 'FF7F6000'; // Marron pour location salle
        }
        
        rowColors[showName] = rowColor;
      });
      
      suiviData.forEach((row, idx) => {
        const showName = row[1]; // Nom du spectacle est en colonne 1
        const rowColor = rowColors[showName] || 'FFFFFFFF';
        
        // Appliquer la couleur à toute la ligne
        for (let i = 0; i < row.length; i++) {
          const cellRef = XLSX.utils.encode_cell({r: mainHeaderRowIndex + 1 + idx, c: i});
          if (!wsSuivi[cellRef]) continue;
          wsSuivi[cellRef].s = wsSuivi[cellRef].s || {};
          wsSuivi[cellRef].s.fill = { patternType: 'solid', fgColor: { rgb: rowColor } };
          
          // Si la couleur est noire, mettre le texte en blanc
          if (rowColor === 'FF000000') {
            wsSuivi[cellRef].s.font = { color: { rgb: 'FFFFFFFF' } };
          }
          
          // Ajouter des bordures
          wsSuivi[cellRef].s.border = {
            top: { style: 'thin', color: { rgb: 'FF000000' } },
            left: { style: 'thin', color: { rgb: 'FF000000' } },
            bottom: { style: 'thin', color: { rgb: 'FF000000' } },
            right: { style: 'thin', color: { rgb: 'FF000000' } }
          };
          
          // Centrer certaines colonnes (ajustées après suppression des colonnes)
          if ([2, 3, 4, 5, 6, 7, 14, 15, 16, 20, 21, 22, 23, 26, 27].includes(i)) {
            wsSuivi[cellRef].s.alignment = { horizontal: 'center', vertical: 'center' };
          }
        }
      });

      // 11) Ajout au classeur avec le nom de feuille approprié
      XLSX.utils.book_append_sheet(wb, wsSuivi, `ANNEE ${currentYear}`);
      
      // 12) Nom du fichier et sauvegarde
      let fileName = `Année_${currentYear}.xlsx`;
      
      // Si un filtre de salle est appliqué, inclure le nom de la salle dans le nom du fichier
      if (roomFilters.length > 0) {
        const filteredRoomNames = rooms
          .filter(room => roomFilters.includes(room.id?.toString()))
          .map(room => room.name)
          .join('_');
        if (filteredRoomNames) {
          fileName = `Année_${currentYear}_${filteredRoomNames}.xlsx`;
        }
      }
      
      // Enregistrer le fichier Excel
      XLSX.writeFile(wb, fileName);
      console.log('Fichier Excel enregistré avec succès:', fileName);
      
      // Informer l'utilisateur que l'export a réussi
      alert(`Export Excel réussi: ${fileName}\n\nLe fichier contient tous les spectacles de l'année ${currentYear} avec la même mise en forme que les statistiques du Dashboard.`);
    } catch (error) {
      console.error('Erreur lors de l\'exportation Excel annuelle:', error);
      alert('Erreur lors de l\'exportation Excel annuelle: ' + error.message);
    }
  };

  // Ajouter un état pour contrôler l'affichage du filtre "Tous les événements"
  const [showAllEventsFilter, setShowAllEventsFilter] = useState(user.role === 'administrateur' || user.isPermanent);
  // État pour contrôler si l'utilisateur peut modifier les événements
  const [canEditEvents, setCanEditEvents] = useState(user.role === 'administrateur');
  // État pour contrôler si l'utilisateur voit ses événements ou tous les événements
  const [showMyEvents, setShowMyEvents] = useState(user.role !== 'administrateur' && !user.isPermanent);
  
  // Effet pour filtrer les utilisateurs en fonction du type d'événement et du type de spectacle
  useEffect(() => {
    if (!users.length) return;
  
    let filtered = [...users];
  
    // 1) Filtrer les utilisateurs en fonction du type d'événement
    switch (newEvent.type) {
      case 'show':
        // Si c'est un spectacle de type co-production, inclure les artistesProds en coproduction
    // Si showType est "coproduction" ou "one", on inclut les artistesProds CoProd
    if (['coproduction', 'one'].includes(newEvent.showType)) {
      // Vérifier si c'est une tournée
      const isTournee = newEvent.roomId && rooms.find(r => r.id === parseInt(newEvent.roomId))?.name === 'Tournée';
      
      filtered = users.filter(user =>
        ['artistesCoProd'].includes(user.role) || user.isPermanent === true
      );
    } else {
      // Dans tous les autres cas, on n’inclut pas les CoProd
      // Vérifier si c'est une tournée
      const isTournee = newEvent.roomId && rooms.find(r => r.id === parseInt(newEvent.roomId))?.name === 'Tournée';
      
      filtered = users.filter(user =>
        ['artistesProd'].includes(user.role)
      );
    }
        break;
      case 'ticketing':
        filtered = users.filter(user => ['billeterie','regie', 'permanence', 'employé', 'administrateur'].includes(user.role));
        break;
      case 'régie':
      case 'calage':
        filtered = users.filter(user => ['billeterie','regie', 'permanence', 'employé', 'administrateur'].includes(user.role));
        break;
      case 'permanence':
        filtered = users.filter(user => ['billeterie','regie', 'permanence', 'employé', 'administrateur'].includes(user.role));
        break;
      case 'event':
        filtered = users; // Permettre tous les rôles pour les événements de type 'event'
        break;
      case 'rental':
        filtered = []; // Aucun utilisateur pour ces types
        break;
      default:
        filtered = users;
    }
  
    // Fonction pour normaliser les chaînes (supprimer les accents)
    const normalizeString = (str) => {
      return str
        ? str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase()
        : '';
    };
    
    // 2) Filtrer **en plus** par prénom, nom ou email (userSearch)
    const normalizedTerm = normalizeString(userSearch.trim());
    if (normalizedTerm) {
      filtered = filtered.filter(user =>
        normalizeString(user.firstName).includes(normalizedTerm) ||
        normalizeString(user.lastName).includes(normalizedTerm) ||
        (user.email ? normalizeString(user.email).includes(normalizedTerm) : false)
      );
    }
  
    // 3) Met à jour le state
    setFilteredUsers(filtered);
  
  }, [users, newEvent.type, newEvent.showType, newEvent.roomId, userSearch, rooms]);  // bien inclure showType, roomId, rooms et userSearch ici
  
  
  // Helper function to check if a room already has 5 shows on a given day
  const checkShowLimit = async (roomId, date) => {
    try {
      // Format the date to YYYY-MM-DD for the API query
      const formattedDate = date.toISOString().split('T')[0];
      
      // Get all events for this room on this day
      const response = await api.get(`/events?roomId=${roomId}&date=${formattedDate}`);
      const roomEvents = response.data.data.events;
      
      // Count shows on this day
      const showCount = roomEvents.filter(event => {
        const eventDate = new Date(event.start);
        return event.type === 'show' && 
               eventDate.toISOString().split('T')[0] === formattedDate;
      }).length;
      
      return showCount >= 5;
    } catch (error) {
      console.error('Error checking show limit:', error);
      return false; // In case of error, allow the operation to proceed
    }
  };
  
  // Fonction pour formater la date pour l'affichage
  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handlePdfChange = (e) => {
    validateAndProcessFiles(e.target.files);
  };
  
  // Ajouter ces fonctions pour gérer le drag & drop
  const handleDragOver = (e) => {
    e.preventDefault();
    // Suppression de e.stopPropagation() pour permettre le défilement
    setIsDragging(true);
  };
  
  const handleDragLeave = (e) => {
    e.preventDefault();
    // Suppression de e.stopPropagation() pour permettre le défilement
    setIsDragging(false);
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    // Suppression de e.stopPropagation() pour permettre le défilement
    setIsDragging(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      validateAndProcessFiles(e.dataTransfer.files);
    }
  };


   // Fonction commune pour valider et traiter les fichiers PDF
const validateAndProcessFiles = (files) => {
  // Limiter à 10 fichiers
  let selectedFiles = Array.from(files);
  if (selectedFiles.length > 10) selectedFiles = selectedFiles.slice(0, 10);
  
  // Vérifier si la catégorie sélectionnée permet plusieurs fichiers
  const category = pdfCategories.find(cat => cat.id === selectedPdfCategory);
  
  // Si la catégorie ne permet pas plusieurs fichiers et qu'on essaie d'en télécharger plusieurs
  if (!category.multiple && selectedFiles.length > 1) {
    setError(`La catégorie "${category.label}" ne permet qu'un seul fichier PDF`);
    return;
  }
  
  // Vérifier si un PDF existe déjà dans cette catégorie (sauf pour 'divers')
  if (!category.multiple && eventPdfs.length > 0) {
    const existingPdfInCategory = eventPdfs.find(pdf => pdf.category === selectedPdfCategory);
    if (existingPdfInCategory) {
      setError(`Un PDF existe déjà dans la catégorie "${category.label}". Veuillez d'abord supprimer le PDF existant.`);
      return;
    }
  }
  
  const validFiles = selectedFiles.filter(file => {
    if (file.type !== 'application/pdf') {
      setError('Seuls les fichiers PDF sont acceptés');
      return false;
    }
    if (file.size > 20 * 1024 * 1024) { // 20 Mo max
      setError('La taille du fichier ne doit pas dépasser 20 Mo');
      return false;
    }
    return true;
  });
  
  setPdfFiles(validFiles);      // stocke les File pour l'upload
  setPdfPreviews(validFiles);   // stocke aussi l'objet File pour l'affichage du nom
  setError('');
};

  const handlePdfDownload = async (pdfUrl, pdfName) => {
   try {
     // 1) Récupérer le flux en blob
     const response = await api.get(pdfUrl, { responseType: 'blob' });
     // 2) Créer un URL blob pour le téléchargement
     const blob = new Blob([response.data], { type: 'application/pdf' });
     const blobUrl = window.URL.createObjectURL(blob);

     // 3) Simuler un <a> pour télécharger
     const link = document.createElement('a');
     link.href = blobUrl;
     link.download = pdfName || 'document.pdf';
     document.body.appendChild(link);
     link.click();
     link.remove();

     // 4) Libérer la mémoire
     window.URL.revokeObjectURL(blobUrl);
   } catch (error) {
     setError("Impossible de télécharger le PDF. Veuillez réessayer.");
   }
  };

  // Fonction pour confirmer la suppression d'un PDF
  const confirmPdfDelete = (pdf, event) => {
    // Empêcher la propagation de l'événement pour éviter la fermeture du modal
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setPdfToDelete(pdf);
    setShowDeletePdfConfirm(true);
  };

  // Fonction pour supprimer un PDF
  const handleDeletePdf = async () => {
    if (!pdfToDelete) return;
    
    try {
      await api.delete(`/events/pdfs/${pdfToDelete.id}`);
      
      // Mettre à jour la liste des PDFs
      setEventPdfs(eventPdfs.filter(pdf => pdf.id !== pdfToDelete.id));
      setSuccess('PDF supprimé avec succès');
      setShowDeletePdfConfirm(false);
      setPdfToDelete(null);
      
      // Effacer le message de succès après 3 secondes
      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (err) {
      console.error('Erreur lors de la suppression du PDF:', err);
      setError(err.response?.data?.message || 'Erreur lors de la suppression du fichier');
      setShowDeletePdfConfirm(false);
      setPdfToDelete(null);
    }
  };

  // Fonction pour pré-charger les PDFs sélectionnés
  const handleUploadPdfs = async () => {
    // Si aucun fichier n'est sélectionné, ne rien faire
    if (!pdfFiles.length) return;
    
    // Vérifier si la catégorie sélectionnée permet plusieurs fichiers
    const category = pdfCategories.find(cat => cat.id === selectedPdfCategory);
    
    // Si la catégorie ne permet pas plusieurs fichiers et qu'on essaie d'en télécharger plusieurs
    if (!category.multiple && pdfFiles.length > 1) {
      setError(`La catégorie "${category.label}" ne permet qu'un seul fichier PDF`);
      return;
    }
    
    // En mode édition, vérifier si un PDF existe déjà dans cette catégorie (pour les catégories à fichier unique)
    if (modalType === 'edit' && currentEvent && !category.multiple) {
      const existingPdfInCategory = eventPdfs.find(pdf => pdf.category === selectedPdfCategory);
      if (existingPdfInCategory) {
        setError(`Un PDF existe déjà dans la catégorie "${category.label}". Veuillez d'abord supprimer le PDF existant.`);
        return;
      }
    }
    
    // En mode édition, télécharger immédiatement les PDFs
    if (modalType === 'edit' && currentEvent) {
      try {
        setUploadingPdf(true);
        setError('');
        
        const formData = new FormData();
        pdfFiles.forEach(file => {
          formData.append('pdfs', file);
        });
        
        // Ajouter la catégorie au formData
        formData.append('category', selectedPdfCategory);
        console.log('FormData:', formData);
        
        const response = await api.post(`/events/${currentEvent.id}/pdfs`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        
        // Mettre à jour la liste des PDFs
        if (response.data && response.data.data && response.data.data.pdfs) {
          setEventPdfs([...eventPdfs, ...response.data.data.pdfs]);
        } else {
          // Rafraîchir la liste des PDFs
          fetchEventPdfs(currentEvent.id);
        }
        
        // Réinitialiser les fichiers sélectionnés
        setPdfFiles([]);
        setPdfPreviews([]);
        setSuccess('PDFs ajoutés avec succès');
        
        // Effacer le message de succès après 3 secondes
        setTimeout(() => {
          setSuccess('');
        }, 3000);
      } catch (err) {
        console.error('Erreur lors du téléchargement des PDFs:', err);
        setError(err.response?.data?.message || 'Erreur lors du téléchargement des fichiers');
      } finally {
        setUploadingPdf(false);
      }
    } else {
      // En mode création, on pré-charge les PDFs dans la liste eventPdfs avec un ID temporaire
      // pour pouvoir les afficher et les gérer avant la création de l'événement
      
      // Générer des IDs temporaires pour les nouveaux PDFs
      const tempPdfs = pdfFiles.map(file => ({
        id: `temp_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
        name: file.name,
        originalName: file.name,
        category: selectedPdfCategory,
        file: file, // Stocker l'objet File pour l'upload ultérieur
        createdAt: new Date().toISOString(),
        isTemp: true // Marquer comme temporaire pour les distinguer des PDFs déjà enregistrés
      }));
      
      // Vérifier si la catégorie permet plusieurs fichiers
      if (!category.multiple) {
        // Si la catégorie ne permet qu'un seul fichier, vérifier s'il y en a déjà un
        const existingTempPdfInCategory = eventPdfs.find(pdf => pdf.category === selectedPdfCategory && pdf.isTemp);
        if (existingTempPdfInCategory) {
          // Remplacer le PDF temporaire existant dans cette catégorie
          setEventPdfs(eventPdfs.filter(pdf => !(pdf.category === selectedPdfCategory && pdf.isTemp)).concat(tempPdfs));
        } else {
          // Ajouter le nouveau PDF
          setEventPdfs([...eventPdfs, ...tempPdfs]);
        }
      } else {
        // Si la catégorie permet plusieurs fichiers, ajouter les nouveaux PDFs
        setEventPdfs([...eventPdfs, ...tempPdfs]);
      }
      
      // Réinitialiser les fichiers sélectionnés pour permettre d'en ajouter d'autres
      setPdfFiles([]);
      setPdfPreviews([]);
      setSuccess('PDFs pré-chargés. Ils seront téléchargés lors de la création de l\'événement.');
      
      // Effacer le message de succès après 3 secondes
      setTimeout(() => {
        setSuccess('');
      }, 3000);
    }
  };
  
  // Réinitialiser les fichiers PDF sélectionnés lors de la fermeture du modal
  useEffect(() => {
    if (!showModal) {
      setPdfFiles([]);
      setPdfPreviews([]);
    }
  }, [showModal]);

  const handleDownloadPdf = async (pdf) => {
    try {
      // 1) Appel vers ton endpoint de téléchargement
      const response = await api.get(
        `/events/${currentEvent.id}/pdfs/${pdf.id}/download`,
        { responseType: 'blob' }
      );
  
      // 2) Créer un objet URL et déclencher le “save as…”
      const blob    = new Blob([response.data], { type: 'application/pdf' });
      const blobUrl = window.URL.createObjectURL(blob);
      const link    = document.createElement('a');
      link.href     = blobUrl;
      // on récupère le nom d'origine depuis ta prop originalName
      link.download = pdf.originalName || pdf.name || 'document.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
  
    } catch (err) {
      console.error('Impossible de télécharger le PDF :', err);
      setError("Impossible de télécharger le PDF. Veuillez réessayer.");
    }
  };

  // Fonction pour formater la date d'ajout du PDF
  const formatPdfDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Fonction pour formater la date pour les inputs
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    
    // Créer une nouvelle date à partir de la chaîne de caractères
    const date = new Date(dateString);
    
    // Ajuster pour le fuseau horaire local en ajoutant le décalage du fuseau horaire
    // Cela garantit que la date affichée dans l'input est la même que celle vue par l'utilisateur
    return new Date(date.getTime() - date.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 16);
  };
  
  // Fonction pour obtenir le libellé d'un type d'événement
  const getEventTypeLabel = (type) => {
    const eventType = eventTypes.find(t => t.value === type);
    return eventType ? eventType.label : type;
  };

  const initialDate = dateParam ? new Date(dateParam) : new Date();

  // ... existing code ...
function normalizeRecurrenceDays(days) {
  return Array.from(new Set(
    (Array.isArray(days) ? days : [])
      .map(d => d === 0 ? 7 : Number(d))
      .filter(d => Number.isInteger(d) && d >= 1 && d <= 7)
  )).sort((a, b) => a - b);
}

function formatDateFr(dateStr) {
  const date = new Date(dateStr);
  return date.toLocaleDateString('fr-FR', {
    day:   '2-digit',
    month: '2-digit',
    year:  'numeric'
  });
}

// Fonction pour générer recurrenceDays à partir de seriesDates
function generateRecurrenceDaysFromSeriesDates(seriesDates) {
  if (!seriesDates || !Array.isArray(seriesDates) || seriesDates.length === 0) {
    return [];
  }
  
  // Extraire les jours de la semaine (1-7) à partir des dates de la série
  const daysOfWeek = seriesDates.map(dateStr => {
    const date = new Date(dateStr);
    let day = date.getDay(); // 0 = dimanche, 1 = lundi, ..., 6 = samedi
    // Convertir 0 (dimanche) en 7 pour correspondre à notre système
    return day === 0 ? 7 : day;
  });
  
  // Retourner un tableau trié de jours uniques
  return [...new Set(daysOfWeek)].sort((a, b) => a - b);
}

// Fonction pour générer seriesDates à partir de recurrenceDays
function generateSeriesDates(startDate, endDate, recurrenceDays) {
  // Normaliser les jours de récurrence
  const normalizedDays = normalizeRecurrenceDays(recurrenceDays);
  if (!startDate || !endDate || !normalizedDays.length) return [];
  
  // Convertir les dates en objets Date
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  // Réinitialiser l'heure à minuit pour la comparaison des jours
  start.setHours(0, 0, 0, 0);
  end.setHours(23, 59, 59, 999);
  
  // Tableau pour stocker les dates de la série
  const seriesDates = [];
  
  // Parcourir chaque jour entre start et end
  const current = new Date(start);
  while (current <= end) {
    // Obtenir le jour de la semaine (1-7, où 1=lundi, 7=dimanche)
    let dayOfWeek = current.getDay();
    // Convertir 0 (dimanche) en 7 pour correspondre à notre système
    if (dayOfWeek === 0) dayOfWeek = 7;
    
    // Si ce jour est dans les jours de récurrence, l'ajouter à seriesDates
    if (normalizedDays.includes(dayOfWeek)) {
      seriesDates.push(formatDateForInput(current).split('T')[0]);
    }
    
    // Passer au jour suivant
    current.setDate(current.getDate() + 1);
  }
  
  return seriesDates;
}
  

// Fonction pour créer un nouvel événement
const handleNewEvent = (info) => {
  // Initialize with current date if no date info is provided
  const now = new Date();
  
  // Safely extract start and end dates
  let startDate, endDate;
  
  if (info && (info.startStr || info.dateStr)) {
    // Use provided date information
    startDate = info.startStr || info.dateStr;
  } else {
    // Default to current date/time if no info provided
    startDate = now.toISOString();
  }
  
  if (info && info.endStr) {
    // Use provided end date
    endDate = info.endStr;
  } else {
    // Create end date 1 hour after start date
    const end = new Date(startDate);
    if (!isNaN(end.getTime())) {
      end.setHours(end.getHours() + 1);
      endDate = end.toISOString();
    } else {
      // Fallback if start date is invalid
      const fallbackEnd = new Date(now);
      fallbackEnd.setHours(fallbackEnd.getHours() + 1);
      endDate = fallbackEnd.toISOString();
    }
  }
  
  // Définir le type par défaut en fonction du rôle de l'utilisateur et du type demandé
  let defaultType;
  
  // Si un type par défaut est spécifié dans info (pour les événements de disponibilité)
  if (info && info.defaultType) {
    defaultType = info.defaultType;
  } else if (user.role === 'administrateur') {
    // Les administrateurs peuvent créer tous types d'événements
    defaultType = 'show';
  } else if (user.isPermanent) {
    // Les utilisateurs permanents peuvent créer des spectacles, locations, événements, disponibilité et non-disponibilité
    // Par défaut on propose 'show' mais ils ont accès à plus de types
    defaultType = 'show';
  } else {
    // Tous les utilisateurs non-administrateurs et non-permanents ne peuvent créer que des événements d'indisponibilité
    defaultType = 'non-disponible';
  }
  const defaultShowType = 'production';
  const defaultShowStatus = 'provisional';
  
  // Déterminer la couleur en fonction du type d'événement et du type/statut de spectacle
  let defaultColor;
  if (defaultType === 'show') {
    // Pour les spectacles, utiliser la fonction getShowColor
    defaultColor = getShowColor(defaultShowType, defaultShowStatus);
  } else if (defaultType === 'rental') {
    // Pour les locations, démarrer sur la couleur de statut provisoire
    defaultColor = getRentalColor('provisional');
  } else {
    // Pour les autres types d'événements, utiliser la couleur définie dans eventTypes
    defaultColor = eventTypes.find(t => t.value === defaultType)?.color || '#9ED2F4';
  }
  
  // Définir le titre par défaut pour les événements de disponibilité/indisponibilité
  let defaultTitle = '';
  if (['disponible', 'non-disponible'].includes(defaultType)) {
    // Laisser le titre vide pour l'instant, il sera mis à jour quand une salle sera sélectionnée
    defaultTitle = `${user.firstName} ${user.lastName} - ${defaultType === 'disponible' ? 'Disponible' : 'Non disponible'} - `;
  }

  const initialEvent = {
    title: defaultTitle,
    start: formatDateForInput(startDate),
    end: formatDateForInput(endDate),
    roomId: null, // Aucune salle sélectionnée par défaut
    roomIds: [], // Pour la sélection multiple de salles

    description: '',
    assignedUsers: [],
    userDetails: [], // Ajouter le tableau pour les détails des utilisateurs
    type: defaultType,
    showStatus: defaultShowStatus,
    showType: defaultShowType,
    rentalStatus: 'provisional', // Statut par défaut pour les locations de salle
    personnelType: 'autre', // Statut par défaut pour les événements de personnel
    // Définir la couleur correcte dès l'initialisation en fonction du type d'événement
    color: defaultType === 'show'
      ? getShowColor(defaultShowType, defaultShowStatus)
      : defaultType === 'rental'
        ? getRentalColor('provisional')
        : eventTypes.find(t => t.value === defaultType)?.color || '#9ED2F4',
    coRealizationPercentage: '',
    hasDecor: false,
    decorDetails: '',
    hasSoundTrack: false,
    soundTrackDetails: '',
    ticketingLocation: '',
    vatPercentage: null, 
    address: '',
    jauge: '',
    flyersOrdered: false,
    hotelOrdered: false,
    transferOrdered: false,
    apartmentOrdered: false,
    sacemDeclared: false,
    sacdDeclared: false,
    productionContact: '',
    stageManagerContact: '',
    expectedAttendees: 0,
    hasSoundTrack: false,
    soundTrackDetails: '',
    isRecurring: false,
    recurrenceId: '',
    recurrenceEndDate: '',
    recurrenceDays: [], // jours de la semaine [1=lundi, 2=mardi, ..., 7=dimanche]
    seriesDates: [],
    eventGuests: [],
    category: '',
  };

  // Réinitialiser les messages d'erreur et de succès
  setError('');
  setSuccess('');
  
  // Réinitialiser les PDFs pour le nouvel événement
  // Vider complètement la liste des PDFs pour le nouvel événement
  setEventPdfs([]);
  setPdfFiles([]);
  setPdfPreviews([]);
  
  // Réinitialiser currentEvent pour éviter les appels à fetchEventPdfs
  setCurrentEvent(null);
  
  setModalType('add');
  setNewEvent(initialEvent);
  setShowModal(true);
  setSelectedPdfCategory('contrat');    // ← on remet la valeur par défaut ici aussi
};
  
  // Fonction pour vérifier si l'utilisateur peut modifier un événement
  const canUserModifyEvent = (event) => {
    // Vérifier si l'événement existe
    if (!event || !event.extendedProps) {
      return false;
    }
    
    // Déboguer le type d'événement
    console.log('canUserModifyEvent - Type d\'événement:', event.type);
    console.log('canUserModifyEvent - Type d\'événement dans extendedProps:', event.extendedProps.type);
    
    // Les administrateurs peuvent tout modifier
    if (user.role === 'administrateur') {
      return true;
    }
    
    const eventType = event.extendedProps.type || event.type;
    
    // Les utilisateurs régie ne peuvent pas modifier les spectacles (lecture seule)
    if (user.role === 'regie' && eventType === 'show') {
      return false;
    }
    
    // Les utilisateurs permanents peuvent modifier les spectacles, locations de salles, événements, cours et non-disponibilités
    if (user.isPermanent) {
      const allowedTypes = ['show', 'rental', 'event', 'cours', 'disponible', 'non-disponible'];
      if (allowedTypes.includes(eventType)) {
        return true;
      }
    }
    
    // Pour les spectacles, empêcher les artistes de les modifier
    if (eventType === 'show') {
      if (['artistesProd', 'artistesCoProd', 'artistes'].includes(user.role)) {
        return false;
      }
    }
    
    // Les artistes peuvent modifier leurs propres événements d'indisponibilité
    const isAvailabilityEvent = ['disponible', 'non-disponible'].includes(eventType);
    const isCreator = event.extendedProps.creator && event.extendedProps.creator.id === user.id;
    
    if (isAvailabilityEvent && isCreator) {
      return true;
    }
    
    // Pour les autres types d'événements, seuls les administrateurs peuvent les modifier
    return false;
  };
  
  // Fonction pour vérifier si l'utilisateur peut voir les détails d'un événement
  const canUserViewEvent = (event) => {
    // Vérifier si l'événement existe
    if (!event || !event.extendedProps) {
      console.warn('canUserViewEvent - Événement null ou undefined');
      return false;
    }
    
    // Déboguer le type d'événement
    console.log('canUserViewEvent - Type d\'événement:', event.type);
    console.log('canUserViewEvent - Type d\'événement dans extendedProps:', event.extendedProps.type);
    
    // Les administrateurs et les utilisateurs permanents peuvent voir tous les événements
    if (user.role === 'administrateur' || user.isPermanent) {
      return true;
    }
    
    // Les utilisateurs régie peuvent voir tous les spectacles en lecture seule
    if (user.role === 'regie' && (event.type === 'show' || event.extendedProps.type === 'show')) {
      return true;
    }
    
    // Tous les utilisateurs peuvent voir les spectacles
    if (event.type === 'show' || event.extendedProps.type === 'show') {
      return true;
    }
    
    // Les utilisateurs peuvent voir les événements auxquels ils sont assignés
    const isUserAssigned = event.extendedProps.users && 
      event.extendedProps.users.some(assignedUser => assignedUser.id === user.id);
    
    if (isUserAssigned) {
      return true;
    }
    
    // Les utilisateurs peuvent voir les événements qu'ils ont créés
    if (event.extendedProps.creator && event.extendedProps.creator.id === user.id) {
      return true;
    }
    
    // Pour les autres événements, l'accès est refusé
    return false;
  };

  

  // Fonction pour récupérer les événements liés pour les événements "non-disponible"
  const getRelatedUnavailableEvents = async (event) => {
    try {
      // Pour les événements "non-disponible", chercher les événements avec le même créateur, même date et même heure
      const eventType = event.extendedProps.type || event.type;
      if (eventType !== 'non-disponible') {
        return [event];
      }

      const eventStart = new Date(event.start);
      const eventEnd = new Date(event.end);
      const creatorId = event.extendedProps.creator?.id;

      if (!creatorId) {
        return [event];
      }

      // Récupérer tous les événements pour trouver les événements liés
      const response = await api.get('/events');
      const responseData = response.data;
      
      // Extraire les événements de la réponse (structure peut varier)
      const allEvents = responseData.data?.events || responseData.events || responseData;
      console.log('Tous les événements récupérés:', allEvents);
      
      // Filtrer les événements liés (même créateur, même type, même date/heure)
      const relatedEvents = allEvents.filter(evt => {
        const evtStart = new Date(evt.start);
        const evtEnd = new Date(evt.end);
        const evtType = evt.type;
        const evtCreatorId = evt.Creator?.id || evt.creator?.id;

        const isMatching = evtType === 'non-disponible' &&
               evtCreatorId === creatorId &&
               evtStart.getTime() === eventStart.getTime() &&
               evtEnd.getTime() === eventEnd.getTime();
               
        console.log(`Événement ${evt.id}: type=${evtType}, créateur=${evtCreatorId}, match=${isMatching}`);
        return isMatching;
      });
      
      console.log('Événements liés trouvés:', relatedEvents);

      return relatedEvents.length > 0 ? relatedEvents : [event];
    } catch (error) {
      console.error('Erreur lors de la récupération des événements liés:', error);
      return [event];
    }
  };

  // Fonction pour gérer le clic sur un événement
  const handleEventClick = async (info) => {
    const event = info.event;
    
    // Déboguer le type d'événement
    console.log('Type d\'événement:', event.type);
    console.log('Type d\'événement dans extendedProps:', event.extendedProps.type);
    console.log('Event complet:', event);
    
    // Vérifier si l'utilisateur peut voir les détails de cet événement
    if (!canUserViewEvent(event)) {
      setError("Vous n'avez pas les permissions nécessaires pour voir cet événement.");
      return;
    }
    
    // Récupérer les événements liés pour les événements "non-disponible"
    const relatedEvents = await getRelatedUnavailableEvents(event);
    console.log('Événements liés trouvés:', relatedEvents);
    
    const cal = info.view.calendar;
    const recId = event.extendedProps.recurrenceId;
    
    // Récupérer les dates de la série
    const seriesDates = recId
      ? seriesDatesMap.current[recId] || []
      : [formatDateForInput(event.start)];  // événement seul
    
    // Si c'est un clone d'une série, on va chercher le master par son id = recId
    const masterEvent = recId
      ? cal.getEventById(recId)
      : null;
    
    // Générer les jours de récurrence à partir des seriesDates pour assurer la cohérence
    let days;
    if (seriesDates.length > 0) {
      // Utiliser seriesDates pour déterminer les jours de récurrence
      days = generateRecurrenceDaysFromSeriesDates(seriesDates);
    } else {
      // Fallback sur les jours stockés si seriesDates est vide
      days = masterEvent
        ? normalizeRecurrenceDays(masterEvent.extendedProps.recurrenceDays)
        : normalizeRecurrenceDays(event.extendedProps.recurrenceDays);
    }
    
    // On clone le tableau pour éviter tout effet de bord
    const processedRecurrenceDays = [...days];
    
    console.log('Jours de récurrence après traitement :', processedRecurrenceDays);
    
    // Récupérer les PDFs associés à l'événement
    fetchEventPdfs(event.id);
    
    // Préparer les détails des utilisateurs
   
    const userDetails = (event.extendedProps.users || []).map(user => {
         // Récupérer les détails soit de user.details, soit de user._through (EventUsers)
         // Donner la priorité à user.details s'il existe et n'est pas vide
         console.log('User dans handleEventClick:', user);
         
         // Vérifier si user.details existe et contient des données non vides
         const hasValidDetails = user.details && 
                               typeof user.details === 'object' && 
                               Object.values(user.details).some(val => val !== '' && val !== null);
         
         // Utiliser user.details s'il est valide, sinon utiliser user._through
         const details = hasValidDetails ? user.details : (user._through || {});
         
         console.log('Détails pour utilisateur', user.id, ':', details);
         console.log('Source des détails:', hasValidDetails ? 'user.details' : 'user._through');
         
         return {
           userId:            user.id,
           accommodation:     details.accommodation || '',
           arrivalLocation:   details.arrivalLocation || '',
           arrivalTime:       details.arrivalTime ? formatDateForInput(details.arrivalTime) : '',
           departureLocation: details.departureLocation || '',
           departureTime:     details.departureTime ? formatDateForInput(details.departureTime) : '',
           additionalFee:     details.additionalFee || '',
           notes:             details.notes || '',
           arrivalTransfer:   details.arrivalTransfer || '',
           departureTransfer: details.departureTransfer || ''
         };
       });
       
    console.log('userDetails extraits:', userDetails);

    console.log('userDetails:', userDetails);
    
    setCurrentEvent(event);
    
    // Déterminer si l'utilisateur peut modifier cet événement
    const canModify = canUserModifyEvent(event);
    
    // Définir le type de modal en fonction des droits de l'utilisateur
    // Si c'est un spectacle et que l'utilisateur peut le modifier, on affiche le mode édition
    // Sinon, on affiche le mode lecture seule
    setModalType(canModify ? 'edit' : 'view');
    console.log('userDetails remappés:', userDetails);
    console.log('Mode du modal:', canModify ? 'edit' : 'view');

    // Ajout d'un log pour déboguer les jours de récurrence
  console.log('Événement à éditer:', event);
  console.log('Jours de récurrence traités:', processedRecurrenceDays);
  
  // Gérer les salles pour les événements "non-disponible"
  const eventType = event.extendedProps.type || event.type;
  let roomId = '';
  let roomIds = [];
  
  // Debug logs détaillés pour comprendre le problème
  console.log('=== DEBUG HANDLEEVENTCLICK START ===');
  console.log('Event clicked:', event);
  console.log('Event type:', eventType);
  console.log('event.extendedProps:', event.extendedProps);
  console.log('event.extendedProps.roomIds:', event.extendedProps.roomIds);
  console.log('event.extendedProps.rooms:', event.extendedProps.rooms);
  console.log('event.extendedProps.room:', event.extendedProps.room);
  console.log('relatedEvents:', relatedEvents);
  console.log('=====================================');
  
  if (eventType === 'non-disponible') {
    // Fonctions helpers pour normaliser les IDs
    const toNumberArray = (val) => {
      if (!val) return [];
      if (Array.isArray(val)) return val.map(n => Number(n)).filter(n => !isNaN(n) && n > 0);
      if (typeof val === 'string') {
        try {
          const parsed = JSON.parse(val);
          if (Array.isArray(parsed)) return parsed.map(n => Number(n)).filter(n => !isNaN(n) && n > 0);
        } catch (_) {}
        return val.split(',').map(n => Number(n)).filter(n => !isNaN(n) && n > 0);
      }
      return [];
    };
    const uniq = arr => Array.from(new Set(arr));
    
    roomId = ''; // on ignore roomId pour ce type
    
    // 1) candidates depuis différentes sources
    const idsFromProp = toNumberArray(event.extendedProps.roomIds);
    const idsFromRooms = toNumberArray((event.extendedProps.rooms || []).map(r => r.id));
    const idsFromRelated = relatedEvents.length > 0
      ? toNumberArray(relatedEvents.map(evt => evt.room?.id || evt.Room?.id || evt.roomId))
      : [];
    
    // 2) fusion + dédupe
    let merged = uniq([...idsFromProp, ...idsFromRooms, ...idsFromRelated]);
    
    // 3) fallback : au moins la salle courante
    if (merged.length === 0) {
      const current = Number(event.extendedProps.room?.id || event.extendedProps.Room?.id || event.roomId);
      if (!isNaN(current) && current > 0) merged = [current];
    }
    
    roomIds = merged.sort((a, b) => a - b);
    
    console.log('=== FUSION ROOMIDS ROBUSTE ===');
    console.log('idsFromProp:', idsFromProp);
    console.log('idsFromRooms:', idsFromRooms);
    console.log('idsFromRelated:', idsFromRelated);
    console.log('Final merged roomIds:', roomIds);
    console.log('==============================');
    
    // Debug logs pour les événements non-disponible
    console.log('=== DEBUG ROOMIDS FINAL ===');
    console.log('Final roomIds after processing:', roomIds);
    console.log('About to set newEvent.roomIds to:', roomIds);
    console.log('============================');
  } else {
    // Pour les autres types d'événements, utiliser roomId normalement
    roomId = event.extendedProps.room?.id || (rooms.length > 0 ? rooms[0].id.toString() : '');
    roomIds = []; // Vider roomIds pour les autres types d'événements
  }
  
  // Utiliser les jours de récurrence traités au lieu de allDays
  setNewEvent({
      id: event.id,
      title: event.title,
      start: formatDateForInput(event.start),
      end: formatDateForInput(event.end),
      roomId: roomId,
      roomIds: roomIds,

      type: event.extendedProps.type || 'show',
      showStatus: event.extendedProps.showStatus || 'provisional',
      rentalStatus: event.extendedProps.rentalStatus || 'provisional',
      showType: event.extendedProps.showType || 'production',
      personnelType: event.extendedProps.personnelType || '',
      description: event.extendedProps.description || '',
      coRealizationPercentage: event.extendedProps.coRealizationPercentage || '',
      color: event.backgroundColor,
      assignedUsers: event.extendedProps.users?.map(user => user.id) || [],
      userDetails: userDetails,
      hasDecor: event.extendedProps.hasDecor || false,
      decorDetails: event.extendedProps.decorDetails || '',
      hasSoundTrack: event.extendedProps.hasSoundTrack || false,
      soundTrackDetails: event.extendedProps.soundTrackDetails || '',
      ticketingLocation: event.extendedProps.ticketingLocation || '',
      vatPercentage: event.extendedProps.vatPercentage, // Champ pour le pourcentage de TVA (conserve la valeur originale ou null)
      address: event.extendedProps.address || '',
      jauge: event.extendedProps.jauge || '',
      flyersOrdered: event.extendedProps.flyersOrdered || false,
      hotelOrdered: event.extendedProps.hotelOrdered || false,
      transferOrdered: event.extendedProps.transferOrdered || false,
      apartmentOrdered: event.extendedProps.apartmentOrdered || false,
      sacemDeclared: event.extendedProps.sacemDeclared || false,
      sacdDeclared: event.extendedProps.sacdDeclared || false,
      productionContact: event.extendedProps.productionContact || '',
      stageManagerContact: event.extendedProps.stageManagerContact || '',
      expectedAttendees: event.extendedProps.expectedAttendees || 0,
      hasSoundTrack: event.extendedProps.hasSoundTrack || false,
      soundTrackDetails: event.extendedProps.soundTrackDetails || '',
      isRecurring: event.extendedProps.isRecurring || false,
      recurrenceId: event.extendedProps.recurrenceId || null,
      recurrenceEndDate: event.extendedProps.recurrenceEndDate ? formatDateForInput(event.extendedProps.recurrenceEndDate)  : '',
      recurrenceDays: processedRecurrenceDays, // Utiliser les jours de récurrence traités
      seriesDates: seriesDates,
      deselectedOccurrences: [],
      eventGuests: event.extendedProps.eventGuests || [],
      category: event.extendedProps.category || 'divers',
    });
    
    // Forcer le mode 'view' pour l'ouverture initiale, l'utilisateur pourra ensuite passer en mode édition
    setModalType('view');
    setShowModal(true);
    setActiveModalTab('general'); // Réinitialiser l'onglet actif
  };

  // Fonction pour gérer la sélection d'utilisateurs
  const handleUserSelection = (userId) => {
    setNewEvent(prev => {
      const assignedUsers = [...prev.assignedUsers];
      const userDetails = [...(prev.userDetails || [])];
      
      if (assignedUsers.includes(userId)) {
        // Retirer l'utilisateur s'il est déjà sélectionné
        return {
          ...prev,
          assignedUsers: assignedUsers.filter(id => id !== userId),
          userDetails: userDetails.filter(detail => detail.userId !== userId)
        };
      } else {
        // Ajouter l'utilisateur s'il n'est pas déjà sélectionné
        return {
          ...prev,
          assignedUsers: [...assignedUsers, userId],
          userDetails: [
                  ...userDetails,
                 {
                   userId: userId,
                   accommodation: '',
                   arrivalLocation: '',
                   arrivalTime: '',
                   arrivalTransfer: '',
                   departureLocation: '',
                   departureTime: '',
                   departureTransfer: '',
                   additionalFee: '',
                   notes: ''
                 }
               ]
        };
      }
    });
        // Mettre à jour la liste des utilisateurs récents
        setRecentUsers(prev => {
          const updated = [userId, ...prev.filter(id => id !== userId)];
          return updated.slice(0, 10);
        });
  };
  
  // Fonction pour mettre à jour les détails d'un utilisateur assigné
 const updateUserDetails = (userId, field, value) => {
    setNewEvent(prev => {
      // Initialiser userDetails s'il n'existe pas
      const userDetails = prev.userDetails || [];
      
      // Trouver l'index de l'utilisateur dans le tableau
      const userIndex = userDetails.findIndex(detail => detail.userId === userId);
      
      // Créer une copie du tableau
      const updatedDetails = [...userDetails];
      
      if (userIndex >= 0) {
        // Mettre à jour les détails existants
        updatedDetails[userIndex] = {
          ...updatedDetails[userIndex],
          [field]: value
        };
      } else {
        // Ajouter de nouveaux détails
        updatedDetails.push({
          userId,
          [field]: value
        });
      }
      
      return {
        ...prev,
        userDetails: updatedDetails
      };
    });
  };

  // Fonction pour ajouter un nouvel invité
  const addGuest = () => {
    setNewEvent(prev => {
      const newGuest = {
        id: Date.now().toString(), // ID unique temporaire
        name: '',
        accommodation: '',
        additionalFee: '',
        arrivalLocation: '',
        arrivalTransfer: '',
        arrivalTime: '',
        departureLocation: '',
        departureTransfer: '',
        departureTime: '',
        notes: ''
      };
      
      return {
        ...prev,
        eventGuests: [...(prev.eventGuests || []), newGuest]
      };
    });
  };

  // Fonction pour mettre à jour les détails d'un invité
  const updateGuestDetails = (guestId, field, value) => {
    setNewEvent(prev => {
      const eventGuests = [...(prev.eventGuests || [])];
      const guestIndex = eventGuests.findIndex(guest => guest.id === guestId);
      
      if (guestIndex >= 0) {
        eventGuests[guestIndex] = {
          ...eventGuests[guestIndex],
          [field]: value
        };
      }
      
      return {
        ...prev,
        eventGuests
      };
    });
  };

  // Fonction pour supprimer un invité
  const removeGuest = (guestId) => {
    setNewEvent(prev => ({
      ...prev,
      eventGuests: (prev.eventGuests || []).filter(guest => guest.id !== guestId)
    }));
  };


  const UserDetailsView = ({ user, eventUser }) => {
    if (!user || !eventUser) return <div>Aucune donnée disponible</div>;
    
    return (
      <div className="space-y-4">
        <div className="flex items-center space-x-2">
          <UserIcon className="h-5 w-5 text-primary" />
          <h3 className="font-semibold text-lg">{user.firstName} {user.lastName}</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div className="bg-gray-50 p-3 rounded-md">
            <p className="text-sm text-gray-500">Hébergement</p>
            <p className="font-medium">{eventUser.accommodation || 'Non spécifié'}</p>
          </div>
          
          <div className="bg-gray-50 p-3 rounded-md">
            <p className="text-sm text-gray-500">Frais supplémentaires</p>
            <p className="font-medium">{eventUser.additionalFee || 'Non spécifié'}</p>
          </div>
          
          <div className="bg-gray-50 p-3 rounded-md">
            <p className="text-sm text-gray-500">Lieu d'arrivée</p>
            <p className="font-medium">{eventUser.arrivalLocation || 'Non spécifié'}</p>
          </div>
          
          <div className="bg-gray-50 p-3 rounded-md">
            <p className="text-sm text-gray-500">Heure d'arrivée</p>
            <p className="font-medium">
              {eventUser.arrivalTime ? new Date(eventUser.arrivalTime).toLocaleString('fr-FR') : 'Non spécifié'}
            </p>
          </div>
          
          <div className="bg-gray-50 p-3 rounded-md">
            <p className="text-sm text-gray-500">Lieu de départ</p>
            <p className="font-medium">{eventUser.departureLocation || 'Non spécifié'}</p>
          </div>
          
          <div className="bg-gray-50 p-3 rounded-md">
            <p className="text-sm text-gray-500">Heure de départ</p>
            <p className="font-medium">
              {eventUser.departureTime ? new Date(eventUser.departureTime).toLocaleString('fr-FR') : 'Non spécifié'}
            </p>
          </div>
          
          <div className="bg-gray-50 p-3 rounded-md">
            <p className="text-sm text-gray-500">Transfert d'arrivée</p>
            <p className="font-medium">{eventUser.arrivalTransfer || 'Non spécifié'}</p>
          </div>
          
          <div className="bg-gray-50 p-3 rounded-md">
            <p className="text-sm text-gray-500">Transfert de départ</p>
            <p className="font-medium">{eventUser.departureTransfer || 'Non spécifié'}</p>
          </div>
        </div>
        
        <div className="bg-gray-50 p-3 rounded-md">
          <p className="text-sm text-gray-500">Notes</p>
          <p className="font-medium">{eventUser.notes || 'Aucune note'}</p>
        </div>
      </div>
    );
  };
  
  const UserDetailsForm = ({ user, eventUser, onChange }) => {
    // Assurez-vous que eventUser est un objet, même s'il est null ou undefined
    const details = eventUser || {};
    
    return (
      <div className="space-y-4">
        <div className="flex items-center space-x-2">
          <UserIcon className="h-5 w-5 text-primary" />
          <h3 className="font-semibold text-lg">{user.firstName} {user.lastName}</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Hébergement
            </label>
            <input
              type="text"
              className="w-full p-2 border border-gray-300 rounded-md"
              value={details.accommodation || ''}
              onChange={(e) => onChange(user.id, 'accommodation', e.target.value)}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Frais supplémentaires
            </label>
            <input
              type="text"
              className="w-full p-2 border border-gray-300 rounded-md"
              value={details.additionalFee || ''}
              onChange={(e) => onChange(user.id, 'additionalFee', e.target.value)}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Lieu d'arrivée
            </label>
            <input
              type="text"
              className="w-full p-2 border border-gray-300 rounded-md"
              value={details.arrivalLocation || ''}
              onChange={(e) => onChange(user.id, 'arrivalLocation', e.target.value)}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Heure d'arrivée
            </label>
            <input
              type="datetime-local"
              className="w-full p-2 border border-gray-300 rounded-md"
              value={details.arrivalTime ? formatDateForInput(details.arrivalTime) : ''}
              onChange={(e) => onChange(user.id, 'arrivalTime', e.target.value)}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Transfert d'arrivée
            </label>
            <input
              type="text"
              className="w-full p-2 border border-gray-300 rounded-md"
              value={details.arrivalTransfer || ''}
              onChange={(e) => onChange(user.id, 'arrivalTransfer', e.target.value)}
              placeholder="Détails du transfert d'arrivée"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Lieu de départ
            </label>
            <input
              type="text"
              className="w-full p-2 border border-gray-300 rounded-md"
              value={details.departureLocation || ''}
              onChange={(e) => onChange(user.id, 'departureLocation', e.target.value)}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Heure de départ
            </label>
            <input
              type="datetime-local"
              className="w-full p-2 border border-gray-300 rounded-md"
              value={details.departureTime ? formatDateForInput(details.departureTime) : ''}
              onChange={(e) => onChange(user.id, 'departureTime', e.target.value)}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Transfert de départ
            </label>
            <input
              type="text"
              className="w-full p-2 border border-gray-300 rounded-md"
              value={details.departureTransfer || ''}
              onChange={(e) => onChange(user.id, 'departureTransfer', e.target.value)}
              placeholder="Détails du transfert de départ"
            />
          </div>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Notes
          </label>
          <textarea
            className="w-full p-2 border border-gray-300 rounded-md"
            rows="3"
            value={details.notes || ''}
            onChange={(e) => onChange(user.id, 'notes', e.target.value)}
          ></textarea>
        </div>
      </div>
    );
  };

  
  
  // Fonction pour afficher la section de sélection d'utilisateurs avec détails
  const renderUserSelectionSection = () => {
    // Pour les artistes en mode vue, afficher les utilisateurs assignés sans possibilité de modification
    if (modalType === 'view') {
      const assignedUsers = currentEvent?.extendedProps?.users || [];
      
      if (assignedUsers.length === 0) {
        return (
          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-500 italic flex items-center">
              <UserGroupIcon className="h-5 w-5 mr-2 text-gray-400" />
              Aucun utilisateur assigné à cet événement
            </p>
          </div>
        );
      }
      
      return (
        <div className="mt-4">
          <div className="space-y-2">
            {assignedUsers.map(assignedUser => (
              <div key={assignedUser.id} className="flex items-center p-3 bg-gray-50 rounded-md border">
                <div className="flex items-center">
                  <div className="h-4 w-4 bg-primary rounded border mr-3"></div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">
                      {assignedUser.firstName} {assignedUser.lastName}
                    </p>
                    <p className="text-xs text-gray-500">
                      {assignedUser.role}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {renderArtistDetails()}
        </div>
      );
    }
    
    // Permettre aux utilisateurs non-administrateurs d'assigner des artistes aux spectacles
    if (user.role !== 'administrateur' && newEvent.type !== 'show') return null;
  
    // Define event types that don't allow user assignment
    const disabledEventTypes = [ 'rental'];
    
    // If current event type doesn't allow user assignment, show message
    if (disabledEventTypes.includes(newEvent.type)) {
      return (
        
        <div className="mt-4 p-4 bg-gray-50 rounded-lg">
          <p className="text-sm text-gray-500 italic flex items-center">
            <UserGroupIcon className="h-5 w-5 mr-2 text-gray-400" />
            L'assignation d'utilisateurs n'est pas disponible pour ce type d'événement
          </p>
        </div>
      );
    }
    
    // Filtrer les utilisateurs selon le type d'événement et la salle
    let availableUsers = filteredUsers;
    
    // Pour les tournées, permettre l'assignation de régie en plus des artistes
    const isTournee = newEvent.roomId && rooms.find(r => r.id === parseInt(newEvent.roomId))?.name === 'Tournée';
    
    if (newEvent.type === 'show' && isTournee) {
      // Pour les spectacles en tournée, inclure artistes et régie
      availableUsers = filteredUsers.filter(user => 
        user.role === 'artistesProd' || 
        user.role === 'artistesCoProd' || 
        user.role === 'artistes' || 
        user.role === 'regie' ||
        user.role === 'administrateur'
      );
    } else if (newEvent.type === 'show') {
      // Pour les autres spectacles, seulement les artistes
      availableUsers = filteredUsers.filter(user => 
        user.role === 'artistesProd' || 
        user.role === 'artistesCoProd' || 
        user.role === 'artistes' ||
        user.role === 'administrateur'
      );
    }

    const sortedUsers = availableUsers.sort((a, b) => {
      const ai = recentUsers.indexOf(a.id);
      const bi = recentUsers.indexOf(b.id);
      if (ai === -1 && bi === -1) return 0;
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    });
    // 2) Ne conserver que les 10 premiers
    const displayedUsers = sortedUsers.slice(0, 10);
    
    // Determine if we should show artist details (only for show events)
    const showArtistDetails = newEvent.type === 'show';
    
    return (
      <div className="mt-4">
      {/* Barre de recherche des utilisateurs et bouton de création */}
     <div className="mb-4 flex space-x-2">
       <input
         type="text"
         placeholder="🔍 Rechercher un prénom…"
         value={userSearch}
         onChange={e => setUserSearch(e.target.value)}
         className="flex-1 px-3 py-2 border rounded-md focus:outline-none focus:border-primary"
       />
       {(user.role === 'administrateur' || user.isPermanent) && (
         <button
           onClick={() => {
             saveEventDraft();
             navigate('/users?addUser=true');
           }}
           className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors flex items-center"
           type="button"
         >
           <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
             <path d="M8 9a3 3 0 100-6 3 3 0 000 6zM8 11a6 6 0 016 6H2a6 6 0 016-6zM16 7a1 1 0 10-2 0v1h-1a1 1 0 100 2h1v1a1 1 0 102 0v-1h1a1 1 0 100-2h-1V7z" />
           </svg>
           Créer un utilisateur
         </button>
       )}
     </div>
        {displayedUsers.length > 0 ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-60 overflow-y-auto p-2 border rounded-md">
              {displayedUsers.map(user => (
                <div 
                  key={user.id}
                  className={`flex items-center p-2 rounded-md cursor-pointer transition-colors ${
                    newEvent.assignedUsers.includes(user.id) 
                      ? 'bg-primary/10 border border-primary/30' 
                      : 'hover:bg-gray-100'
                  }`}
                  onClick={() => handleUserSelection(user.id)}
                >
                  <input
                    type="checkbox"
                    checked={newEvent.assignedUsers.includes(user.id)}
                    onChange={() => {}} // Handled by the div onClick
                    className="h-4 w-4 text-primary rounded border-gray-300 focus:ring-primary"
                  />
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-700">
                      {user.firstName} {user.lastName}
                    </p>
                    <p className="text-xs text-gray-500">
                      {user.role}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            
            {showArtistDetails && newEvent.assignedUsers.length > 0 && (
              <div className="mt-4 border rounded-lg p-4 bg-gray-50">
                <h3 className="font-medium text-primary mb-3 flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                  </svg>
                  Détails des artistesProds
                </h3>
                
                <div className="space-y-6">
                  {newEvent.assignedUsers.map(userId => {
                    const selectedUser = availableUsers.find(u => u.id === userId);
                    if (!selectedUser) return null;
                    
                    // Get user details if they exist
                    const userDetail = (newEvent.userDetails || []).find(detail => detail.userId === userId) || {};
                    
                    return (
                      <div key={`details-${userId}`} className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                        <h4 className="font-medium mb-3 pb-2 border-b text-primary flex items-center">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" viewBox="0 0 20 20" fill="currentColor">
                            <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
                          </svg>
                          {selectedUser.firstName} {selectedUser.lastName}
                        </h4>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {/* Logement */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 text-primary" viewBox="0 0 20 20" fill="currentColor">
                                <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
                              </svg>
                              Logement
                            </label>
                            <input
                              type="text"
                              value={userDetail.accommodation || ''}
                              onChange={(e) => updateUserDetails(userId, 'accommodation', e.target.value)}
                              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                              placeholder="Hôtel, adresse..."
                            />
                          </div>
                          
                          {/* Supplément */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 text-primary" viewBox="0 0 20 20" fill="currentColor">
                                <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
                              </svg>
                              Supplément (€)
                            </label>
                            <input
                              type="number"
                              value={userDetail.additionalFee || ''}
                              onChange={(e) => updateUserDetails(userId, 'additionalFee', e.target.value)}
                              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                              placeholder="0.00"
                              step="0.01"
                              min="0"
                            />
                          </div>
                          
                          {/* Arrivée */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 text-primary" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                              </svg>
                              Lieu d'arrivée
                            </label>
                            <input
                              type="text"
                              value={userDetail.arrivalLocation || ''}
                              onChange={(e) => updateUserDetails(userId, 'arrivalLocation', e.target.value)}
                              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                              placeholder="Gare, aéroport..."
                            />
                          </div>
                          
                          {/* Transfert arrivée */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                              </svg>
                              Transfert arrivée
                            </label>
                            <input
                              type="text"
                              value={userDetail.arrivalTransfer || ''}
                              onChange={(e) => updateUserDetails(userId, 'arrivalTransfer', e.target.value)}
                              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                              placeholder="Détails du transfert..."
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 text-primary" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                              </svg>
                              Heure d'arrivée
                            </label>
                            <input
                              type="datetime-local"
                              value={userDetail.arrivalTime}  
                              onChange={(e) => updateUserDetails(userId, 'arrivalTime', e.target.value)}
                              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                            />
                          </div>
                          
                          {/* Départ */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 text-primary" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                              </svg>
                              Lieu de départ
                            </label>
                            <input
                              type="text"
                              value={userDetail.departureLocation || ''}
                              onChange={(e) => updateUserDetails(userId, 'departureLocation', e.target.value)}
                              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                              placeholder="Gare, aéroport..."
                            />
                          </div>
                          
                          {/* Transfert départ */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                              </svg>
                              Transfert départ
                            </label>
                            <input
                              type="text"
                              value={userDetail.departureTransfer || ''}
                              onChange={(e) => updateUserDetails(userId, 'departureTransfer', e.target.value)}
                              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                              placeholder="Détails du transfert..."
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 text-primary" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                              </svg>
                              Heure de départ
                            </label>
                            <input
                              type="datetime-local"
                              value={userDetail.departureTime}
                              onChange={(e) => updateUserDetails(userId, 'departureTime', e.target.value)}
                              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                            />
                          </div>
                          
                          {/* Notes */}
                          <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 text-primary" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M18 13V5a2 2 0 00-2-2H4a2 2 0 00-2 2v8a2 2 0 002 2h3l3 3 3-3h3a2 2 0 002-2zM5 7a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1zm1 3a1 1 0 100 2h3a1 1 0 100-2H6z" clipRule="evenodd" />
                              </svg>
                              Notes
                            </label>
                            <textarea
                              value={userDetail.notes || ''}
                              onChange={(e) => updateUserDetails(userId, 'notes', e.target.value)}
                              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                              rows="2"
                              placeholder="Informations supplémentaires..."
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-sm text-gray-500 italic p-2 border rounded-md">
            Aucun utilisateur disponible pour ce type d'événement
          </div>
        )}
      </div>
    );
  };
  
  // Fonction pour afficher les détails des artistesProds dans la vue d'événement
  const renderArtistDetails = () => {
    if (modalType !== 'view' || !currentEvent || currentEvent.extendedProps.type !== 'show') return null;
    
    const assignedUsers = currentEvent.extendedProps.users || [];
    
    if (assignedUsers.length === 0) return null;
    
    return (
      <div className="mt-6 border-t pt-4">
        <h3 className="text-lg font-semibold text-primary mb-4 flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
          </svg>
          Détails des artistesProds
        </h3>
        
        <div className="space-y-6">
          {assignedUsers.map(artist => {
            // Get the EventUser details from the through table
            const details = artist.EventUser || {};
            
            // Check if there are any details to show
            const hasDetails = details.accommodation || details.additionalFee || 
                              details.arrivalLocation || details.arrivalTime ||
                              details.departureLocation || details.departureTime ||
                              details.notes;
            
            return (
              <div key={`artist-${artist.id}`} className="p-4 bg-gray-50 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                <h4 className="font-medium text-primary mb-3 pb-2 border-b flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
                  </svg>
                  {artist.firstName} {artist.lastName}
                </h4>
                
                {!hasDetails && (
                  <p className="text-sm text-gray-500 italic">Aucun détail spécifique pour cet artistesProd</p>
                )}
                
                {hasDetails && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    {details.accommodation && (
                      <div className="flex items-start">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-primary flex-shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                          <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
                        </svg>
                        <div>
                          <span className="font-medium block text-gray-700">Logement</span>
                          <span className="text-gray-600">{details.accommodation}</span>
                        </div>
                      </div>
                    )}
                    
                    {details.additionalFee && (
                      <div className="flex items-start">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-primary flex-shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                          <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
                        </svg>
                        <div>
                          <span className="font-medium block text-gray-700">Supplément</span>
                          <span className="text-gray-600">{details.additionalFee}€</span>
                        </div>
                      </div>
                    )}
                    
                    {details.arrivalLocation && (
                      <div className="flex items-start">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-primary flex-shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                        </svg>
                        <div>
                          <span className="font-medium block text-gray-700">Lieu d'arrivée</span>
                          <span className="text-gray-600">{details.arrivalLocation}</span>
                        </div>
                      </div>
                    )}
                    
                    {details.arrivalTransfer && (
                      <div className="flex items-start">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-primary flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                        </svg>
                        <div>
                          <span className="font-medium block text-gray-700">Transfert arrivée</span>
                          <span className="text-gray-600">{details.arrivalTransfer}</span>
                        </div>
                      </div>
                    )}
                    
                    {details.arrivalTime && (
                      <div className="flex items-start">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-primary flex-shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                        </svg>
                        <div>
                          <span className="font-medium block text-gray-700">Heure d'arrivée</span>
                          <span className="text-gray-600">{formatDate(details.arrivalTime)}</span>
                        </div>
                      </div>
                    )}
                    
                    {details.departureLocation && (
                      <div className="flex items-start">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-primary flex-shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                        </svg>
                        <div>
                          <span className="font-medium block text-gray-700">Lieu de départ</span>
                          <span className="text-gray-600">{details.departureLocation}</span>
                        </div>
                      </div>
                    )}
                    
                    {details.departureTransfer && (
                      <div className="flex items-start">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-primary flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                        </svg>
                        <div>
                          <span className="font-medium block text-gray-700">Transfert départ</span>
                          <span className="text-gray-600">{details.departureTransfer}</span>
                        </div>
                      </div>
                    )}
                    
                    {details.departureTime && (
                      <div className="flex items-start">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-primary flex-shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                        </svg>
                        <div>
                          <span className="font-medium block text-gray-700">Heure de départ</span>
                          <span className="text-gray-600">{formatDate(details.departureTime)}</span>
                        </div>
                      </div>
                    )}
                    
                    {details.notes && (
                      <div className="md:col-span-2 flex items-start">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-primary flex-shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M18 13V5a2 2 0 00-2-2H4a2 2 0 00-2 2v8a2 2 0 002 2h3l3 3 3-3h3a2 2 0 002-2zM5 7a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1zm1 3a1 1 0 100 2h3a1 1 0 100-2H6z" clipRule="evenodd" />
                        </svg>
                        <div>
                          <span className="font-medium block text-gray-700">Notes</span>
                          <span className="text-gray-600 whitespace-pre-line">{details.notes}</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      
    );
  };

  // Fonction pour vérifier si un événement annulé peut changer de statut en fonction des chevauchements
  const checkCancelledEventStatusChange = async (event, newStatus) => {
    // Si l'événement n'est pas annulé ou si on veut le mettre en annulé, pas besoin de vérifier
    if (event.extendedProps.showStatus !== 'cancelled' || newStatus === 'cancelled') {
      return { canChange: true };
    }
    
    // Si l'événement est annulé et qu'on veut changer son statut, vérifier les chevauchements
    try {
      // Récupérer tous les événements pour la même salle
      const response = await api.get(`/events?roomId=${event.extendedProps.room.id}`);
      const roomEvents = response.data.data.events;
      
      // Convertir les dates de l'événement
      const eventStart = new Date(event.start);
      const eventEnd = new Date(event.end);
      
      // Vérifier les chevauchements
      const overlappingEvents = roomEvents.filter(otherEvent => {
        // Ignorer l'événement lui-même
        if (otherEvent.id.toString() === event.id.toString()) {
          return false;
        }
        
        const otherStart = new Date(otherEvent.start);
        const otherEnd = new Date(otherEvent.end);
        
        // Vérifier si les événements se chevauchent dans le temps
        const isOverlapping = (
          (eventStart < otherEnd && eventStart >= otherStart) ||
          (eventEnd > otherStart && eventEnd <= otherEnd) ||
          (eventStart <= otherStart && eventEnd >= otherEnd)
        );
        
        // Si chevauchement, vérifier si les types d'événements peuvent se chevaucher
        // Pour un événement annulé qui change de statut, on doit vérifier comme s'il n'était pas annulé
        if (isOverlapping) {
          // Simuler que l'événement a déjà le nouveau statut (non annulé)
          return !canEventsOverlap(event.extendedProps.type, otherEvent.type, newStatus);
        }
        
        return false;
      });
      
      if (overlappingEvents.length > 0) {
        // Trouver les noms des types d'événements qui ne peuvent pas se chevaucher
        const conflictingTypes = [...new Set(overlappingEvents.map(evt => 
          getEventTypeLabel(evt.type)
        ))];
        
        return { 
          canChange: false, 
          message: `Impossible de changer le statut car cet événement chevaucherait les types suivants : ${conflictingTypes.join(', ')}` 
        };
      }
      
      return { canChange: true };
    } catch (error) {
      console.error('Erreur lors de la vérification des chevauchements:', error);
      return { 
        canChange: false, 
        message: 'Erreur lors de la vérification des chevauchements. Veuillez réessayer.' 
      };
    }
  };

  const handleConfirmShow = useCallback(async (newStatus) => {
    if (!currentEvent) return;
    
    try {
      setError('');
      setSuccess('');

      const currentShowType = currentEvent.showType || 
      (currentEvent.extendedProps && currentEvent.extendedProps.showType) || 'production';
      
      // Vérifier si l'événement annulé peut changer de statut
      const statusCheck = await checkCancelledEventStatusChange(currentEvent, newStatus);
      if (!statusCheck.canChange) {
        setError(statusCheck.message);
        return;
      }
      
      // Trouver la couleur et le libellé du statut
      const statusOption = showStatusOptions.find(option => option.value === newStatus);
      const statusLabel = statusOption ? statusOption.label : newStatus;

      const statusColor = getShowColor(currentShowType, newStatus);
      
      // Mettre à jour l'événement avec le nouveau statut
      await api.patch(`/events/${currentEvent.id}`, {
        showStatus: newStatus,
        showType: currentShowType,
        color: statusColor,
        ticketingLocation: currentEvent.extendedProps.ticketingLocation,
        roomId: currentEvent.extendedProps.room?.id || currentEvent.extendedProps.roomId,
        type: currentEvent.extendedProps.type,
      });
      
      setSuccess(`Statut du spectacle mis à jour: ${statusLabel}`);
      // Ne ferme pas le modal si le modal de confirmation de suppression est ouvert
      if (!showDeletePdfConfirm) {
        setShowModal(false);
      }
      fetchEvents();
    } catch (error) {
      setError(error.response?.data?.message || 'Erreur lors de la mise à jour du statut');
    }
  }, [currentEvent, api, fetchEvents]);
  
  // Fonction pour mettre à jour le statut d'une location de salle
  const handleConfirmRental = useCallback(async (newStatus) => {
    if (!currentEvent) return;
    
    try {
      setError('');
      setSuccess('');
      
      // Trouver la couleur et le libellé du statut
      const statusOption = rentalStatusOptions.find(option => option.value === newStatus);
      const statusLabel = statusOption ? statusOption.label : newStatus;

      const statusColor = getRentalColor(newStatus);
      
      // Mettre à jour l'événement avec le nouveau statut
      await api.patch(`/events/${currentEvent.id}`, {
        rentalStatus: newStatus,
        color: statusColor,
        roomId: currentEvent.extendedProps.room?.id || currentEvent.extendedProps.roomId,
        type: currentEvent.extendedProps.type,
      });
      
      setSuccess(`Statut de la location mis à jour: ${statusLabel}`);
      // Ne ferme pas le modal si le modal de confirmation de suppression est ouvert
      if (!showDeletePdfConfirm) {
        setShowModal(false);
      }
      fetchEvents();
    } catch (error) {
      setError(error.response?.data?.message || 'Erreur lors de la mise à jour du statut');
    }
  }, [currentEvent, api, fetchEvents]);

  // Gestion du presse-papier
  const handleCopyEvent = async () => {
    if (!currentEvent) return;
    
    // Vérifier les permissions en fonction du rôle de l'utilisateur
    if (user.role !== 'administrateur' && !user.isPermanent) {
      setError("Vous n'avez pas les permissions nécessaires pour copier un événement.");
      return;
    }
    
    // S'assurer que les PDFs sont bien chargés avant de copier l'événement
    await fetchEventPdfs(currentEvent.id);
    
    // Préparer les détails des utilisateurs
    const userDetails = (currentEvent.extendedProps.users || []).map(user => {
      // Récupérer les détails soit de user.details, soit de user._through (EventUsers)
      // Donner la priorité à user.details s'il existe et n'est pas vide
      const hasValidDetails = user.details && 
                            typeof user.details === 'object' && 
                            Object.values(user.details).some(val => val !== '' && val !== null);
      
      // Utiliser user.details s'il est valide, sinon utiliser user._through
      const details = hasValidDetails ? user.details : (user._through || {});
      
      return {
        userId:            user.id,
        accommodation:     details.accommodation || '',
        arrivalLocation:   details.arrivalLocation || '',
        arrivalTime:       details.arrivalTime ? formatDateForInput(details.arrivalTime) : '',
        departureLocation: details.departureLocation || '',
        departureTime:     details.departureTime ? formatDateForInput(details.departureTime) : '',
        additionalFee:     details.additionalFee || '',
        notes:             details.notes || '',
        arrivalTransfer:   details.arrivalTransfer || '',
        departureTransfer: details.departureTransfer || ''
      };
    });
    
    // Vérifier que les PDFs sont bien disponibles
    console.log('PDFs disponibles pour la copie:', eventPdfs);
    
    setClipboardEvent({
      title: currentEvent.title,
      type: currentEvent.extendedProps.type,
      roomId: currentEvent.extendedProps.room?.id,
      roomIds: currentEvent.extendedProps.roomIds,

      description: currentEvent.extendedProps.description,
      color: currentEvent.color,
      showStatus: currentEvent.extendedProps.showStatus,
      showType: currentEvent.extendedProps.showType,
      rentalStatus: currentEvent.extendedProps.rentalStatus,
      duration: new Date(currentEvent.end) - new Date(currentEvent.start),
      originalStart: currentEvent.start, // Stocker la date de début originale pour préserver l'heure
      assignedUsers: currentEvent.extendedProps.users?.map(user => user.id) || [],
      users: currentEvent.extendedProps.users || [],    // <-- fiche complète
      userDetails: userDetails, // <-- Ajouter les détails des utilisateurs
      documents: eventPdfs || [],    // <-- la liste des fichiers
      eventGuests: currentEvent.extendedProps.eventGuests || [],
      category: currentEvent.extendedProps.category,
      decorDetails: currentEvent.extendedProps.decorDetails || '',
      soundTrackDetails: currentEvent.extendedProps.soundTrackDetails || '',
      coRealizationPercentage: currentEvent.extendedProps.coRealizationPercentage || '',
      ticketingLocation: currentEvent.extendedProps.ticketingLocation || '',
      vatPercentage: currentEvent.extendedProps.vatPercentage, // Champ pour le pourcentage de TVA (conserve la valeur originale ou null)
      flyersOrdered: currentEvent.extendedProps.flyersOrdered || false,
      hotelOrdered: currentEvent.extendedProps.hotelOrdered || false,
      apartmentOrdered: currentEvent.extendedProps.apartmentOrdered || false,
      sacemDeclared: currentEvent.extendedProps.sacemDeclared || false,
      sacdDeclared: currentEvent.extendedProps.sacdDeclared || false,
      transferOrdered: currentEvent.extendedProps.transferOrdered || false,
      productionContact: currentEvent.extendedProps.productionContact || '',
      stageManagerContact: currentEvent.extendedProps.stageManagerContact || '',
      expectedAttendees: currentEvent.extendedProps.expectedAttendees || '',
      hasSoundTrack: currentEvent.extendedProps.hasSoundTrack || false,
      hasDecor: currentEvent.extendedProps.hasDecor || false,
      address: currentEvent.extendedProps.address || '',
      jauge: currentEvent.extendedProps.jauge || '',
    });
    
    setSuccess('Événement copié dans le presse-papier avec tous les détails');
    setShowModal(false);
  };

  // fonction pour switch le modal
  const handleTabChange = (tabName) => {
    setActiveModalTab(tabName);
  };
  
  // Component for the tabs UI
  const ModalTabs = () => {
    // Different tabs for view mode and add/edit mode
    if (modalType === 'view') {
      return (
        <div className="border-b border-gray-200 mb-4">
          <nav className="-mb-px flex space-x-4">
            <button
              onClick={() => handleTabChange('general')}
              className={`py-2 px-3 border-b-2 font-medium text-sm ${
                activeModalTab === 'general'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Informations générales
            </button>
            
            <button
              onClick={() => handleTabChange('users')}
              className={`py-2 px-3 border-b-2 font-medium text-sm ${
                activeModalTab === 'users'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Utilisateurs assignés
            </button>
            
            <button
              onClick={() => handleTabChange('documents')}
              className={`py-2 px-3 border-b-2 font-medium text-sm ${
                activeModalTab === 'documents'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Documents
            </button>
          </nav>
        </div>
      );
    }
    
    // Tabs for add/edit mode
    // Only show artist details tab for show events
    const showArtistTab = newEvent.type === 'show';
    
    return (
      <div className="border-b border-gray-200 mb-4">
        <nav className="-mb-px flex space-x-4">
          <button
            onClick={() => handleTabChange('general')}
            className={`py-2 px-3 border-b-2 font-medium text-sm ${
              activeModalTab === 'general'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Informations générales
          </button>
          
          <button
            onClick={() => handleTabChange('users')}
            className={`py-2 px-3 border-b-2 font-medium text-sm ${
              activeModalTab === 'users'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Utilisateurs assignés
          </button>
          
          {showArtistTab && (
            <button
              onClick={() => handleTabChange('artists')}
              className={`py-2 px-3 border-b-2 font-medium text-sm ${
                activeModalTab === 'artists'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Détails des artistesProds
            </button>
          )}
        </nav>
      </div>
    );
  };
  
  const handlePasteEvent = async (info) => {
    if (!clipboardEvent) {
      setError('Aucun événement dans le presse-papier');
      return;
    }
    
    // Vérifier les permissions en fonction du rôle de l'utilisateur
    if (user.role !== 'administrateur' && !user.isPermanent) {
      setError("Vous n'avez pas les permissions nécessaires pour coller un événement.");
      return;
    }
    
    try {
      // Créer la date de début à partir de la date cliquée
      let clickedDate;
      
      if (info.dateStr) {
        // Handle date string format from dateClick
        clickedDate = new Date(info.dateStr);
      } else if (info.start) {
        // Handle Date object from select
        clickedDate = new Date(info.start);
      } else {
        throw new Error('Format de date invalide');
      }
      
      // Vérifier si la date est valide
      if (isNaN(clickedDate.getTime())) {
        setError('Date invalide. Veuillez réessayer.');
        return;
      }
      
      // Préserver l'heure de l'événement original
      // Récupérer l'heure et les minutes de l'événement copié (si disponible)
      const originalDate = new Date(clipboardEvent.originalStart || new Date());
      
      // Appliquer l'heure et les minutes de l'événement original à la date cliquée
      clickedDate.setHours(originalDate.getHours());
      clickedDate.setMinutes(originalDate.getMinutes());
      
      // S'assurer que la durée est un nombre valide
      let duration = 3600000; // 1 heure par défaut
      
      if (typeof clipboardEvent.duration === 'number' && !isNaN(clipboardEvent.duration) && clipboardEvent.duration > 0) {
        duration = clipboardEvent.duration;
      }
      
      // Calculer la date de fin en fonction de la durée de l'événement original
      const endDate = new Date(clickedDate.getTime() + duration);
      
      // Vérifier si la date de fin est valide
      if (isNaN(endDate.getTime())) {
        setError('Impossible de calculer la date de fin. Veuillez réessayer.');
        return;
      }
      
  // Déterminer la couleur en fonction du type d'événement
  let eventColor;
  
  if (clipboardEvent.type === 'show') {
    // Pour les spectacles, utiliser la fonction getShowColor qui prend en compte le type et le statut
    const showType = clipboardEvent.showType || 'production';
    const showStatus = clipboardEvent.showStatus || 'provisional';
    eventColor = getShowColor(showType, showStatus);
  } else if (clipboardEvent.type === 'rental') {
    // Pour les locations, utiliser la couleur basée sur le statut de location
    const rentalStatus = clipboardEvent.rentalStatus || 'provisional';
    eventColor = getRentalColor(rentalStatus);
  } else {
    // Pour les autres types d'événements, utiliser la couleur définie dans eventTypes
    const eventTypeObj = eventTypes.find(type => type.value === clipboardEvent.type);
    eventColor = eventTypeObj ? eventTypeObj.color : eventTypes[0].color;
  }
      
      // Préparer les données de l'événement
      const eventData = {
        title: clipboardEvent.title,
        start: clickedDate.toISOString(),
        end: endDate.toISOString(),
        roomId: clipboardEvent.roomId,
        roomIds: clipboardEvent.roomIds,

        type: clipboardEvent.type,
        description: clipboardEvent.description,
        color: eventColor,
        creatorId: user.id,
        eventGuests: clipboardEvent.eventGuests || [],
        assignedUsers: clipboardEvent.assignedUsers || [],
        userDetails: clipboardEvent.userDetails || [], // Ajouter les détails des utilisateurs
        category: clipboardEvent.category || '',
        decorDetails: clipboardEvent.decorDetails,
        soundTrackDetails: clipboardEvent.soundTrackDetails,
        coRealizationPercentage: clipboardEvent.coRealizationPercentage,
        vatPercentage: clipboardEvent.vatPercentage, // Champ pour le pourcentage de TVA (conserve la valeur originale ou null)
        address: clipboardEvent.address || '',
        jauge: clipboardEvent.jauge || '',
        flyersOrdered: clipboardEvent.flyersOrdered || false,
        hotelOrdered: clipboardEvent.hotelOrdered || false,
        apartmentOrdered: clipboardEvent.apartmentOrdered || false,
        transferOrdered: clipboardEvent.transferOrdered || false,
        sacdDeclared: clipboardEvent.sacdDeclared || false,
        sacemDeclared: clipboardEvent.sacemDeclared || false,
        productionContact: clipboardEvent.productionContact || '',
        stageManagerContact: clipboardEvent.stageManagerContact || '',
        expectedAttendees: clipboardEvent.expectedAttendees || '',
        hasSoundTrack: clipboardEvent.hasSoundTrack || false,
        hasDecor: clipboardEvent.hasDecor || false,
        // Exclure les propriétés de récurrence pour permettre la création d'une nouvelle récurrence
        // recurrenceId, recurrenceEndDate, recurrenceDays et isRecurring ne sont pas copiés
        // Inclure les champs spécifiques au type d'événement
        ...(clipboardEvent.type === 'show' && {
          showStatus: clipboardEvent.showStatus || 'provisional',
          showType: clipboardEvent.showType || 'production',
          coRealizationPercentage: clipboardEvent.coRealizationPercentage || '',
          ticketingLocation: clipboardEvent.ticketingLocation || ''
        })
      };
      
      // Sauvegarde du clipboard pour la copie des PDFs
      const savedClipboard = {...clipboardEvent};
      // Ne plus vider le presse-papier pour permettre de coller plusieurs fois
      // setClipboardEvent(null);
      
      // Réinitialiser les PDFs pour le nouvel événement
      setEventPdfs([]);
      setPdfFiles([]);
      setPdfPreviews([]);
      
      // Créer le nouvel événement
      const response = await api.post('/events', eventData);
      const newEventId = response.data.data.event.id;
      
      // Copier les PDFs de l'événement original vers le nouvel événement
      if (savedClipboard.documents && savedClipboard.documents.length > 0) {
        try {
          console.log('Début de la copie des PDFs:', savedClipboard.documents);
          
          // Pour chaque PDF dans l'événement original
          for (const pdf of savedClipboard.documents) {
            console.log('Traitement du PDF:', pdf);
            
            // Télécharger le PDF original
            const pdfResponse = await api.get(`/events/pdfs/${pdf.id}/download`, {
              responseType: 'blob'
            });
            
            console.log('PDF téléchargé avec succès, taille:', pdfResponse.data.size);
            
            // Créer un objet File à partir du blob
            const pdfFile = new File([pdfResponse.data], pdf.originalName || 'document.pdf', {
              type: 'application/pdf'
            });
            
            // Créer un FormData pour l'upload
            const formData = new FormData();
            formData.append('pdfs', pdfFile);
            formData.append('category', pdf.category || 'autre');
            
            console.log('Upload du PDF vers le nouvel événement:', newEventId, 'catégorie:', pdf.category || 'autre');
            
            // Uploader le PDF au nouvel événement
            const uploadResponse = await api.post(`/events/${newEventId}/pdfs`, formData, {
              headers: {
                'Content-Type': 'multipart/form-data'
              }
            });
            
            console.log('PDF uploadé avec succès:', uploadResponse.data);
          }
          
          // Mettre à jour les PDFs pour le nouvel événement
          await fetchEventPdfs(newEventId);
          
          console.log(`${savedClipboard.documents.length} PDFs copiés avec succès`);
          setSuccess(`Événement collé avec succès, incluant ${savedClipboard.documents.length} PDFs`);
        } catch (pdfError) {
          console.error('Erreur lors de la copie des PDFs:', pdfError);
          // Ne pas bloquer le processus si la copie des PDFs échoue
          setError('Événement créé mais erreur lors de la copie des PDFs: ' + (pdfError.response?.data?.message || pdfError.message));
        }
      }
      
      setSuccess('Événement collé avec succès');
      fetchEvents();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error pasting event:', error);
      setError(error.response?.data?.message || 'Erreur lors de la création de l\'événement');
      setTimeout(() => setError(''), 5000);
    }
  

  // Gestion des interactions avec le calendrier
  const handleDateClick = (info) => {
    setSelectedTimeInfo(info);
    setShowSelectionDialog(true);
  };
  


    // Fonction pour uploader un PDF
const handlePdfUpload = async () => {
  if (!pdfFile || !currentEvent) {
    setError('Veuillez sélectionner un fichier PDF');
    return;
  }
  
  // Vérifier si le nombre maximum de PDFs est atteint
  if (eventPdfs.length >= 10) {
    setError('Vous avez atteint le nombre maximum de PDFs (10) pour cet événement');
    return;
  }

  // Vérifier si la catégorie sélectionnée permet plusieurs fichiers
  const category = pdfCategories.find(cat => cat.id === selectedPdfCategory);
  
  // Vérifier si un PDF existe déjà dans cette catégorie (sauf pour 'divers')
  if (!category.multiple) {
    const existingPdfInCategory = eventPdfs.find(pdf => pdf.category === selectedPdfCategory);
    if (existingPdfInCategory) {
      setError(`Un PDF existe déjà dans la catégorie "${category.label}". Veuillez d'abord supprimer le PDF existant.`);
      return;
    }
  }

  try {
    setUploadingPdf(true);
    const formData = new FormData();
    formData.append('pdfs', pdfFile);
    formData.append('category', selectedPdfCategory); // ← ici on override le defaultValue "autre"

     console.log( 'Envoi PDF -> URL:', `/events/${currentEvent.id}/pdfs`,'– fichiers:', pdfFile,'– catégorie:', selectedPdfCategory, );

    const response = await api.post(`/events/${currentEvent.id}/pdfs`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });

    // Ajouter le nouveau PDF à la liste
    setEventPdfs([...eventPdfs, response.data.data.pdf]);
    setPdfFile(null);
    setSuccess('PDF ajouté avec succès');
    
    // Réinitialiser le champ de fichier
    document.getElementById('pdf-upload').value = '';
  } catch (err) {
    console.error('Erreur lors de l\'upload:', err);
    setError(err.response?.data?.message || 'Erreur lors de l\'upload du fichier');
  } finally {
    setUploadingPdf(false);
  }
};
    
    // Fonction pour gérer le changement de fichier PDF



// Fonction pour confirmer la suppression d'un PDF




// Fonction pour gérer le changement de fichier PDF
const handlePdfFileChange = (e) => {
  const file = e.target.files[0];
  if (file) {
    if (file.type !== 'application/pdf') {
      setError('Seuls les fichiers PDF sont acceptés');
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) { // 10MB
      setError('La taille du fichier ne doit pas dépasser 10MB');
      return;
    }
    
    setPdfFile(file);
    setError('');
  }
};

// Function to toggle PDF section
const togglePdfSection = async () => {
  const newState = !showPdfSection;
  setShowPdfSection(newState);
  
  if (newState && currentEvent?.id) {
    await fetchEventPdfs(currentEvent.id);
  }
};
    
    // Préparer les données de l'événement
    const eventData = {
      title: newEvent.title,
      start: new Date(newEvent.start).toISOString(),
      end: new Date(newEvent.end).toISOString(),
      // Pour les événements "non-disponible", utiliser roomIds au lieu de roomId
      ...(newEvent.type === 'non-disponible' ? 
        { roomIds: newEvent.roomIds } : 
        { roomId: newEvent.roomId }
      ),
      type: newEvent.type,
      personnelType: newEvent.personnelType,
      description: newEvent.description,
      creatorId: user.id,
      assignedUsers: newEvent.assignedUsers,
      // Include all form fields
      ticketingLocation: newEvent.ticketingLocation || '',
      hasDecor: newEvent.hasDecor || false,
      decorDetails: newEvent.decorDetails || '',
      flyersOrdered: newEvent.flyersOrdered ?? false,
      adress: newEvent.address || '',
      jauge: newEvent.jauge || '',
      hotelOrdered: newEvent.hotelOrdered?? false,
      apartmentOrdered: newEvent.apartmentOrdered?? false,
      sacemDeclared: newEvent.sacemDeclared ?? false,
      sacdDeclared: newEvent.sacdDeclared ?? false,
      transferOrdered: newEvent.transferOrdered?? false,
      productionContact: newEvent.productionContact || '',
      stageManagerContact: newEvent.stageManagerContact || '',
      expectedAttendees: newEvent.expectedAttendees || '',
      hasSoundTrack: newEvent.hasSoundTrack || false,
      soundTrackDetails: newEvent.soundTrackDetails || '',
      isRecurring: newEvent.isRecurring || false,
      recurrenceId: newEvent.recurrenceId || null,
      recurrenceDays: newEvent.recurrenceDays || [],
      recurrenceEndDate: newEvent.recurrenceEndDate || null,
      eventGuests: newEvent.eventGuests || [],
      category: newEvent.category,
      // Inclure les champs modifiés
      modifiedFields: modalType === 'edit' ? modifiedFields : {},
      
      // Include show-specific fields if applicable
      ...(newEvent.type === 'show' && {
        showStatus: newEvent.showStatus,
        showType: newEvent.showType,
        coRealizationPercentage: newEvent.coRealizationPercentage,
        vatPercentage: newEvent.vatPercentage,
        // Définir la couleur en fonction du type et du statut du spectacle
        color: getShowColor(newEvent.showType, newEvent.showStatus)
      }),
      // Include rental-specific fields if applicable
      ...(newEvent.type === 'rental' && {
        rentalStatus: newEvent.rentalStatus,
        // Définir la couleur en fonction du statut de la location
        color: getRentalColor(newEvent.rentalStatus)
      })
    };

    console.log('Final updated event:', eventData);
    
    if (modalType === 'add') {
      if (newEvent.isRecurring && newEvent.recurrenceDays.length > 0 && newEvent.recurrenceEndDate) {
        try {
          // Créer des événements récurrents pour tous les types d'événements
          const startDate = new Date(newEvent.start);
          const endDate = new Date(newEvent.recurrenceEndDate);
          const eventStartTime = startDate.getHours() * 60 + startDate.getMinutes(); // Minutes depuis minuit
          const eventEndTime = new Date(newEvent.end).getHours() * 60 + new Date(newEvent.end).getMinutes();
          const eventDuration = (new Date(newEvent.end) - startDate) / (1000 * 60); // Durée en minutes
          
          
          // Tableau pour stocker toutes les promesses de création d'événements
          const eventPromises = [];
          
          // Créer un événement pour le jour de début (premier événement)
          // S'assurer que les détails utilisateur sont inclus dans la création
          eventPromises.push(api.post('/events', {
            ...eventData,
            // Inclure explicitement les détails utilisateur
            userDetails: newEvent.userDetails || []
          }));
          
          // Convertir les jours de récurrence de chaînes en nombres
          const recurrenceDays = newEvent.recurrenceDays.map(day => Number(day));
          // S'assurer que les jours de récurrence sont également des nombres dans eventData
          eventData.recurrenceDays = eventData.recurrenceDays.map(day => Number(day));
          
          // Créer un événement pour chaque jour sélectionné entre la date de début et la date de fin
          const currentDate = new Date(startDate);
          currentDate.setDate(currentDate.getDate() + 1); // Commencer au jour suivant
          currentDate.setHours(0, 0, 0, 0); // Réinitialiser l'heure à minuit
          
          let eventCount = 1; // Commencer à 1 pour inclure l'événement initial
          
          while (currentDate <= endDate) {
            let dayOfWeek = currentDate.getDay(); // 0 = dimanche, 1 = lundi, etc.
            // Convertir 0 (dimanche) en 7 pour correspondre à notre système de récurrence
            if (dayOfWeek === 0) dayOfWeek = 7;
            
            // Vérifier si ce jour de la semaine est sélectionné pour la récurrence
            if (recurrenceDays.includes(dayOfWeek)) {
              // Créer un nouvel événement pour ce jour
              const newEventStart = new Date(currentDate);
              newEventStart.setHours(Math.floor(eventStartTime / 60), eventStartTime % 60, 0, 0);
              
              const newEventEnd = new Date(currentDate);
              newEventEnd.setHours(Math.floor(eventEndTime / 60), eventEndTime % 60, 0, 0);
              
              // Si l'heure de fin est avant l'heure de début, cela signifie que l'événement se termine le jour suivant
              if (eventEndTime < eventStartTime) {
                newEventEnd.setDate(newEventEnd.getDate() + 1);
              }
              
              // S'assurer que tous les détails utilisateur sont inclus dans chaque événement récurrent
              const newEventData = {
                ...eventData,
                start: newEventStart.toISOString(),
                end: newEventEnd.toISOString(),
                // Inclure explicitement les détails utilisateur pour s'assurer qu'ils sont propagés
                userDetails: newEvent.userDetails || []
              };
              
              // Ajouter la promesse de création d'événement au tableau
              eventPromises.push(api.post('/events', newEventData));
              eventCount++;
            }
            
            // Passer au jour suivant
            currentDate.setDate(currentDate.getDate() + 1);
          }
          
          // Attendre que tous les événements soient créés
          const results = await Promise.all(eventPromises);
          console.log(`${results.length} événements récurrents créés avec succès`);
          setSuccess(`${eventCount} événements récurrents créés avec succès`);
          // Supprimer le brouillon après création réussie
          sessionStorage.removeItem('eventDraft');
        } catch (error) {
          console.error('Erreur lors de la création des événements récurrents:', error);
          throw error; // Propager l'erreur pour qu'elle soit gérée par le bloc catch parent
        }
      } else {
        // Créer un seul événement
        // S'assurer que les détails utilisateur sont inclus dans la création
        const res = await api.post('/events', {
          ...eventData,
          // Inclure explicitement les détails utilisateur
          userDetails: newEvent.userDetails || []
        });
        setSuccess('Événement créé avec succès');
        // Supprimer le brouillon après création réussie
        sessionStorage.removeItem('eventDraft');
      }
    } else if (modalType === 'edit') {
      // Si l'événement est récurrent et que la récurrence n'est pas désactivée
      if (currentEvent.extendedProps.isRecurring && newEvent.isRecurring) {
        try {
          // S'assurer que les jours de récurrence sont des nombres
          eventData.recurrenceDays = Array.isArray(eventData.recurrenceDays) ? eventData.recurrenceDays.map(day => Number(day)) : [];
          
          // Mettre à jour tous les événements de la série en utilisant le recurrenceId
          // S'assurer que les détails utilisateur sont inclus dans la mise à jour
          const response = await api.patch(
            `/events/${newEvent.recurrenceId}/recurring`,
            { 
              ...eventData, 
              recurrenceId: newEvent.recurrenceId,
              // Inclure explicitement les détails utilisateur pour s'assurer qu'ils sont propagés
              userDetails: newEvent.userDetails || [] 
            }
          );
          setSuccess('Tous les événements de la série ont été mis à jour avec succès');
        } catch (error) {
          console.error('Erreur lors de la mise à jour des événements récurrents:', error);
          throw error;
        }
      } else {
        // S'assurer que les jours de récurrence sont des nombres même pour les événements non récurrents
        eventData.recurrenceDays = Array.isArray(eventData.recurrenceDays) ? eventData.recurrenceDays.map(day => Number(day)) : [];
        
        // Mise à jour d'un seul événement
        // S'assurer que les détails utilisateur sont inclus dans la mise à jour
        await api.patch(`/events/${newEvent.id}`, {
          ...eventData,
          // Inclure explicitement les détails utilisateur
          userDetails: newEvent.userDetails || []
        });
        setSuccess('Événement mis à jour avec succès');
      }
    }

    setShowModal(false);
    fetchEvents();
  };

  // Gestion des interactions avec le calendrier
  const handleDateClick = (info) => {
    setSelectedTimeInfo(info);
    setShowSelectionDialog(true);
  };
  
  const handleSelect = (selectInfo) => {
    setSelectedTimeInfo(selectInfo);
    setShowSelectionDialog(true);
  };
  
  // État pour suivre les champs modifiés
  const [modifiedFields, setModifiedFields] = useState({});
  
  // État pour stocker la dernière valeur de personnelType
  const [lastPersonnelType, setLastPersonnelType] = useState('');

  // Fonction pour gérer les changements dans le formulaire
  const handleInputChange = (e) => {
    const { name, value, type : inputType, checked } = e.target;
    
    // Empêcher les utilisateurs permanents de changer le type d'événement à autre chose que 'show', 'rental', 'event', 'cours', 'disponible' ou 'non-disponible'
    if (name === 'type' && user.isPermanent && user.role !== 'administrateur' && 
        !['show', 'rental', 'event', 'cours', 'disponible', 'non-disponible'].includes(value)) {
      setError("En tant qu'utilisateur autorisé, vous ne pouvez créer ou modifier que des événements de type 'Spectacle', 'Location salle', 'Événement', 'Cours' ou indiquer votre disponibilité.");
      return;
    }
    
    // Empêcher les utilisateurs normaux de changer le type d'événement à autre chose que 'non-disponible'
    if (name === 'type' && !user.isPermanent && user.role !== 'administrateur' && 
        !['non-disponible'].includes(value)) {
      setError("En tant qu'utilisateur normal, vous ne pouvez qu'indiquer votre indisponibilité.");
      return;
    }
    
    // Marquer le champ comme modifié
    setModifiedFields(prev => ({
      ...prev,
      [name]: true
    }));
    
    setNewEvent(prev => {
      // Créer une copie de l'état précédent avec la nouvelle valeur
      const updatedEvent = {
        ...prev,
        [name]: inputType === 'checkbox' ? checked : value,
      };
      
      // Si on change le type d'événement
      if (name === 'type') {
        // Si le type actuel est 'personnel', sauvegarder la valeur de personnelType
        if (prev.type === 'personnel') {
          setLastPersonnelType(prev.personnelType);
        }
        
        // Si le nouveau type est 'personnel', restaurer la dernière valeur de personnelType ou définir 'autre' par défaut
        if (value === 'personnel') {
          updatedEvent.personnelType = lastPersonnelType || 'autre';
        }
        
        // Si le type d'événement est changé à 'non-disponible', gérer les roomIds et le titre
      if (['non-disponible'].includes(value)) {
        // Si on passe à 'non-disponible', initialiser roomIds avec la salle actuelle si elle existe
        if (prev.roomId && (!prev.roomIds || prev.roomIds.length === 0)) {
          updatedEvent.roomIds = [prev.roomId];
        } else if (!prev.roomIds || prev.roomIds.length === 0) {
          // Si aucune salle n'est sélectionnée, initialiser avec un tableau vide
          updatedEvent.roomIds = [];
        }
        
        // Générer le titre basé sur les salles sélectionnées
        const selectedRoomNames = updatedEvent.roomIds
          .map(roomId => rooms.find(r => r.id === parseInt(roomId))?.name)
          .filter(name => name)
          .sort()
          .join(', ');
        
        updatedEvent.title = selectedRoomNames 
          ? `${user.firstName} ${user.lastName} - Non disponible - ${selectedRoomNames}`
          : `${user.firstName} ${user.lastName} - Non disponible`;
      } else {
        // Si on change vers un autre type, réinitialiser roomIds
        updatedEvent.roomIds = [];
      }
        
        // Si le type d'événement est changé à 'rental', s'assurer que la couleur correspond au statut
        if (value === 'rental') {
          updatedEvent.color = getRentalColor(prev.rentalStatus || 'provisional');
          // S'assurer que le statut de location est défini
          if (!updatedEvent.rentalStatus) {
            updatedEvent.rentalStatus = 'provisional';
          }
        }
      }
      
      // Si on change personnelType, mettre à jour lastPersonnelType
      if (name === 'personnelType') {
        setLastPersonnelType(value);
      }
      
      // Si on change le statut de location, mettre à jour la couleur
      if (name === 'rentalStatus' && prev.type === 'rental') {
        updatedEvent.color = getRentalColor(value);
      }
      
      // Si la salle est modifiée pour un événement de type 'non-disponible', mettre à jour roomIds et le titre
      if (name === 'roomId' && ['non-disponible'].includes(prev.type)) {
        // Mettre à jour roomIds avec la nouvelle salle sélectionnée
        if (value) {
          updatedEvent.roomIds = [value];
          const roomName = rooms.find(r => r.id === parseInt(value))?.name || '';
          updatedEvent.title = `${user.firstName} ${user.lastName} - Non disponible - ${roomName}`;
        } else {
          // Si aucune salle n'est sélectionnée, vider roomIds
          updatedEvent.roomIds = [];
          updatedEvent.title = `${user.firstName} ${user.lastName} - Non disponible`;
        }
      }

 // Si on vient de changer la date de début, on fixe la fin 1 h 30 plus tard
 if (name === 'start') {
   // value est la nouvelle date de début (string ISO ou Date selon ton input)
   const startDate = new Date(value);
   // on ajoute 90 minutes
   const endDate   = new Date(startDate.getTime() + 90 * 60 * 1000);
   // on formate pour un <input type="datetime-local"> : "YYYY-MM-DDThh:mm"
   updatedEvent.end = endDate.toISOString().slice(0,16);
 }
      
      // Si on change le statut d'un spectacle ou le type de spectacle, on met à jour la couleur
      if ((name === 'showStatus' || name === 'showType') && prev.type === 'show') {
        // Utiliser le nouveau statut ou conserver l'ancien
        const newStatus = name === 'showStatus' ? value : prev.showStatus;
        // Utiliser le nouveau type de spectacle ou conserver l'ancien
        const newShowType = name === 'showType' ? value : prev.showType;
        // Mettre à jour la couleur en fonction du type de spectacle et du statut
        updatedEvent.color = getShowColor(newShowType, newStatus);
      }
      // Si on change le type d'événement, on met à jour sa couleur
      else if (name === 'type') {
        if (value === 'show') {
          // Si le nouveau type est 'show', utiliser getShowColor
          updatedEvent.color = getShowColor(updatedEvent.showType, updatedEvent.showStatus);
        } else {
          // Sinon, utiliser la couleur définie dans eventTypes
          updatedEvent.color = eventTypes.find(o => o.value === value)?.color || prev.color;
        }
      }
      
      // Si on change la date de début, la date de fin ou les jours de récurrence, on met à jour seriesDates
      if (prev.isRecurring && (name === 'start' || name === 'recurrenceEndDate') && 
          updatedEvent.start && updatedEvent.recurrenceEndDate && 
          Array.isArray(updatedEvent.recurrenceDays) && updatedEvent.recurrenceDays.length > 0) {
        
        // Générer les nouvelles dates de série
        updatedEvent.seriesDates = generateSeriesDates(
          updatedEvent.start,
          updatedEvent.recurrenceEndDate,
          updatedEvent.recurrenceDays
        );
      }
      
      return updatedEvent;
    });
   };
   
   // Fonction pour éditer un événement existant
const handleEdit = () => {
  if (!currentEvent) return;

   const recId = currentEvent.extendedProps.recurrenceId;
    // Récupérer les dates de la série
    const seriesDates = recId
      ? seriesDatesMap.current[recId] || []
      : [formatDateForInput(currentEvent.start)];
    
    // Générer les jours de récurrence à partir des seriesDates pour assurer la cohérence
    let days = [];
    if (seriesDates.length > 0) {
      // Utiliser seriesDates pour déterminer les jours de récurrence
      days = generateRecurrenceDaysFromSeriesDates(seriesDates);
    } else {
      // Fallback sur les jours stockés si seriesDates est vide
      days = normalizeRecurrenceDays(currentEvent.extendedProps.recurrenceDays);
    }
  
   // Mapping avec formatage des dates
   const userDetails = (currentEvent.extendedProps.users || []).map(user => {
    // 'details' est bien alimenté dans fetchEvents : details: u.EventUsers ?? {}
    const d = user.details || {};
    return {
      userId:            user.id,
      accommodation:     d.accommodation    || '',
      arrivalLocation:   d.arrivalLocation  || '',
      arrivalTime:       d.arrivalTime
                          ? formatDateForInput(d.arrivalTime)
                          : '',
      departureLocation: d.departureLocation || '',
      departureTime:     d.departureTime
                          ? formatDateForInput(d.departureTime)
                          : '',
      additionalFee:     d.additionalFee    || '',
      notes:             d.notes            || '',
      arrivalTransfer:   d.arrivalTransfer  || '',
      departureTransfer: d.departureTransfer|| ''
    };
  });
  
  setNewEvent({
    id: currentEvent.id,
    title: currentEvent.title,
    start: formatDateForInput(currentEvent.start),
    end: formatDateForInput(currentEvent.end),
    roomId: currentEvent.extendedProps.room?.id || (rooms.length > 0 ? rooms[0].id.toString() : ''),
    roomIds: Array.isArray(currentEvent.extendedProps?.roomIds)
      ? currentEvent.extendedProps.roomIds.map(Number)
      : (currentEvent.extendedProps?.rooms || [])
          .map(r => Number(r.id))
          .filter(id => !isNaN(id)),

    type: currentEvent.extendedProps.type || 'show',
    showStatus: currentEvent.extendedProps.showStatus || 'provisional',
    rentalStatus: currentEvent.extendedProps.rentalStatus || 'provisional',
    showType: currentEvent.extendedProps.showType || 'production',
    description: currentEvent.extendedProps.description || '',
    coRealizationPercentage: currentEvent.extendedProps.coRealizationPercentage || '',
    color: currentEvent.color,
    assignedUsers: userDetails.map(d => d.userId),
    userDetails: userDetails,
    hasDecor: currentEvent.extendedProps.hasDecor || false,
    decorDetails: currentEvent.extendedProps.decorDetails || '',
    ticketingLocation: currentEvent.extendedProps.ticketingLocation || '',
    vatPercentage: currentEvent.extendedProps.vatPercentage !== undefined ? currentEvent.extendedProps.vatPercentage : null,
    flyersOrdered: currentEvent.extendedProps.flyersOrdered || false,
    address: currentEvent.extendedProps.address || '',
    jauge: currentEvent.extendedProps.jauge || '',
    hotelOrdered: currentEvent.extendedProps.hotelOrdered || false,
    apartmentOrdered: currentEvent.extendedProps.apartmentOrdered || false,
    transferOrdered: currentEvent.extendedProps.transferOrdered || false,
    sacdDeclared: currentEvent.extendedProps.sacdDeclared || false,
    sacemDeclared: currentEvent.extendedProps.sacemDeclared || false,
    productionContact: currentEvent.extendedProps.productionContact || '',
    stageManagerContact: currentEvent.extendedProps.stageManagerContact || '',
    expectedAttendees: currentEvent.extendedProps.expectedAttendees || '',
    hasSoundTrack: currentEvent.extendedProps.hasSoundTrack || false,
    soundTrackDetails: currentEvent.extendedProps.soundTrackDetails || '',
    isRecurring: currentEvent.extendedProps.isRecurring || false,
    recurrenceId: currentEvent.extendedProps.recurrenceId || '',
    recurrenceEndDate: currentEvent.extendedProps.recurrenceEndDate || '',
    recurrenceDays: days,
    seriesDates: seriesDates,
    // Récupérer personnelType de l'événement existant ou utiliser '' comme valeur par défaut
    personnelType: currentEvent.extendedProps.personnelType || '',
    eventGuests: currentEvent.extendedProps.eventGuests || [],
    category: currentEvent.extendedProps.category || 'divers',
  });
  
  setModalType('edit');
  setShowModal(true);
};  
  // Fonction pour ouvrir le modal de confirmation de suppression
  const handleDelete = (eventId) => {
    if (!currentEvent) return;
    
    // Vérifier si l'événement est récurrent
    const isRecurring = currentEvent.extendedProps?.recurrenceDays && 
                       currentEvent.extendedProps.recurrenceDays.length > 0;
    
    if (isRecurring) {
      // Pour les événements récurrents, afficher le modal de choix
      setEventToDelete(currentEvent);
      setShowDeleteEventConfirm(true);
    } else {
      // Pour les événements non récurrents, utiliser la confirmation standard
      if (window.confirm('Êtes-vous sûr de vouloir supprimer cet événement ?')) {
        deleteEvent(currentEvent.id, 'single');
      }
    }
  };
  
  // Fonction pour restaurer un événement supprimé
  const undoDeleteEvent = async () => {
    if (!recentlyDeletedEvent) return;
    
    try {
      // Récupérer les données de l'événement supprimé
      const { id, option, event } = recentlyDeletedEvent;
      
      if (event) {
        console.log('Événement à restaurer:', event);
        
        // Vérifier si l'événement a une salle associée
        const roomId = event.roomId || (event.room && event.room.id) || (event.extendedProps && event.extendedProps.room && event.extendedProps.room.id);
        
        if (!roomId) {
          console.error('Erreur: roomId est requis pour restaurer un événement');
          setError('Impossible de restaurer l\'événement : la salle est manquante');
          return;
        }
        
        // S'assurer que les champs obligatoires sont présents
        if (!event.title) {
          console.error('Erreur: title est requis pour restaurer un événement');
          setError('Impossible de restaurer l\'événement : le titre est manquant');
          return;
        }
        
        if (!event.start || !event.end) {
          console.error('Erreur: start et end sont requis pour restaurer un événement');
          setError('Impossible de restaurer l\'événement : les dates de début et/ou de fin sont manquantes');
          return;
        }
        
        // Préparer les données de l'événement pour l'API
        const eventData = {
          title: event.title,
          start: event.start,
          end: event.end,
          // Conserver le type d'événement original
          type: event.type || (event.extendedProps && event.extendedProps.type) || 'other',
          description: event.description || '',
          roomId: roomId,
          roomIds: event.roomIds || [],

          // Déterminer la couleur en fonction du type d'événement
          color: '',
          showStatus: event.showStatus || (event.extendedProps && event.extendedProps.showStatus) || 'provisional',
          rentalStatus: event.rentalStatus || (event.extendedProps && event.extendedProps.rentalStatus) || 'provisional',
          showType: event.showType || (event.extendedProps && event.extendedProps.showType) || 'production',
          personnelType: event.personnelType || null,
          coRealizationPercentage: event.coRealizationPercentage || null,
          // S'assurer que les booléens sont correctement définis
          hasDecor: event.hasDecor === true,
          decorDetails: event.decorDetails || '',
          hasSoundTrack: event.hasSoundTrack === true,
          soundTrackDetails: event.soundTrackDetails || '',
          flyersOrdered: event.flyersOrdered === true,
          address: event.address || '',
          jauge: event.jauge || '',
          vatPercentage: event.vatPercentage || null,
          hotelOrdered: event.hotelOrdered === true,
          transferOrdered: event.transferOrdered === true,
          apartmentOrdered: event.apartmentOrdered === true,
          sacdDeclared: event.sacdDeclared === true,
          sacemDeclared: event.sacemDeclared === true,
          productionContact: event.productionContact || '',
          stageManagerContact: event.stageManagerContact || '',
          expectedAttendees: event.expectedAttendees || 0,
          isRecurring: event.isRecurring === true,
          recurrenceId: event.recurrenceId || (event.extendedProps && event.extendedProps.recurrenceId) || null,
          recurrenceEndDate: event.recurrenceEndDate || (event.extendedProps && event.extendedProps.recurrenceEndDate) || null,
          recurrenceDays: Array.isArray(event.recurrenceDays) ? event.recurrenceDays : 
                         (event.extendedProps && Array.isArray(event.extendedProps.recurrenceDays)) ? event.extendedProps.recurrenceDays : [],
          ticketingLocation: event.ticketingLocation || 'online',
          category: event.category || 'divers'
        };
        
        // Récupérer les utilisateurs assignés
        // Vérifier d'abord dans event.extendedProps.users, puis dans event.users
        const users = (event.extendedProps && event.extendedProps.users && Array.isArray(event.extendedProps.users)) ? 
                      event.extendedProps.users : 
                      (event.users && Array.isArray(event.users)) ? event.users : [];
        
        if (users.length > 0) {
          eventData.assignedUsers = users.map(user => user.id);
          
          // Récupérer les détails des utilisateurs si disponibles
          eventData.userDetails = users.map(user => ({
            userId: user.id,
            accommodation: user.details?.accommodation || '',
            arrivalLocation: user.details?.arrivalLocation || '',
            arrivalTime: user.details?.arrivalTime || null,
            departureLocation: user.details?.departureLocation || '',
            departureTime: user.details?.departureTime || null,
            additionalFee: user.details?.additionalFee || '',
            notes: user.details?.notes || '',
            arrivalTransfer: user.details?.arrivalTransfer || '',
            departureTransfer: user.details?.departureTransfer || ''
          }));
        } else {
          eventData.assignedUsers = [];
          eventData.userDetails = [];
        }
        
        // Récupérer les invités si disponibles
        // Vérifier d'abord dans event.eventGuests, puis dans event.extendedProps.eventGuests
        const eventGuests = (event.eventGuests && Array.isArray(event.eventGuests)) ? 
                           event.eventGuests : 
                           (event.extendedProps && event.extendedProps.eventGuests && Array.isArray(event.extendedProps.eventGuests)) ? 
                           event.extendedProps.eventGuests : [];
        
        eventData.eventGuests = eventGuests;
        
        // Déterminer la couleur en fonction du type d'événement et du statut
        if (eventData.type === 'show') {
          // Utiliser la fonction getShowColor pour déterminer la couleur du spectacle
          eventData.color = getShowColor(eventData.showType, eventData.showStatus);
        } else if (eventData.type === 'rental') {
          // Utiliser la fonction getRentalColor pour déterminer la couleur de la location
          eventData.color = getRentalColor(eventData.rentalStatus);
        } else {
          // Pour les autres types d'événements, trouver la couleur correspondante dans eventTypes
          const eventType = eventTypes.find(type => type.value === eventData.type);
          if (eventType) {
            eventData.color = eventType.color;
          }
        }
        
        console.log('Données de l\'événement à restaurer:', eventData);
        
        // Si c'était une série, nous devons recréer la série
        if (option === 'series' && event.isRecurring) {
          const response = await api.post('/events', eventData);
          
          // Si l'événement avait des PDFs, les restaurer également
          if (event.pdfs && event.pdfs.length > 0) {
            try {
              // Récupérer l'ID du nouvel événement créé
              const newEventId = response.data.id || response.data.data?.event?.id;
              
              // Informer l'utilisateur que les PDFs ne peuvent pas être restaurés automatiquement
              console.log('Les PDFs ne peuvent pas être restaurés automatiquement car nous ne pouvons pas sauvegarder les fichiers eux-mêmes.');
              
              // Afficher un message pour informer l'utilisateur
              setSuccess('Série d\'événements restaurée avec succès. Veuillez réattacher les PDFs manuellement si nécessaire.');
            } catch (pdfError) {
              console.error('Erreur lors de la restauration des PDFs:', pdfError);
            }
          } else {
            setSuccess('Série d\'événements restaurée avec succès');
          }
        } else {
          // Sinon, recréer l'événement unique
          const response = await api.post('/events', eventData);
          
          // Si l'événement avait des PDFs, les restaurer également
          if (event.pdfs && event.pdfs.length > 0) {
            try {
              // Récupérer l'ID du nouvel événement créé
              const newEventId = response.data.id || response.data.data?.event?.id;
              
              // Informer l'utilisateur que les PDFs ne peuvent pas être restaurés automatiquement
              console.log('Les PDFs ne peuvent pas être restaurés automatiquement car nous ne pouvons pas sauvegarder les fichiers eux-mêmes.');
              
              // Afficher un message pour informer l'utilisateur
              setSuccess('Événement restauré avec succès. Veuillez réattacher les PDFs manuellement si nécessaire.');
            } catch (pdfError) {
              console.error('Erreur lors de la restauration des PDFs:', pdfError);
            }
          } else {
            setSuccess('Événement restauré avec succès');
          }
        }
        
        // Masquer l'option d'annulation
        setShowUndoDelete(false);
        setRecentlyDeletedEvent(null);
        
        // Rafraîchir les événements
        fetchEvents();
        
        // Masquer le message de succès après un délai
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (error) {
      console.error('Erreur lors de la restauration:', error);
      setError(error.response?.data?.message || 'Erreur lors de la restauration de l\'événement');
    }
  };
  
  // Fonction qui effectue la suppression réelle
  const deleteEvent = async (eventId, option) => {
    if (!eventId) return;
    
    try {
      // Récupérer les PDFs de l'événement avant de le supprimer
      let eventPdfsToSave = [];
      
      if (currentEvent) {
        try {
          // Récupérer les PDFs de l'événement
          const pdfResponse = await api.get(`/events/${eventId}/pdfs`);
          if (pdfResponse.data && pdfResponse.data.data) {
            eventPdfsToSave = pdfResponse.data.data.map(pdf => ({
              id: pdf.id,
              name: pdf.name,
              category: pdf.category,
              url: pdf.url,
              file: null // Nous ne pouvons pas sauvegarder le fichier lui-même, seulement les métadonnées
            }));
          }
        } catch (pdfError) {
          console.error('Erreur lors de la récupération des PDFs:', pdfError);
        }
      }
      
      // Sauvegarder l'événement avant de le supprimer pour permettre l'annulation
      if (currentEvent) {
        // S'assurer que toutes les propriétés nécessaires sont correctement définies
        const eventToSave = {
          ...currentEvent,
          // Propriétés obligatoires
          title: currentEvent.title,
          start: currentEvent.start,
          end: currentEvent.end,
          type: currentEvent.type || (currentEvent.extendedProps && currentEvent.extendedProps.type) || 'other',
          roomId: currentEvent.roomId || (currentEvent.room && currentEvent.room.id) || (currentEvent.extendedProps && currentEvent.extendedProps.room && currentEvent.extendedProps.room.id),
          room: currentEvent.room || (currentEvent.extendedProps && currentEvent.extendedProps.room),
          roomIds: currentEvent.roomIds || (currentEvent.extendedProps && currentEvent.extendedProps.roomIds),


          // Propriétés de statut
          showStatus: currentEvent.showStatus || (currentEvent.extendedProps && currentEvent.extendedProps.showStatus),
          rentalStatus: currentEvent.rentalStatus || (currentEvent.extendedProps && currentEvent.extendedProps.rentalStatus),
          showType: currentEvent.showType || (currentEvent.extendedProps && currentEvent.extendedProps.showType),
          // Propriétés booléennes
          hasDecor: currentEvent.hasDecor === true,
          hasSoundTrack: currentEvent.hasSoundTrack === true,
          flyersOrdered: currentEvent.flyersOrdered === true,
          stageManagerContact: currentEvent.stageManagerContact || '',
          address: currentEvent.address || '',
          jauge: currentEvent.jauge || '',
          hotelOrdered: currentEvent.hotelOrdered === true,
          transferOrdered: currentEvent.transferOrdered === true,
          apartmentOrdered: currentEvent.apartmentOrdered === true,
          sacdDeclared: currentEvent.sacdDeclared === true,
          sacemDeclared: currentEvent.sacemDeclared === true,
          isRecurring: currentEvent.isRecurring === true,
          // Propriétés de récurrence
          recurrenceId: currentEvent.recurrenceId || (currentEvent.extendedProps && currentEvent.extendedProps.recurrenceId),
          recurrenceEndDate: currentEvent.recurrenceEndDate || (currentEvent.extendedProps && currentEvent.extendedProps.recurrenceEndDate),
          recurrenceDays: Array.isArray(currentEvent.recurrenceDays) ? currentEvent.recurrenceDays : 
                         (currentEvent.extendedProps && Array.isArray(currentEvent.extendedProps.recurrenceDays)) ? 
                         currentEvent.extendedProps.recurrenceDays : [],
          // Utilisateurs assignés
          users: (currentEvent.extendedProps && currentEvent.extendedProps.users) || currentEvent.users || [],
          // PDFs
          pdfs: eventPdfsToSave
        };
        
        console.log('Événement sauvegardé pour restauration:', eventToSave);
        
        setRecentlyDeletedEvent({
          id: eventId,
          option: option,
          event: eventToSave
        });
      } else {
        setRecentlyDeletedEvent({
          id: eventId,
          option: option,
          event: null
        });
      }
      
      // Ajouter le paramètre option pour indiquer s'il faut supprimer toute la série
      await api.delete(`/events/${eventId}?deleteOption=${option}`);
      
      setSuccess(option === 'series' ? 
        'Série d\'événements supprimée avec succès' : 
        'Événement supprimé avec succès');
      
      // Afficher l'option d'annulation
      setShowUndoDelete(true);
      
      // Fermer les modals
      setShowDeleteEventConfirm(false);
      if (!showDeletePdfConfirm) {
        setShowModal(false);
      }
      
      fetchEvents();
      
      // Masquer le message de succès et l'option d'annulation après un délai
      setTimeout(() => {
        setSuccess('');
        setShowUndoDelete(false);
        setRecentlyDeletedEvent(null); // Nettoyer les données de l'événement supprimé
      }, 10000); // 10 secondes pour annuler
    } catch (error) {
      setError(error.response?.data?.message || 'Erreur lors de la suppression de l\'événement');
    }
  };

  const handleAssignedUsersChange = (e) => {
    const selectedOptions = Array.from(e.target.selectedOptions, option => parseInt(option.value));
    
    // Update assigned users
    setNewEvent(prev => {
      // Get existing user details
      const existingDetails = prev.userDetails || [];
      
      // Keep details for users that are still selected
      const remainingDetails = existingDetails.filter(detail => 
        selectedOptions.includes(parseInt(detail.userId))
      );
      
      // Add empty details for newly selected users
      const newUserDetails = selectedOptions
        .filter(userId => !existingDetails.some(detail => parseInt(detail.userId) === userId))
        .map(userId => ({
          userId,
          accommodation: '',
          arrivalLocation: '',
          arrivalTime: null,
          departureLocation: '',
          departureTime: null,
          additionalFee: '',
          notes: '',
          arrivalTransfer: '',
          departureTransfer: ''
        }));
      
      return {
        ...prev,
        assignedUsers: selectedOptions,
        userDetails: [...remainingDetails, ...newUserDetails]
      };
    });
  };

  
  
  // Fonction améliorée pour vérifier si un événement peut chevaucher d'autres événements
  const canEventsOverlap = (movingEventType, stillEventType, stillEventStatus) => {
    // Autoriser explicitement le chevauchement entre indisponibilités
    if (movingEventType === 'non-disponible' && stillEventType === 'non-disponible') {
      return true;
    }
    // Si l'événement immobile est un spectacle annulé, autoriser le chevauchement
    if (stillEventType === 'show' && stillEventStatus === 'cancelled') {
      return true;
    }
    
    // Trouver la configuration du type d'événement en mouvement
    const movingEventConfig = eventTypes.find(type => type.value === movingEventType);
    
    // Si on ne trouve pas la configuration, ne pas autoriser le chevauchement
    if (!movingEventConfig) return false;
    
    // Vérifier si le type d'événement immobile est dans la liste des types avec lesquels
    // l'événement en mouvement peut se chevaucher
    return movingEventConfig.canOverlapWith.includes(stillEventType);
  };

  
  
  // Fonction de soumission du formulaire améliorée
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setError('');
      setSuccess('');

      // Vérifier les permissions en fonction du rôle de l'utilisateur
      if (user.role === 'administrateur') {
        // Les administrateurs peuvent créer tous les types d'événements
      } else if (user.isPermanent) {
        // Les utilisateurs permanents peuvent créer des événements de type 'show', 'rental', 'event', 'cours' et indiquer leur disponibilité
        if (!['show', 'rental', 'event', 'cours', 'disponible', 'non-disponible'].includes(newEvent.type)) {
          setError("En tant qu'utilisateur autorisé, vous ne pouvez créer que des événements de type 'Spectacle', 'Location salle', 'Événement', 'Cours' ou indiquer votre disponibilité.");
          return;
        }
      } else {
        // Les utilisateurs normaux ne peuvent créer que des événements d'indisponibilité
        if (!['non-disponible'].includes(newEvent.type)) {
          setError("Vous n'êtes autorisé qu'à indiquer votre indisponibilité.");
          return;
        }
      }
      
      // Validation obligatoire d'assignation d'utilisateurs
      // Exceptions : spectacles 'one' et 'coproduction', événements 'non-disponible', 'event', locations de salle et cours
      const isShowOneOrCoproduction = newEvent.type === 'show' && (newEvent.showType === 'one' || newEvent.showType === 'coproduction');
      const isNonDisponible = newEvent.type === 'non-disponible';
      const isRental = newEvent.type === 'rental';
      const isCours = newEvent.type === 'cours';
      const isEvent = newEvent.type === 'event';
      
      if (!isShowOneOrCoproduction && !isNonDisponible && !isRental && !isCours && !isEvent) {
        if (!newEvent.assignedUsers || newEvent.assignedUsers.length === 0) {
          setError("Vous devez assigner au moins un utilisateur à cet événement.");
          return;
        }
      }
      
      // Validation pour la récurrence
      if (newEvent.isRecurring) {
        if (newEvent.recurrenceDays.length === 0) {
          setError("Veuillez sélectionner au moins un jour de la semaine pour la récurrence.");
          return;
        }
        
        if (!newEvent.recurrenceEndDate) {
          setError("Veuillez définir une date de fin pour la récurrence.");
          return;
        }
        
        const startDate = new Date(newEvent.start);
        const endDate = new Date(newEvent.recurrenceEndDate);
        
        if (endDate < startDate) {
          setError("La date de fin de récurrence doit être postérieure à la date de début.");
          return;
        }
      }

      console.log('▶️ Envoi PDFs, catégorie sélectionnée :', selectedPdfCategory);

      // Générer un titre automatique pour les événements d'indisponibilité
      let eventToSubmit = { ...newEvent };
      if (['non-disponible'].includes(eventToSubmit.type)) {
        const roomName = rooms.find(r => r.id === parseInt(eventToSubmit.roomId))?.name || '';
        eventToSubmit.title = `${user.firstName} ${user.lastName} - Non disponible - ${roomName}`;
        
        // Si l'utilisateur est un utilisateur simple (non administrateur et non permanent)
        // et crée un événement de type 'non-disponible', l'assigner automatiquement
        if (!user.isPermanent && user.role !== 'administrateur') {
          // Vérifier si l'utilisateur n'est pas déjà assigné
          if (!eventToSubmit.assignedUsers.includes(user.id)) {
            // Ajouter l'utilisateur courant aux utilisateurs assignés
            eventToSubmit.assignedUsers = [...eventToSubmit.assignedUsers, user.id];
            
            // Ajouter les détails de l'utilisateur s'ils n'existent pas déjà
            if (!eventToSubmit.userDetails.some(detail => detail.userId === user.id)) {
              eventToSubmit.userDetails.push({
                userId: user.id,
                accommodation: '',
                arrivalLocation: '',
                arrivalTime: null,
                departureLocation: '',
                departureTime: null,
                additionalFee: '',
                notes: '',
                arrivalTransfer: '',
                departureTransfer: ''
              });
            }
          }
        }
      }
      
      // Check show limit if this is a show and we're adding a new event
      if (newEvent.type === 'show' && modalType === 'add') {
        const hasReachedLimit = await checkShowLimit(newEvent.roomId, new Date(newEvent.start));
        if (hasReachedLimit) {
          setError('Impossible d\'ajouter plus de 5 spectacles par jour dans cette salle');
          return;
        }
      }
      
      // Cas spécial: édition d'une indisponibilité où seules les salles sont modifiées
      // Inclure le titre car il est automatiquement régénéré lors d'un changement de salle
      const isNonDispoRoomOnlyEdit = (
        modalType === 'edit' &&
        newEvent.type === 'non-disponible' &&
        modifiedFields && Object.keys(modifiedFields).length > 0 &&
        Object.keys(modifiedFields).every(k => k === 'roomIds' || k === 'roomId' || k === 'title')
      );

      // Vérifier les chevauchements d'événements pour la salle (sauf cas spécial ci-dessus)
      if (!isNonDispoRoomOnlyEdit && newEvent.roomId) {
        // Convertir les dates du nouvel événement
        const newStart = new Date(newEvent.start);
        const newEnd = new Date(newEvent.end);

        // Ne récupérer que les événements potentiellement en conflit (même salle + même plage horaire)
        const marginMs = 1;
        const startIso = new Date(newStart.getTime() - marginMs).toISOString();
        const endIso = new Date(newEnd.getTime() + marginMs).toISOString();

        const response = await api.get(`/events?roomId=${encodeURIComponent(newEvent.roomId)}&start=${encodeURIComponent(startIso)}&end=${encodeURIComponent(endIso)}`);
        const roomEvents = response.data.data.events;
        const roomIdInt = parseInt(newEvent.roomId, 10);
        const isSameRoom = (evt) => {
          const direct = evt?.roomId != null ? parseInt(evt.roomId, 10) : null;
          const nested = evt?.room?.id != null ? parseInt(evt.room.id, 10) : null;
          if (direct === roomIdInt || nested === roomIdInt) return true;
          if (Array.isArray(evt?.roomIds) && evt.roomIds.map(id => parseInt(id, 10)).includes(roomIdInt)) return true;
          if (Array.isArray(evt?.rooms) && evt.rooms.map(r => parseInt(r?.id, 10)).includes(roomIdInt)) return true;
          return false;
        };
        
        // Vérifier les chevauchements
        const overlappingEvents = roomEvents.filter(event => {
          if (!isSameRoom(event)) return false;

          // Ignorer l'événement actuel lors de l'édition
          if (modalType === 'edit' && event.id.toString() === newEvent.id.toString()) {
            return false;
          }
          
          const eventStart = new Date(event.start);
          const eventEnd = new Date(event.end);

          if (isNaN(eventStart.getTime()) || isNaN(eventEnd.getTime())) {
            return false;
          }

          if (eventEnd <= eventStart) {
            return false;
          }
          
          // Vérifier si les événements se chevauchent dans le temps
          const isOverlapping = (
            (newStart < eventEnd && newStart >= eventStart) ||
            (newEnd > eventStart && newEnd <= eventEnd) ||
            (newStart <= eventStart && newEnd >= eventEnd)
          );
          
          // Si chevauchement, vérifier si les types d'événements peuvent se chevaucher
          if (isOverlapping) {
            // Logique spéciale pour la salle "Tournée"
            const currentRoom = rooms.find(r => r.id === parseInt(newEvent.roomId));
            if (currentRoom?.name === 'Tournée' && newEvent.type === 'show' && event.type === 'show') {
              // Pour les spectacles en tournée, permettre le chevauchement s'ils ont des adresses différentes
              const newEventAddress = newEvent.address || '';
              const existingEventAddress = event.address || '';
              
              // Si les adresses sont différentes (et non vides), autoriser le chevauchement
              if (newEventAddress && existingEventAddress && newEventAddress !== existingEventAddress) {
                return false; // Pas de conflit
              }
            }
            
            // Vérifier si les types peuvent se chevaucher
            // Passer le statut de l'événement pour permettre le chevauchement avec des spectacles annulés
            return !canEventsOverlap(newEvent.type, event.type, event.showStatus);
          }
          
          return false;
        });
        
        if (overlappingEvents.length > 0) {
          const formatParis = (iso) => {
            if (!iso) return '';
            const d = new Date(iso);
            return isNaN(d.getTime())
              ? ''
              : d.toLocaleString('fr-FR', { timeZone: 'Europe/Paris', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
          };
          const resolveRoomLabel = (evt) => {
            if (evt?.room?.name) return evt.room.name;
            if (Array.isArray(evt?.rooms) && evt.rooms.length > 0) {
              return evt.rooms.map(r => r?.name).filter(Boolean).join(', ');
            }
            return '';
          };

          const details = overlappingEvents
            .slice(0, 5)
            .map(evt => {
              const roomLabel = resolveRoomLabel(evt);
              const typeLabel = getEventTypeLabel(evt.type);
              const title = evt.title || 'Sans titre';
              const startTxt = formatParis(evt.start);
              const endTxt = formatParis(evt.end);
              return `• ${typeLabel} — "${title}"${roomLabel ? ` — ${roomLabel}` : ''} (${startTxt} → ${endTxt})`;
            })
            .join('\n');

          setError(`Cet événement ne peut pas chevaucher les événements suivants :\n${details}`);
          return;
        }
      }
      
      // Vérifier les chevauchements d'événements pour les utilisateurs assignés
      // Ne pas bloquer lors d'une édition d'indisponibilité où seules les salles sont modifiées
      if (!isNonDispoRoomOnlyEdit && newEvent.assignedUsers && newEvent.assignedUsers.length > 0) {
        // Récupérer tous les événements qui pourraient chevaucher le nouvel événement
        const newStart = new Date(newEvent.start);
        const newEnd = new Date(newEvent.end);
        
        // Formater les dates pour l'API
        const formattedStart = newStart.toISOString();
        const formattedEnd = newEnd.toISOString();
        
        // Récupérer tous les événements dans cette plage horaire
        // Ajouter une marge de 1 milliseconde pour permettre les événements consécutifs
        const marginMs = 1;
        const formattedStartWithMargin = new Date(newStart.getTime() - marginMs).toISOString();
        const formattedEndWithMargin = new Date(newEnd.getTime() + marginMs).toISOString();
        
        const response = await api.get(`/events?start=${formattedStartWithMargin}&end=${formattedEndWithMargin}`);
        const allEvents = response.data.data.events;
        
        // Filtrer les événements qui ont des utilisateurs assignés en commun avec le nouvel événement
        const conflictingUserEvents = [];
        
        for (const event of allEvents) {
          // Ignorer l'événement actuel lors de l'édition
          if (modalType === 'edit' && event.id.toString() === newEvent.id.toString()) {
            continue;
          }
          // Ignorer les événements appartenant à la même série lors de l'édition d'une indisponibilité
          // pour éviter les faux conflits d'auto-chevauchement entre doublons multi-salles
          if (modalType === 'edit' && newEvent.type === 'non-disponible') {
            const currentRecurrenceId = newEvent.recurrenceId || null;
            const eventRecurrenceId = event.recurrenceId || (event.extendedProps && event.extendedProps.recurrenceId) || null;
            if (currentRecurrenceId && eventRecurrenceId && currentRecurrenceId === eventRecurrenceId) {
              continue; // même série -> ne pas considérer comme conflit
            }
          }
          
          // Fonction pour normaliser les noms (supprimer les accents, les espaces et mettre en minuscules)
          const normalizeString = (str) => {
            return str
              .normalize("NFD") // Décomposer les caractères accentués
              .replace(/[\u0300-\u036f]/g, "") // Supprimer les accents
              .toLowerCase() // Mettre en minuscules
              .replace(/\s+/g, ""); // Supprimer tous les espaces
          };
          
          // Vérifier si l'événement a des utilisateurs assignés
          if (event.Users && event.Users.length > 0) {
            // Récupérer les utilisateurs assignés au nouvel événement avec leurs noms complets
            const newEventUsers = newEvent.assignedUsers.map(userId => {
              const user = users.find(u => u.id === userId);
              return user ? { 
                id: user.id, 
                fullName: `${user.firstName} ${user.lastName}`,
                normalizedName: normalizeString(`${user.firstName} ${user.lastName}`)
              } : null;
            }).filter(u => u !== null);
            
            // Récupérer les utilisateurs de l'événement existant avec leurs noms complets
            const eventUsers = event.Users.map(user => ({
              id: user.id,
              fullName: `${user.firstName} ${user.lastName}`,
              normalizedName: normalizeString(`${user.firstName} ${user.lastName}`)
            }));
            
            // Vérifier si des utilisateurs ont le même nom complet (prénom + nom)
            const conflictingUserDetails = [];
            
            // Vérifier si les événements se chevauchent réellement dans le temps
            const eventStart = new Date(event.start);
            const eventEnd = new Date(event.end);
            
            // Vérifier si les événements se chevauchent réellement (en excluant les événements consécutifs)
            const isOverlapping = (
              (newStart < eventEnd && newStart > eventStart) || // Le nouvel événement commence pendant l'événement existant
              (newEnd > eventStart && newEnd < eventEnd) ||    // Le nouvel événement se termine pendant l'événement existant
              (newStart <= eventStart && newEnd >= eventEnd)   // Le nouvel événement englobe l'événement existant
            );
            
            // Ne vérifier les conflits d'utilisateurs que si les événements se chevauchent réellement
            if (isOverlapping) {
              for (const newUser of newEventUsers) {
                for (const existingUser of eventUsers) {
                  // Vérifier si le nom normalisé correspond (sans accents et insensible à la casse)
                  if (newUser.normalizedName === existingUser.normalizedName) {
                    // Vérification spéciale pour les événements de type 'non-disponible'
                    // Si l'utilisateur essaie de créer un événement et qu'il est déjà marqué comme non-disponible
                    // OU si l'utilisateur essaie de créer un événement 'non-disponible' et qu'il est déjà assigné à un autre événement
                    const isCreatingNonDisponible = newEvent.type === 'non-disponible';
                    const existingEventIsNonDisponible = event.type === 'non-disponible';

                    // Autoriser l'auto-chevauchement des indisponibilités pour le même utilisateur
                    // (éviter les faux positifs lorsqu'on ajoute/retire des salles à la même heure)
                    if (isCreatingNonDisponible && existingEventIsNonDisponible) {
                      continue; // ne pas enregistrer ce conflit
                    }
                    
                    // Empêcher l'assignation si :
                    // 1. L'utilisateur crée un événement normal mais est déjà marqué comme non-disponible
                    // 2. L'utilisateur crée un événement 'non-disponible' mais est déjà assigné à un autre événement
                    // 3. Tout autre conflit d'assignation d'utilisateur
                    let conflictReason = '';
                    if (!isCreatingNonDisponible && existingEventIsNonDisponible) {
                      conflictReason = ' (utilisateur non disponible)';
                    } else if (isCreatingNonDisponible && !existingEventIsNonDisponible) {
                      conflictReason = ' (utilisateur déjà assigné à un événement)';
                    }
                    
                    conflictingUserDetails.push({
                      userId: existingUser.id,
                      userName: existingUser.fullName,
                      eventId: event.id,
                      eventTitle: event.title + conflictReason,
                      eventStart: new Date(event.start).toLocaleString('fr-FR', { timeZone: 'Europe/Paris' }),
                      eventEnd: new Date(event.end).toLocaleString('fr-FR', { timeZone: 'Europe/Paris' }),
                      // ✅ Ajout : nom de salle "sécurisé"
                      conflictingEventRoom: resolveEventRoomNames(event) || getRoomNameById(event?.room?.id ?? event?.roomId) || 'salle inconnue'
                    });
                    break; // Sortir de la boucle interne une fois qu'un conflit est trouvé pour cet utilisateur
                  }
                }
              }
            }
            
            if (conflictingUserDetails.length > 0) {
              conflictingUserEvents.push({
                event,
                conflictingUsers: conflictingUserDetails
              });
            }
          }
        }
        
        if (conflictingUserEvents.length > 0) {
          // Créer un message d'erreur détaillé
          let errorMessage = 'Impossible de créer l\'événement car les utilisateurs suivants sont déjà assignés à un autre événement qui se chevauche avec celui-ci :';
          
          // Regrouper par utilisateur pour éviter les doublons
          const userConflicts = {};
          
          conflictingUserEvents.forEach(({ conflictingUsers }) => {
            conflictingUsers.forEach(user => {
              if (!userConflicts[user.userId]) {
                userConflicts[user.userId] = {
                  userName: user.userName,
                  conflicts: []
                };
              }
              userConflicts[user.userId].conflicts.push({
                eventTitle: user.eventTitle,
                eventStart: user.eventStart,
                eventEnd: user.eventEnd,
                conflictingEventRoom: user.conflictingEventRoom
              });
            });
          });
          
          // Construire le message d'erreur
          Object.values(userConflicts).forEach(({ userName, conflicts }) => {
            errorMessage += `\n\n- ${userName} est déjà assigné à :`;
            conflicts.forEach(conflict => {
              errorMessage += `\n  • "${conflict.eventTitle}" dans la salle "${conflict.conflictingEventRoom}" (${conflict.eventStart} - ${conflict.eventEnd})`;
            });
          });
          
          setError(errorMessage);
          return;
        }
      }
      
      // Préparer les données de l'événement
      // Si l'utilisateur est permanent et crée un événement 'non-disponible', l'ajouter automatiquement aux utilisateurs assignés
      let assignedUsers = [...(newEvent.assignedUsers || [])];
      if (user.isPermanent && newEvent.type === 'non-disponible' && modalType === 'add') {
        // Vérifier si l'utilisateur n'est pas déjà assigné
        if (!assignedUsers.includes(user.id)) {
          assignedUsers.push(user.id);
        }
      }
      
      // Calculer la couleur finale pour la création selon type/statut
      const computedColor = (
        newEvent.type === 'show'
          ? getShowColor(newEvent.showType, newEvent.showStatus || 'provisional')
          : newEvent.type === 'rental'
            ? getRentalColor(newEvent.rentalStatus || 'provisional')
            : (eventTypes.find(t => t.value === newEvent.type)?.color || newEvent.color)
      );

      const eventData = {
        title: newEvent.title,
        start: new Date(newEvent.start).toISOString(),
        end: new Date(newEvent.end).toISOString(),
        // Pour les événements 'non-disponible', utiliser roomIds pour la sélection multiple
        // Pour les autres types, utiliser roomId
        ...(newEvent.type === 'non-disponible' ? {
          roomIds: newEvent.roomIds && newEvent.roomIds.length > 0 ? newEvent.roomIds.map(id => parseInt(id)) : []
        } : {
          roomId: newEvent.roomId ? parseInt(newEvent.roomId) : null
        }),

        type: newEvent.type,
        personnelType: newEvent.personnelType,
        description: newEvent.description,
        color: computedColor,
        // Inclure creatorId uniquement lors de la création d'un nouvel événement, pas lors de la mise à jour
        ...(modalType === 'add' && { creatorId: user.id }),
        assignedUsers: assignedUsers,
        // Toujours inclure personnelType, quel que soit le type d'événement
        personnelType: newEvent.personnelType || lastPersonnelType || '',
        userDetails: newEvent.userDetails.map(detail => ({
          userId: detail.userId,
          accommodation: detail.accommodation || '',
          arrivalLocation: detail.arrivalLocation || '',
          // Préserver l'heure locale en ajustant pour le fuseau horaire
          arrivalTime: detail.arrivalTime ? 
            new Date(new Date(detail.arrivalTime).getTime() + new Date(detail.arrivalTime).getTimezoneOffset() * 60000).toISOString() : null,
          departureLocation: detail.departureLocation || '',
          // Préserver l'heure locale en ajustant pour le fuseau horaire
          departureTime: detail.departureTime ? 
            new Date(new Date(detail.departureTime).getTime() + new Date(detail.departureTime).getTimezoneOffset() * 60000).toISOString() : null,
          additionalFee: detail.additionalFee || '',
          notes: detail.notes || '',
          arrivalTransfer: detail.arrivalTransfer || '',
          departureTransfer: detail.departureTransfer || ''
        })),
        // Include all form fields
        ticketingLocation: newEvent.ticketingLocation || '', 
        hasDecor: newEvent.hasDecor || false,
        decorDetails: newEvent.decorDetails || '',
        flyersOrdered: newEvent.flyersOrdered || false,
        vatPercentage: newEvent.vatPercentage || '',
        hotelOrdered: newEvent.hotelOrdered || false,
        apartmentOrdered: newEvent.apartmentOrdered || false,
        sacemDeclared: newEvent.sacemDeclared ?? false,
        sacdDeclared: newEvent.sacdDeclared ?? false,
        transferOrdered: newEvent.transferOrdered || false,
        productionContact: newEvent.productionContact || '',
        stageManagerContact: newEvent.stageManagerContact || '',
        expectedAttendees: newEvent.expectedAttendees || 0,
        jauge: newEvent.jauge || '',
        address: newEvent.address || '',
        hasSoundTrack: newEvent.hasSoundTrack || false,
        soundTrackDetails: newEvent.soundTrackDetails || '',
        isRecurring: newEvent.isRecurring || false,
        recurrenceId: newEvent.recurrenceId || null,
        recurrenceEndDate: newEvent.recurrenceEndDate || null,
        recurrenceDays: newEvent.recurrenceDays ? newEvent.recurrenceDays.map(day => Number(day)) : [],
        eventGuests: newEvent.eventGuests || [],
        category: newEvent.category || 'divers',
        // Include show-specific fields if applicable
        ...(newEvent.type === 'show' && {
          showStatus: newEvent.showStatus,
          showType: newEvent.showType,
          coRealizationPercentage: newEvent.coRealizationPercentage
        }),
        // Include rental-specific fields if applicable
        ...(newEvent.type === 'rental' && {
          rentalStatus: newEvent.rentalStatus
        })
      };
      
      // LOGS DE DÉBOGAGE - Données de l'événement
      console.log('=== DÉBOGAGE SAUVEGARDE ÉVÉNEMENT ===');
      console.log('Mode modal:', modalType);
      console.log('Type d\'événement:', newEvent.type);
      console.log('newEvent.roomId:', newEvent.roomId);
      console.log('newEvent.roomIds:', newEvent.roomIds);
      console.log('eventData.roomId:', eventData.roomId);
      console.log('Données eventData complètes:', JSON.stringify(eventData, null, 2));

      eventData.seriesDates = [...newEvent.seriesDates];
      // On récupère l’ID de récurrence depuis le state / eventData
      const recId = eventData.recurrenceId || newEvent.recurrenceId;
      
      
      // Créer ou mettre à jour l'événement
      if (modalType === 'add') {
        if (newEvent.isRecurring && newEvent.recurrenceDays.length > 0 && newEvent.recurrenceEndDate) {
          try {
            // Créer des événements récurrents pour tous les types d'événements
            const startDate = new Date(newEvent.start);
            const endDate = new Date(newEvent.recurrenceEndDate);
            const eventStartTime = startDate.getHours() * 60 + startDate.getMinutes(); // Minutes depuis minuit
            const eventEndTime = new Date(newEvent.end).getHours() * 60 + new Date(newEvent.end).getMinutes();
            const eventDuration = new Date(newEvent.end) - new Date(newEvent.start); // Durée en millisecondes
            
            // Générer un identifiant unique pour cette série d'événements récurrents
            const recurrenceId = `recurrence_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
            console.log('ID de récurrence généré:', recurrenceId);
            
            // Tableau pour stocker toutes les promesses de création d'événements
            const eventPromises = [];
            
            // Convertir les jours de récurrence en nombres et s'assurer qu'ils sont uniques
            const recurrenceDays = [...new Set(newEvent.recurrenceDays.map(day => Number(day)))];
            console.log('Jours de récurrence sélectionnés:', recurrenceDays);
            
            // Vérifier si le jour de début est inclus dans les jours de récurrence
            let startDayOfWeek = startDate.getDay();
            // Convertir 0 (dimanche) en 7 pour correspondre à notre système de récurrence
            if (startDayOfWeek === 0) startDayOfWeek = 7;
            console.log('Jour de la semaine de la date de début:', startDayOfWeek);
            
            // Créer un événement pour le jour de début (premier événement - le master)
            // Toujours créer l'événement initial, même s'il n'est pas dans les jours récurrents
            // Ne pas définir l'ID manuellement, laisser le backend le générer
            const masterEventData = {
              ...eventData,
              recurrenceId: recurrenceId,
              recurrenceDays: [...recurrenceDays]  // On s'assure de toujours copier le tableau
            };
            
            // Si l'utilisateur est permanent et crée un événement 'non-disponible', s'assurer qu'il est assigné
            if (user.isPermanent && newEvent.type === 'non-disponible') {
              if (!masterEventData.assignedUsers.includes(user.id)) {
                masterEventData.assignedUsers = [...masterEventData.assignedUsers, user.id];
              }
            }
            
            eventPromises.push(api.post('/events', masterEventData));
            console.log('Premier événement (master) ajouté pour la date:', startDate.toISOString());
            
            // Créer un événement pour chaque jour sélectionné entre la date de début et la date de fin
            const currentDate = new Date(startDate);
            currentDate.setDate(currentDate.getDate() + 1); // Commencer au jour suivant
            currentDate.setHours(0, 0, 0, 0); // Réinitialiser l'heure à minuit
            
            let eventCount = 1; // Commencer à 1 pour inclure l'événement initial
            
            while (currentDate <= endDate) {
              let dayOfWeek = currentDate.getDay(); // 0 = dimanche, 1 = lundi, etc.
              // Convertir 0 (dimanche) en 7 pour correspondre à notre système de récurrence
              if (dayOfWeek === 0) dayOfWeek = 7;
              
              // Vérifier si ce jour de la semaine est sélectionné pour la récurrence
              if (recurrenceDays.includes(dayOfWeek)) {
                // Créer un nouvel événement pour ce jour
                const newEventStart = new Date(currentDate);
                newEventStart.setHours(Math.floor(eventStartTime / 60), eventStartTime % 60, 0, 0);
                
                // Calculer la fin en ajoutant la durée de l'événement original
                const newEventEnd = new Date(newEventStart.getTime() + eventDuration);
                
                // Déterminer la couleur correcte selon le type d'événement
                let eventColor;
                if (newEvent.type === 'show') {
                  eventColor = getShowColor(newEvent.showType, newEvent.showStatus);
                } else if (newEvent.type === 'rental') {
                  eventColor = getRentalColor(newEvent.rentalStatus);
                } else {
                  eventColor = eventTypes.find(t => t.value === newEvent.type)?.color || eventData.color;
                }
                
                // Pour les clones, on ne définit pas d'id spécifique (FullCalendar en générera un)
                // mais on s'assure de copier le tableau recurrenceDays
                const newEventData = {
                  ...eventData,
                  recurrenceId: recurrenceId,
                  recurrenceDays: [...recurrenceDays],  // On s'assure de toujours copier le tableau
                  start: newEventStart.toISOString(),
                  end: newEventEnd.toISOString(),
                  color: eventColor, // S'assurer que la couleur correspond au type d'événement
                  // Inclure le type de personnel si l'événement est de type 'personnel'
                  ...(newEvent.type === 'personnel' && {
                    personnelType: newEvent.personnelType
                  })
                };
                
                // Si l'utilisateur est permanent et crée un événement 'non-disponible', s'assurer qu'il est assigné
                if (user.isPermanent && newEvent.type === 'non-disponible') {
                  if (!newEventData.assignedUsers.includes(user.id)) {
                    newEventData.assignedUsers = [...newEventData.assignedUsers, user.id];
                  }
                }
                
                // Ajouter la promesse de création d'événement au tableau
                eventPromises.push(api.post('/events', newEventData));
                console.log('Événement récurrent ajouté pour la date:', newEventStart.toISOString());
                eventCount++;
              }
              
              // Passer au jour suivant
              currentDate.setDate(currentDate.getDate() + 1);
            }
            
            console.log(`Tentative de création de ${eventPromises.length} événements récurrents`);
            
            // Attendre que tous les événements soient créés
            const results = await Promise.all(eventPromises);
            console.log(`${results.length} événements récurrents créés avec succès`);
            console.log('Détail des résultats:', results.map(r => r.data));
            
            // Gérer les PDFs pour *toutes* les occurrences créées
            if (results.length > 0) {
              try {
                // Récupérer les PDFs temporaires pré-chargés dans eventPdfs
                const tempPdfs = eventPdfs.filter(pdf => pdf.isTemp);
                
                if (tempPdfs.length > 0) {
                  // Regrouper les PDFs par catégorie
                  const pdfsByCategory = {};
                  tempPdfs.forEach(pdf => {
                    if (!pdfsByCategory[pdf.category]) {
                      pdfsByCategory[pdf.category] = [];
                    }
                    pdfsByCategory[pdf.category].push(pdf.file);
                  });
                  
                  // Pour chaque occurrence d'événement
                  const allAttachPromises = [];
                  
                  results.forEach(resEvt => {
                    const id = resEvt.data.data?.event?.id || resEvt.data.id;
                    
                    // Optimisation: Créer un seul FormData pour tous les PDFs
                    const form = new FormData();
                    
                    // Ajouter tous les fichiers avec leur catégorie
                    tempPdfs.forEach(pdf => {
                      form.append('pdfs', pdf.file);
                      form.append('categories', pdf.category);
                    });
                    
                    // Faire un seul appel API pour tous les PDFs
                    allAttachPromises.push(api.post(`/events/${id}/pdfs/batch`, form));
                  });
                  
                  await Promise.all(allAttachPromises);
                  console.log(`PDFs ajoutés aux ${results.length} événements récurrents`);
                } else if (pdfFiles.length) {
                  // Fallback pour les PDFs qui seraient encore dans pdfFiles
                  // Pour chaque occurrence, on récupère son ID et on poste les PDFs
                  const attachPromises = results.map(resEvt => {
                    const id = resEvt.data.data?.event?.id || resEvt.data.id;
                    
                    // Préparer un FormData pour chaque événement
                    const form = new FormData();
                    pdfFiles.forEach(f => form.append('pdfs', f));
                    // Utiliser la même catégorie pour tous les fichiers
                    pdfFiles.forEach(() => form.append('categories', selectedPdfCategory));
                    
                    return api.post(`/events/${id}/pdfs/batch`, form);
                  });

                  await Promise.all(attachPromises);
                  console.log(`PDFs ajoutés aux ${results.length} événements récurrents`);
                }
              } catch (err) {
                console.error('Erreur lors de l\'ajout des PDFs aux événements récurrents :', err);
              }
            }
            
            setSuccess(`${eventCount} événements récurrents créés avec succès`);
            // Supprimer le brouillon après création réussie
            sessionStorage.removeItem('eventDraft');
          } catch (error) {
            console.error('Erreur lors de la création des événements récurrents:', error);
            setError(error.response?.data?.message || 'Erreur lors de la création des événements récurrents');
            return; // Arrêter l'exécution en cas d'erreur
          }
        } else {
          // Création d'un ou plusieurs événements (selon le type et les salles sélectionnées)
          let createdIds = [];
          try {
            // Un seul POST, même pour les non-disponibles multi-salles (roomIds inclus)
            const res = await api.post('/events', eventData);
            console.log('Réponse de création d\'événement:', res.data);

            // Extraire l'ID de l'événement créé en fonction de la structure de la réponse
            const createdId = res.data.data?.event?.id || res.data.id;

            if (!createdId) {
              console.error('ID de l\'événement créé non trouvé dans la réponse:', res.data);
              throw new Error('ID de l\'événement créé non trouvé');
            }

            createdIds = [createdId];
            console.log('ID de l\'événement créé:', createdId);
            setSuccess('Événement créé avec succès');

            // Gérer les PDFs temporaires pour les événements créés
            if (eventPdfs.filter(pdf => pdf.isTemp).length > 0 || pdfFiles.length > 0) {
              try {
                const tempPdfs = eventPdfs.filter(pdf => pdf.isTemp);
                
                if (tempPdfs.length > 0) {
                  // Ajouter les PDFs à tous les événements créés
                  const pdfPromises = createdIds.map(eventId => {
                    const form = new FormData();
                    tempPdfs.forEach(pdf => {
                      form.append('pdfs', pdf.file);
                      form.append('categories', pdf.category);
                    });
                    return api.post(`/events/${eventId}/pdfs/batch`, form);
                  });
                  
                  await Promise.all(pdfPromises);
                  console.log(`PDFs ajoutés aux ${createdIds.length} événements`);
                }
              } catch (err) {
                console.error('Erreur lors de l\'ajout des PDFs :', err);
              }
            }
          } catch (error) {
            console.error('Erreur lors de la création de l\'événement:', error);
            
            // Vérifier si l'erreur est due à un conflit d'utilisateurs
            if (error.response?.status === 409 && error.response?.data?.conflictingUsers) {
              console.debug('[409] payload:', error?.response?.data);
              const conflictingUsers = error?.response?.data?.conflictingUsers || [];

              let errorMessage = 'Impossible de créer l\'événement car les utilisateurs suivants sont déjà assignés à un autre événement qui se chevauche :\n';

              const fmtParis = (isoLike) => {
                if (!isoLike) return 'N/A';
                const d = new Date(isoLike);
                return d.toLocaleString('fr-FR', {
                  timeZone: 'Europe/Paris',
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit'
                });
              };

              conflictingUsers.forEach((u) => {
                // ✅ nom de salle robuste (lit nom direct, id, repli via events, repli via current event)
                const roomName = resolveConflictRoomName(u, newEvent) || 'salle inconnue';
                
                if (!roomName || roomName === 'salle inconnue') {
                  console.warn('Conflit sans salle résolue:', u);
                }

                const title = u.conflictingEventTitle || u.eventTitle || 'Événement';
                const start = u.conflictingEventStart || u.eventStart;
                const end   = u.conflictingEventEnd   || u.eventEnd;

                errorMessage += `\n- ${u.userName} est déjà assigné à : • "${title}" dans la salle "${roomName}" (${fmtParis(start)} - ${fmtParis(end)})`;
              });

              setError(errorMessage);
            } else {
              setError(error.response?.data?.message || 'Erreur lors de la création de l\'événement');
            }
            
            return; // Arrêter l'exécution en cas d'erreur
          }
          
          // Supprimer le brouillon après création réussie
          sessionStorage.removeItem('eventDraft');
        }

      } else {
        // Vérifier si l'événement fait partie d'une série récurrente OU si on veut créer une nouvelle récurrence
        if (newEvent.isRecurring && currentEvent.extendedProps.recurrenceId) {
          // Cas 1: Modification d'une série récurrente existante
          try {
            // Récupérer tous les événements de la même série récurrente
            const recurrenceId = currentEvent.extendedProps.recurrenceId;
            const response = await api.get(`/events?recurrenceId=${recurrenceId}&type=${newEvent.type}`);
            const recurrentEvents = response.data.data.events;
            
            console.log(`Mise à jour de ${recurrentEvents.length} événements récurrents avec recurrenceId: ${recurrenceId}`);
            
            // Calculer la différence de temps entre l'ancienne et la nouvelle date de début
            const oldStartTime = new Date(currentEvent.start).getTime();
            const newStartTime = new Date(newEvent.start).getTime();
            const timeDifference = newStartTime - oldStartTime;
            
            // Calculer la nouvelle durée de l'événement
            const newDuration = new Date(newEvent.end).getTime() - new Date(newEvent.start).getTime();
            
            // Vérifier si la date de fin de récurrence a été modifiée
            const oldRecurrenceEndDate = currentEvent.extendedProps.recurrenceEndDate 
              ? new Date(currentEvent.extendedProps.recurrenceEndDate) 
              : null;
            const newRecurrenceEndDate = newEvent.recurrenceEndDate 
              ? new Date(newEvent.recurrenceEndDate) 
              : null;
            
            // Mettre à jour chaque événement existant de la série
            const updatePromises = recurrentEvents.map(event => {
              // Déterminer la couleur correcte selon le type d'événement
              let eventColor;
              if (newEvent.type === 'show') {
                eventColor = getShowColor(newEvent.showType, newEvent.showStatus);
              } else if (newEvent.type === 'rental') {
                eventColor = getRentalColor(newEvent.rentalStatus);
              } else {
                eventColor = eventTypes.find(t => t.value === newEvent.type)?.color || event.color;
              }
              
              // Créer une copie des données d'événement pour cet événement spécifique
              const eventSpecificData = {
                ...eventData,
                // Mettre à jour les dates en appliquant le même décalage temporel
                start: new Date(new Date(event.start).getTime() + timeDifference).toISOString(),
                end: new Date(new Date(event.start).getTime() + timeDifference + newDuration).toISOString(),
                // S'assurer que le recurrenceId est préservé
                recurrenceId: recurrenceId,
                // S'assurer que la couleur est correcte
                color: eventColor,
                // Inclure le type de personnel si l'événement est de type 'personnel'
                ...(newEvent.type === 'personnel' && {
                  personnelType: newEvent.personnelType
                })
              };
              
              return api.patch(`/events/${event.id}`, eventSpecificData);
            });
            
            // Attendre que toutes les mises à jour soient terminées
            await Promise.all(updatePromises);
            
            // Si la date de fin de récurrence a été modifiée et est plus tardive que l'ancienne,
            // créer de nouveaux événements pour les dates supplémentaires
            if (newRecurrenceEndDate && (!oldRecurrenceEndDate || newRecurrenceEndDate > oldRecurrenceEndDate)) {
              console.log('La date de fin de récurrence a été prolongée, création de nouveaux événements');
              
              // Trouver la date la plus tardive parmi les événements existants
              const latestEventDate = new Date(Math.max(...recurrentEvents.map(event => new Date(event.start).getTime())));
              latestEventDate.setHours(23, 59, 59, 999); // Fin de la journée
              
              // Créer des événements pour les nouvelles dates
              const startDate = new Date(newEvent.start);
              const eventStartTime = startDate.getHours() * 60 + startDate.getMinutes(); // Minutes depuis minuit
              
              // Convertir les jours de récurrence en nombres et s'assurer qu'ils sont uniques
              const recurrenceDays = [...new Set(newEvent.recurrenceDays.map(day => Number(day)))];
              console.log('Jours de récurrence sélectionnés:', recurrenceDays);
              
              // Créer un événement pour chaque jour sélectionné entre la dernière date existante et la nouvelle date de fin
              const currentDate = new Date(latestEventDate);
              currentDate.setDate(currentDate.getDate() + 1); // Commencer au jour suivant
              currentDate.setHours(0, 0, 0, 0); // Réinitialiser l'heure à minuit
              
              const newEventPromises = [];
              
              while (currentDate <= newRecurrenceEndDate) {
                let dayOfWeek = currentDate.getDay(); // 0 = dimanche, 1 = lundi, etc.
                // Convertir 0 (dimanche) en 7 pour correspondre à notre système de récurrence
                if (dayOfWeek === 0) dayOfWeek = 7;
                
                // Vérifier si ce jour de la semaine est sélectionné pour la récurrence
                if (recurrenceDays.includes(dayOfWeek)) {
                  // Créer un nouvel événement pour ce jour
                  const newEventStart = new Date(currentDate);
                  newEventStart.setHours(Math.floor(eventStartTime / 60), eventStartTime % 60, 0, 0);
                  
                  // Calculer la fin en ajoutant la durée de l'événement
                  const newEventEnd = new Date(newEventStart.getTime() + newDuration);
                  
                  // Déterminer la couleur correcte selon le type d'événement
                  let eventColor;
                  if (newEvent.type === 'show') {
                    eventColor = getShowColor(newEvent.showType, newEvent.showStatus);
                  } else {
                    eventColor = eventTypes.find(t => t.value === newEvent.type)?.color || currentEvent.color;
                  }
                  
                  const newEventData = {
                    ...eventData,
                    start: newEventStart.toISOString(),
                    end: newEventEnd.toISOString(),
                    recurrenceId: recurrenceId,
                    color: eventColor, // Utiliser la couleur correcte selon le type d'événement
                    // Inclure le type de personnel si l'événement est de type 'personnel'
                    ...(newEvent.type === 'personnel' && {
                      personnelType: newEvent.personnelType
                    })
                  };
                  
                  // Ajouter la promesse de création d'événement au tableau
                  newEventPromises.push(api.post('/events', newEventData));
                  console.log('Nouvel événement récurrent ajouté pour la date:', newEventStart.toISOString());
                }
                
                // Passer au jour suivant
                currentDate.setDate(currentDate.getDate() + 1);
              }
              
              if (newEventPromises.length > 0) {
                console.log(`Tentative de création de ${newEventPromises.length} nouveaux événements récurrents`);
                const results = await Promise.all(newEventPromises);
                console.log(`${results.length} nouveaux événements récurrents créés avec succès`);
                
                // Gérer les PDFs pour les nouveaux événements créés
                if (results.length > 0) {
                  try {
                    // Récupérer les PDFs temporaires pré-chargés dans eventPdfs
                    const tempPdfs = eventPdfs.filter(pdf => pdf.isTemp);
                    
                    if (tempPdfs.length > 0) {
                      // Regrouper les PDFs par catégorie
                      const pdfsByCategory = {};
                      tempPdfs.forEach(pdf => {
                        if (!pdfsByCategory[pdf.category]) {
                          pdfsByCategory[pdf.category] = [];
                        }
                        pdfsByCategory[pdf.category].push(pdf.file);
                      });
                      
                      // Pour chaque nouvel événement
                      const allAttachPromises = [];
                      
                      results.forEach(resEvt => {
                        const id = resEvt.data.data?.event?.id || resEvt.data.id;
                        
                        // Optimisation: Créer un seul FormData pour tous les PDFs
                        const form = new FormData();
                        
                        // Ajouter tous les fichiers avec leur catégorie
                        tempPdfs.forEach(pdf => {
                          form.append('pdfs', pdf.file);
                          form.append('categories', pdf.category);
                        });
                        
                        // Faire un seul appel API pour tous les PDFs
                        allAttachPromises.push(api.post(`/events/${id}/pdfs/batch`, form));
                      });
                      
                      await Promise.all(allAttachPromises);
                      console.log(`PDFs ajoutés aux ${results.length} nouveaux événements récurrents`);
                    }
                  } catch (err) {
                    console.error('Erreur lors de l\'ajout des PDFs aux nouveaux événements récurrents :', err);
                  }
                }
              }
            }
            
            // Si la date de fin de récurrence a été modifiée et est plus précoce que l'ancienne,
            // supprimer les événements qui sont après la nouvelle date de fin
            if (newRecurrenceEndDate && oldRecurrenceEndDate && newRecurrenceEndDate < oldRecurrenceEndDate) {
              console.log('La date de fin de récurrence a été raccourcie, suppression des événements excédentaires');
              
              // Identifier les événements à supprimer (ceux dont la date de début est après la nouvelle date de fin)
              const eventsToDelete = recurrentEvents.filter(event => {
                const eventStart = new Date(event.start);
                return eventStart > newRecurrenceEndDate;
              });
              
              if (eventsToDelete.length > 0) {
                console.log(`Suppression de ${eventsToDelete.length} événements récurrents excédentaires`);
                
                const deletePromises = eventsToDelete.map(event => {
                  return api.delete(`/events/${event.id}`);
                });
                
                await Promise.all(deletePromises);
                console.log(`${eventsToDelete.length} événements récurrents excédentaires supprimés avec succès`);
              }
            }
            
            // Ajouter les PDFs uniquement à l'événement actuel
          // Récupérer les PDFs temporaires pré-chargés dans eventPdfs
          const tempPdfs = eventPdfs.filter(pdf => pdf.isTemp);
          
          if (tempPdfs.length > 0) {
            // Optimisation: Créer un seul FormData pour tous les PDFs
            const form = new FormData();
            
            // Ajouter tous les fichiers avec leur catégorie
            tempPdfs.forEach(pdf => {
              form.append('pdfs', pdf.file);
              form.append('categories', pdf.category);
            });
            
            // Faire un seul appel API pour tous les PDFs
            await api.post(`/events/${newEvent.id}/pdfs/batch`, form);
            console.log(`PDFs ajoutés à l'événement ${newEvent.id} en un seul appel`);
          } else if (pdfFiles.length) {
            // Fallback pour les PDFs qui seraient encore dans pdfFiles
            const form = new FormData();
            pdfFiles.forEach(f => form.append('pdfs', f));
            // Utiliser la même catégorie pour tous les fichiers
            pdfFiles.forEach(() => form.append('categories', selectedPdfCategory));
            await api.post(`/events/${newEvent.id}/pdfs/batch`, form);
          }
            
            setSuccess(`${recurrentEvents.length} événements récurrents mis à jour avec succès`);
          } catch (error) {
            console.error('Erreur lors de la mise à jour des événements récurrents:', error);
            setError(error.response?.data?.message || 'Erreur lors de la mise à jour des événements récurrents');
            return;
          }
        } else if (newEvent.isRecurring && !currentEvent.extendedProps.recurrenceId) {
          // Cas 2: Création d'une nouvelle récurrence sur un événement qui n'en avait pas (ex: événement collé)
          console.log('Création d\'une nouvelle récurrence sur un événement non-récurrent');
          
          // Générer un nouvel ID de récurrence unique
          const recurrenceId = `rec_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          console.log('Nouvel ID de récurrence généré:', recurrenceId);
          
          // Mettre à jour l'événement actuel avec les nouvelles propriétés de récurrence
          const updatedEventData = {
            ...eventData,
            recurrenceId: recurrenceId,
            isRecurring: true
          };
          
          try {
            // Mettre à jour l'événement existant
            await api.patch(`/events/${newEvent.id}`, updatedEventData);
            
            // Créer les événements récurrents supplémentaires
            const startDate = new Date(newEvent.start);
            const endDate = new Date(newEvent.end);
            const duration = endDate.getTime() - startDate.getTime();
            const eventStartTime = startDate.getHours() * 60 + startDate.getMinutes();
            
            // Convertir les jours de récurrence en nombres et s'assurer qu'ils sont uniques
            const recurrenceDays = [...new Set(newEvent.recurrenceDays.map(day => Number(day)))];
            console.log('Jours de récurrence sélectionnés:', recurrenceDays);
            
            // Créer des événements pour chaque jour sélectionné jusqu'à la date de fin de récurrence
            const currentDate = new Date(startDate);
            currentDate.setDate(currentDate.getDate() + 1); // Commencer au jour suivant
            currentDate.setHours(0, 0, 0, 0);
            
            const recurrenceEndDate = new Date(newEvent.recurrenceEndDate);
            const newEventPromises = [];
            
            while (currentDate <= recurrenceEndDate) {
              let dayOfWeek = currentDate.getDay();
              if (dayOfWeek === 0) dayOfWeek = 7; // Convertir dimanche (0) en 7
              
              if (recurrenceDays.includes(dayOfWeek)) {
                const newEventStart = new Date(currentDate);
                newEventStart.setHours(Math.floor(eventStartTime / 60), eventStartTime % 60, 0, 0);
                const newEventEnd = new Date(newEventStart.getTime() + duration);
                
                // Déterminer la couleur correcte selon le type d'événement
                let eventColor;
                if (newEvent.type === 'show') {
                  eventColor = getShowColor(newEvent.showType, newEvent.showStatus);
                } else if (newEvent.type === 'rental') {
                  eventColor = getRentalColor(newEvent.rentalStatus);
                } else {
                  eventColor = eventTypes.find(t => t.value === newEvent.type)?.color || currentEvent.color;
                }
                
                const newRecurrentEventData = {
                  ...eventData,
                  start: newEventStart.toISOString(),
                  end: newEventEnd.toISOString(),
                  recurrenceId: recurrenceId,
                  isRecurring: true,
                  color: eventColor,
                  ...(newEvent.type === 'personnel' && {
                    personnelType: newEvent.personnelType
                  })
                };
                
                newEventPromises.push(api.post('/events', newRecurrentEventData));
                console.log('Nouvel événement récurrent programmé pour:', newEventStart.toISOString());
              }
              
              currentDate.setDate(currentDate.getDate() + 1);
            }
            
            if (newEventPromises.length > 0) {
              console.log(`Création de ${newEventPromises.length} nouveaux événements récurrents`);
              const results = await Promise.all(newEventPromises);
              console.log(`${results.length} nouveaux événements récurrents créés avec succès`);
              
              // Gérer les PDFs pour les nouveaux événements créés
              const tempPdfs = eventPdfs.filter(pdf => pdf.isTemp);
              if (tempPdfs.length > 0) {
                const allAttachPromises = [];
                
                // Ajouter les PDFs à l'événement principal
                const mainForm = new FormData();
                tempPdfs.forEach(pdf => {
                  mainForm.append('pdfs', pdf.file);
                  mainForm.append('categories', pdf.category);
                });
                allAttachPromises.push(api.post(`/events/${newEvent.id}/pdfs/batch`, mainForm));
                
                // Ajouter les PDFs aux nouveaux événements récurrents
                results.forEach(resEvt => {
                  const id = resEvt.data.data?.event?.id || resEvt.data.id;
                  const form = new FormData();
                  tempPdfs.forEach(pdf => {
                    form.append('pdfs', pdf.file);
                    form.append('categories', pdf.category);
                  });
                  allAttachPromises.push(api.post(`/events/${id}/pdfs/batch`, form));
                });
                
                await Promise.all(allAttachPromises);
                console.log(`PDFs ajoutés à tous les événements récurrents`);
              }
              
              setSuccess(`Récurrence créée avec succès: ${results.length + 1} événements au total`);
            } else {
              setSuccess('Événement mis à jour avec récurrence');
            }
            
          } catch (error) {
            console.error('Erreur lors de la création de la nouvelle récurrence:', error);
            setError(error.response?.data?.message || 'Erreur lors de la création de la récurrence');
            return;
          }
        } else {
          // Mise à jour d'un seul événement (non récurrent)
          console.log('=== MISE À JOUR ÉVÉNEMENT SIMPLE ===');
          console.log('ID de l\'événement à mettre à jour:', newEvent.id);
          console.log('Données envoyées à l\'API:', JSON.stringify(eventData, null, 2));
          
          try {
            const response = await api.patch(`/events/${newEvent.id}`, eventData);
            console.log('Réponse de mise à jour:', response.data);
          } catch (error) {
            console.error('Erreur lors de la mise à jour de l\'événement:', error);
            
            // Vérifier si l'erreur est due à un conflit d'utilisateurs
            if (error.response?.status === 409 && error.response?.data?.conflictingUsers) {
              console.debug('[409] payload:', error?.response?.data);
              const conflictingUsers = error?.response?.data?.conflictingUsers || [];
              
              let errorMessage = 'Impossible de mettre à jour l\'événement car les utilisateurs suivants sont déjà assignés à un autre événement qui se chevauche :\n';
              
              const fmtParis = (isoLike) => {
                if (!isoLike) return 'N/A';
                const d = new Date(isoLike);
                return d.toLocaleString('fr-FR', {
                  timeZone: 'Europe/Paris',
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit'
                });
              };
              
              conflictingUsers.forEach((u) => {
                // ✅ nom de salle robuste (lit nom direct, id, repli via events, repli via current event)
                const roomName = resolveConflictRoomName(u, newEvent) || 'salle inconnue';
                
                if (!roomName || roomName === 'salle inconnue') {
                  console.warn('Conflit sans salle résolue:', u);
                }
                
                const title = u.conflictingEventTitle || u.eventTitle || 'Événement';
                const start = u.conflictingEventStart || u.eventStart;
                const end   = u.conflictingEventEnd   || u.eventEnd;
                
                errorMessage += `\n- ${u.userName} est déjà assigné à : • "${title}" dans la salle "${roomName}" (${fmtParis(start)} - ${fmtParis(end)})`;
              });
              
              setError(errorMessage);
            } else {
              setError(error.response?.data?.message || 'Erreur lors de la mise à jour de l\'événement');
            }
            
            return; // Arrêter l'exécution en cas d'erreur
          }
          
          // Envoi des nouveaux PDFs
          // Récupérer les PDFs temporaires pré-chargés dans eventPdfs
          const tempPdfs = eventPdfs.filter(pdf => pdf.isTemp);
          
          if (tempPdfs.length > 0) {
            // Regrouper les PDFs par catégorie
            const pdfsByCategory = {};
            tempPdfs.forEach(pdf => {
              if (!pdfsByCategory[pdf.category]) {
                pdfsByCategory[pdf.category] = [];
              }
              pdfsByCategory[pdf.category].push(pdf.file);
            });
            
            // Créer une promesse pour chaque catégorie de PDF
            const uploadPromises = Object.entries(pdfsByCategory).map(([category, files]) => {
              const form = new FormData();
              files.forEach(file => form.append('pdfs', file));
              form.append('category', category);
              return api.post(`/events/${newEvent.id}/pdfs`, form);
            });
            
            // Attendre que tous les uploads soient terminés
            await Promise.all(uploadPromises);
            console.log(`PDFs ajoutés à l'événement ${newEvent.id}`);
          } else if (pdfFiles.length) {
            // Fallback pour les PDFs qui seraient encore dans pdfFiles
            const form = new FormData();
            pdfFiles.forEach(f => form.append('pdfs', f));
            form.append('category', selectedPdfCategory);
            await api.post(`/events/${newEvent.id}/pdfs`, form);
          }
          
          setSuccess('Événement mis à jour avec succès');
        }
      }
      
      // Ne ferme pas le modal si le modal de confirmation de suppression est ouvert
      if (!showDeletePdfConfirm) {
        setShowModal(false);
      }
      await fetchEvents();                            // recharge ton state React "events"
      calendarRef.current.getApi().refetchEvents();  // mémoire interne de FullCalendar
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.message || 'Erreur lors de la création/modification de l\'événement');
      // Keep the error message visible longer
      setTimeout(() => setError(''), 5000);
    }
  };

  // Rendu du composant
  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
      {/* En-tête du calendrier */}
      <div className="bg-primary text-white p-4 flex justify-between items-center">
        <h2 className="text-xl font-semibold">Calendrier des événements</h2>
        <div className="flex items-center gap-3">
          {clipboardEvent && user.role === 'administrateur' && (
            <div className="flex items-center bg-gold/20 text-gold px-3 py-1 rounded-full text-sm">
              <span className="mr-1">Événement en mémoire (collage multiple):</span>
              <span className="font-medium">{clipboardEvent.title}</span>
            </div>
          )}
          <button
            onClick={() => handleNewEvent({
              start: new Date(),
              end: new Date(new Date().getTime() + 2 * 60 * 60 * 1000)
            })}
            className="bg-gold hover:bg-gold/90 text-primary px-4 py-2 rounded-md flex items-center shadow-sm transition-colors"
          >
            <PlusIcon className="h-5 w-5 mr-1" />
            {user.role === 'administrateur' 
              ? 'Créer un événement' 
              : user.isPermanent 
                ? 'Créer un spectacle / Indiquer mon indisponibilité' 
                : user.role === 'artistes'
                  ? 'Indiquer mon indisponibilité'
                  : 'Indiquer mon indisponibilité'}
          </button>
        </div>
      </div>
      
      {/* Les alertes ont été déplacées dans le modal */}
      
      {/* Filtres */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 mb-4">
  <div className="flex justify-between items-center mb-3">
    <h3 className="text-lg font-semibold text-primary flex items-center">
      <ClipboardIcon className="h-5 w-5 mr-2 text-accent" />
      Filtres du calendrier
    </h3>
    <div className="flex items-center space-x-2">
      <div className="relative group">
        <button 
          className="text-sm bg-primary text-white px-3 py-1.5 rounded-md hover:bg-primary/90 flex items-center"
        >
          <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
          Exporter
        </button>
        <div className="absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg overflow-hidden z-20 transform scale-0 group-hover:scale-100 opacity-0 group-hover:opacity-100 transition-all duration-200 origin-top-right">
          <div className="py-1">
            <button onClick={() => exportToPDF('week')} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center">
              <DocumentTextIcon className="h-4 w-4 mr-2 text-red-500" />
              PDF (Semaine)
            </button>
            <button onClick={() => exportToPDF('month')} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center">
              <DocumentTextIcon className="h-4 w-4 mr-2 text-red-500" />
              PDF (Mois)
            </button>
            <button onClick={() => exportToExcel('week')} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center">
              <DocumentTextIcon className="h-4 w-4 mr-2 text-green-600" />
              Excel (Semaine)
            </button>
            <button onClick={() => exportToExcel('month')} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center">
              <DocumentTextIcon className="h-4 w-4 mr-2 text-green-600" />
              Excel (Mois)
            </button>
            <div className="border-t border-gray-200 my-1"></div>
            <div className="px-4 py-2 text-xs text-gray-500 font-medium uppercase tracking-wide">
              Mon planning
            </div>
            <button onClick={() => exportPersonalPDF('week')} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center">
              <DocumentTextIcon className="h-4 w-4 mr-2 text-purple-500" />
              PDF Personnel (Semaine)
            </button>
            <button onClick={() => exportPersonalPDF('month')} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center">
              <DocumentTextIcon className="h-4 w-4 mr-2 text-purple-500" />
              PDF Personnel (Mois)
            </button>
            <button onClick={() => exportPersonalPDF('quarter')} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center">
              <DocumentTextIcon className="h-4 w-4 mr-2 text-purple-500" />
              PDF Personnel (3 Mois)
            </button>
            {(user.isPermanent || user.role === 'administrateur') && (
              <>
                <div className="border-t border-gray-200 my-1"></div>
                <div className="px-4 py-2 text-xs text-gray-500 font-medium uppercase tracking-wide">
                  Export annuel
                </div>
                <button onClick={() => exportToExcelYear()} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center">
                  <DocumentTextIcon className="h-4 w-4 mr-2 text-orange-500" />
                  Année_{new Date().getFullYear()}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
      <button 
        onClick={resetAllFilters}
        className="text-sm text-red-600 hover:text-red-800 flex items-center"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
        Réinitialiser tous les filtres
      </button>
    </div>
  </div>
  
  <div className="mb-4">
    <div className="bg-gray-50 p-3 rounded-md">
      <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        Rechercher un événement
      </label>
      <div className="relative">
        {/* Champ de recherche optimisé */}
        <div className="relative flex items-center">
          <div className="flex absolute inset-y-0 left-0 items-center pl-3 pointer-events-none">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <input
            type="text"
            value={searchState.term}
            onChange={handleSearchInput}
            onKeyDown={handleKeyDown}
            placeholder="Rechercher par nom ou description..."
            autoComplete="off"
            className={`block p-2.5 pl-10 w-full text-sm text-gray-900 bg-white rounded-md border ${searchState.isSearching ? 'border-primary' : 'border-gray-300'} focus:ring-primary focus:border-primary search-input`}
          />
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
            {searchState.isSearching && (
              <svg className="animate-spin h-5 w-5 text-primary mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            )}
            {searchState.term && (
              <button
                onClick={clearSearch}
                className="text-gray-400 hover:text-gray-600 focus:outline-none"
                aria-label="Effacer la recherche"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            )}
          </div>
        </div>
        
        {/* Résultats de recherche améliorés avec navigation au clavier - position fixe sous l'input */}
        {searchState.showResults && searchState.term.trim() !== '' && (
          <div className="absolute z-50 top-full left-0 right-0 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg overflow-hidden">
            {searchState.results.length > 0 ? (
              <div>
                <div className="bg-gray-50 px-3 py-2 border-b border-gray-200">
                  <p className="text-xs font-medium text-gray-700">
                    {searchState.results.length} résultat{searchState.results.length > 1 ? 's' : ''} trouvé{searchState.results.length > 1 ? 's' : ''}
                  </p>
                </div>
                <ul className="divide-y divide-gray-200 max-h-60 overflow-y-auto">
                  {searchState.results.slice(0, 5).map((event, index) => (
                    <li 
                      key={event.id} 
                      className={`px-3 py-2 hover:bg-gray-50 cursor-pointer transition-colors duration-150 ${index === searchState.selectedIndex ? 'bg-gray-100' : ''}`}
                      onClick={() => navigateToEvent(event)}
                    >
                      <div className="flex items-start">
                        <div 
                          className="flex-shrink-0 h-4 w-1 rounded-full mt-1" 
                          style={{ backgroundColor: event.backgroundColor || '#3788d8' }}
                        ></div>
                        <div className="ml-2 flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{event.title}</p>
                          <div className="flex flex-wrap items-center text-xs text-gray-500 mt-1">
                            <CalendarIcon className="h-3 w-3 mr-1 flex-shrink-0" />
                            <span className="mr-2">{format(new Date(event.start), 'dd/MM/yyyy HH:mm')}</span>
                            {event.extendedProps?.room && (
                              <div className="flex items-center">
                                <MapPinIcon className="h-3 w-3 mr-1 flex-shrink-0" />
                                <span className="truncate">{event.extendedProps.room.name}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                  {searchState.results.length > 5 && (
                    <li className="px-3 py-2 text-xs text-center text-gray-500">
                      + {searchState.results.length - 5} autre{searchState.results.length - 5 > 1 ? 's' : ''} résultat{searchState.results.length - 5 > 1 ? 's' : ''}
                    </li>
                  )}
                </ul>
              </div>
            ) : (
              <div className="px-3 py-4 text-center">
                {searchState.isSearching ? (
                  <div className="flex items-center justify-center">
                    <svg className="animate-spin h-4 w-4 text-primary mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <p className="text-sm text-gray-500">Recherche en cours...</p>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">Aucun événement trouvé pour "{searchState.term}"</p>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  </div>
  
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
    {/* Filtre par salle */}
    <div className="bg-gray-50 p-3 rounded-md">
      <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
        <MapPinIcon className="h-4 w-4 mr-1 text-accent" />
        Filtrer par salle
      </label>
      
      <div className="relative">
        <select 
          onChange={(e) => {
            const selectedValue = e.target.value;
            if (selectedValue && !roomFilters.includes(selectedValue)) {
              setRoomFilters([...roomFilters, selectedValue]);
            }
            // Reset la valeur du select après sélection
            e.target.value = '';
          }}
          className="form-select w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary/20"
        >
          <option value="">Sélectionner une salle...</option>
          {rooms.map(room => (
            !roomFilters.includes(room.id.toString()) && 
            <option key={room.id} value={room.id.toString()}>{room.name}</option>
          ))}
        </select>
      </div>
      
      {/* Badges des salles sélectionnées */}
      <div className="mt-2 flex flex-wrap gap-2">
        {roomFilters.length === 0 ? (
          <span className="text-xs text-gray-500 italic">Toutes les salles</span>
        ) : (
          roomFilters.map(roomId => {
            const room = rooms.find(r => r.id.toString() === roomId);
            return (
              <div key={roomId} className="bg-primary/10 text-primary px-2 py-1 rounded-full text-xs flex items-center">
                <span>{room ? room.name : roomId}</span>
                <button 
                  onClick={() => setRoomFilters(roomFilters.filter(id => id !== roomId))}
                  className="ml-1 text-primary hover:text-red-500"
                >
                  <XMarkIcon className="h-3 w-3" />
                </button>
              </div>
            );
          })
        )}
        
        {roomFilters.length > 0 && (
          <button
            onClick={() => setRoomFilters([])}
            className="text-xs text-gray-500 hover:text-primary underline flex items-center"
          >
            Tout effacer
          </button>
        )}
      </div>
    </div>
    
    {/* Filtre par type */}
    <div className="bg-gray-50 p-3 rounded-md">
      <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
        <ClipboardIcon className="h-4 w-4 mr-1 text-accent" />
        Filtrer par type
      </label>
      
      <div className="relative">
        <select 
          onChange={(e) => {
            const selectedValue = e.target.value;
            if (selectedValue && !typeFilters.includes(selectedValue)) {
              setTypeFilters([...typeFilters, selectedValue]);
            }
            // Reset la valeur du select après sélection
            e.target.value = '';
          }}
          className="form-select w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary/20"
        >
          <option value="">Sélectionner un type...</option>
          {eventTypes.map(type => (
            !typeFilters.includes(type.value) && 
            <option key={type.value} value={type.value}>{type.label}</option>
          ))}
        </select>
      </div>
      
      {/* Badges des types sélectionnés */}
      <div className="mt-2 flex flex-wrap gap-2">
        {typeFilters.length === 0 ? (
          <span className="text-xs text-gray-500 italic">Tous les types</span>
        ) : (
          typeFilters.map(typeValue => {
            const type = eventTypes.find(t => t.value === typeValue);
            return (
              <div 
                key={typeValue} 
                className="px-2 py-1 rounded-full text-xs flex items-center text-white"
                style={{ backgroundColor: type ? type.color : '#888' }}
              >
                <span>{type ? type.label : typeValue}</span>
                <button 
                  onClick={() => setTypeFilters(typeFilters.filter(t => t !== typeValue))}
                  className="ml-1 text-white hover:text-red-200"
                >
                  <XMarkIcon className="h-3 w-3" />
                </button>
              </div>
            );
          })
        )}
        
        {typeFilters.length > 0 && (
          <button
            onClick={() => setTypeFilters([])}
            className="text-xs text-gray-500 hover:text-primary underline flex items-center"
          >
            Tout effacer
          </button>
        )}
      </div>
    </div>
    
    {/* Option de filtrage des événements - disponible pour tous les utilisateurs */}
    <div className="bg-gray-50 p-3 rounded-md flex items-center">
      <label className="flex items-center cursor-pointer w-full">
        <div className="relative">
          <input
            type="checkbox"
            checked={showAllEvents}
            onChange={(e) => setShowAllEvents(e.target.checked)}
            className="sr-only"
          />
          <div className={`block w-10 h-6 rounded-full transition-colors ${showAllEvents ? 'bg-primary' : 'bg-gray-300'}`}></div>
          <div className={`absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${showAllEvents ? 'transform translate-x-4' : ''}`}></div>
        </div>
        <div className="ml-3">
          <span className="text-sm font-medium text-gray-700">
            {showAllEvents ? "Afficher tous les événements" : "Mes événements uniquement"}
          </span>
          <p className="text-xs text-gray-500 mt-1">
            <UserGroupIcon className="h-3 w-3 inline mr-1" />
            {showAllEvents ? "Tous les événements" : "Événements créés et assignés"}
          </p>
        </div>
      </label>
    </div>
  </div>
  
  {/* Bouton d'annulation de suppression (affiché dans la barre d'outils) */}
  {showUndoDelete && (
    <div className="mt-4 bg-blue-100 border-l-4 border-blue-500 text-blue-700 p-3 rounded-md shadow-sm">
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <InformationCircleIcon className="h-5 w-5 mr-2" />
          <p>Événement supprimé</p>
        </div>
        <div className="flex items-center">
          <button 
            onClick={undoDeleteEvent} 
            className="px-3 py-1 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors mr-2"
          >
            Restaurer
          </button>
          <button 
            onClick={() => setShowUndoDelete(false)} 
            className="text-blue-700 hover:text-blue-900"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  )}
  
  {/* Résumé des filtres actifs */}
  <div className="mt-4 pt-3 border-t border-gray-200">
    <div className="flex items-center text-sm text-gray-600">
      <span className="font-medium mr-2">Filtres actifs:</span>
      {roomFilters.length === 0 && typeFilters.length === 0 ? (
        <span className="text-gray-500 italic">Aucun filtre appliqué</span>
      ) : (
        <div className="flex flex-wrap gap-2">
          {roomFilters.map(roomId => {
            const room = rooms.find(r => r.id.toString() === roomId);
            return (
              <div key={`summary-${roomId}`} className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full text-xs flex items-center">
                <MapPinIcon className="h-3 w-3 mr-1" />
                <span>{room ? room.name : roomId}</span>
              </div>
            );
          })}
          
          {typeFilters.map(typeValue => {
            const type = eventTypes.find(t => t.value === typeValue);
            return (
              <div 
                key={`summary-${typeValue}`} 
                className="px-2 py-0.5 rounded-full text-xs flex items-center text-white"
                style={{ backgroundColor: type ? type.color : '#888' }}
              >
                <ClipboardIcon className="h-3 w-3 mr-1" />
                <span>{type ? type.label : typeValue}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  </div>
</div>

       

      
      {/* Message d'annulation de suppression (affiché même quand le modal est fermé) */}
      {showUndoDelete && !showModal && (
        <div className="fixed bottom-4 right-4 bg-blue-100 border-l-4 border-blue-500 text-blue-700 p-4 rounded shadow-lg z-[70] max-w-md">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <InformationCircleIcon className="h-5 w-5 mr-2" />
              <p>Événement supprimé</p>
            </div>
            <div className="flex items-center">
              <button 
                onClick={undoDeleteEvent} 
                className="mr-3 text-blue-700 hover:text-blue-900 font-medium"
              >
                Annuler
              </button>
              <button 
                onClick={() => setShowUndoDelete(false)} 
                className="text-blue-700 hover:text-blue-900"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Calendrier */}
      <div className="p-4">
        <div className="calendar-container bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, rrulePlugin]}
            initialView={calendarView}
            headerToolbar={{
              left: 'prev,next today',
              center: 'title',
              right: 'dayGridMonth,timeGridWeek,timeGridDay'
            }}
            viewDidMount={(info) => {
              // Sauvegarder la vue actuelle dans le sessionStorage
              const currentView = info.view.type;
              setCalendarView(currentView);
              sessionStorage.setItem('calendarView', currentView);
            }}
            locale="fr"
            buttonText={{
              today: "Aujourd'hui",
              month: 'Mois',
              week: 'Semaine',
              day: 'Jour'
            }}
            firstDay={1}
            slotMinTime="07:00:00"
            slotMaxTime="23:59:00"
            allDaySlot={false}
            editable={user.isPermanent || user.role === 'administrateur'}
            selectable={user.isPermanent || user.role === 'administrateur'}
            selectMirror={true}
            moreLinkClick="popover"
            weekends={true}
            nowIndicator={true}
            views={{
              dayGridMonth: {
                dayMaxEvents: true, // Afficher tous les événements en vue mensuelle
                dayMaxEventRows: true
              }
            }}
            events={filteredEvents.map(event => ({
              ...event,
              // Les classes sont maintenant gérées par eventClassNames
            }))}
            eventContent={renderEventContent}
            eventClick={handleEventClick}
            dateClick={handleDateClick}
            select={user.isPermanent || user.role === 'administrateur' ? handleSelect : undefined}
            height="auto"
            eventTimeFormat={{
              hour: '2-digit',
              minute: '2-digit',
              meridiem: false,
              hour12: false
            }}
            slotLabelFormat={{
              hour: '2-digit',
              minute: '2-digit',
              meridiem: false,
              hour12: false
            }}
            dayMaxEventRows={false}
            eventDisplay="block"
            eventOrderStrict={true}
            eventOrder={function(a, b) {
              // Obtenir la vue actuelle du calendrier
              const currentView = calendarRef.current?.getApi().view.type;
              
              const aIsShow = (a.type === 'show') || (a.extendedProps && a.extendedProps.type === 'show');
              const bIsShow = (b.type === 'show') || (b.extendedProps && b.extendedProps.type === 'show');
              
              // En vue mois : ordre chronologique pour tous les événements
              if (currentView === 'dayGridMonth') {
                // Trier par heure de début puis par heure de fin
                let aStart = 0;
                let bStart = 0;
                
                if (a.start) {
                  aStart = typeof a.start.valueOf === 'function' ? a.start.valueOf() : new Date(a.start).getTime();
                }
                
                if (b.start) {
                  bStart = typeof b.start.valueOf === 'function' ? b.start.valueOf() : new Date(b.start).getTime();
                }
                
                if (aStart !== bStart) {
                  return aStart - bStart;
                }
                
                let aEnd = 0;
                let bEnd = 0;
                
                if (a.end) {
                  aEnd = typeof a.end.valueOf === 'function' ? a.end.valueOf() : new Date(a.end).getTime();
                }
                
                if (b.end) {
                  bEnd = typeof b.end.valueOf === 'function' ? b.end.valueOf() : new Date(b.end).getTime();
                }
                
                return aEnd - bEnd;
              }
              
              // En vue semaine/jour : priorité aux spectacles
              if (aIsShow && !bIsShow) {
                return 1; // a (spectacle) après b dans l'ordre de rendu = a au premier plan
              }
              if (!aIsShow && bIsShow) {
                return -1; // b (spectacle) après a dans l'ordre de rendu = b au premier plan
              }
              
              // Si les deux sont des spectacles ou aucun n'est un spectacle, trier normalement
              let aStart = 0;
              let bStart = 0;
              
              if (a.start) {
                aStart = typeof a.start.valueOf === 'function' ? a.start.valueOf() : new Date(a.start).getTime();
              }
              
              if (b.start) {
                bStart = typeof b.start.valueOf === 'function' ? b.start.valueOf() : new Date(b.start).getTime();
              }
              
              if (aStart !== bStart) {
                return aStart - bStart;
              }
              
              let aEnd = 0;
              let bEnd = 0;
              
              if (a.end) {
                aEnd = typeof a.end.valueOf === 'function' ? a.end.valueOf() : new Date(a.end).getTime();
              }
              
              if (b.end) {
                bEnd = typeof b.end.valueOf === 'function' ? b.end.valueOf() : new Date(b.end).getTime();
              }
              
              return aEnd - bEnd;
            }}
            eventOverlap={true}
            eventConstraint={false}
            selectOverlap={true}
             eventClassNames={(arg) => {
                 // Classes pour les événements de type 'show'
                 const showClass = arg.event.extendedProps.type === 'show' ? ['show-event-priority'] : [];
                 
                 // Classe pour les événements provisoires (tous types de spectacles)
                 const provisionalClass = 
                   arg.event.extendedProps.type === 'show' && 
                   arg.event.extendedProps.showStatus === 'provisional' 
                     ? ['provisional-event'] 
                     : [];
                 
                 // Combiner les classes
                 return [...showClass, ...provisionalClass];
               }}
               eventDidMount={info => {
                const { el, event } = info;
              
                if (event.extendedProps.type) {
                  el.setAttribute('data-event-type', event.extendedProps.type);
                }
              
                // Tooltip stylé pour les shows
                if (event.extendedProps.type === 'show') {
                  tippy(el, {
                    content: event.title,
                    appendTo: () => document.body,
                    placement: 'top-start',
                    delay: [300, 100],          // entrée + sortie plus douces
                    arrow: true,
                    maxWidth: '250px',          // largeur max fixe
                    allowHTML: false,
                    interactive: false,
                    animation: 'scale',         // légère montée en échelle
                    duration: [250, 200],       // 250ms in / 200ms out
                    className: [
                      'bg-white',               // fond blanc
                      'text-gray-900',          // texte sombre
                      'px-3', 'py-2',           // padding
                      'rounded-xl',             // bords très arrondis
                      'shadow-lg',              // grosse ombre
                      'border', 'border-gray-200', // bordure fine
                      'text-sm', 'font-medium'  // taille et graisse
                    ].join(' ')
                  });
                }
              }}
            />
        </div>
      </div>
      
      {/* Modal pour la création/édition/visualisation d'événements */}
      {showModal && (
      <div 
        key={newEvent.recurrenceId || newEvent.id}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 backdrop-blur-sm"
        onClick={(e) => {
          // Ne ferme pas le modal lors d'un clic à l'extérieur
          // Le modal ne peut être fermé que par le bouton X ou les actions spécifiques
          e.stopPropagation();
        }}
      >
        {/* Message d'annulation de suppression */}
        {showUndoDelete && (
          <div className="fixed top-4 left-1/2 transform -translate-x-1/2 bg-blue-100 border-l-4 border-blue-500 text-blue-700 p-4 rounded shadow-lg z-[70] max-w-md w-full">
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <InformationCircleIcon className="h-5 w-5 mr-2" />
                <p>Événement supprimé</p>
              </div>
              <div className="flex items-center">
                <button 
                  onClick={undoDeleteEvent} 
                  className="mr-3 text-blue-700 hover:text-blue-900 font-medium"
                >
                  Annuler
                </button>
                <button 
                  onClick={() => setShowUndoDelete(false)} 
                  className="text-blue-700 hover:text-blue-900"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Messages d'erreur au-dessus du modal */}
        {error && (
          <div className="fixed top-4 left-1/2 transform -translate-x-1/2 bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded shadow-lg z-[60] max-w-md w-full">
            <div className="flex justify-between items-center">
              <p className="font-bold">Erreur</p>
              <button 
                onClick={() => setError('')} 
                className="text-red-700 hover:text-red-900"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <p>{error}</p>
          </div>
        )}
        
        {success && (
          <div className="fixed top-4 left-1/2 transform -translate-x-1/2 bg-green-100 border-l-4 border-green-500 text-green-700 p-4 rounded shadow-lg z-[60] max-w-md w-full">
            <div className="flex justify-between items-center">
              <p className="font-bold">Succès</p>
              <button 
                onClick={() => setSuccess('')} 
                className="text-green-700 hover:text-green-900"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <p>{success}</p>
          </div>
        )}
        
        {/* Boîte de dialogue de confirmation de suppression de PDF - Ajout pour s'assurer qu'elle s'affiche en mode édition */}
        {showDeletePdfConfirm && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]" 
            onClick={(e) => {
              e.stopPropagation();
              // Ne rien faire d'autre pour empêcher la fermeture accidentelle
            }}
          >
            <div 
              className="bg-white rounded-lg p-6 max-w-sm w-full mx-4" 
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Confirmer la suppression
              </h3>
              <p className="text-gray-500 mb-6">
                Êtes-vous sûr de vouloir supprimer ce document ? Cette action est irréversible.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowDeletePdfConfirm(false);
                    setPdfToDelete(null);
                    // Ne pas fermer le modal principal
                  }}
                  className="px-4 py-2 text-gray-500 hover:text-gray-700 font-medium rounded-md hover:bg-gray-100 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleDeletePdf();
                    // Ne pas fermer le modal principal
                  }}
                  className="px-4 py-2 bg-red-600 text-white font-medium rounded-md hover:bg-red-700 transition-colors"
                >
                  Supprimer
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Modal de confirmation de suppression d'événement récurrent */}
        {showDeleteEventConfirm && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]" 
            onClick={(e) => {
              e.stopPropagation();
              // Ne rien faire d'autre pour empêcher la fermeture accidentelle
            }}
          >
            <div 
              className="bg-white rounded-lg p-6 max-w-md w-full mx-4" 
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Supprimer l'événement
              </h3>
              <p className="text-gray-500 mb-6">
                Cet événement fait partie d'une série récurrente. Que souhaitez-vous supprimer ?
              </p>
              
              <div className="space-y-4 mb-6">
                <div 
                  className={`p-4 border rounded-lg cursor-pointer transition-all ${deleteOption === 'single' ? 'border-primary bg-primary/10' : 'border-gray-300 hover:border-primary/50'}`}
                  onClick={() => setDeleteOption('single')}
                >
                  <div className="flex items-center">
                    <div className={`w-5 h-5 rounded-full border flex items-center justify-center ${deleteOption === 'single' ? 'border-primary' : 'border-gray-400'}`}>
                      {deleteOption === 'single' && <div className="w-3 h-3 rounded-full bg-primary"></div>}
                    </div>
                    <div className="ml-3">
                      <h4 className="font-medium">Cet événement uniquement</h4>
                      <p className="text-sm text-gray-600">Supprimer seulement cette occurrence de l'événement</p>
                    </div>
                  </div>
                </div>
                
                <div 
                  className={`p-4 border rounded-lg cursor-pointer transition-all ${deleteOption === 'series' ? 'border-primary bg-primary/10' : 'border-gray-300 hover:border-primary/50'}`}
                  onClick={() => setDeleteOption('series')}
                >
                  <div className="flex items-center">
                    <div className={`w-5 h-5 rounded-full border flex items-center justify-center ${deleteOption === 'series' ? 'border-primary' : 'border-gray-400'}`}>
                      {deleteOption === 'series' && <div className="w-3 h-3 rounded-full bg-primary"></div>}
                    </div>
                    <div className="ml-3">
                      <h4 className="font-medium">Toute la série</h4>
                      <p className="text-sm text-gray-600">Supprimer cet événement et toutes ses occurrences</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowDeleteEventConfirm(false);
                    setEventToDelete(null);
                  }}
                  className="px-4 py-2 text-gray-500 hover:text-gray-700 font-medium rounded-md hover:bg-gray-100 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    deleteEvent(eventToDelete.id, deleteOption);
                  }}
                  className="px-4 py-2 bg-red-600 text-white font-medium rounded-md hover:bg-red-700 transition-colors"
                >
                  Supprimer
                </button>
              </div>
            </div>
          </div>
        )}
        <div 
          className="bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-gray-200"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Modal header */}
          <div className="bg-gradient-to-r from-primary to-secondary text-white px-6 py-4 flex justify-between items-center rounded-t-lg">
            <h3 className="text-xl font-semibold flex items-center">
              {modalType === 'view' ? (
                <span className="flex items-center">
                  <span className="mr-2">📅</span>
                  {currentEvent?.title}
                </span>
              ) : modalType === 'edit' ? (
                <span className="flex items-center">
                  <PencilIcon className="h-5 w-5 mr-2" />
                  Modifier l'événement
                </span>
              ) : (
                <span className="flex items-center">
                  <PlusIcon className="h-5 w-5 mr-2" />
                  {user.role === 'administrateur' ? 'Créer un événement' : user.isPermanent ? 'Créer un spectacle' : 'indiquer mon indisponibilité'}
                </span>
              )}
            </h3>
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (!showDeletePdfConfirm) {
                  // Sauvegarder le brouillon si l'événement a un titre
                  if (newEvent.title && newEvent.title.trim() && modalType !== 'view') {
                    saveEventDraft();
                  }
                  setShowModal(false);
                }
              }}
              className="text-white hover:text-gold transition-colors"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        
        {/* Modal content - Design amélioré */}
        <div className="p-6 ">
          {/* Onglets du modal avec design amélioré */}
          <div className="flex border-b mb-4 relative">
            {/* Indicateur de l'onglet actif avec animation */}
            <div 
              className="absolute bottom-0 h-0.5 bg-primary transition-all duration-300 ease-in-out"
              style={{
                left: (() => {
                  // Calculer la position en fonction des onglets visibles
                  const hasUsersTab = user.role === 'regie' || user.isPermanent || user.role === 'administrateur' || user.role === 'artiste' || (user.role === 'artistesProd' && modalType === 'view');
                  const hasDocumentsTab = user.role === 'regie' || user.isPermanent || user.role === 'administrateur';
                  
                  if (activeModalTab === 'general') return '0%';
                  if (activeModalTab === 'users' && hasUsersTab) {
                    return hasDocumentsTab ? '33.33%' : '50%';
                  }
                  if (activeModalTab === 'documents' && hasDocumentsTab) {
                    return hasUsersTab ? '66.66%' : '50%';
                  }
                  return '0%';
                })(),
                width: (() => {
                  // Calculer la largeur en fonction du nombre d'onglets visibles
                  const hasUsersTab = user.role === 'regie' || user.isPermanent || user.role === 'administrateur' || user.role === 'artiste' || (user.role === 'artistesProd' && modalType === 'view');
                  const hasDocumentsTab = user.role === 'regie' || user.isPermanent || user.role === 'administrateur';
                  
                  const tabCount = 1 + (hasUsersTab ? 1 : 0) + (hasDocumentsTab ? 1 : 0);
                  return `${100 / tabCount}%`;
                })(),
                transform: 'translateX(0)',
                opacity: 1
              }}
            ></div>
            
            <button
              className={`flex-1 px-4 py-3 font-medium transition-all duration-200 ${activeModalTab === 'general' ? 'text-primary' : 'text-gray-500 hover:text-gray-700'}`}
              onClick={() => setActiveModalTab('general')}
            >
              <span className="flex items-center justify-center">
                <ClipboardIcon className={`h-4 w-4 mr-1.5 transition-all ${activeModalTab === 'general' ? 'text-primary' : 'text-gray-400'}`} />
                <span className={`transition-all ${activeModalTab === 'general' ? 'font-semibold' : ''}`}>Général</span>
              </span>
            </button>
            
            {/* Onglet Utilisateurs - visible pour régie, permanents, administrateurs, artistes et artistesProd (uniquement en mode view) */}
            {(user.role === 'regie' || user.isPermanent || user.role === 'administrateur' || user.role === 'artiste' || (user.role === 'artistesProd' && modalType === 'view')) && (
            <button
              className={`flex-1 px-4 py-3 font-medium transition-all duration-200 ${activeModalTab === 'users' ? 'text-primary' : 'text-gray-500 hover:text-gray-700'}`}
              onClick={() => setActiveModalTab('users')}
            >
              <span className="flex items-center justify-center">
                <UserGroupIcon className={`h-4 w-4 mr-1.5 transition-all ${activeModalTab === 'users' ? 'text-primary' : 'text-gray-400'}`} />
                 <span className={`transition-all ${activeModalTab === 'users' ? 'font-semibold' : ''}`}>Utilisateurs</span>
                 {modalType === 'view' && currentEvent?.extendedProps?.users && currentEvent.extendedProps.users.length > 0 && (
                   <span className="ml-2 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-red-100 bg-red-600 rounded-full">
                     {currentEvent.extendedProps.users.length}
                   </span>
                 )}
                 {modalType !== 'view' && newEvent.assignedUsers.length > 0 && (
                  <span className="ml-1.5 bg-primary text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {newEvent.assignedUsers.length}
                  </span>
                )}
              </span>
            </button>
            )}
            
            {/* Onglet Documents - visible pour régie, permanents et administrateurs (pas pour artistes) */}
            {(user.role === 'regie' || user.isPermanent || user.role === 'administrateur') && (
              <button
                className={`flex-1 px-4 py-3 font-medium transition-all duration-200 ${activeModalTab === 'documents' ? 'text-primary' : 'text-gray-500 hover:text-gray-700'}`}
                onClick={() => {
                  setActiveModalTab('documents');
                  if (currentEvent) {
                    fetchEventPdfs(currentEvent.id);
                  }
                }}
              >
                <span className="flex items-center justify-center">
                  <DocumentTextIcon className={`h-4 w-4 mr-1.5 transition-all ${activeModalTab === 'documents' ? 'text-primary' : 'text-gray-400'}`} />
                  <span className={`transition-all ${activeModalTab === 'documents' ? 'font-semibold' : ''}`}>Documents</span>
                  {modalType === 'view' && eventPdfs.length > 0 && (
                    <span className="ml-1.5 bg-primary text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      {eventPdfs.length}
                    </span>
                  )}
                </span>
              </button>
            )}
          </div>
          {modalType === 'view' ? (
            // View mode - Design amélioré
            <div>

              {/* Information sur le créateur - Design amélioré et plus discret */}
              <div className="mb-4 flex flex-col space-y-1">
                {currentEvent?.extendedProps?.creator && (
                  <div className="inline-flex items-center text-xs text-gray-500 italic">
                    <UserIcon className="h-3 w-3 mr-1 text-primary/70" />
                    Créé par <span className="font-medium ml-1 text-primary/80">{currentEvent.extendedProps.creator.firstName} {currentEvent.extendedProps.creator.lastName}</span>
                    {currentEvent?.extendedProps?.createdAt && (
                      <span className="ml-1">le <span className="font-medium text-primary/80">{formatDate(currentEvent.extendedProps.createdAt)}</span></span>
                    )}
                  </div>
                )}
                
                {currentEvent?.extendedProps?.lastModifier && (
                  <div className="inline-flex items-center text-xs text-gray-500 italic">
                    <PencilIcon className="h-3 w-3 mr-1 text-primary/70" />
                    Dernière modification par <span className="font-medium ml-1 text-primary/80">{currentEvent.extendedProps.lastModifier.firstName} {currentEvent.extendedProps.lastModifier.lastName}</span>
                    {currentEvent?.extendedProps?.updatedAt && (
                      <span className="ml-1">le <span className="font-medium text-primary/80">{formatDate(currentEvent.extendedProps.updatedAt)}</span></span>
                    )}
                  </div>
                )}
              </div>
              
              
              {/* Contenu des onglets avec animation de transition */}
              <div className="relative overflow-y-auto min-h-[450px]">
                
                {/* Onglet Général */}
                <div 
                  className={`transition-all duration-300 absolute w-full ${activeModalTab === 'general' ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-full pointer-events-none'}`}
                  style={{ transform: activeModalTab === 'general' ? 'translateX(0)' : 'translateX(100px)' }}
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div className="flex items-start bg-gray-50 p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                      <ClockIcon className="h-6 w-6 text-primary mr-3 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-gray-500">Début</p>
                        <p className="font-medium">{formatDate(currentEvent?.start)}</p>
                      </div>
                    </div>
                    
                    
                    <div className="flex items-start bg-gray-50 p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                      <ClockIcon className="h-6 w-6 text-primary mr-3 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-gray-500">Fin</p>
                        <p className="font-medium">{formatDate(currentEvent?.end)}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start bg-gray-50 p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                      <MapPinIcon className="h-6 w-6 text-primary mr-3 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-gray-500">Salle</p>
                        <p className="font-medium">
                          {
                            // Pour les événements d'indisponibilité, afficher toutes les salles sélectionnées
                            currentEvent?.extendedProps?.type === 'non-disponible'
                              ? (
                                  (currentEvent?.extendedProps?.rooms && currentEvent.extendedProps.rooms.length > 0)
                                    ? currentEvent.extendedProps.rooms.map(r => r.name).join(', ')
                                    : 'Non spécifiée'
                                )
                              : (currentEvent?.extendedProps?.room?.name || 'Non spécifiée')
                          }
                        </p>
                      </div>
                    </div>


                    {currentEvent?.extendedProps?.address && currentEvent.extendedProps.address.trim() !== '' && (
                      <div className="flex items-start bg-gray-50 p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                        <HomeIcon className="h-6 w-6 text-primary mr-3 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-gray-500">Adresse</p>
                          <p className="font-medium">
                            {currentEvent.extendedProps.address}
                          </p>
                        </div>
                      </div>
                    )}

                    
                    <div className="flex items-start bg-gray-50 p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                      <div className="h-6 w-6 rounded-full mr-3 mt-0.5 flex-shrink-0" style={{ backgroundColor: currentEvent?.color }}></div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">Type</p>
                        <p className="font-medium">{getEventTypeLabel(currentEvent?.extendedProps?.type)}</p>
                      </div>
                    </div>
                    
                    {/* Affichage du type de personnel */}
                    {currentEvent?.extendedProps?.type === 'personnel' && currentEvent?.extendedProps?.personnelType && (
                      <div className="flex items-start bg-gray-50 p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                        <div className="h-6 w-6 rounded-full mr-3 mt-0.5 flex-shrink-0" style={{ backgroundColor: '#000000' }}></div>
                        <div>
                          <p className="text-sm font-medium text-gray-500">Type de personnel</p>
                          <p className="font-medium">
                            {personnelTypeOptions.find(option => option.value === currentEvent.extendedProps.personnelType)?.label || currentEvent.extendedProps.personnelType}
                          </p>
                        </div>
                      </div>
                    )}
                    
                    {/* Affichage du statut pour les spectacles */}
                    {currentEvent?.extendedProps?.type === 'show' && (
                      <div className="flex items-start bg-gray-50 p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                        <div 
                          className="h-6 w-6 rounded-full mr-3 mt-0.5 flex-shrink-0" 
                          style={{ 
                            backgroundColor: currentEvent?.extendedProps?.showStatus === 'cancelled' 
                              ? showStatusOptions.find(s => s.value === 'cancelled')?.color 
                              : getShowColor(currentEvent?.extendedProps?.showType || 'production', currentEvent?.extendedProps?.showStatus),
                            border: currentEvent?.extendedProps?.showStatus === 'provisional' ? '1px solid #000000' : 'none'
                          }}
                        ></div>
                        <div>
                          <p className="text-sm font-medium text-gray-500">Statut</p>
                          <p 
                            className="font-medium" 
                            style={{ 
                              color: currentEvent?.extendedProps?.showStatus === 'provisional' ? '#000000' : 'inherit'
                            }}
                          >
                            {showStatusOptions.find(s => s.value === currentEvent?.extendedProps?.showStatus)?.label || 'Non spécifié'}
                          </p>
                        </div>
                      </div>
                    )}
                    {/* Contact Production */}
                      {currentEvent?.extendedProps?.type === 'show' && currentEvent?.extendedProps?.productionContact && (
                      <div className="flex items-start bg-gray-50 p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                        <UserIcon className="h-6 w-6 text-primary mr-3 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-gray-500">Contact Production</p>
                          <p className="font-medium">{currentEvent?.extendedProps?.productionContact}</p>
                        </div>
                      </div>
                    )}

                    {/* Contact Régisseur - affiché seulement pour les tournées */}
                      {currentEvent?.extendedProps?.type === 'show' && 
                       currentEvent?.extendedProps?.room?.name === 'Tournée' && 
                       currentEvent?.extendedProps?.stageManagerContact && (
                      <div className="flex items-start bg-gray-50 p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                        <UserIcon className="h-6 w-6 text-primary mr-3 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-gray-500">Contact Régisseur</p>
                          <p className="font-medium">{currentEvent?.extendedProps?.stageManagerContact}</p>
                        </div>
                      </div>
                    )}

                    
                    
                    {/* Informations complémentaires */}
                    {currentEvent?.extendedProps?.type === 'show' && (
                      <div className="flex items-start bg-gray-50 p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                        <InformationCircleIcon className="h-6 w-6 text-primary mr-3 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-gray-500">Informations complémentaires</p>
                          <div className="font-medium space-y-1 mt-1">
                            <p>Flyer commandé: {currentEvent?.extendedProps?.flyersOrdered ? 'Oui' : 'Non'}</p>
                            <p>Hôtel: {currentEvent?.extendedProps?.hotelOrdered ? 'Oui' : 'Non'}</p>
                            <p>Appartement: {currentEvent?.extendedProps?.apartmentOrdered ? 'Oui' : 'Non'}</p>
                            <p>Bande son: {currentEvent?.extendedProps?.hasSoundTrack ? 'Oui' : 'Non'}</p>
                            <p>Transfert: {currentEvent?.extendedProps?.transferOrdered ? 'Oui' : 'Non'}</p>
                            <p>DECLA SACD: {currentEvent?.extendedProps?.sacdDeclared ? 'Oui' : 'Non'}</p>
                            <p>DECLA SACEM: {currentEvent?.extendedProps?.sacemDeclared ? 'Oui' : 'Non'}</p>
                         
                            {currentEvent?.extendedProps?.soundTrackDetails && (
                              <p>Détails bande son: {currentEvent?.extendedProps?.soundTrackDetails}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      {/* --- Carte VHR --- */}
                      {currentEvent?.extendedProps?.type === 'show'
                        && currentEvent.extendedProps.expectedAttendees > 0 && (
                        <div className="flex items-start bg-gray-50 p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                          <UserGroupIcon className="h-6 w-6 text-primary mr-3 mt-0.5" />
                          <div>
                            <p className="text-sm font-medium text-gray-700">
                              Nombre VHR : {currentEvent.extendedProps.expectedAttendees}
                            </p>
                          </div>
                        </div>
                      )}

                      {/* --- Carte Jauge --- */}
                      {currentEvent?.extendedProps?.type === 'show'
                        && currentEvent.extendedProps.jauge > 0 && (
                        <div className="flex items-start bg-gray-50 p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                          {/* vous pouvez remplacer ChartBarIcon par l’icône de votre choix */}
                          <ChartBarIcon className="h-6 w-6 text-primary mr-3 mt-0.5" />
                          <div>
                            <p className="text-sm font-medium text-gray-700">
                              Jauge : {currentEvent.extendedProps.jauge}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>


                    {/* Pourcentage de co-réalisation - affiché seulement si non nul */}
                    {currentEvent?.extendedProps?.type === 'show' && 
                     currentEvent?.extendedProps?.coRealizationPercentage !== undefined && 
                     currentEvent?.extendedProps?.coRealizationPercentage !== 0 && (
                      <div className="flex items-start bg-gray-50 p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                        <CurrencyDollarIcon className="h-6 w-6 text-primary mr-3 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-gray-700">
                            Pourcentage PROD de co-réalisation : {currentEvent.extendedProps.coRealizationPercentage} %
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Lieu de vente des billets - affiché seulement si non nul et si statut est ticketsOpen */}
                    {currentEvent?.extendedProps?.type === 'show' && 
                     currentEvent?.extendedProps?.showStatus === 'ticketsOpen' && 
                     currentEvent?.extendedProps?.ticketingLocation && 
                     currentEvent?.extendedProps?.ticketingLocation.trim() !== '' && (
                      <div className="flex items-start bg-gray-50 p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                        <TicketIcon className="h-6 w-6 text-primary mr-3 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-gray-700">
                            Lieu de vente des billets : {currentEvent.extendedProps.ticketingLocation}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Détails utilisateurs - regroupés dans une seule case */}
                    {currentEvent?.extendedProps?.type === 'show' && 
                     currentEvent?.extendedProps?.users && 
                     currentEvent.extendedProps.users.some(user => 
                       user.details?.arrivalTime || 
                       (user.details?.arrivalLocation && user.details.arrivalLocation.trim() !== '') ||
                       (user.details?.arrivalTransfer && user.details.arrivalTransfer.trim() !== '') ||
                       user.details?.departureTime || 
                       (user.details?.departureLocation && user.details.departureLocation.trim() !== '') ||
                       (user.details?.departureTransfer && user.details.departureTransfer.trim() !== '') ||
                       (user.details?.accommodation && user.details.accommodation.trim() !== '')
                     ) && (
                      <div className="flex items-start bg-gray-50 p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                        <UserGroupIcon className="h-6 w-6 text-primary mr-3 mt-0.5" />
                        <div className="w-full">
                          <h4 className="text-sm font-medium text-gray-700 mb-2">Détails utilisateurs</h4>
                          <div className="space-y-2">
                            {currentEvent.extendedProps.users.some(user => user.details?.accommodation && user.details.accommodation.trim() !== '') && (
                              <div>
                                <p className="text-sm text-gray-700">
                                  <span className="font-medium">Logement :</span> {currentEvent.extendedProps.users
                                    .filter(user => user.details?.accommodation && user.details.accommodation.trim() !== '')
                                    .map(user => `${user.firstName} ${user.lastName}: ${user.details.accommodation}`)
                                    .join(', ')}
                                </p>
                              </div>
                            )}
                            
                            {currentEvent.extendedProps.users.some(user => user.details?.arrivalTime) && (
                              <div>
                                <p className="text-sm text-gray-700">
                                  <span className="font-medium">Heure arrivée :</span> {currentEvent.extendedProps.users
                                    .filter(user => user.details?.arrivalTime)
                                    .map(user => `${user.firstName} ${user.lastName}: ${new Date(user.details.arrivalTime).toLocaleString('fr-FR')}`)
                                    .join(', ')}
                                </p>
                              </div>
                            )}
                            
                            {currentEvent.extendedProps.users.some(user => user.details?.arrivalLocation && user.details.arrivalLocation.trim() !== '') && (
                              <div>
                                <p className="text-sm text-gray-700">
                                  <span className="font-medium">Lieu d'arrivée :</span> {currentEvent.extendedProps.users
                                    .filter(user => user.details?.arrivalLocation && user.details.arrivalLocation.trim() !== '')
                                    .map(user => `${user.firstName} ${user.lastName}: ${user.details.arrivalLocation}`)
                                    .join(', ')}
                                </p>
                              </div>
                            )}
                            
                            {currentEvent.extendedProps.users.some(user => user.details?.arrivalTransfer && user.details.arrivalTransfer.trim() !== '') && (
                              <div>
                                <p className="text-sm text-gray-700">
                                  <span className="font-medium">Transfert arrivée :</span> {currentEvent.extendedProps.users
                                    .filter(user => user.details?.arrivalTransfer && user.details.arrivalTransfer.trim() !== '')
                                    .map(user => `${user.firstName} ${user.lastName}: ${user.details.arrivalTransfer}`)
                                    .join(', ')}
                                </p>
                              </div>
                            )}
                            
                            {currentEvent.extendedProps.users.some(user => user.details?.departureTime) && (
                              <div>
                                <p className="text-sm text-gray-700">
                                  <span className="font-medium">Heure départ :</span> {currentEvent.extendedProps.users
                                    .filter(user => user.details?.departureTime)
                                    .map(user => `${user.firstName} ${user.lastName}: ${new Date(user.details.departureTime).toLocaleString('fr-FR')}`)
                                    .join(', ')}
                                </p>
                              </div>
                            )}
                            
                            {currentEvent.extendedProps.users.some(user => user.details?.departureLocation && user.details.departureLocation.trim() !== '') && (
                              <div>
                                <p className="text-sm text-gray-700">
                                  <span className="font-medium">Lieu de départ :</span> {currentEvent.extendedProps.users
                                    .filter(user => user.details?.departureLocation && user.details.departureLocation.trim() !== '')
                                    .map(user => `${user.firstName} ${user.lastName}: ${user.details.departureLocation}`)
                                    .join(', ')}
                                </p>
                              </div>
                            )}
                            
                            {currentEvent.extendedProps.users.some(user => user.details?.departureTransfer && user.details.departureTransfer.trim() !== '') && (
                              <div>
                                <p className="text-sm text-gray-700">
                                  <span className="font-medium">Transfert départ :</span> {currentEvent.extendedProps.users
                                    .filter(user => user.details?.departureTransfer && user.details.departureTransfer.trim() !== '')
                                    .map(user => `${user.firstName} ${user.lastName}: ${user.details.departureTransfer}`)
                                    .join(', ')}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* TVA billetterie - affichée seulement si > 0 */}
                    {currentEvent.extendedProps.vatPercentage > 0 && (
                      <div className="flex items-start bg-gray-50 p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                        <ReceiptPercentIcon className="h-6 w-6 text-primary mr-3 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-gray-700">
                            TVA : {currentEvent.extendedProps.vatPercentage} %
                          </p>
                        </div>
                      </div>
                    )}
                  
                  </div>

                  
                  {currentEvent?.extendedProps?.type === 'show' && currentEvent?.extendedProps?.hasDecor && (
                    <div className="mb-6 bg-gray-50 p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                      <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                        <div className="h-4 w-4 rounded-full mr-2 bg-primary"></div>
                        Décor
                      </h4>
                      <p className="text-sm text-gray-600 whitespace-pre-line">
                        {currentEvent.extendedProps.decorDetails || "Aucun détail fourni"}
                      </p>
                    </div>
                  )}
                  
                  {currentEvent?.extendedProps?.description && (
                    <div className="mb-6 bg-gray-50 p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                      <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                        <ClipboardIcon className="h-5 w-5 mr-2 text-primary" />
                        Description
                      </h4>
                      <p className="text-gray-600 whitespace-pre-line">{currentEvent?.extendedProps?.description}</p>
                    </div>
                  )}

                
                  
                  {/* Show status buttons for shows - Design amélioré */}
                  {currentEvent?.extendedProps?.type === 'show' && (user.role === 'administrateur' || user.isPermanent) && (
                    <div className="mt-8 border-t pt-6">
                      <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                        <div className="h-4 w-4 rounded-full mr-2 bg-primary"></div>
                        Changer le statut du spectacle
                      </h4>
                      <div className="flex flex-wrap gap-3">
                        {showStatusOptions.map(status => (
                          <button
                            key={status.value}
                            onClick={() => handleConfirmShow(status.value)}
                            className={`px-4 py-2 rounded-md text-sm flex items-center shadow-sm hover:shadow transition-shadow ${
                              currentEvent?.extendedProps?.showStatus === status.value 
                                ? 'ring-2 ring-offset-2 ring-white' 
                                : 'hover:opacity-90'
                            }`}
                            style={{ 
                              backgroundColor: status.value === 'cancelled' 
                                ? status.color 
                                : getShowColor(currentEvent?.extendedProps?.showType || 'production', status.value),
                              color: status.value === 'provisional' ? '#000000' : 'white',
                              border: status.value === 'provisional' ? '1px solid #000000' : 'none'
                            }}
                            disabled={currentEvent?.extendedProps?.showStatus === status.value}
                          >
                            {currentEvent?.extendedProps?.showStatus === status.value && (
                              <CheckIcon className="h-4 w-4 mr-1.5" />
                            )}
                            {status.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Rental status buttons - Design similaire aux boutons de statut de spectacle */}
                  {currentEvent?.extendedProps?.type === 'rental' && user.role === 'administrateur' && (
                    <div className="mt-8 border-t pt-6">
                      <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                        <div className="h-4 w-4 rounded-full mr-2 bg-primary"></div>
                        Changer le statut de la location
                      </h4>
                      <div className="flex flex-wrap gap-3">
                        {rentalStatusOptions.map(status => (
                          <button
                            key={status.value}
                            onClick={() => handleConfirmRental(status.value)}
                            className={`px-4 py-2 rounded-md text-sm flex items-center shadow-sm hover:shadow transition-shadow ${
                              currentEvent?.extendedProps?.rentalStatus === status.value 
                                ? 'ring-2 ring-offset-2 ring-white' 
                                : 'hover:opacity-90'
                            }`}
                            style={{ 
                              backgroundColor: getRentalColor(status.value),
                              color: status.value === 'provisional' ? '#000000' : 'black',
                              border: status.value === 'provisional' ? '1px solid #000000' : 'none'
                            }}
                            disabled={currentEvent?.extendedProps?.rentalStatus === status.value}
                          >
                            {currentEvent?.extendedProps?.rentalStatus === status.value && (
                              <CheckIcon className="h-4 w-4 mr-1.5" />
                            )}
                            {status.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Onglet Utilisateurs */}
                <div 
                  className={`transition-all duration-300 absolute w-full ${activeModalTab === 'users' ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-full pointer-events-none'}`}
                  style={{ transform: activeModalTab === 'users' ? 'translateX(0)' : activeModalTab === 'general' ? 'translateX(100px)' : 'translateX(-100px)' }}
                >
                  {currentEvent?.extendedProps?.users && currentEvent.extendedProps.users.length > 0 ? (
                    <div className="space-y-6">
                      <h3 className="text-lg font-medium text-primary mb-4 flex items-center">
                        <UserGroupIcon className="h-5 w-5 mr-2" />
                        Utilisateurs assignés à cet événement
                      </h3>
                      
                      {/* Affichage détaillé similaire au modal edit mais en lecture seule */}
                      <div className="space-y-6">
                        {currentEvent.extendedProps.users.map(eventUser => {
                          // Récupérer les détails depuis la structure correcte
                          const details = eventUser.details || eventUser._through || {};
                          const hasDetails = details.accommodation || details.additionalFee || 
                                            details.arrivalLocation || details.arrivalTime ||
                                            details.departureLocation || details.departureTime ||
                                            details.arrivalTransfer || details.departureTransfer ||
                                            details.notes;
                          
                          console.log('Détails utilisateur dans view modal:', eventUser.id, details);
                          
                          return (
                            <div key={eventUser.id} className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200">
                              <h4 className="font-medium mb-3 pb-2 border-b text-primary flex items-center">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" viewBox="0 0 20 20" fill="currentColor">
                                  <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
                                </svg>
                                {eventUser.firstName} {eventUser.lastName}
                                <span className="ml-2 text-sm font-normal text-gray-500">({eventUser.role})</span>
                              </h4>
                              
                              {!hasDetails && (
                                <p className="text-sm text-gray-500 italic">Aucun détail spécifique pour cet utilisateur</p>
                              )}
                              
                              {hasDetails && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                  {/* Logement */}
                                  {details.accommodation && (
                                    <div className="flex items-start">
                                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-primary flex-shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                                        <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
                                      </svg>
                                      <div>
                                        <span className="font-medium block text-gray-700">Logement</span>
                                        <span className="text-gray-600">{details.accommodation}</span>
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* Supplément */}
                                  {details.additionalFee && (
                                    <div className="flex items-start">
                                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-primary flex-shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                                        <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
                                      </svg>
                                      <div>
                                        <span className="font-medium block text-gray-700">Supplément</span>
                                        <span className="text-gray-600">{details.additionalFee}€</span>
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* Lieu d'arrivée */}
                                  {details.arrivalLocation && (
                                    <div className="flex items-start">
                                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-primary flex-shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                                      </svg>
                                      <div>
                                        <span className="font-medium block text-gray-700">Lieu d'arrivée</span>
                                        <span className="text-gray-600">{details.arrivalLocation}</span>
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* Transfert arrivée */}
                                  {details.arrivalTransfer && (
                                    <div className="flex items-start">
                                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-primary flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                                      </svg>
                                      <div>
                                        <span className="font-medium block text-gray-700">Transfert arrivée</span>
                                        <span className="text-gray-600">{details.arrivalTransfer}</span>
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* Heure d'arrivée */}
                                  {details.arrivalTime && (
                                    <div className="flex items-start">
                                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-primary flex-shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                                      </svg>
                                      <div>
                                        <span className="font-medium block text-gray-700">Heure d'arrivée</span>
                                        <span className="text-gray-600">{new Date(details.arrivalTime).toLocaleString('fr-FR')}</span>
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* Lieu de départ */}
                                  {details.departureLocation && (
                                    <div className="flex items-start">
                                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-primary flex-shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                                      </svg>
                                      <div>
                                        <span className="font-medium block text-gray-700">Lieu de départ</span>
                                        <span className="text-gray-600">{details.departureLocation}</span>
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* Transfert départ */}
                                  {details.departureTransfer && (
                                    <div className="flex items-start">
                                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-primary flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                                      </svg>
                                      <div>
                                        <span className="font-medium block text-gray-700">Transfert départ</span>
                                        <span className="text-gray-600">{details.departureTransfer}</span>
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* Heure de départ */}
                                  {details.departureTime && (
                                    <div className="flex items-start">
                                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-primary flex-shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                                      </svg>
                                      <div>
                                        <span className="font-medium block text-gray-700">Heure de départ</span>
                                        <span className="text-gray-600">{new Date(details.departureTime).toLocaleString('fr-FR')}</span>
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* Notes */}
                                  {details.notes && (
                                    <div className="md:col-span-2 flex items-start mt-3 pt-3 border-t">
                                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-primary flex-shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M18 13V5a2 2 0 00-2-2H4a2 2 0 00-2 2v8a2 2 0 002 2h3l3 3 3-3h3a2 2 0 002-2zM5 7a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1zm1 3a1 1 0 100 2h3a1 1 0 100-2H6z" clipRule="evenodd" />
                                      </svg>
                                      <div>
                                        <span className="font-medium block text-gray-700">Notes</span>
                                        <span className="text-gray-600 whitespace-pre-line">{details.notes}</span>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-10 bg-gray-50 rounded-lg">
                      <UserGroupIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-500 mb-1">Aucun utilisateur assigné</h3>
                      <p className="text-gray-400">Cet événement n'a pas d'utilisateurs assignés.</p>
                    </div>
                  )}
                </div>
                
                {/* Onglet Documents */}
                <div 
                  className={`transition-all duration-300 absolute w-full ${activeModalTab === 'documents' ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-full pointer-events-none'}`}
                  style={{ transform: activeModalTab === 'documents' ? 'translateX(0)' : 'translateX(-100px)' }}
                >
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-medium text-primary flex items-center">
                      <DocumentTextIcon className="h-5 w-5 mr-2" />
                      Documents associés
                    </h3>
                    
                  {/* Bouton d'upload - visible uniquement en mode add/edit et pas pour les utilisateurs régie */}
                  {modalType !== 'view' && user.role !== 'regie' && (
                    <label className="cursor-pointer bg-primary hover:bg-primary/90 text-white px-4 py-2 rounded-md flex items-center shadow-sm transition-colors">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      Ajouter un PDF
                      <input
                        type="file"
                        accept=".pdf"
                        className="hidden"
                        onChange={handlePdfChange}
                        multiple
                      />
                    </label>
                  )}
                  </div>
                  
                  {/* Zone de drag & drop pour l'upload de PDF - visible uniquement en mode add/edit et pas pour les utilisateurs régie */}
                  {modalType !== 'view' && user.role !== 'regie' && (
                    <div 
                      className={`border-2 ${isDragging ? 'border-primary bg-primary/10' : 'border-dashed border-gray-300'} rounded-lg p-6 mb-4 text-center transition-colors`}
                      onDragOver={(e) => handleDragOver(e)}
                      onDragLeave={(e) => handleDragLeave(e)}
                      onDrop={(e) => handleDrop(e)}
                    >
                      <div className="flex flex-col items-center justify-center space-y-2">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">Glissez et déposez</span> vos fichiers PDF ici ou
                        </p>
                        <label className="cursor-pointer text-primary hover:text-primary/80 font-medium">
                          parcourir vos fichiers
                          <input 
                            type="file" 
                            accept=".pdf" 
                            className="hidden" 
                            onChange={handlePdfChange} 
                            multiple 
                          />
                        </label>
                        <p className="text-xs text-gray-500">PDF uniquement (max. 20 Mo par fichier, 10 fichiers max)</p>
                      </div>
                    </div>
                  )}
                  
                  {/* Sélection de la catégorie de PDF - visible uniquement en mode add/edit et pas pour les utilisateurs régie */}
                  {modalType !== 'view' && user.role !== 'regie' && (
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Catégorie du document</label>
                      <select
                        value={selectedPdfCategory}
                        onChange={(e) => setSelectedPdfCategory(e.target.value)}
                        className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary"
                      >
                        {pdfCategories.map(category => (
                          <option key={category.id} value={category.id}>
                            {category.label} {!category.multiple ? '(1 fichier max)' : '(plusieurs fichiers)'}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  
                  {/* Affichage des fichiers sélectionnés pour upload - pas pour les utilisateurs régie */}
                  {pdfFiles.length > 0 && user.role !== 'regie' && (
                    <div className="mb-4">
                      <div className="flex justify-between items-center mb-2">
                        <h4 className="text-sm font-medium text-gray-700">Fichiers à télécharger</h4>
                        <button
                          type="button"                            // ← empêche le submit
                          onClick={(e) => {
                            e.preventDefault();                  // ← sécurité supplémentaire
                            setPdfFiles([]);
                            setPdfPreviews([]);
                          }}
                          className="text-sm text-red-600 hover:text-red-800"
                        >
                          Effacer la sélection
                        </button>
                      </div>
                      
                      <div className="grid grid-cols-1 gap-2 mb-3">
                        {pdfFiles.map((file, index) => (
                          <div key={index} className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg p-3">
                            <div className="flex items-center">
                              <DocumentTextIcon className="h-5 w-5 text-blue-500 mr-2" />
                              <span className="text-sm text-gray-700 truncate max-w-xs">{file.name}</span>
                            </div>
                             <button
                               type="button"                           // ← empêche le submit
                               onClick={(e) => {
                                 e.preventDefault();                   // ← sécurité supplémentaire
                                 const newFiles = [...pdfFiles];
                                 newFiles.splice(index, 1);
                                 setPdfFiles(newFiles);

                                 const newPreviews = [...pdfPreviews];
                                 newPreviews.splice(index, 1);
                                 setPdfPreviews(newPreviews);
                               }}
                               className="text-red-500 hover:text-red-700"
                              >
                               <XMarkIcon className="h-5 w-5" />
                             </button>
                          </div>
                        ))}
                      </div>
                      
                      <button
                        type="button"
                        onClick={handleUploadPdfs}
                        disabled={uploadingPdf}
                        className={`w-full py-2 rounded-md text-white font-medium flex items-center justify-center ${uploadingPdf ? 'bg-gray-400' : 'bg-primary hover:bg-primary/90'}`}
                      >
                        {uploadingPdf ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Téléchargement en cours...
                          </>
                        ) : (
                          <>{modalType === 'edit' ? 'Télécharger' : 'Pré-charger'} {pdfFiles.length} fichier{pdfFiles.length > 1 ? 's' : ''} dans {pdfCategories.find(c => c.id === selectedPdfCategory)?.label}</>
                        )}
                      </button>
                    </div>
                  )}
                  
                  {/* Liste des PDFs existants regroupés par catégorie */}
                  {eventPdfs && eventPdfs.length > 0 ? (
                    <div className="space-y-6">
                      {/* Regrouper les PDFs par catégorie */}
                      {pdfCategories.map(category => {
                        // Pour les utilisateurs régie, ne montrer que la catégorie 'divers'
                        if (user.role === 'regie' && category.id !== 'divers') {
                          return null;
                        }
                        
                        // Filtrer les PDFs par catégorie
                        const categoryPdfs = eventPdfs.filter(pdf => pdf.category === category.id);
                        
                        // Ne pas afficher les catégories vides
                        if (categoryPdfs.length === 0) return null;
                        
                        return (
                          <div key={category.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                            <h4 className="font-medium text-primary mb-3 flex items-center">
                              <DocumentTextIcon className="h-5 w-5 mr-2" />
                              {category.label} ({categoryPdfs.length})
                            </h4>
                            
                            <div className="grid grid-cols-1 gap-3">
                              {categoryPdfs.map(pdf => (
                                <div key={pdf.id} className="flex items-center justify-between bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-all duration-200 hover:border-primary/30">
                                  <div className="flex items-center flex-grow mr-4">
                                    <div className="bg-red-50 p-2 rounded-lg mr-4">
                                      <DocumentTextIcon className="h-8 w-8 text-red-500" />
                                    </div>
                                    <div className="min-w-0 flex-grow">
                                      <div className="flex items-center">
                                        <p className="font-medium text-gray-900 mb-1 truncate max-w-xs" title={pdf.originalName}>
                                          {pdf.name || 'Document PDF'}
                                        </p>
                                        <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full">
                                          PDF
                                        </span>
                                      </div>
                                      <p className="text-sm text-gray-500 flex items-center">
                                        <ClockIcon className="h-4 w-4 mr-1" />
                                        Ajouté le {formatPdfDate(pdf.createdAt)}
                                      </p>
                                    </div>
                                  </div>
                                  <div className="flex space-x-3 flex-shrink-0">
                                    <button
                                      type="button"
                                      onClick={() => handleDownloadPdf(pdf)}
                                      className="p-2.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors flex items-center"
                                      title={`Télécharger ${pdf.originalName || 'le document'}`}
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                      </svg>
                                    </button>
                                    {/* Masquer le bouton de suppression pour les utilisateurs régie */}
                                    {user.role !== 'regie' && (
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          confirmPdfDelete(pdf, e);
                                        }}
                                        className="p-2.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors flex items-center"
                                        title="Supprimer le document"
                                      >
                                        <TrashIcon className="h-6 w-6" />
                                      </button>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                      
                      {/* Afficher les PDFs sans catégorie (pour la compatibilité avec les anciens PDFs) */}
                      {/* Pour les utilisateurs régie, ne pas afficher les PDFs non catégorisés */}
                      {user.role !== 'regie' && eventPdfs.filter(pdf => !pdf.category).length > 0 && (
                        <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                          <h4 className="font-medium text-primary mb-3 flex items-center">
                            <DocumentTextIcon className="h-5 w-5 mr-2" />
                            Non catégorisés ({eventPdfs.filter(pdf => !pdf.category).length})
                          </h4>
                          
                          <div className="grid grid-cols-1 gap-3">
                            {eventPdfs.filter(pdf => !pdf.category).map(pdf => (
                              <div key={pdf.id} className="flex items-center justify-between bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-all duration-200 hover:border-primary/30">
                                <div className="flex items-center flex-grow mr-4">
                                  <div className="bg-red-50 p-2 rounded-lg mr-4">
                                    <DocumentTextIcon className="h-8 w-8 text-red-500" />
                                  </div>
                                  <div className="min-w-0 flex-grow">
                                    <div className="flex items-center">
                                      <p className="font-medium text-gray-900 mb-1 truncate max-w-xs" title={pdf.originalName}>
                                        {pdf.name || 'Document PDF'}
                                      </p>
                                      <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full">
                                        PDF
                                      </span>
                                    </div>
                                    <p className="text-sm text-gray-500 flex items-center">
                                      <ClockIcon className="h-4 w-4 mr-1" />
                                      Ajouté le {formatPdfDate(pdf.createdAt)}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex space-x-3 flex-shrink-0">
                                  <button
                                    type="button"
                                    onClick={() => handleDownloadPdf(pdf)}
                                    className="p-2.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors flex items-center"
                                    title={`Télécharger ${pdf.originalName || 'le document'}`}
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                    </svg>
                                  </button>
                                  {/* Masquer le bouton de suppression pour les utilisateurs régie */}
                                  {user.role !== 'regie' && (
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        confirmPdfDelete(pdf, e);
                                      }}
                                      className="p-2.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors flex items-center"
                                      title="Supprimer le document"
                                    >
                                      <TrashIcon className="h-6 w-6" />
                                    </button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-10 bg-gray-50 rounded-lg">
                      <DocumentTextIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-500 mb-1">Aucun document</h3>
                      <p className="text-gray-400">Aucun document PDF n'est associé à cet événement.</p>
                    </div>
                  )}

                  {/* Modal de confirmation de suppression */}
                  {showDeletePdfConfirm && (
                    <div 
                      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]" 
                      onClick={(e) => {
                        e.stopPropagation();
                        // Ne rien faire d'autre pour empêcher la fermeture accidentelle
                      }}
                    >
                      <div 
                        className="bg-white rounded-lg p-6 max-w-sm w-full mx-4" 
                        onClick={(e) => e.stopPropagation()}
                      >
                        <h3 className="text-lg font-medium text-gray-900 mb-4">
                          Confirmer la suppression
                        </h3>
                        <p className="text-gray-500 mb-6">
                          Êtes-vous sûr de vouloir supprimer ce document ? Cette action est irréversible.
                        </p>
                        <div className="flex justify-end space-x-3">
                          <button
                          type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowDeletePdfConfirm(false);
                              setPdfToDelete(null);
                              // Ne pas fermer le modal principal
                            }}
                            className="px-4 py-2 text-gray-500 hover:text-gray-700 font-medium rounded-md hover:bg-gray-100 transition-colors"
                          >
                            Annuler
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleDeletePdf();
                              // Ne pas fermer le modal principal
                            }}
                            className="px-4 py-2 bg-red-600 text-white font-medium rounded-md hover:bg-red-700 transition-colors"
                          >
                            Supprimer
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="flex justify-between mt-6 pt-6 border-t">
                {canUserModifyEvent(currentEvent) && (
                  <div>
                    <button
                      onClick={handleEdit}
                      className="bg-primary hover:bg-primary/90 text-white px-4 py-2 rounded-md mr-2 flex items-center shadow-sm transition-colors"
                    >
                      <PencilIcon className="h-4 w-4 mr-1.5" />
                      Modifier
                    </button>
                  </div>
                )}
                
                {canUserModifyEvent(currentEvent) && (
                  <div className="flex">
                  <button
                    type="button"                                       /* empêche le submit */
                    onClick={(e) => { e.preventDefault(); handleCopyEvent(); }}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md mr-2 flex items-center shadow-sm transition-colors"
                  >
                    <ClipboardIcon className="h-4 w-4 mr-1.5" />
                    Copier
                  </button>
                    
                  <button
                    type="button"                                       /* empêche le submit */
                    onClick={(e) => { e.preventDefault(); handleDelete(currentEvent.id); }}
                    className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md flex items-center shadow-sm transition-colors"
                  >
                    <TrashIcon className="h-4 w-4 mr-1.5" />
                    Supprimer
                  </button>
                  </div>
                )}
                {!canUserModifyEvent(currentEvent) && (
                  <div className="text-gray-500 italic">
                    Vous pouvez uniquement consulter cet événement
                  </div>
                )}
              </div>
            </div>
          ) : (
            // Add/Edit mode - Design amélioré
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Contenu des onglets avec animation de transition */}
              <div className="relative">
                {/* Onglet Général */}
                <div 
                  className={`transition-all duration-300 ${activeModalTab === 'general' ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-full absolute inset-0 pointer-events-none'}`}
                  style={{ transform: activeModalTab === 'general' ? 'translateX(0)' : 'translateX(100px)' }}
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                        <div className="h-3 w-3 bg-primary rounded-full mr-2"></div>
                        Titre
                      </label>
                      <input
                        type="text"
                        name="title"
                        value={newEvent.title}
                        onChange={handleInputChange}
                        className="form-input w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary/20"
                        required
                        placeholder="Titre de l'événement"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                        <ClockIcon className="h-4 w-4 text-primary mr-2" />
                        Début
                      </label>
                      <input
                        type="datetime-local"
                        name="start"
                        value={newEvent.start}
                        onChange={handleInputChange}
                        className="form-input w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary/20"
                        required
                        readOnly={modalType === 'view'}
                        disabled={modalType === 'view'}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                        <ClockIcon className="h-4 w-4 text-primary mr-2" />
                        Fin
                      </label>
                      <input
                        type="datetime-local"
                        name="end"
                        value={newEvent.end}
                        onChange={handleInputChange}
                        className="form-input w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary/20"
                        required
                        readOnly={modalType === 'view'}
                        disabled={modalType === 'view'}
                      />
                    </div>
                    
                    
                    <div className="md:col-span-2 bg-gray-50 p-4 rounded-lg border border-gray-200 shadow-sm">
                      <div className="flex items-center justify-between mb-3">
                        <label className="block text-sm font-medium text-gray-700 flex items-center">
                          <div className="h-4 w-4 rounded-full mr-2 bg-primary"></div>
                          <span className="text-primary">Récurrence de l'événement</span>
                        </label>
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id="isRecurring"
                            name="isRecurring"
                            checked={newEvent.isRecurring}
                            onChange={(e) => {
                              // Ne pas initialiser automatiquement les jours de récurrence
                              setNewEvent({...newEvent, isRecurring: e.target.checked});
                            }}
                            className="h-4 w-4 text-primary rounded border-gray-300 focus:ring-primary"
                            disabled={modalType === 'view'}
                            readOnly={modalType === 'view'}
                          />
                          <label htmlFor="isRecurring" className="ml-2 text-sm text-gray-600 hover:text-primary transition-colors">
                            Créer un événement récurrent
                          </label>
                        </div>
                      </div>
                      
                      {newEvent.isRecurring ? (
                        <div className="transition-all duration-300 space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Jours de récurrence
                            </label>
                            <div className="flex flex-wrap gap-2">
                              {[
                                { value: 1, label: 'Lun' },
                                { value: 2, label: 'Mar' },
                                { value: 3, label: 'Mer' },
                                { value: 4, label: 'Jeu' },
                                { value: 5, label: 'Ven' },
                                { value: 6, label: 'Sam' },
                                { value: 7, label: 'Dim' }
                              ].map((day) => (
                                <button
                                  key={day.value}
                                  type="button"
                                  onClick={() => {
                                    // S'assurer que recurrenceDays est un tableau
                                    const days = Array.isArray(newEvent.recurrenceDays) ? [...newEvent.recurrenceDays] : [];
                                    // Convertir day.value en nombre pour assurer une comparaison cohérente
                                    const dayValue = Number(day.value);
                                    // Vérifier si le jour est déjà dans le tableau (après conversion en nombre)
                                    const index = days.findIndex(d => Number(d) === dayValue);
                                    if (index !== -1) {
                                      // Retirer le jour s'il est déjà sélectionné
                                      days.splice(index, 1);
                                    } else {
                                      // Ajouter le jour s'il n'est pas sélectionné
                                      days.push(dayValue); // Utiliser dayValue qui est déjà converti en nombre
                                    }
                                    // Assurez-vous que tous les jours sont des nombres uniques
                                    const numericDays = [...new Set(days.map(d => Number(d)))];
                                    console.log('Jours de récurrence sélectionnés:', numericDays);
                                    
                                    // Mettre à jour les seriesDates si nécessaire
                                    let updatedSeriesDates = newEvent.seriesDates;
                                    if (newEvent.start && newEvent.recurrenceEndDate) {
                                      updatedSeriesDates = generateSeriesDates(
                                        newEvent.start, 
                                        newEvent.recurrenceEndDate, 
                                        numericDays
                                      );
                                    }
                                    
                                    setNewEvent({
                                      ...newEvent, 
                                      recurrenceDays: numericDays,
                                      seriesDates: updatedSeriesDates
                                    });
                                  }}
                                  className={`px-3 py-1.5 rounded-md text-sm font-medium ${newEvent.recurrenceDays && newEvent.recurrenceDays.some(d => Number(d) === Number(day.value)) 
                                    ? 'bg-primary text-white' 
                                    : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                                >
                                  {day.label}
                                </button>
                              ))}
                            </div>
                          </div>

                          <div>
                            <label htmlFor="recurrenceEndDate" className="block text-sm font-medium text-gray-700 mb-1">
                              Date de fin de récurrence
                            </label>
                            <input
                              type="date"
                              id="recurrenceEndDate"
                              name="recurrenceEndDate"
                              value={newEvent.recurrenceEndDate}
                              onChange={handleInputChange}
                              className="form-input w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary/20"
                              min={newEvent.start ? newEvent.start.split('T')[0] : ''}
                              required={newEvent.isRecurring}
                            />
                          </div>
                          <p className="text-xs text-gray-500 italic bg-white p-3 rounded border border-gray-200">
                            L'événement sera créé pour chaque jour sélectionné entre la date de début et la date de fin de récurrence.
                          </p>
                        </div>
                      ) : (
                        <div className="text-sm text-gray-500 italic bg-white p-3 rounded border border-gray-200">
                          Activez cette option pour créer automatiquement plusieurs occurrences de cet événement sur des jours spécifiques.
                        </div>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                        <MapPinIcon className="h-4 w-4 text-primary mr-2" />
                        {newEvent.type === 'non-disponible' ? 'Salles (sélection multiple)' : 'Salle'}
                      </label>
                      
                      {newEvent.type === 'non-disponible' ? (
                        <MultiRoomSelector
                          rooms={rooms}
                          selectedRoomIds={newEvent.roomIds}
                          onSelectionChange={(data) => {
                            // data contient { roomIds, title, selectedRooms }
                            console.log('=== MULTIROOMSELECTOR ONCHANGE ===');
                            console.log('MultiRoomSelector onSelectionChange called with:', data);
                            console.log('Current newEvent.roomIds before update:', newEvent.roomIds);
                            console.log('New roomIds from selector:', data.roomIds);
                            console.log('===================================');
                            setNewEvent(prev => ({
                              ...prev,
                              roomIds: data.roomIds,
                              title: data.title
                            }));
                            // Marquer les champs comme modifiés pour permettre le bypass des conflits
                            setModifiedFields(prev => ({
                              ...prev,
                              roomIds: true,
                              title: true
                            }));
                          }}
                          userName={`${user.firstName} ${user.lastName}`}
                          disabled={modalType === 'view'}
                        />
                      ) : (
                        // Interface de sélection simple pour les autres types d'événements
                        <select
                          name="roomId"
                          value={newEvent.roomId}
                          onChange={handleInputChange}
                          className="form-input w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary/20"
                          required
                        >
                          <option value="">Sélectionner une salle</option>
                          {rooms.map(room => (
                            <option key={room.id} value={room.id}>{room.name}</option>
                          ))}
                        </select>
                      )}
                    </div>
                
                {/* Champs conditionnels pour la salle Tournée */}
                {rooms.find(room => room.id === parseInt(newEvent.roomId))?.name === 'Tournée' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                        <MapPinIcon className="h-4 w-4 text-primary mr-2" />
                        Adresse
                      </label>
                      <input
                        type="text"
                        name="address"
                        value={newEvent.address}
                        onChange={handleInputChange}
                        className="form-input w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary/20"
                        placeholder="Adresse du lieu de tournée"
                      />
                    </div>
                  </>
                )}
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                    <div className="h-4 w-4 rounded-full mr-2" style={{ backgroundColor: eventTypes.find(t => t.value === newEvent.type)?.color }}></div>
                    Type d'événement
                  </label>
                  <select
                    name="type"
                    value={newEvent.type}
                    onChange={handleInputChange}
                    className="form-input w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary/20"
                    required
                  >
                       {user.role === 'administrateur' ? (
                      // Tous les types pour les administrateurs
                      eventTypes.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))
                    ) : user.isPermanent ? (
                      // 'show', 'rental', 'event', 'cours', 'disponible' et 'non-disponible' pour les utilisateurs permanents
                      eventTypes
                        .filter(type => ['show', 'rental', 'event', 'cours', 'disponible', 'non-disponible'].includes(type.value))
                        .map(type => (
                          <option key={type.value} value={type.value}>{type.label}</option>
                        ))
                    ) : (
                      // Seulement 'non-disponible' pour les utilisateurs normaux
                      eventTypes
                        .filter(type => ['non-disponible'].includes(type.value))
                        .map(type => (
                          <option key={type.value} value={type.value}>{type.label}</option>
                        ))
                    )}
                  </select>
                </div>
                
                

                
                {newEvent.type === 'rental' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                      <div className="h-4 w-4 rounded-full mr-2" style={{ backgroundColor: rentalStatusOptions.find(s => s.value === newEvent.rentalStatus)?.color }}></div>
                      Statut de la location
                    </label>
                    <select
                      name="rentalStatus"
                      value={newEvent.rentalStatus || 'provisional'}
                      onChange={handleInputChange}
                      className="form-input w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary/20"
                    >
                      {rentalStatusOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                
                {newEvent.type === 'personnel' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                      <div className="h-4 w-4 rounded-full mr-2" style={{ backgroundColor: '#000000' }}></div>
                      Type de personnel
                    </label>
                    <select
                      name="personnelType"
                      value={newEvent.personnelType || ''}
                      onChange={handleInputChange}
                      className="form-input w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary/20"
                    >
                      {personnelTypeOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                
                {newEvent.type === 'show' && (
                  <>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                        <div className="h-4 w-4 rounded-full mr-2" style={{ backgroundColor: showTypeOptions.find(t => t.value === newEvent.showType)?.color }}></div>
                        Type de spectacle
                      </label>
                      <select
                        name="showType"
                        value={newEvent.showType}
                        onChange={handleInputChange}
                        className="form-input w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary/20"
                      >
                        {showTypeOptions.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="md:col-span-2 bg-gray-50 p-4 rounded-lg border border-gray-200 shadow-sm">
                      <div className="flex items-center justify-between mb-3">
                        <label className="block text-sm font-medium text-gray-700 flex items-center">
                          <div className="h-4 w-4 rounded-full mr-2 bg-primary"></div>
                          <span className="text-primary">Décor du spectacle</span>
                        </label>
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id="hasDecor"
                            checked={newEvent.hasDecor}
                            onChange={(e) => setNewEvent({...newEvent, hasDecor: e.target.checked})}
                            className="h-4 w-4 text-primary rounded border-gray-300 focus:ring-primary"
                          />
                          <label htmlFor="hasDecor" className="ml-2 text-sm text-gray-600 hover:text-primary transition-colors">
                            Ce spectacle nécessite un décor
                          </label>
                        </div>
                      </div>
                      
                      {newEvent.hasDecor ? (
                        <div className="transition-all duration-300">
                          <textarea
                            name="decorDetails"
                            value={newEvent.decorDetails}
                            onChange={handleInputChange}
                            className="form-textarea w-full h-24 rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary/20"
                            placeholder="Détails du décor (dimensions, matériaux, temps de montage/démontage, besoins techniques spécifiques...)"
                          ></textarea>
                          <p className="text-xs text-gray-500 mt-1 italic">
                            Précisez toutes les informations importantes concernant le décor pour faciliter l'organisation.
                          </p>
                        </div>
                      ) : (
                        <div className="text-sm text-gray-500 italic bg-white p-3 rounded border border-gray-200">
                          Aucun décor n'est prévu pour ce spectacle. Cochez la case ci-dessus si vous souhaitez ajouter des informations sur le décor.
                        </div>
                      )}
                    </div>
                    
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                        <div className="h-4 w-4 rounded-full mr-2" style={{ 
                          backgroundColor: newEvent.showStatus === 'cancelled' 
                            ? showStatusOptions.find(s => s.value === 'cancelled')?.color 
                            : getShowColor(newEvent.showType || 'production', newEvent.showStatus) 
                        }}></div>
                        Statut du spectacle
                      </label>
                      <select
                        name="showStatus"
                        value={newEvent.showStatus}
                        onChange={handleInputChange}
                        className="form-input w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary/20"
                      >
                        {showStatusOptions.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    

                    
                    {/* commande de flyers */}
                    <div className="bg-gray-50 p-3 rounded-md flex items-center">
                      <label htmlFor="flyersOrdered" className="flex items-center cursor-pointer w-full relative">
                      {/* Checkbox cachée */}
                      <input
                        id="flyersOrdered"
                        name="flyersOrdered"
                        type="checkbox"
                        checked={newEvent.flyersOrdered ?? false}
                        onChange={handleInputChange}
                        className="sr-only peer"
                      />

                      {/* Le track du switch */}
                      <div className="block w-10 h-6 rounded-full bg-gray-300 peer-checked:bg-primary transition-colors duration-300"></div>
                      {/* Le knob */}
                      <div className="absolute left-1 top-1/2 transform -translate-y-1/2 w-4 h-4 rounded-full shadow-md bg-white peer-checked:translate-x-4 transition-transform duration-300"> </div>

                      {/* Texte à droite */}
                     <div className="ml-3">
                      <span className="text-sm font-medium text-gray-700 peer-checked:text-primary peer-checked:font-semibold transition-colors duration-300">  Flyers commandés </span>
                    </div>
                 </label>
               </div>

                {/* commande d'Hôtel */}
                <div className="bg-gray-50 p-3 rounded-md flex items-center">
                  <label htmlFor="hotelOrdered" className="flex items-center cursor-pointer w-full relative">
                    {/* Checkbox cachée */}
                    <input
                      id="hotelOrdered"
                      name="hotelOrdered"
                      type="checkbox"
                      checked={newEvent.hotelOrdered ?? false}
                      onChange={handleInputChange}
                      className="sr-only peer"
                    />

                    {/* Le track du switch */}
                    <div className="block w-10 h-6 rounded-full bg-gray-300 peer-checked:bg-primary transition-colors duration-300"></div>
                    {/* Le knob */}
                    <div className="absolute left-1 top-1/2 transform -translate-y-1/2
                                    w-4 h-4 rounded-full shadow-md bg-white
                                    peer-checked:translate-x-4 transition-transform duration-300">
                    </div>

                    {/* Texte à droite */}
                    <div className="ml-3">
                      <span className="text-sm font-medium text-gray-700
                                      peer-checked:text-primary peer-checked:font-semibold
                                      transition-colors duration-300">
                        Hôtel réservé
                      </span>
                    </div>
                  </label>
                </div>

                {/* Bande son/Conduite */}
                <div className="bg-gray-50 p-3 rounded-md flex items-center">
                  <label htmlFor="hasSoundTrack" className="flex items-center cursor-pointer w-full relative">
                    {/* Checkbox cachée */}
                    <input
                      id="hasSoundTrack"
                      name="hasSoundTrack"
                      type="checkbox"
                      checked={newEvent.hasSoundTrack ?? false}
                      onChange={(e) => setNewEvent({...newEvent, hasSoundTrack: e.target.checked})}
                      className="sr-only peer"
                    />

                    {/* Le track du switch */}
                    <div className="block w-10 h-6 rounded-full bg-gray-300 peer-checked:bg-primary transition-colors duration-300"></div>
                    {/* Le knob */}
                    <div className="absolute left-1 top-1/2 transform -translate-y-1/2
                                    w-4 h-4 rounded-full shadow-md bg-white
                                    peer-checked:translate-x-4 transition-transform duration-300">
                    </div>

                    {/* Texte à droite */}
                    <div className="ml-3">
                      <span className="text-sm font-medium text-gray-700
                                      peer-checked:text-primary peer-checked:font-semibold
                                      transition-colors duration-300">
                        Bande son/Conduite régie
                      </span>
                    </div>
                  </label>
                </div>


                
                {/* commande d'Appartement */}  
                <div className="bg-gray-50 p-3 rounded-md flex items-center">
                  <label htmlFor="apartmentOrdered" className="flex items-center cursor-pointer w-full relative">
                    <input
                      id="apartmentOrdered"
                      name="apartmentOrdered"
                      type="checkbox"
                      checked={newEvent.apartmentOrdered ?? false}
                      onChange={handleInputChange}
                      className="sr-only peer"
                    />
                    <div className="block w-10 h-6 rounded-full bg-gray-300 peer-checked:bg-primary transition-colors duration-300"></div>
                    <div className="absolute left-1 top-1/2 transform -translate-y-1/2
                                    w-4 h-4 rounded-full shadow-md bg-white
                                    peer-checked:translate-x-4 transition-transform duration-300">
                    </div>
                    <div className="ml-3">
                      <span className="text-sm font-medium text-gray-700
                                      peer-checked:text-primary peer-checked:font-semibold
                                      transition-colors duration-300">
                        Appartement réservé
                      </span>
                    </div>
                  </label>
                </div>

               {/* commande de Transfert */}
                <div className="bg-gray-50 p-3 rounded-md flex items-center">
                  <label htmlFor="transferOrdered" className="flex items-center cursor-pointer w-full relative">
                    <input
                      id="transferOrdered"
                      name="transferOrdered"
                      type="checkbox"
                      checked={newEvent.transferOrdered ?? false}
                      onChange={handleInputChange}
                      className="sr-only peer"
                    />
                    {/* track */}
                    <div className="block w-10 h-6 rounded-full bg-gray-300 peer-checked:bg-primary transition-colors duration-300"></div>
                    {/* thumb */}
                    <div
                      className="absolute left-1 top-1/2 transform -translate-y-1/2
                                w-4 h-4 rounded-full shadow-md bg-white
                                peer-checked:translate-x-4 transition-transform duration-300"
                    ></div>
                    {/* label */}
                    <div className="ml-3">
                      <span
                        className="text-sm font-medium text-gray-700
                                  peer-checked:text-primary peer-checked:font-semibold
                                  transition-colors duration-300"
                      >
                        Transfert réservé
                      </span>
                    </div>
                  </label>
                </div>

                {/* DECLA SACD */}
                <div className="bg-gray-50 p-3 rounded-md flex items-center">
                  <label htmlFor="sacdDeclared" className="flex items-center cursor-pointer w-full relative">
                    <input
                      id="sacdDeclared"
                      name="sacdDeclared"
                      type="checkbox"
                      checked={newEvent.sacdDeclared ?? false}
                      onChange={handleInputChange}
                      className="sr-only peer"
                    />
                    {/* track */}
                    <div className="block w-10 h-6 rounded-full bg-gray-300 peer-checked:bg-primary transition-colors duration-300"></div>
                    {/* thumb */}
                    <div
                      className="absolute left-1 top-1/2 transform -translate-y-1/2
                                w-4 h-4 rounded-full shadow-md bg-white
                                peer-checked:translate-x-4 transition-transform duration-300"
                    ></div>
                    {/* label */}
                    <div className="ml-3">
                      <span
                        className="text-sm font-medium text-gray-700
                                  peer-checked:text-primary peer-checked:font-semibold
                                  transition-colors duration-300"
                      >
                        DECLA SACD
                      </span>
                    </div>
                  </label>
                </div>

                {/* DECLA SACEM */}
                <div className="bg-gray-50 p-3 rounded-md flex items-center">
                  <label htmlFor="sacemDeclared" className="flex items-center cursor-pointer w-full relative">
                    <input
                      id="sacemDeclared"
                      name="sacemDeclared"
                      type="checkbox"
                      checked={newEvent.sacemDeclared ?? false}
                      onChange={handleInputChange}
                      className="sr-only peer"
                    />
                    {/* track */}
                    <div className="block w-10 h-6 rounded-full bg-gray-300 peer-checked:bg-primary transition-colors duration-300"></div>
                    {/* thumb */}
                    <div
                      className="absolute left-1 top-1/2 transform -translate-y-1/2
                                w-4 h-4 rounded-full shadow-md bg-white
                                peer-checked:translate-x-4 transition-transform duration-300"
                    ></div>
                    {/* label */}
                    <div className="ml-3">
                      <span
                        className="text-sm font-medium text-gray-700
                                  peer-checked:text-primary peer-checked:font-semibold
                                  transition-colors duration-300"
                      >
                        DECLA SACEM
                      </span>
                    </div>
                  </label>
                </div>


               
                      {/* Champ pour le pourcentage de TVA - affiché uniquement si > 0 en mode view */}
                        {(modalType === 'add' || modalType === 'edit' || (modalType === 'view' && newEvent.vatPercentage > 0)) && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                              <div className="h-4 w-4 rounded-full mr-2 bg-red-500"></div>
                              Pourcentage de TVA (%)
                            </label>
                            <input
                              type="number"
                              name="vatPercentage"
                              value={newEvent.vatPercentage ?? ''}
                              onChange={(e) => {
                                // Si la valeur est vide, définir vatPercentage à null
                                if (e.target.value === '') {
                                  setNewEvent({...newEvent, vatPercentage: null});
                                } else {
                                  handleInputChange(e);
                                }
                              }}
                              min="0"
                              max="100"
                              step="0.1"
                              className="form-input w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary/20"
                              placeholder="Ex: 20"
                              disabled={modalType === 'view'}
                            />
                          </div>
                        )}
                      

                     {/* Add ticketing location field when status is ticketsOpen */}
                     {newEvent.showStatus === 'ticketsOpen' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                          <div className="h-4 w-4 rounded-full mr-2 bg-green-500"></div>
                          Lieu de vente des billets
                        </label>
                        <input
                        id="ticketingLocation"
                          type="text"
                          name="ticketingLocation"
                          value={newEvent.ticketingLocation ?? ''}   
                          onChange={handleInputChange}
                          className="form-input w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary/20"
                          placeholder="Ex: Site web, Fnac, etc."
                        />
                      </div>
                     )}
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                        <div className="h-4 w-4 rounded-full mr-2 bg-gold"></div>
                        Pourcentage PROD de co-réalisation (%)
                      </label>
                      <input
                        type="number"
                        name="coRealizationPercentage"
                        value={newEvent.coRealizationPercentage}
                        onChange={handleInputChange}
                        className="form-input w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary/20"
                        min="0"
                        max="100"
                        placeholder="Ex: 20"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                        <div className="h-4 w-4 rounded-full mr-2 bg-primary"></div>
                        Contact Production
                      </label>
                      <input
                        id= "productionContact"
                        type="text"
                        name="productionContact"
                        value={newEvent.productionContact ?? ''}
                        onChange={handleInputChange}
                        className="form-input w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary/20"
                        placeholder="Nom et coordonnées du contact production"
                      />
                    </div>
                    
                    {/* Contact Régisseur - affiché seulement pour les tournées */}
                    {newEvent.roomId && rooms.find(r => r.id === parseInt(newEvent.roomId))?.name === 'Tournée' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                          <div className="h-4 w-4 rounded-full mr-2 bg-primary"></div>
                          Contact Régisseur
                        </label>
                        <input
                          id="stageManagerContact"
                          type="text"
                          name="stageManagerContact"
                          value={newEvent.stageManagerContact ?? ''}
                          onChange={handleInputChange}
                          className="form-input w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary/20"
                          placeholder="Nom et coordonnées du contact régisseur"
                        />
                      </div>
                    )}
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                        <div className="h-4 w-4 rounded-full mr-2 bg-blue-500"></div>
                        Nombre VHR
                      </label>
                      <input
                        id="expectedAttendees"
                        type="number"
                        name="expectedAttendees"
                        value={newEvent.expectedAttendees || 0}
                        onChange={handleInputChange}
                        className="form-input w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary/20"
                        min="0"
                        placeholder="Ex: 10"
                      />
                    </div>
                    
                    {/* Champ jauge pour les tournées */}
                    {newEvent.roomId && rooms.find(r => r.id === parseInt(newEvent.roomId))?.name === 'Tournée' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                          <UserGroupIcon className="h-4 w-4 text-primary mr-2" />
                          Jauge
                        </label>
                        <input
                          type="number"
                          name="jauge"
                          value={newEvent.jauge}
                          onChange={handleInputChange}
                          className="form-input w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary/20"
                          placeholder="Nombre de places"
                          min="0"
                        />
                      </div>
                    )}

                  </>
                )}

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1 mt-2 flex items-center">
                    <ClipboardIcon className="h-4 w-4 text-primary mr-2" />
                    Informations supplémentaires
                  </label>
                  <textarea
                    name="description"
                    value={newEvent.description}
                    onChange={handleInputChange}
                    className="form-input w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary/20"
                    rows="3"
                    placeholder="informations supplémentaire pour l''événement..."
                  ></textarea>
                </div>
              </div>
                </div>
                
                {/* Onglet Utilisateurs */}
                <div 
                  className={`transition-all duration-300 ${activeModalTab === 'users' ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-full absolute inset-0 pointer-events-none'}`}
                  style={{ transform: activeModalTab === 'users' ? 'translateX(0)' : activeModalTab === 'general' ? 'translateX(100px)' : 'translateX(-100px)' }}
                  >
                  <div className="space-y-6">
                    <h3 className="text-lg font-medium text-primary mb-4 flex items-center">
                      <UserGroupIcon className="h-5 w-5 mr-2" />
                      Utilisateurs assignés à cet événement
                    </h3>
                    
                    {renderUserSelectionSection()}
                  </div>
                </div>
                
                {/* Onglet Documents */}
                <div
                  className={`transition-all duration-300 ${
                    activeModalTab === 'documents'
                      ? 'opacity-100 translate-x-0'
                      : 'opacity-0 translate-x-full absolute inset-0 pointer-events-none'
                  }`}
                  style={{
                    transform: activeModalTab === 'documents'
                      ? 'translateX(0)'
                      : 'translateX(-100px)'
                  }}
                >
                  {/* En-tête */}
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-medium text-primary flex items-center">
                      <DocumentTextIcon className="h-5 w-5 mr-2" />
                      Documents associés
                    </h3>
                    {/* Bouton d’upload */}
                    {modalType !== 'view' && (
                      <label className="cursor-pointer bg-primary hover:bg-primary/90 text-white px-4 py-2 rounded-md flex items-center shadow-sm transition-colors">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                        Ajouter un PDF
                        <input
                          type="file"
                          accept=".pdf"
                          className="hidden"
                          onChange={handlePdfChange}
                          multiple
                        />
                      </label>
                    )}
                  </div>

{/* Zone de drag & drop pour l'upload de PDF - visible uniquement en mode add/edit */}
{modalType !== 'view' ? (
  <div 
    className={`border-2 ${isDragging ? 'border-primary bg-primary/10' : 'border-dashed border-gray-300'} rounded-lg p-6 mb-4 text-center transition-colors`}
    onDragOver={(e) => handleDragOver(e)}
    onDragLeave={(e) => handleDragLeave(e)}
    onDrop={(e) => handleDrop(e)}
  >
    <div className="flex flex-col items-center justify-center space-y-2">
      <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
      </svg>
      <p className="text-sm text-gray-600">
        <span className="font-medium">Glissez et déposez</span> vos fichiers PDF ici ou
      </p>
      <label className="cursor-pointer text-primary hover:text-primary/80 font-medium">
        parcourir vos fichiers
        <input 
          type="file" 
          accept=".pdf" 
          className="hidden" 
          onChange={handlePdfChange} 
          multiple 
        />
      </label>
      <p className="text-xs text-gray-500">PDF uniquement (max. 20 Mo par fichier, 10 fichiers max)</p>
    </div>
  </div>
) : null}

{/* Choix de la catégorie - visible uniquement en mode add/edit */}
{modalType !== 'view' ? (
  <div className="mb-4">
    <label className="block text-sm font-medium text-gray-700 mb-2">
      Catégorie du document
    </label>
    <select
      value={selectedPdfCategory}
      onChange={e => setSelectedPdfCategory(e.target.value)}
      className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary"
    >
      {pdfCategories.map(cat => (
        <option key={cat.id} value={cat.id}>
          {cat.label} {cat.multiple ? '(plusieurs fichiers)' : '(1 fichier max)'}
        </option>
      ))}
    </select>
  </div>
) : null}

                  {/* Preview avant upload - visible uniquement en mode add/edit */}
                  {modalType !== 'view' && pdfFiles.length > 0 && (
                    <div className="mb-4">
                      <div className="flex justify-between items-center mb-2">
                        <h4 className="text-sm font-medium text-gray-700">Fichiers à télécharger</h4>
                        <button
                        type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            setPdfFiles([]);
                            setPdfPreviews([]);
                          }}
                          className="text-sm text-red-600 hover:text-red-800"
                        >
                          Effacer la sélection
                        </button>
                      </div>
                      <div className="grid grid-cols-1 gap-2 mb-3">
                        {pdfFiles.map((file, idx) => (
                          <div key={idx} className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg p-3">
                            <div className="flex items-center">
                              <DocumentTextIcon className="h-5 w-5 text-blue-500 mr-2" />
                              <span className="text-sm text-gray-700 truncate max-w-xs">{file.name}</span>
                            </div>
                            <button
                            type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                const nf = [...pdfFiles]; nf.splice(idx, 1); setPdfFiles(nf);
                                const np = [...pdfPreviews]; np.splice(idx, 1); setPdfPreviews(np);
                              }}
                              className="text-red-500 hover:text-red-700"
                            >
                              <XMarkIcon className="h-5 w-5" />
                            </button>
                          </div>
                        ))}
                      </div>
                      <button
                        type="button"
                        onClick={handleUploadPdfs}
                        disabled={uploadingPdf}
                        className={`w-full py-2 rounded-md text-white font-medium flex items-center justify-center ${
                          uploadingPdf ? 'bg-gray-400' : 'bg-primary hover:bg-primary/90'
                        }`}
                      >
                        {uploadingPdf
                          ? (
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                          )
                          : `${modalType === 'edit' ? 'Télécharger' : 'Pré-charger'} ${pdfFiles.length} fichier${pdfFiles.length > 1 ? 's' : ''} dans ${pdfCategories.find(c => c.id === selectedPdfCategory)?.label}`
                        }
                      </button>
                    </div>
                  )}

                  {/* Liste des documents (identique au mode View) */}
                  {eventPdfs.length > 0 ? (
                    <div className="space-y-6">
                      {pdfCategories.map(cat => {
                        const catPdfs = eventPdfs.filter(p => p.category === cat.id);
                        if (!catPdfs.length) return null;
                        return (
                          <div key={cat.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                            <h4 className="font-medium text-primary mb-3 flex items-center">
                              <DocumentTextIcon className="h-5 w-5 mr-2" />
                              {cat.label} ({catPdfs.length})
                            </h4>
                            <div className="grid grid-cols-1 gap-3">
                              {catPdfs.map(pdf => (
                                <div key={pdf.id} className="flex items-center justify-between bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-all duration-200 hover:border-primary/30">
                                  <div className="flex items-center flex-grow mr-4">
                                    <div className="bg-red-50 p-2 rounded-lg mr-4">
                                      <DocumentTextIcon className="h-8 w-8 text-red-500" />
                                    </div>
                                    <div className="min-w-0 flex-grow">
                                      <div className="flex items-center">
                                        <p className="font-medium text-gray-900 mb-1 truncate max-w-xs" title={pdf.originalName}>
                                          {pdf.name}
                                        </p>
                                        <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full">PDF</span>
                                      </div>
                                      <p className="text-sm text-gray-500 flex items-center">
                                        <ClockIcon className="h-4 w-4 mr-1" />
                                        Ajouté le {formatPdfDate(pdf.createdAt)}
                                      </p>
                                    </div>
                                  </div>
                                  <div className="flex space-x-3 flex-shrink-0">
                                    <button type="button" onClick={() => handleDownloadPdf(pdf)} className="p-2.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Télécharger">
                                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                      </svg>
                                    </button>
                                    <button 
                                      type="button"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        confirmPdfDelete(pdf, e);
                                      }} 
                                      className="p-2.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors" 
                                      title="Supprimer"
                                    >
                                      <TrashIcon className="h-6 w-6" />
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )
                      })}
                      {eventPdfs.filter(p => !p.category).length > 0 && (
                        <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                          <h4 className="font-medium text-primary mb-3 flex items-center">
                            <DocumentTextIcon className="h-5 w-5 mr-2" />
                            Non catégorisés ({eventPdfs.filter(p => !p.category).length})
                          </h4>
                          <div className="grid grid-cols-1 gap-3">
                            {eventPdfs.filter(p => !p.category).map(pdf => (
                              <div key={pdf.id} className="flex items-center justify-between bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-all duration-200 hover:border-primary/30">
                                <div className="flex items-center flex-grow mr-4">
                                  <div className="bg-red-50 p-2 rounded-lg mr-4">
                                    <DocumentTextIcon className="h-8 w-8 text-red-500" />
                                  </div>
                                  <div className="min-w-0 flex-grow">
                                    <div className="flex items-center">
                                      <p className="font-medium text-gray-900 mb-1 truncate max-w-xs" title={pdf.originalName}>
                                        {pdf.name || 'Document PDF'}
                                      </p>
                                      <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full">PDF</span>
                                    </div>
                                    <p className="text-sm text-gray-500 flex items-center">
                                      <ClockIcon className="h-4 w-4 mr-1" />
                                      Ajouté le {formatPdfDate(pdf.createdAt)}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex space-x-3 flex-shrink-0">
                                  <button type="button" onClick={() => handleDownloadPdf(pdf)} className="p-2.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Télécharger">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                    </svg>
                                  </button>
                                  <button 
                                    type="button"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      confirmPdfDelete(pdf, e);
                                    }} 
                                    className="p-2.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors" 
                                    title="Supprimer"
                                  >
                                    <TrashIcon className="h-6 w-6" />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-10 bg-gray-50 rounded-lg">
                      <DocumentTextIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-500 mb-1">Aucun document</h3>
                      <p className="text-gray-400">Aucun document PDF n'est associé à cet événement.</p>
                    </div>
                  )}
                </div>

              </div>
              
              <div className="flex justify-between mt-8 pt-4 border-t">
                {/* Bouton d'envoi d'e-mail - affiché uniquement en mode view ou edit pour les événements existants */}
                {(modalType === 'view' || modalType === 'edit') && currentEvent && currentEvent.id && (
                  <button
                    type="button"
                    onClick={async (e) => {
                      e.stopPropagation();
                      try {
                        const response = await api.post(`/events/${currentEvent.id}/send-email`);
                        
                        if (response.data) {
                          alert('E-mail envoyé avec succès aux utilisateurs assignés!');
                        }
                      } catch (error) {
                        console.error('Erreur lors de l\'envoi de l\'e-mail:', error);
                        const errorMessage = error.response?.data?.message || error.message || 'Erreur inconnue';
                        alert(`Erreur lors de l'envoi de l'e-mail: ${errorMessage}`);
                      }
                    }}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md transition-colors flex items-center"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    Envoyer e-mail
                  </button>
                )}
                
                <div className="flex">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!showDeletePdfConfirm) {
                         setShowModal(false);
                       } else {
                         // Fermer le modal de confirmation et permettre la fermeture du modal principal
                         setShowDeletePdfConfirm(false);
                         setPdfToDelete(null);
                         setShowModal(false);
                       }
                    }}
                    className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded-md mr-3 transition-colors"
                  >
                    {modalType === 'view' ? 'Fermer' : 'Annuler'}
                  </button>
                  
                  {(modalType === 'add' || (modalType !== 'view' && canUserModifyEvent(currentEvent))) && (
                    <button
                      type="submit"
                      className="bg-primary hover:bg-primary/90 text-white px-6 py-2 rounded-md shadow-sm transition-colors flex items-center"
                    >
                      {modalType === 'add' ? (
                      <>
                        <PlusIcon className="h-4 w-4 mr-1.5" />
                        Créer
                      </>
                    ) : (
                      <>
                        <CheckIcon className="h-4 w-4 mr-1.5" />
                        Mettre à jour
                      </>
                    )}
                    </button>
                  )}
                </div>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  )}
      
      {/* Dialog for selecting action when clicking on calendar - for all users */}
      {showSelectionDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[85vh] overflow-y-auto">
            <div className="bg-primary text-white px-6 py-4 flex justify-between items-center">
              <h3 className="text-lg font-semibold">Que souhaitez-vous faire ?</h3>
              <button 
                onClick={() => setShowSelectionDialog(false)}
                className="text-white hover:text-gray-200"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            
            <div className="p-6 flex flex-col gap-3">
              {(user.isPermanent || user.role === 'administrateur') && (
                <button
                  onClick={() => {
                    setShowSelectionDialog(false);
                    handleNewEvent(selectedTimeInfo);
                  }}
                  className="bg-primary text-white px-4 py-3 rounded-md flex items-center justify-center"
                >
                  <PlusIcon className="h-5 w-5 mr-2" />
                  {user.role === 'administrateur' ? 'Créer un nouvel événement' : 'Créer un spectacle'}
                </button>
              )}
              
              {/* Bouton pour indiquer son indisponibilité - accessible uniquement aux utilisateurs non permanents et non administrateurs */}
              {!user.isPermanent && user.role !== 'administrateur' && (
                <button
                  onClick={() => {
                    setShowSelectionDialog(false);
                    // Préparer un événement d'indisponibilité
                    const availabilityEvent = {
                      ...selectedTimeInfo,
                      defaultType: 'non-disponible'
                    };
                    handleNewEvent(availabilityEvent);
                  }}
                  className="bg-red-600 text-white px-4 py-3 rounded-md flex items-center justify-center"
                >
                  <CalendarIcon className="h-5 w-5 mr-2" />
                  Indiquer mon indisponibilité
                </button>
              )}
              
              {clipboardEvent && (user.role === 'administrateur' || user.isPermanent) && (
                <button
                  onClick={() => {
                    setShowSelectionDialog(false);
                    handlePasteEvent(selectedTimeInfo);
                  }}
                  className="bg-blue-600 text-white px-4 py-3 rounded-md flex items-center justify-center"
                >
                  <ClipboardIcon className="h-5 w-5 mr-2" />
                  Coller l'événement "{clipboardEvent.title}" (multiple)
                </button>
              )}
              
              <button
                onClick={() => setShowSelectionDialog(false)}
                className="bg-gray-300 text-gray-800 px-4 py-3 rounded-md"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Calendar;
